import { isEditableTarget } from "./_typing-target.js";   // ASK-180 §3.11 item 4: the one focus rule
import * as THREE from "../../vendor/three.module.js";
import { createGltfLoader, getKtx2Loader } from "./_gltf-loader-factory.js";   // shared Draco+KTX2 loader (+ loose-map path)
import { token, tokenAlpha } from "./_tokens.js";
import { fovForAspect } from "./_fov-policy.js";   // ASK-142 R6: the lens holds on both axes (Unreal aspect-ratio axis constraint)
import { createCoverStream } from "./_cover-stream.js";
const ALWAYS_WANTED = Symbol("cover:always");           // a cover a customer asked for directly (hold, page turn)
import { bookBinding } from "./_book-binding.js";   // ASK-139 F-139aj: a book's binding side (the mirror itself is lazy)
import { anchorFromCatName, composedAnchorOf } from "./_composed-attribution.js";   // ASK-147 §14.21
import { fixtureLabelFor } from "./_anchor-name.js";   // D-073: one reader for both anchor namespaces   // ASK-135: canvas chrome resolves the ONE token sheet
import { OrbitControls } from "../../vendor/OrbitControls.js";
import { Reflector } from "../../vendor/Reflector.js";
import { HDRLoader } from "../../vendor/HDRLoader.js";

// Shared helpers — DEG, easings, outline overlays, lane orientation. Lives in
// its own file so fixture modules under `fixtures/` can import them without
// circling back through Stage.
import {
	DEG,
	LID_OPEN_ANGLE,
	COMIC_LEAN_BACK,
	COMIC_LEAN_FORWARD,
	easeInOutQuad,
	getStorageLaneSlotQuaternion,
	boxForwardFromAnchors,
	addOutline,
	removeOutline,
} from "./helpers.js";

// Side-effect import: every fixture module self-registers with the registry
// at load time. New product types are added by dropping a file under
// `fixtures/` and importing it from `fixtures/index.js`.
import "./fixtures/index.js";
import { matchFixture } from "./fixtures/registry.js";
import { resolveAnchorLabel } from "./fixtures/_label-helpers.js";
import { composeTemplate } from "./template-composition.js";
import { applyHeaderSignage, applyFixtureLibraryMaterials, buildSignageMaterial } from "./_material-apply.js";   // DECISION-053 signage hot-swap + ASK-085 fixture material rebinds
import { resolveRenderTier, getTierProfile, classifyGpu, createDynamicResolution } from "./_render-tier.js";   // ASK-084 Part 0: shared capability tier · ASK-142 R7 DRS
import { stepLook, stepWalkVelocity } from "./_feel.js";   // ASK-142 R7: frame-rate-independent look smoothing + walk ramps
import { resolveShellSlot } from "./_shell-slot-resolver.js";   // ASK-084 Part 1: enrol shell surfaces as shadow receivers
import { computeShadowFit, DEFAULT_ROOM_BOUNDS, shadowDarkness, SHADOW_INTENSITY_DEFAULT } from "./_shadow-fit.js";   // ASK-084 Part 1: fit shadow cameras to bounds + shadowIntensity
import { resolveInstanceTransform, persistedPose, yawDelta, freePlaceKnobs, isFreePlaced,
         hostOf, hostFromAncestors, orphanedPlacements } from "./_instance-transform.js";   // DECISION-050 §2: row.<field> ?? shellAnchor.<field> · DECISION-061 §3: {host, local transform}
import { layoutProducts } from "./_socket-layout.js";                 // M8 Slice-1 socket render
import { frameContains, isGrammarSocket, lineMatchesAccepts, typeRegistry, displayScaleFor } from "../../../packages/layout-grammar/src/grammar.js";   // ASK-121: a screen pixel → the region's own `{u,v}`; ASK-126: registry-bridged eligibility
import { loadProductCatalogue, resolveProductRenderSource } from "./engine/_product-catalogue-loader.js";   // P2-E2 ruling 2: a non-comic seat draws its catalogue GLB, or refuses BY NAME
import { deriveFixtureKind, isComposerConvergedKind } from "./_socket-transform.js";
import { lightsForAnchor } from "./_light-instance.js";   // ASK-088 slice-3a: catalogue lights[] → runtime light model
import { resolveFixtureKindBinding } from "./_material-binding.js";   // ASK-085 part-2: fixture finish byKind

// Generic interactable dispatcher — kinds register themselves on import.
// See _interactable-registry.js for the dispatcher contract. All fixture
// kinds are migrated; Bailey's custom AABB hit-test stays inline below as
// a Stage 3 follow-up.
import { runDispatcher, dispatchInteractE, resetDispatcher, activeInteractable } from "./_interactable-registry.js";
import { browseTargetOf, isVerbSeat, seatedGrainForVerb } from "./_browse-door.js";   // ASK-154 seam 2 — the verb pair's eager door (the motion is lazy)
import { slotBrowse } from "../../../packages/layout-grammar/src/browse.js";
import { boxHintFor, boxHintKey } from "./_box-hint.js";
import { toGltfUv } from "./_gltf-uv.js";
import { lidOpenAngleFor } from "./_lid-angle.js";
import { exitRingRadius } from "./_exit-ring.js";   // ASK-159 §2.18dl — the exit ring reads EXIT_ANCHOR's scale (the door's half-width)
import { compileBounded } from "./_compile-bounded.js";   // ASK-145 §11.9 — a shader warm-up that stops at its bound   // ASK-106 — the authored open angle, not the constant   // F-CU16b — procedural media geometry speaks the glTF UV convention   // F-CU16a — the box prompt is (hovered × open), not hover alone
import "./_interactable-kinds/vinyl-bin.js";
import "./_interactable-kinds/stack-table.js";
import "./_interactable-kinds/pile-table.js";
import "./_interactable-kinds/book-row.js";
import "./_interactable-kinds/spinner-rack.js";
import "./_interactable-kinds/wall-comic.js";
import "./_interactable-kinds/vinyl-wall-shelf.js";
import "./_interactable-kinds/longbox-lid.js";
import "./_interactable-kinds/seated-item.js";   // ASK-137 F-115k — a product standing on a holder's surface
import { baileyKind as _bk } from "./_interactable-kinds/bailey.js";

// Physics world — DECISION-019 sub-decision 5. World is created on scene
// compose with gravity from scene.gravity (Earth default); stepped each
// frame in updateAnimations. No bodies register today; future fixture /
// product placement systems consume the world reference via stage.physicsWorld.
import { resolveGravity } from "./_scene-gravity.js";   // the cannon-es world is lazy: `physics()` (ASK-177 §13a item 5)

// Asset-URL resolver — DECISION-020 Phase 1 dual-path. Prefers Forge-CDN
// `assetKey` (constructs ${VITE_SCENE_CDN_URL}/${key} — key is the full canonical path), falls back
// to legacy `assetUrl` when assetKey is absent. Migration completes at
// Phase 3 when Forge stops emitting assetUrl.
import { resolveProjectAssetUrl, resolveBindingAssetUrl, describeAssetSource } from "./_asset-url-resolver.js";
import { loadFixtureCatalogue, isCatalogueShared } from "./_fixture-catalogue-loader.js";

/** The tallest rise one walking step may climb (ASK-159 §2.18aa; Unreal MaxStepHeight 0.45, Unity stepOffset 0.3). */
const MAX_STEP_UP_M = 0.35;
// engine/index.js side-effect imports register all primitives + bundled
// manifests on module load. composeFixture is invoked in the fixture-
// build loop when a manifest is available for the kind.
import { composeFixture, loadFixtureManifest, cloneInWorldFrame } from "./engine/index.js";
import { declaredInteractions, composerOwnsKind } from "./_kind-declarations.js";   // ASK-159 §2.18gb ruling 7: what a kind DECLARES, never a name list

// The build banner reads the page's own `kapowx-build` stamp (sha + builtAt, written at build) — never a hand-bumped
// string (ASK-159 §2.17 item 8: it said 2026-05-08-v21 for five months).
console.log(`[kapowx stage] build ${(typeof document !== "undefined" && document.querySelector('meta[name="kapowx-build"]')?.getAttribute("content")) || "unstamped (dev server)"}`);
import {
	boxedComicReferenceMeters,
	optionalComicNodes,
	requiredComicNodes,
	resolveComicMaterialSlots,
} from "../authoring/contracts.js";

const TABLE_MATERIAL_COLORS = {
	"Walnut Display": "#7c5230",
	"Painted Pine": "#d6c8b0",
	"Industrial Steel": "#8a9099",
};

// D-076 cl.5 — the render layer a HELD object is drawn on, over the world (see `_liftHeld`).
const HELD_LAYER = 5;

export class RuntimeTestStage {
	constructor({ mount, onComicSelect, onBoxHover, onHeroPosUpdate, onPortalEnter }) {
		this.mount = mount;
		// ASK-142 R1 — every hold/release already reports here (18 call sites, one per hero kind): the
		// held product's faces swap shelf → hero on hold and back on release, behind the same seam.
		this.onComicSelect = (payload) => { try { this._onHoldChange(payload?.product || null); } catch (err) { console.warn("[cover-lod] hold swap failed:", err?.message || err); } onComicSelect?.(payload); };
		this.onBoxHover = onBoxHover || null;
		this.onHeroPosUpdate = onHeroPosUpdate || null;
		this.onPortalEnter = onPortalEnter || null;
		this.activeTriggers = [];
		this._armedTriggers = new Set();
		this._bindSelectionChrome();   // ASK-128 §chrome — the ONE subscription that owns the selection chrome
		this.textureLoader = new THREE.TextureLoader();
		// Shared GLTFLoader (Draco geometry + KTX2/Basis textures) from the loader factory —
		// the SAME singletons the studio viewport + loose-map loader (loadKtx2Map) use, so a
		// single KTX2 detectSupport() covers embedded AND loose maps (DECISION-053 Slice-2:
		// folded stage.js onto the factory, retiring its inline dup). Gates Content's GLB bloat
		// fix (Draco + KTX2, 4–8× less VRAM). detectSupport() needs the renderer → run below,
		// after renderer init.
		this.gltfLoader = createGltfLoader();
		this.textureCache = new Map();
		// ASK-142 R2 — feed imagery streams through ONE pool (bounded, visible-first, off-thread decode);
		// `_textureInflight` dedups a URL two seats ask for while its first fetch is still running.
		this._coverStream = null;   // created once the render tier is known (below) — its raw-picture cap is per tier
		this._textureWanters = new Map();   // baseKey → Set<node> asking for it: a fetch starts when ANY of them is drawn
		this._coverBindings = new Map();    // productId → [{ face, rawUrl, apply(tex), placeholder() }] — the LOD swap's handles
		this._textureInflight = new Map();
		this.raycaster = new THREE.Raycaster();
		this.pointer = new THREE.Vector2();
		this.interactiveMeshes = [];
		this.lidMeshes = [];
		this.comicEntries = [];
		this.currentToken = 0;
		this.heroExtract = null;
		this.clock = new THREE.Clock();
		this.physicsWorld = null;

		this.boxState = "none";
		this.browseIndex = 0;
		this.heroComicIndex = null;
		this.boxEntries = new Map();
		// Tracks the comic_root currently outlined as the box's browse focus.
		// In box-open/browsing state, the comic at browseIndex wears the warm-
		// gold outline so the player can see what E will read before pressing.
		// The keyboard owns this selection — crosshair-over-other-comics-in-box
		// does NOT override it (AAA inventory pattern: d-pad is authoritative).
		this.focusedBoxComicRoot = null;
		// Aim-driven browse mode for vinyl-bin crates. While the crosshair
		// is on any sleeve in a rack, the rack becomes the "active" one:
		// only the sleeve at browseIdx is highlight + E-eligible; ←/→ cycle
		// through the rack's catalog; other fixtures suppress (modal scope).
		// Aiming away (or aiming at a different rack / a vinyl-wall-shelf) resets.
		// Shape: { fixtureIndex, browseIdx, sleeves: [vinylSleeves entries] }.
		this.activeVinylBin = null;
		// Stack-table crates (comics). Each entry in this.stackTables is a
		// physical stack; activeStackTable points at whichever one the
		// crosshair is currently engaged with. Cycle anim re-orders the
		// sleeves so the current top moves to the bottom on arrow press.
		// Shape (entry on this.stackTables[]):
		//   { fixtureIndex, anchorPos, faceDir, sleeves[], cycleCount,
		//     cycleAnim: { progress } | null }
		// activeStackTable is just a reference to one of those entries.
		this.stackTables = [];
		this.activeStackTable = null;

		// E-Open sub-state — Resident-Evil-style "examine the inside" mode.
		// Enters from inspect-hero when the focused product carries
		// `hasInterior=true` + `interiorImages[]`. Replaces the closed-cover
		// hero mesh with a procedural "open book" (two flat pages side-by-
		// side) textured from interiorImages. Arrow keys cycle through page
		// pairs; R closes back to the closed cover. Tracks the procedural
		// mesh + its materials so we can dispose them cleanly on close.
		this.inspectOpened = false;
		this.inspectPageIdx = 0;
		this.openBookGroup = null;
		this.openBookLeftMat = null;
		this.openBookRightMat = null;
		// ASK-174 (Client 5d) — every sound runs through the lazy sound engine (`_sound-engine.js`, `this._sound`): the page-turn
		// rustle is a cue gated by the ONE preference model (Settings → Audio → Cues), no longer its own P toggle.
		this._audioCtx = null;
		this._sound = null;
		// Canonical single-source-of-truth for "is something in hero state".
		// Each per-kind entry method populates this; each exit clears it.
		// Read by _updateEHint, exitHero() dispatcher, and anything else that
		// needs to know what's in focus without inspecting six separate flags.
		// Shape: { kind, product, mesh } where kind is one of
		//   "longbox" | "wall-comic" | "vinyl" | "pile" | "book" | "spinner"
		this.activeFixtureHero = null;
		this.activeBoxId = null;
		this.hoveredBoxId = null;
		this.shopLights = [];
		this.shopFlickers = [];
		// companions: hook ──────────────────────────────────────────────
		// Runtime-only ornaments (Bailey companion dog, future pets).
		// Each entry has a tick(dt) method called from updateAnimations.
		// See assets/js/runtime-test/companions/*.js
		this.companions = [];
		// companions: end ───────────────────────────────────────────────
		this.neonSignMat   = null;
		this.neonSignLight = null;
		this.neonFlicker   = null;
		this.openSignMat   = null;
		this.openSignLight = null;
		this.openFlicker   = null;
		this.fanGroup      = null;
		this.fanSpeed      = 0.5;
		this.motesSystem   = null;
		this.motesVel      = null;
		this._motesBounds  = null;
		this.comicFlipped   = false;
		this.comicFlipAnim  = null;
		this._heroBaseQuat  = new THREE.Quaternion();

		// Wall comic shelf interaction
		this.wallComics = [];         // { mesh, product, worldPos }
		this.wallShelfInnerX = 0;     // east wall inner face X, set in buildWallShelves
		this.hoveredWallComic = null; // currently highlighted mesh
		this.activeWallComic  = null; // { mesh, product, worldPos } while inspecting
		this.wallComicHeroEntry = null; // { entry, fromPos, fromQuat, heroPos, heroQuat, animProgress, returning }

		// Vinyl bin interaction (parallel to wall comics; will merge into a generic
		// "wall product" abstraction once the post-vinyl strategy refactor lands).
		this.vinylSleeves    = [];   // { mesh, product, worldPos, baseQuat }
		this.hoveredVinyl    = null;
		this.vinylHeroEntry  = null; // same shape as wallComicHeroEntry

		// Comic pile state
		this.pileComics = [];        // [{ mesh, product, jitterX, jitterZ }]
		this.pileRightStack = [];    // comic indices bottom→top in right pile
		this.pileLeftStack  = [];    // comic indices bottom→top in left pile
		this.pileTableSurfaceY = 0;
		this.pileCenterLeft  = new THREE.Vector3();
		this.pileCenterRight = new THREE.Vector3();
		this.pileHeroPos  = new THREE.Vector3();
		this.pileHeroQuat = new THREE.Quaternion();
		this.pileState    = "none";  // "none" | "hero"
		this.pileArcAnim  = null;    // { comicIdx, fromPos, toPos, progress } arc in flight
		this.pileHeroEntry = null;   // { comicIdx, fromPos, fromQuat, returning }
		this.hoveredPileComic = null; // mesh of top pile comic when cursor is over it
		this.dustParticles     = null;
		this.dustVelocities    = null;
		this.dustHomePositions = null;
		this.dustRoomBounds    = null;
		// Book shelf interaction
		this.bookEntries    = [];   // [{mesh, basePosition, slideZ}]  slideZ = current z-offset in metres
		this.hoveredBook    = null; // book entry currently under crosshair
		this.bookBrowseIndex = -1;  // index into bookEntries selected by arrow keys, -1 = none
		this.bookState      = "none"; // "none" | "hero"
		this.bookHeroEntry  = null;   // {entry, fromPos, fromQuat, midPos, heroPos, heroQuat, animProgress, returning}

		// Spinner rack interaction
		this.spinnerRack = null; // populated by buildSpinnerRack()

		this.lidNode = null;
		this.lidBaseQuat = new THREE.Quaternion();
		this.lidPivotNode = null;
		this.lidPivotBaseQuat = new THREE.Quaternion();
		this.lidCurrentAngle = 0;
		this.lidTargetAngle = 0;
		this.lidMeshNode = null;
		this.lidMeshBaseY = 0;
		this._lidDebugLogged = false;
		this.cameraAnim = {
			active: false,
			fromPos: new THREE.Vector3(),
			toPos: new THREE.Vector3(),
			fromTarget: new THREE.Vector3(),
			toTarget: new THREE.Vector3(),
			progress: 0,
			duration: 0.75,
		};

		this.activeLaneCenter = new THREE.Vector3();
		this.activeBoxForward = new THREE.Vector3(0, 0, 1);

		this.scene = new THREE.Scene();
		this.scene.background = new THREE.Color("#f3eadc");

		this.camera = new THREE.PerspectiveCamera(42, 1, 0.05, 500);
		this.camera.position.set(0.9, 0.72, 1.25);

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
		// ASK-084 Part 0 — shared render tier (shadows/textures/IBL/LOD/… all read this). Detected
		// once from renderer.capabilities + a coarse GPU/mobile heuristic; ?tier= overrides for
		// testing + render-truth captures. Drives quality knobs via tierProfile (TIER_PROFILES).
		this.renderTier = (() => {
			let override = null, isMobile = false, gpuClass = null;
			try { override = new URLSearchParams(window.location.search).get("tier"); } catch { /* no location */ }
			try { isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(window.navigator?.userAgent || ""); } catch { /* no navigator */ }
			const caps = this.renderer.capabilities || {};
			try {
				const gl = this.renderer.getContext();
				const ext = gl.getExtension("WEBGL_debug_renderer_info");
				this.gpuRenderer = ext ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "") : "";   // ASK-142 R7: the Report names the GPU
				gpuClass = classifyGpu(this.gpuRenderer);
			} catch { /* debug_renderer_info unavailable */ }
			const t = resolveRenderTier({ override, isWebGL2: caps.isWebGL2 !== false, isMobile, maxTextureSize: caps.maxTextureSize, gpuClass });
			console.log(`[render-tier] ${t} (mobile=${isMobile}, maxTex=${caps.maxTextureSize}, gpu=${gpuClass || "?"}${override ? `, override=${override}` : ""})`);
			return t;
		})();
		this.tierProfile = getTierProfile(this.renderTier);
		this._coverStream = createCoverStream({
			upload: (tex) => this.renderer?.initTexture?.(tex),
			ktx2Loader: () => getKtx2Loader(this.renderer),
			maxRawSide: this.tierProfile.rawPictureMaxSide || 0,   // ASK-142 §14.2 — the uncooked fallback obeys the tier
		});
		// ASK-084 Part 2 — lighting/shadow model override for A/B captures (?model=A|B). Null → the
		// recipe's shadows.model, else the tier default (high→A directional, else B contact).
		try { this.shadowModelOverride = new URLSearchParams(window.location.search).get("model"); } catch { this.shadowModelOverride = null; }
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.tierProfile.pixelRatioCap));
		// ASK-142 R7 — dynamic resolution: the tier's DPR ladder, stepped down when the rolling frame time
		// passes 20 ms and probed back up when it recovers (`_render-tier.js` createDynamicResolution).
		// `?drs=0` pins the DPR (render-truth captures want a fixed drawing buffer).
		try { this._drsOff = new URLSearchParams(window.location.search).get("drs") === "0"; } catch { this._drsOff = false; }
		try { this._browsePoseOff = new URLSearchParams(window.location.search).get("browsePose") === "0"; } catch { this._browsePoseOff = false; }   // ASK-142 F-142e opt-out (A/B captures)
		this._newDrs();
		this._frameMs = new Float32Array(600);   // the last ~10 s of frame intervals, for the Report's fps / worst frame
		this._frameN = 0;
		this.renderer.shadowMap.enabled = true;
		// VSM (variance) soft shadows — PCFSoftShadowMap is deprecated in this three
		// build (silently falls back to hard PCFShadowMap + logs a warning). VSM honours
		// light.shadow.radius/blurSamples so the recipe's `softness` knob still softens
		// the contact-shadow edge. (WS4 contact shadows, walkthrough fix.)
		this.renderer.shadowMap.type = THREE.VSMShadowMap;
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.0;
		this.mount.appendChild(this.renderer.domElement);

		// KTX2/Basis transcoding picks the GPU-native compressed format (ASTC/
		// ETC/BC/PVRTC) by probing renderer capabilities — must run after the
		// WebGLRenderer exists. Detects the SHARED singleton (used by both the
		// GLTFLoader and loadKtx2Map), so embedded + loose KTX2 both transcode.
		getKtx2Loader(this.renderer);

		this.controls = new OrbitControls(this.camera, this.renderer.domElement);
		this.controls.enableDamping = true;
		this.controls.zoomToCursor = true;
		this.controls.minDistance = 0.18;
		this.controls.maxDistance = 24;
		this.controls.enableZoom = false;
		// Disable OrbitControls' click-drag rotate — replaced by pointer-lock
		// mouse-look (rotates camera in place, no walls clipped through).
		this.controls.enableRotate = false;
		this.controls.target.set(0, 0.18, 0);

		this.controls.addEventListener("change", () => {
			clearTimeout(this._camLogThrottle);
			this._camLogThrottle = setTimeout(() => this._logCameraPos("orbit"), 600);
		});

		// Pointer-lock mouse-look state (FPS / Minecraft pattern)
		// ASK-142 R7: yaw/pitch is the rig's TARGET (what the pointer asked for); viewYaw/viewPitch is what the
		// camera shows, eased toward it each frame (`_tickLook`). `easing` is true only while they differ.
		this._mouseLook = { yaw: 0, pitch: 0, viewYaw: 0, viewPitch: 0, easing: false, sensitivity: 0.0022, locked: false };
		this._setupPointerLockControls();
		this._setupEditCameraGuards();   // ASK-087 slice-c amend: Edit wheel/pinch/dblclick guards
		// ASK-139 F-139c — the phone walk (left-half stick · right-half drag-look · tap = interact) is a
		// LAZY chunk loaded only where a finger can be the pointer: a desktop never pays for it, and a
		// phone has it long before the shop's world is up for the first touch.
		try {
			if (window.matchMedia?.("(any-pointer: coarse)").matches) {
				import("./_touch-walk.js").then((m) => m.installTouchWalk(this))
					.catch((err) => console.warn(`[touch] walk unavailable — ${err?.message || err}`));
			}
		} catch { /* no matchMedia */ }

		this.stageRoot = new THREE.Group();
		this.scene.add(this.stageRoot);

		// ── Camera debug ──────────────────────────────────────────────────────
		this._camLogThrottle = null;
		// Expose on window so devtools console can call kapowxCam() any time.
		window.kapowxCam = () => this._logCameraPos("manual");
		console.log(
			"%c[KapowX] Camera debug active — call kapowxCam() in console for a snapshot",
			"color:#e6a347;font-weight:bold",
		);

		this.roomRoot = new THREE.Group();
		this.roomRoot.name = "roomRoot";
		this.scene.add(this.roomRoot);

		// Base lights — warm dim ambient + soft fill. Shop-specific point lights are
		// added via addAtmosphericLighting() when a full scene is loaded.
		this.ambientLight = new THREE.AmbientLight(0xffe8cc, 0.32);
		this.scene.add(this.ambientLight);

		this.fillLight = new THREE.DirectionalLight(0xfff4e0, 0.22);
		this.fillLight.position.set(-1.5, 3, 2.5);
		this.fillLight.castShadow = false;   // ASK-084: fill NEVER casts (unconfigured 512/500 map = the
		//                                      Bailey-tail square artifact). The per-mode owner's fitted
		//                                      caster (_configureShadows) is the only shadow source.
		this.scene.add(this.fillLight);

		this.heroSpotLight = new THREE.PointLight(0xffffff, 0, 3, 1.5);
		this.heroSpotLight.visible = false;
		this.scene.add(this.heroSpotLight);

		this.floor = new THREE.Mesh(
			new THREE.CircleGeometry(2.2, 48),
			new THREE.MeshStandardMaterial({
				color: new THREE.Color("#e8dccb"),
				roughness: 0.95,
				metalness: 0.01,
			}),
		);
		this.floor.rotation.x = -Math.PI * 0.5;
		this.floor.receiveShadow = true;
		this.scene.add(this.floor);

		this._heldKeys = new Set();
		this._walkSpeed = 1.8; // m/s base

		this.handleResize = this.handleResize.bind(this);
		this.handlePointer = this.handlePointer.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleKeyUp = this.handleKeyUp.bind(this);
		this.handleWheel = this.handleWheel.bind(this);
		this.animate = this.animate.bind(this);
		this._wheelCooldown = false;

		window.addEventListener("resize", this.handleResize);
		// ASK-142 R6 — a phone rotation resizes the MOUNT, and on iOS `resize` can fire before layout has
		// settled (the read would size the camera to the old shape). Observing the mount itself is the
		// platform's answer: it fires once the box has its new size, whatever caused the change.
		if (typeof ResizeObserver === "function") {
			this._mountResizeObserver = new ResizeObserver(() => this.handleResize());
			this._mountResizeObserver.observe(this.mount);
		}
		window.addEventListener("keydown", this.handleKeyDown);
		window.addEventListener("keyup", this.handleKeyUp);
		this.renderer.domElement.addEventListener("pointerdown", this.handlePointer);
		this.renderer.domElement.addEventListener("pointermove", this.handlePointerMove);
		// capture:true lets us run before OrbitControls and stopImmediatePropagation when browsing
		this.renderer.domElement.addEventListener("wheel", this.handleWheel, { capture: true, passive: false });

		this.mount.style.position = "relative";
		this.browseControlsEl = this.createBrowseControls();
		// Insert between the stage canvas and the stage-copy block so it lives
		// outside the canvas element and receives pointer events cleanly.
		const afterMount = this.mount.nextSibling;
		const controlsParent = this.mount.parentElement || this.mount;
		controlsParent.insertBefore(this.browseControlsEl, afterMount);

		this._eHintEl = document.createElement("div");
		this._eHintEl.className = "kapowx-e-hint";
		this._eHintEl.hidden = true;
		controlsParent.appendChild(this._eHintEl);

		this._initCompass(controlsParent);

		this.handleResize();
		this.animate();
	}

	// Skyrim/Far-Cry-style compass strip at top centre. Shows cardinal directions
	// scrolling under a centre pointer, tick marks every 15°, waypoint markers for
	// scene portals/exits, and a numeric heading readout. Convention: +Z = North,
	// +X = East (atan2 of forward XZ projection).
	_initCompass(parent) {
		this._compassEl = document.createElement("div");
		this._compassEl.className = "kapowx-compass";

		this._compassCanvas = document.createElement("canvas");
		this._compassCanvas.width = 480;
		this._compassCanvas.height = 44;
		this._compassEl.appendChild(this._compassCanvas);

		this._compassHeadingEl = document.createElement("div");
		this._compassHeadingEl.className = "kapowx-compass__heading";
		this._compassHeadingEl.textContent = "000°  N";
		this._compassEl.appendChild(this._compassHeadingEl);

		parent.appendChild(this._compassEl);
	}

	/**
	 * ASK-128 §walk-polish (a) — THE NORTH ROSE, for the view that is read as a MAP.
	 *
	 * The drawing lives in `studio-v1/ws3/_north-rose.js`, editor-only and lazy, for the same reason
	 * the zone scene does: it is Studio chrome and a customer never sees it. Measured — keeping it here
	 * put the customer's initial JS 0.5 KB over the 1392 KB ceiling and `check-bundle-budget` caught it
	 * before it shipped. What stays in the stage is the SWITCH: which instrument the current view gets,
	 * decided in one place so the two can never both be up, or both be missing.
	 */
	_ensureNorthRose() {
		if (this._northRose || this._northRoseLoading) return this._northRoseLoading;
		const parent = this._compassEl?.parentElement || this.mount;
		if (!parent) return null;
		this._northRoseLoading = import("./studio-v1/ws3/_north-rose.js")
			.then((m) => { this._northRose = m.makeNorthRose(this, parent); })
			.catch((err) => { console.warn("[edit] north rose unavailable", err); });
		return this._northRoseLoading;
	}

	/** ONE instrument per view: the rose in the Studio's orbit camera, the heading tape in Play. */
	_updateNorthRose(headingDeg) {
		const authoring = this._cameraAuthoring === true;
		if (authoring && !this._northRose) { this._ensureNorthRose(); return; }   // arrives next frame
		if (this._northRose) this._northRose.setVisible(authoring);
		if (this._compassEl && this._compassEl.style.display !== (authoring ? "none" : "")) {
			this._compassEl.style.display = authoring ? "none" : "";
		}
		if (authoring && this._northRose) this._northRose.update(headingDeg);
	}

	_updateCompass() {
		if (!this._compassCanvas) return;
		const canvas = this._compassCanvas;
		// §94.12-3 — the phone strip draws its own compact tape (24 px, labels at the 11 px floor), not a squashed one.
		const k = this._compassCompact ? 1 : 0;
		if (canvas.height !== (k ? 24 : 44)) { canvas.width = k ? 280 : 480; canvas.height = k ? 24 : 44; }
		const ctx = canvas.getContext("2d");
		const W = canvas.width;
		const H = canvas.height;

		// Heading from camera forward (XZ projection). +Z = N, +X = E.
		const forward = new THREE.Vector3();
		this.camera.getWorldDirection(forward);
		forward.y = 0;
		if (forward.lengthSq() < 1e-6) return;
		forward.normalize();
		const headingDeg = (Math.atan2(forward.x, forward.z) * 180 / Math.PI + 360) % 360;
		// §walk-polish (a) — the same heading, read by the instrument that suits the view. The rose
		// takes over in the Studio's orbit camera and the tape keeps Play; `_updateNorthRose` owns the
		// swap so there is one place that decides which is on screen.
		this._updateNorthRose(headingDeg);
		if (this._cameraAuthoring === true) return;   // the tape below is not drawn while the rose is up

		const cardinals = [
			{ angle:   0, label: "N",  major: true  },
			{ angle:  45, label: "NE", major: false },
			{ angle:  90, label: "E",  major: true  },
			{ angle: 135, label: "SE", major: false },
			{ angle: 180, label: "S",  major: true  },
			{ angle: 225, label: "SW", major: false },
			{ angle: 270, label: "W",  major: true  },
			{ angle: 315, label: "NW", major: false },
		];

		// Heading readout (e.g., "012°  NNE")
		const nearest = cardinals.reduce((best, c) => {
			const d = Math.abs(((c.angle - headingDeg + 540) % 360) - 180);
			return d < best.d ? { d, label: c.label } : best;
		}, { d: Infinity, label: "" });
		this._compassHeadingEl.textContent =
			`${Math.round(headingDeg).toString().padStart(3, "0")}°  ${nearest.label}`;
		// §94.12-3 — the phone strip fades after 3 s without a turn and returns on the next (CSS decides who fades).
		const now = performance.now();
		if (this._compassLastHeading == null || Math.abs(((headingDeg - this._compassLastHeading + 540) % 360) - 180) > 0.5) { this._compassLastHeading = headingDeg; this._compassTurnAt = now; }
		this._compassEl.classList.toggle("is-idle", now - this._compassTurnAt > 3000);

		ctx.clearRect(0, 0, W, H);

		// ASK-135 / D-072 — the compass tape is CHROME painted into a canvas, so it resolves the
		// same tokens the stylesheet does instead of carrying its own amber. It was the last warm
		// thing in the customer's HUD and a grep of the CSS could never see it.
		const groundFade = tokenAlpha("gg-surface-ground", 0);
		const ground55   = tokenAlpha("gg-surface-ground", 0.55);
		const ground70   = tokenAlpha("gg-surface-ground", 0.70);

		// Faded strip (gradient at edges)
		const bg = ctx.createLinearGradient(0, 0, W, 0);
		bg.addColorStop(0,    groundFade);
		bg.addColorStop(0.15, ground55);
		bg.addColorStop(0.5,  ground70);
		bg.addColorStop(0.85, ground55);
		bg.addColorStop(1,    groundFade);
		ctx.fillStyle = bg;
		ctx.fillRect(0, k ? 0 : 6, W, k ? H : H - 6);

		const visibleSpan = 120; // degrees visible
		const pxPerDeg = W / visibleSpan;
		const cx = W / 2;

		// Minor tick marks every 15° (skip cardinals — they get their own ticks)
		ctx.fillStyle = tokenAlpha("gg-text-body", 0.30);
		for (let a = 0; a < 360; a += 15) {
			if (a % 45 === 0) continue;
			const delta = ((a - headingDeg + 540) % 360) - 180;
			if (Math.abs(delta) > visibleSpan / 2) continue;
			const x = cx + delta * pxPerDeg;
			ctx.fillRect(x - 0.5, k ? 1 : 12, 1, k ? 4 : 6);
		}

		// Cardinal ticks + labels. A bearing is READ, so the tape is set in the mono face
		// (ASK-135 v2.2 names the compass as a mono surface) at the 11 px floor.
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		const monoFace = token("gg-font-mono") || "monospace";
		const inkMajor = token("gg-text-body");
		const inkMinor = tokenAlpha("gg-text-body", 0.65);
		const tickMajor = tokenAlpha("gg-text-body", 1);
		const tickMinor = tokenAlpha("gg-text-body", 0.55);
		for (const { angle, label, major } of cardinals) {
			const delta = ((angle - headingDeg + 540) % 360) - 180;
			if (Math.abs(delta) > visibleSpan / 2) continue;
			const x = cx + delta * pxPerDeg;
			ctx.fillStyle = major ? tickMajor : tickMinor;
			ctx.fillRect(x - 1, k ? 0 : 8, 2, k ? (major ? 6 : 4) : (major ? 14 : 10));
			ctx.fillStyle = major ? inkMajor : inkMinor;
			ctx.font = major ? `600 ${k ? 11 : 13}px ${monoFace}` : `11px ${monoFace}`;
			ctx.fillText(label, x, H * (k ? 0.62 : 0.72));
		}

		// Waypoints — portals (hub) and exits (shop). Use the effective exits
		// (computed in setFullSceneContext from shell EXIT_ANCHOR or scene.exits)
		// rather than scene.exits raw, so the compass agrees with the actual
		// trigger position when the shell overrides scene.exits.
		const cam = this.camera.position;
		const scene = this.currentScene;
		const wps = [];
		// `waypointColor` is the SCENE's own choice and stays authored content. Only the
		// platform's fallback is a token: a portal with no opinion draws in the accent, and
		// an exit — a way OUT, which always succeeds — draws in success.
		if (Array.isArray(scene?.portals)) {
			for (const p of scene.portals) {
				if (!p?.position) continue;
				wps.push({ pos: p.position, color: p.waypointColor || token("gg-accent") });
			}
		}
		const effExits = this._effectiveExits || (Array.isArray(scene?.exits) ? scene.exits : []);
		for (const e of effExits) {
			if (!e?.position) continue;
			wps.push({ pos: e.position, color: token("gg-state-success-text") });
		}
		for (const wp of wps) {
			const dx = wp.pos.x - cam.x;
			const dz = wp.pos.z - cam.z;
			if (dx * dx + dz * dz < 0.04) continue; // skip if standing on it
			const wpHeading = (Math.atan2(dx, dz) * 180 / Math.PI + 360) % 360;
			const delta = ((wpHeading - headingDeg + 540) % 360) - 180;
			if (Math.abs(delta) > visibleSpan / 2) continue;
			const x = cx + delta * pxPerDeg;
			// Filled diamond at the bottom of the strip
			ctx.fillStyle = wp.color;
			ctx.beginPath();
			ctx.moveTo(x,     H - (k ? 6 : 10));
			ctx.lineTo(x - 5, H - (k ? 2 : 4));
			ctx.lineTo(x,     H + 2);
			ctx.lineTo(x + 5, H - (k ? 2 : 4));
			ctx.closePath();
			ctx.fill();
			// Outline for contrast
			ctx.strokeStyle = tokenAlpha("gg-surface-ground", 0.6);
			ctx.lineWidth = 1;
			ctx.stroke();
		}

		// Centre pointer (downward triangle above the strip) — "where you are looking" is
		// an interactive readout, so it is the accent. It was the last amber in the HUD.
		ctx.fillStyle = token("gg-accent");
		ctx.beginPath();
		ctx.moveTo(cx - 7, 0);
		ctx.lineTo(cx + 7, 0);
		ctx.lineTo(cx,     k ? 5 : 8);
		ctx.closePath();
		ctx.fill();
	}

	// ─── Full-scene entry point ─────────────────────────────────────────────────

	/** 🔴 ASK-142 R2 — NO FRAME IS DRAWN OF A HALF-COMPOSED SHOP. The render loop used to keep drawing
	 *  the scene behind the (opaque) loader while compose added fixtures one by one, so every new
	 *  material variant compiled its shader SYNCHRONOUSLY inside a frame (`setProgram → getUniforms →
	 *  onFirstUse`: 50–100 ms stalls, measured by CPU profile on the phone run) — work nobody could
	 *  see, done in the slowest possible way. Now the loop holds while compose runs, compose ends with
	 *  one parallel `compileAsync` of everything it built, and the first frame draws the finished shop.
	 *  A counter, not a flag, so overlapping composes (a hop mid-load) release only when all are done;
	 *  `finally`, so a compose that throws can never leave the canvas frozen. */
	/** Resolves when no full compose is in flight (see rehydrateSeatedProducts). */
	async _composeSettled() {
		while ((this._composeDepth || 0) > 0) await this._composeIdle;
	}

	async setFullSceneContext(args) {
		if (!this._composeDepth) this._composeIdle = new Promise((r) => { this._resolveComposeIdle = r; });
		this._composeDepth = (this._composeDepth || 0) + 1;
		let result;
		try { result = await this._composeFullScene(args); }
		finally {
			this._composeDepth -= 1;
			// Build the ambient graph behind the loader (it is created suspended until a gesture
			// resumes it), so the first touch costs a resume, not the construction.
			if (!this._composeDepth) { this._initAmbientAudio(); this._sound?.setVenue(this.sceneKind); this._resolveComposeIdle?.(); }   // in finally: a compose that throws must not strand a waiting reseat
		}
		// ASK-142 R2d — the FIRST frame of the finished shop is drawn while the loader still covers it.
		// It carries whatever the bounded compileAsync left plus the first shadow-map pass (measured:
		// a 72 ms task at +9 ms after loader-clear, in the frame the customer first sees). Releasing the
		// hold and returning one frame later puts that cost behind the loader. Bounded: a hidden tab
		// (no rAF) returns after 300 ms rather than waiting.
		if (!this._composeDepth) await this._afterFrames(2, 300);
		return result;
	}

	_afterFrames(n, maxMs) {
		return new Promise((resolve) => {
			const timer = setTimeout(resolve, maxMs);
			const step = (k) => (k <= 0 ? (clearTimeout(timer), resolve()) : requestAnimationFrame(() => step(k - 1)));
			step(n);
		});
	}

	async _composeFullScene({ scene, products, projectAssets, templatePresets, templateCatalogue, materialLibrary, templateSurfaceMaterials, fixtureCatalogue, productTypes, productCatalogue, commerce, shopTitle, shopId, librarySha, templatesSha, onProgress }) {
		this.currentToken += 1;
		this._coverBindings.clear();   // ASK-142 R1 — LOD handles belong to the scene that bound them
		this._heldCoverId = null;
		this._heldCoverProduct = null;   // ASK-170 §2: nothing is held in a fresh scene (the presence record's h follows this)
		const token = this.currentToken;
		// ASK-159 §2.18z D3 — the arrival rule is a lazy chunk (the customer-initial budget, §94.12-2), started HERE so it
		// has arrived long before the shell does; awaited at the spawn step below.
		this._spawnClearance ||= import("./_spawn-clearance.js");
		// ASK-099 item 1 — active shopId + boot mint sha, stashed so the lighting-recipe
		// path can resolve per-shop minted env HDRIs (${CDN}/${shopId}/${sha}/env/…) when a
		// shop's shell carries no assetKey of its own (test-shop). See template-composition.js.
		this.currentShopId = shopId || null;
		this._librarySha   = librarySha || null;
		// ASK-124 B3 — the templates-tree pin, so a lighting recipe's env HDRI resolves to
		// the SHARED `templates/${templatesSha}/env/…` key rather than to a per-shop copy.
		this._templatesSha = templatesSha || null;
		// M7 Chunk 3 / 2c — staged scene-load progress: the shell GLB (dominant asset)
		// reports byte-progress here; the app drives a progress bar from it (ASK-080).
		this._sceneLoadProgress = typeof onProgress === "function" ? onProgress : null;
		this.currentScene = scene || null;
		// DECISION-026 Phase 2 — registry-driven `applyProductTextures` reads
		// the per-`kapowxProductType` `surfaceSlots[]` registry from boot.json
		// (per ASK-047 ship). Stash on the stage instance so per-product
		// composer paths can look up without an extra round-trip.
		this._productTypes = Array.isArray(productTypes) ? productTypes : null;
		this._composedProducts = Array.isArray(products) ? products : null;   // ASK-170 §2: a held product another shopper names by id
		// P2-E2 ruling 2 — boot.productCatalogue: where a non-comic product seat's mesh
		// comes from (concrete-type key, or a feed line's renderAssetKey).
		this._productCatalogue = (productCatalogue && typeof productCatalogue === "object") ? productCatalogue : null;
		this.sceneKind = (scene?.sceneKind === "hub") ? "hub" : "shop";
		this.resetStage();

		// Physics world (DECISION-019 sub-decision 5). Recreated per scene so
		// gravity tracks the shell manifest. Earth default until Forge starts
		// emitting scene.gravity from Phase A manifests (ASK-022).
		const g = this.sceneGravity = resolveGravity(scene?.gravity);
		this.physicsWorld = null; this._physicsLoad = null;   // created on demand by `physics()` — the first body's caller
		console.log(`[runtime] scene gravity (${g.x.toFixed(2)}, ${g.y.toFixed(2)}, ${g.z.toFixed(2)}) m/s²${g.isDefault ? " (Earth default — scene.gravity absent)" : " (from scene.gravity)"}`);
		// Reset shell-derived room bounds; populated by _loadShopGlbShell on shell load
		this._shellRoomBounds = null;

		if (this.sceneKind === "hub") {
			return this._setHubSceneContext(scene, projectAssets, token);
		}

		// Strict shop contract: every shop scene must have a `shopGeometryBinding`
		// and the shell GLB must load. See documentation/SHOP_CONTRACT.md.
		let hasBinding = !!scene?.shopGeometryBinding;
		// Graceful fallback for the template-mint synth path (ASK-071): the
		// synth-path scene write emits `projectAssets.shop` (the shell GLB)
		// but drops `scene.shopGeometryBinding` (same family as the dropped
		// fixtures/palette). When the shell asset is unambiguously present we
		// default the binding to it — the exact value demo-store carries — so
		// the shop renders instead of fataling. LOUD warn (not silent): this
		// masks a mint gap that Forge must fix; the warn keeps it visible.
		if (!hasBinding && scene && projectAssets?.shop) {
			scene.shopGeometryBinding = { assetKey: "shop", scale: 1 };
			hasBinding = true;
			console.warn(
				"[shop-contract] scene.shopGeometryBinding missing — defaulting to " +
				"projectAssets.shop {assetKey:'shop',scale:1}. The Forge synth-path mint " +
				"must emit shopGeometryBinding (ASK-071); this fallback is a safety net, not the fix."
			);
		}
		if (!hasBinding) {
			return this._reportShopContractViolation(
				`scene.shopGeometryBinding is required for every shop. ` +
				`Set assetKey to the shell GLB asset id in project.json.`
			);
		}
		// Cache early so _collectShopAnchors (inside _loadShopGlbShell) can register
		// composer-converged catalogue kinds (DECISION-052). Re-set below with siblings too.
		this._fixtureCatalogueCached = fixtureCatalogue || null;
		const shellRoot = await this._loadShopGlbShell(scene, projectAssets, token);
		if (token !== this.currentToken) return createEmptyReport();
		this._prefetchComposeAssets({ fixtureCatalogue, products });   // ASK-142 R2b — see below
		if (!shellRoot) {
			return this._reportShopContractViolation(
				`Shell GLB failed to load (assetKey="${scene.shopGeometryBinding.assetKey}"). ` +
				`Check that the asset is registered in project.json and reachable.`
			);
		}
		// Cache shop-wide comic assets so fixture builders can read them without
		// re-resolving per anchor. Vinyl/listening fixtures ignore these.
		// ASK-142 R2b — started now, awaited after the template materials: nothing in between reads it,
		// so it no longer costs its own round-trip in front of them.
		const sharedComicBinding = scene?.runtimeBindings?.sharedComic || null;
		const comicPending = this.resolveComicTemplate(sharedComicBinding, token, "shared comic");

		// Cache for hot-swap from the Studio overlay. recomposeTemplate()
		// re-runs the composition step against the same shell + freshly
		// updated knob values without a full scene reload.
		this._shellRoot           = shellRoot;
		this._templatePresetsCached  = templatePresets   || null;
		this._templateCatalogueCached = templateCatalogue || null;
		this._materialLibraryCached  = materialLibrary  || null;
		this._templateSurfaceMaterialsCached = templateSurfaceMaterials || null;
		this._projectAssetsCached    = projectAssets     || null;
		this._fixtureCatalogueCached = fixtureCatalogue  || null;
		this._fixtureSignageRebinds  = [];   // per-scene: composeFixture registers header signage for live hot-swap (DECISION-053 Slice-2)
		this._fixtureMaterialRebinds = [];   // per-scene: composeFixture registers fixture library-materials for live repaint (ASK-085)
		this.deselect?.();                   // ASK-087: drop any Edit selection — its anchor is from the old scene
		this._fixtureGroups = new Map();     // ASK-087 slice-2: per-anchor transform handles are per-scene
		this._fixtureContents = new Map();   // ASK-087 slice-2: per-anchor seated-product ownership (socket path)
		this._surfaceRegions  = new Map();   // ASK-121: per-anchor `surface` socket regions, in WORLD metres (drop-target `{u,v}`)
		this._commerceCached         = commerce          || null;
		this._shopTitleCached        = shopTitle         || null;   // ASK-129 F5 — cached beside commerce; recomposeTemplate reads both

		// Template composition: when scene.template is set, swap shell-mesh
		// materials and apply the chosen lighting recipe. Opt-in — when absent,
		// falls through to the legacy warm-interior helper below. Async since
		// DECISION-053 Slice-2 (loose-KTX2 library materials load before the swap).
		const composeResult = await composeTemplate({
			shellRoot,
			scene,
			stage: this,
			templatePresets: templatePresets || null,
			templateCatalogue: templateCatalogue || null,
			materialLibrary: materialLibrary || null,
			templateSurfaceMaterials: templateSurfaceMaterials || null,
			projectAssets:   projectAssets   || null,
			commerce:        commerce        || null,
			shopTitle:       shopTitle       || null,   // ASK-129 F5 — the boot's shop title, for the sign
		});
		const comicResult = await comicPending;
		if (token !== this.currentToken) return createEmptyReport();
		this._currentComicAssets = {
			template: comicResult.template,
			materialSlots: resolveComicMaterialSlots(scene?.projectSettings),
			mode: comicResult.mode,
			assetKey: comicResult.assetKey,   // Client 4ad — the truth read: which key the shared comic resolved from,
			assetUrl: comicResult.assetUrl,   // and the CDN URL it loaded ("" on the procedural fallback)
			errors: comicResult.errors,
			warnings: comicResult.warnings,
		};
		// Lights + signs this composition pass added — used by
		// recomposeTemplate to clear only its own additions, leaving
		// fixture-builder products (which also live in shopLights for
		// scene-cleanup tracking) alone.
		this._currentTemplateLights = composeResult.lightsAdded || [];
		this._currentTemplateSigns  = composeResult.signsAdded  || [];

		// Shell shops always carry their own baked lighting (or accept the warm
		// interior helper). The procedural bulb-grid path is gone with the strict
		// shop contract — every shop has a shell GLB.
		if (!composeResult.lightingHandled) {
			this._applyShellInteriorLighting(scene);
		}

		// Fixtures render through the ONE composer path (DECISION-052 — converged + walkthrough-
		// verified 2026-06-30 / coord 58af9eb). Every fixture kind is registered in
		// `_collectShopAnchors` (via a registry pattern, or — for catalogue-driven kinds with no
		// pattern — derived from the anchor-empty name) then composed below. The parallel socket-
		// render gate, the legacy per-kind builders (old PASS 2), and the `fixtures/*-anchor.js`
		// modules were RETIRED here: the composer consumes the catalogue sockets directly — single-
		// slot via the manifest slot-layout (longbox browse-bin), multi-socket via `_seatTableSocket`
		// (pile-table FEATURED/FAN/PILE), geometry-owned via interactions (spinner friction-spin).
		// Un-ported interaction models (stack-cycle, flip-browse, spine-pickup) are captured in the
		// coordination-repo morgue; their machinery stays live (dormant) in this file.
		const registeredAnchors = this._shopAnchors?.registered;
		// ─── Composer (AAA single-pipeline) — extracted to _composeRegisteredFixtures so the
		//     live re-seat primitive (rehydrateSeatedProducts, ASK-099) runs the SAME pipeline
		//     over the same anchors with a fresh product pool. Anchors composed here are
		//     excluded from the catalogue-instance pass below.
		// 🔴 ASK-142 F-142c — ALWAYS, even on a shell with NO fixture anchors. The pass handles that case
		// itself (it stores the pool + seating signature and runs the zone pass), but this call sat inside
		// `if (registeredAnchors.size > 0)`, so a pre-bake or space-first shell never stored a pool. A
		// merchant's crate then arrived through the /instances hydrate's newcomer pass against NOTHING —
		// measured on the hub ×2: "hydrated newcomer pool: 0 line(s) · assigned <crate>=0 · signature
		// none" → "no vinyl products — rendering empty", while the feed seated both records.
		const composedAnchorIds = await this._composeRegisteredFixtures({ scene, products, fixtureCatalogue, token });
		if (token !== this.currentToken) return createEmptyReport();
		if (registeredAnchors && registeredAnchors.size > 0) {

			// ─── Catalogue-instance renderer (kept) — for catalogue kinds with NO composer
			//     rendering (e.g. decor: lamps, busts). No per-kind builders remain, so every
			//     registered kind is a candidate; composed anchors are skipped.
			if (fixtureCatalogue) {
				await this._renderCatalogueInstances({
					registeredAnchors,
					kindsWithBuilders: new Set(),
					fixtureCatalogue,
					skipAnchorIds: composedAnchorIds,
					token,
				});
			}
		}

		if (token !== this.currentToken) return createEmptyReport();
		// ASK-094: apply persisted uniform scales on the BOOT compose too (not just recompose) — a
		// scaled fixture must render at its size on a fresh load, reading uniformScale off the boot's
		// scene.instances entry (captured into the anchor entry by _collectInstancesAsAnchors above).
		this._applyInstanceScales();
		this.floor.position.y = 0;
		this.setSceneOverviewCamera();

		// Runtime-side metadata publishing: per-anchor capacity + spatial
		// context (proximityToSpawn, nearestFixtures, visibleFromSpawn) so
		// the GCG curator has authoritative numbers for allocation. Mutates
		// scene.semantic.fixtures[] in memory; future GCG-endpoint POST will
		// publish these values to the server.
		this._publishRuntimeMetadata(scene);

		// Decor panel (storefront image / "Coming Soon" billboard). No-op when
		// no `decorations.imagePanel` or `decorations.placeholderPanel` configured.
		this._applyDecorPanel(scene, projectAssets);

		// ASK-159 §2.18z D3 — the engine's arrival rule: no spawn stands inside the exit's ring (see
		// `_spawn-clearance.js`). Applied to the shell's spawns ONCE, here, so the compose arrival, Play's
		// respawn (`_respawnForPlay`) and every probe that reads `spawns.default` agree on one pose.
		try {
			(await this._spawnClearance).clearShellSpawns(this._shopAnchors, this._shellExitRadius(this._shopAnchors?.exit), (pos, dir, far) =>
				this._firstWallHit(new THREE.Vector3(pos.x, pos.y, pos.z), new THREE.Vector3(dir.x, 0, dir.z), far));
		} catch (err) { console.warn("[spawn] arrival rule unavailable:", err?.message || err); }
		if (token !== this.currentToken) return createEmptyReport();

		// Strict spawn: shell markers only. If the requested spawnRef has no
		// matching SPAWN_<ref> empty AND there's no SPAWN default, the contract
		// is violated. No project.json or stair-landing fallbacks.
		const spawnRef    = scene?._activeSpawnRef;
		const shellSpawns = this._shopAnchors?.spawns || null;
		let spawn = null;
		let spawnSource = null;
		if (shellSpawns) {
			if (spawnRef && shellSpawns.named[spawnRef]) {
				spawn = shellSpawns.named[spawnRef];
				spawnSource = `SPAWN_${spawnRef.toUpperCase().replace(/-/g, "_")}`;
			} else if (shellSpawns.default) {
				spawn = shellSpawns.default;
				spawnSource = "SPAWN";
			}
		}
		if (!spawn) {
			const expected = spawnRef
				? `SPAWN_${spawnRef.toUpperCase().replace(/-/g, "_")} (and _LOOK)`
				: "SPAWN (and SPAWN_LOOK)";
			return this._reportShopContractViolation(
				`Shell GLB is missing required marker: ${expected}.`
			);
		}
		const sp = spawn.position, sl = spawn.lookAt;
		console.log(`[spawn] source=${spawnSource} pos=(${sp.x.toFixed(2)},${sp.y.toFixed(2)},${sp.z.toFixed(2)}) look=(${sl.x.toFixed(2)},${sl.y.toFixed(2)},${sl.z.toFixed(2)})`);
		const startPos    = new THREE.Vector3(spawn.position.x, spawn.position.y, spawn.position.z);
		const startTarget = new THREE.Vector3(spawn.lookAt.x, spawn.lookAt.y, spawn.lookAt.z);
		this.closedCameraPos    = startPos.clone();
		this.closedCameraTarget = startTarget.clone();
		this.camera.position.copy(startPos);
		if (this.controls) {
			this.controls.target.copy(startTarget);
			this.controls.update();
		}

		// Strict exit: shell EXIT_ANCHOR is required. Target/spawnRef can be
		// overridden via scene.exits[0] (for cases where authoring tools want
		// to redirect a shop's exit to somewhere other than the hub), but the
		// position/radius come from the shell.
		const shellExitAnchor = this._shopAnchors?.exit || null;
		if (!shellExitAnchor) {
			return this._reportShopContractViolation(
				`Shell GLB is missing required marker: EXIT_ANCHOR.`
			);
		}
		const sceneExits = Array.isArray(scene?.exits) ? scene.exits : [];
		const effectiveExits = [{
			id: "shell-exit",
			label: sceneExits[0]?.label || "Back to street",
			target: sceneExits[0]?.target || "galleria-hub",
			spawnRef: sceneExits[0]?.spawnRef || "from-portal-1",
			position: {
				x: shellExitAnchor.position.x,
				y: shellExitAnchor.position.y,
				z: shellExitAnchor.position.z,
			},
			radius: this._shellExitRadius(shellExitAnchor),
		}];
		console.log(`[exits] count=${effectiveExits.length} source=shell-anchor`);
		for (const e of effectiveExits) {
			const p = e.position;
			console.log(`[exits]   id=${e.id} target=${e.target} spawnRef=${e.spawnRef} pos=(${p.x?.toFixed(2)},${p.y?.toFixed(2)},${p.z?.toFixed(2)}) r=${e.radius}`);
		}
		// Stash effective exits on `this` so the compass HUD and any downstream
		// consumer reads the same source of truth.
		this._effectiveExits = effectiveExits;
		this.activeTriggers = effectiveExits.map(e => ({
			id: e.id,
			position: e.position,
			radius: e.radius ?? 0.5,
			target: e.target,
			spawnRef: e.spawnRef,
			label: e.label || e.id,
		}));
		this._armedTriggers.clear();

		// Green exit waypoints — compact in-shop variant: small floor disc +
		// floating arrow only (no light beam / sky beam, which read as
		// hub-scale wayfinding and feel out of place inside a small room).
		this._buildPortalWaypoints(effectiveExits, "#4ade80", 0.35, { compact: true });
		// D-086 cl.6 — the exit wears the door's "Back to street" nameplate grammar (today's ring until the entrance-6 module)
		import("./_hub-entrance.js").then((m) => m.mountExitNameplate(this, this.activeTriggers[0])).catch((err) => console.warn("[entrance] exit nameplate:", err?.message || err));

		// ASK-142 R2 — compile every shader the composed shop needs BEFORE its first frame, in parallel
		// where the GPU driver allows (KHR_parallel_shader_compile, three's `compileAsync`), instead of
		// synchronously inside the first frames' draw calls (`GetProgramiv` stalls, measured 40–100 ms
		// each on the phone trace). Bounded: a driver that never reports completion costs ≤ 3 s, never a hang.
		// ASK-142 R2c — work that must happen behind the loader if it can (a companion whose bytes have
		// already arrived is parsed and placed HERE, so its shaders join the compile below).
		try { await this.beforeFirstFrame?.(token); } catch (err) { console.warn("[compose] beforeFirstFrame:", err?.message || err); }
		// ASK-142 §12 — the shop's LIGHT is part of the shop being open: its environment HDRI (the 1K LOD
		// on a phone) is fetched since composeTemplate began; finish its decode + PMREM bake HERE, behind
		// the loader, instead of a 50 ms RGBE decode landing in the customer's first second of walking
		// (measured, CPU profile). Bounded: a slow HDRI never holds the shop past 4 s — it lands late.
		if (this._shopEnvPending?.promise) {
			await Promise.race([this._shopEnvPending.promise.catch(() => {}), new Promise((r) => setTimeout(r, 4000))]);
			if (token !== this.currentToken) return createEmptyReport();
		}
		try {
			// ASK-145 §11.9 — bounded AND stopped: a race bounded the await but left three's 10 ms
			// `isReady` poll (a sync GPU round trip) running into the customer's first second (LoAF).
			await compileBounded(this.renderer, this.scene, this.camera, {
				timeoutMs: 3000, label: "shop", log: (this._compileLog ||= []), isCurrent: () => token === this.currentToken,
			});
		} catch (err) {
			console.warn("[compose] shader pre-compile skipped:", err?.message || err);
		}

		return {
			summary: this.sceneSummary(),
			meta: [
				`Click a longbox lid to open it`,
				comicResult.mode === "mesh" ? "shared comic asset bound" : "procedural comic fallback",
			],
			fixture: { label: "Scene render", mode: "scene", errors: [], warnings: [] },
			content: { label: "Comic render", mode: comicResult.mode, errors: [...comicResult.errors], warnings: [...comicResult.warnings] },
		};
	}

	// Hard-fail shop contract violation. Logs to console, surfaces in the
	// runtime-test report, leaves the player in the reset (empty) state so the
	// failure is visually obvious. No silent fallback to a broken-looking
	// scene. See documentation/SHOP_CONTRACT.md.
	_reportShopContractViolation(reason) {
		const fullMsg = `[shop-contract] FATAL: ${reason}`;
		console.error(fullMsg);
		console.error("See documentation/SHOP_CONTRACT.md for the contract spec.");
		// Reset the camera somewhere predictable so the user knows something
		// went wrong (rather than spawning at a random fallback location).
		this.camera.position.set(0, 1.7, 5);
		if (this.controls) {
			this.controls.target.set(0, 1.4, 0);
			this.controls.update();
		}
		this.scene.background = new THREE.Color(0x140404);
		this.activeTriggers = [];
		this._effectiveExits = [];
		return {
			summary: `Shop contract violation — see console.`,
			meta: ["shop-contract:fail"],
			fixture: { label: "Shop", mode: "error", errors: [reason], warnings: [] },
			content: { label: "Shop", mode: "error", errors: [reason], warnings: [] },
		};
	}

	// Loads the shell GLB into roomRoot, applies optional visibility/flat-shade
	// passes from the binding, and harvests anchor empties for the downstream
	// fixture builders. Lighting / decor / triggers / spawn are handled by the
	// caller (`setFullSceneContext`) so this method has a single responsibility.
	async _loadShopGlbShell(scene, projectAssets, token) {
		const binding = scene.shopGeometryBinding || {};
		const slot = binding.assetKey || "shop";  // binding.assetKey is the SLOT name in projectAssets, distinct from the new DECISION-020 assetKey CDN path field on the entry itself.
		const url = resolveProjectAssetUrl(projectAssets, slot);
		if (!url) {
			console.warn(`[shop-shell] missing projectAssets['${slot}'].assetKey / .assetUrl`);
			return null;
		}
		console.log(`[shop-shell] loading ${slot} from ${url} ${describeAssetSource(projectAssets, slot)}`);
		try {
			const gltf = await this.gltfLoader.loadAsync(url, (ev) => {
				if (this._sceneLoadProgress && ev && ev.total) this._sceneLoadProgress({ phase: "scene", loaded: ev.loaded, total: ev.total });
			});
			if (token !== this.currentToken) return null;
			const root = gltf.scene || gltf.scenes?.[0];
			if (!root) {
				console.warn("[shop-shell] GLB loaded but contained no scene root");
				return null;
			}
			const scale = binding.scale ?? 1.0;
			if (scale !== 1.0) {
				root.scale.setScalar(scale);
			}
			root.updateMatrixWorld(true);
			this.roomRoot.add(root);
			this._applyShellVisibilityFilters(root, binding);
			this._shopAnchors = this._collectShopAnchors(root);
			// Resolver dual-path — closes ASK-021 (c) "resolver pivoted off
			// semanticType-matching for Phase B". Today's path: GLB anchors
			// carry `semanticType` (e.g. "LONGBOX_ANCHOR") → matched to
			// fixtureKind via `matchFixture()` in `_collectShopAnchors`.
			// Phase B path (per DECISION-019): scene.instances[] carries
			// explicit `fixtureKind`; no semanticType match needed.
			// `_collectInstancesAsAnchors` (below) reads scene.instances if
			// present and registers them by their explicit fixtureKind. When
			// Forge ships Phase B composer cutover (emits instances[] +
			// drops semanticType from anchors), the new path activates
			// automatically; the legacy path becomes a no-op since
			// matchFixture() returns null without semanticType. No further
			// runtime code change at this seam.
			this._collectInstancesAsAnchors(scene?.instances || [], this._shopAnchors);
			// Derive room bounds (width/depth/height) from the shell's bbox so
			// project.json doesn't need to manually duplicate the GLB footprint.
			// Excludes anchors and bulb meshes so the bbox tracks the *room*, not
			// the smallest fixture poking out at the corner.
			const wallBbox = new THREE.Box3();
			let included = 0;
			root.traverse((node) => {
				if (!node.isMesh || !node.visible) return;
				// Skip fixtures + emissive bulbs etc. — they shouldn't extend the
				// playable bounds. Heuristic: the room shell is named with the
				// `Shell_Wall_*` / `Shell_Floor` / `Shell_Ceiling` convention.
				if (!/^Shell_(Floor|Ceiling|Wall_)/i.test(node.name)) return;
				wallBbox.expandByObject(node);
				included++;
			});
			if (included > 0) {
				const size = new THREE.Vector3();
				wallBbox.getSize(size);
				this._shellRoomBounds = {
					width:  Math.max(1.0, size.x),
					depth:  Math.max(1.0, size.z),
					height: Math.max(1.5, size.y),
				};
				// ASK-087 slice-c amend: keep the shell's true world centre for the Edit-orbit pivot
				// (the room isn't guaranteed to sit on the origin), so orbit is god's-eye not head-turn.
				this._shellRoomCenter = wallBbox.getCenter(new THREE.Vector3());
			} else {
				this._shellRoomBounds = null;
				this._shellRoomCenter = null;
			}
			const a = this._shopAnchors;
			const rb = this._shellRoomBounds;
			// Build a per-fixture-kind summary from the registered Map.
			const kindCounts = [];
			for (const [kind, arr] of a.registered.entries()) {
				kindCounts.push(`${arr.length} ${kind}`);
			}
			console.log(
				`[shop-shell] loaded ${url.split("/").pop()} — anchors: ` +
				`${kindCounts.join(", ") || "(no fixture anchors)"}, ` +
				`exit=${!!a.exit}, ` +
				`spawn-default=${!!a.spawns.default}, ` +
				`spawn-named=[${Object.keys(a.spawns.named).join(",") || "—"}], ` +
				`room=${rb ? `${rb.width.toFixed(1)}×${rb.depth.toFixed(1)}×${rb.height.toFixed(1)}m (from GLB)` : "(no shell walls found)"}`
			);
			if (a.exit) {
				const p = a.exit.position;
				console.log(`[shop-shell]   EXIT at runtime (${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}) radius=${a.exit.scale.x.toFixed(2)}`);
			}
			for (const [ref, val] of Object.entries(a.spawns.named)) {
				const p = val.position, l = val.lookAt;
				console.log(`[shop-shell]   SPAWN[${ref}] pos=(${p.x.toFixed(2)},${p.y.toFixed(2)},${p.z.toFixed(2)}) look=(${l.x.toFixed(2)},${l.y.toFixed(2)},${l.z.toFixed(2)})`);
			}
			return root;
		} catch (err) {
			console.error("[shop-shell] load failed:", err);
			return null;
		}
	}

	// Re-run template composition against the cached shell with whatever
	// is now on `currentScene.template.knobs`. Clears only the prior
	// composition's lights (not the whole `shopLights` array, which also
	// holds fixture-builder products for scene-cleanup tracking) so the
	// products on shelves / racks / tables stay put. Materials get
	// replaced wholesale by composeTemplate (it sets node.material directly)
	// so no per-mesh reset is needed. Used by the Shop Design Studio
	// overlay for live knob preview without a scene reload.
	async recomposeTemplate() {
		if (!this._shellRoot || !this.currentScene) return false;
		// DECISION-050 §2 must be re-applied HERE, not only at shell load. `_collectInstancesAsAnchors`
		// used to run in exactly one place — inside _loadShopGlbShell — which happens once at boot,
		// BEFORE Studio authenticates and hydrates /instances. So on reload the moved row arrived,
		// scene.instances carried it, and nothing ever consumed it: every fixture recomposed at its
		// shell-anchor position and the merchant's move vanished. Hydrate → recompose is precisely the
		// path a reload takes, so the precedence has to be evaluated on it.
		// Idempotent by construction: resolution is against the ORIGINAL shell anchor pose (captured
		// once, see _collectInstancesAsAnchors), never against an already-overridden value — so
		// applying it twice is a no-op, and a row whose transform is REMOVED correctly falls back to
		// the shell anchor instead of freezing at its last moved position.
		this._collectInstancesAsAnchors(this.currentScene?.instances || [], this._shopAnchors);
		const clearTracked = (list) => {
			for (const obj of list) {
				this.scene.remove(obj);
				if (obj.parent) obj.parent.remove(obj);
				const idx = this.shopLights.indexOf(obj);
				if (idx >= 0) this.shopLights.splice(idx, 1);
				// Free GPU resources on disposable meshes (signs).
				if (obj.geometry?.dispose) obj.geometry.dispose();
				if (obj.material?.dispose) obj.material.dispose();
			}
		};
		clearTracked(this._currentTemplateLights || []);
		clearTracked(this._currentTemplateSigns  || []);
		this._currentTemplateLights = [];
		this._currentTemplateSigns  = [];
		const result = await composeTemplate({
			shellRoot:       this._shellRoot,
			scene:           this.currentScene,
			stage:           this,
			templatePresets: this._templatePresetsCached,
			templateCatalogue: this._templateCatalogueCached,
			materialLibrary: this._materialLibraryCached,
			templateSurfaceMaterials: this._templateSurfaceMaterialsCached,
			projectAssets:   this._projectAssetsCached,
			commerce:        this._commerceCached,
			shopTitle:       this._shopTitleCached,      // ASK-129 F5 — survives a recompose, same as commerce
		});
		// 🔴 A SHOP WITHOUT A TEMPLATE STILL HAS MERCHANT FIXTURES. This used to `return false` here,
		// which skipped everything below — including `applyPersistedInstanceTransforms` and
		// `_renderHydratedCatalogueNewcomers`, the two steps that put a merchant's own placements on
		// screen after the /instances hydrate.
		//
		// `composeTemplate` reports `composed: false` for a scene with no `template.id`
		// (template-composition.js:95), and a D-068 space-first shell legitimately has none —
		// `e2e-multiroom` carries `template: {knobs:{shop:{}}}`. So on that shop every merchant
		// placement vanished on reload: the row hydrated, the anchor registered, and the one pass that
		// would have composed it was never reached. MEASURED (ASK-129 Phase C, 2026-09-19): first
		// authenticated load → no node within 240 s, 2/2; calling
		// `_renderHydratedCatalogueNewcomers()` by hand at that exact moment composed it immediately,
		// which is what proved the pass works and simply never ran.
		//
		// Nothing below needs a template: the signage and material rebind loops iterate arrays that are
		// empty when nothing composed, and the lighting fallback at the end is precisely what a
		// template-less shop wants. The RETURN VALUE still reports whether the TEMPLATE composed, so
		// every caller's existing meaning is unchanged — only the work is no longer skipped with it.
		this._currentTemplateLights = result.lightsAdded || [];
		this._currentTemplateSigns  = result.signsAdded  || [];
		// ASK-111 — re-assert the dollhouse after composing. A merchant already IN Edit when a module
		// composes would otherwise get a fresh `Shell_Ceiling` dropped over their head, and every
		// placement ray would start hitting it again. Idempotent, so re-asserting costs nothing.
		if (this._cameraAuthoring) this.setDollhouse(true);
		// Persisted §2 transforms land on the COMPOSED fixtures here. The anchor entries were updated
		// above, but this recompose keeps the existing fixture meshes (see below) — so without this the
		// merchant's move updates data nothing on screen reads.
		this.applyPersistedInstanceTransforms(this.currentScene?.instances || []);
		// ASK-088 slice-3b — render catalogue instances (luminaires / decor) that appeared via a
		// POST-BOOT hydrate. setFullSceneContext runs _renderCatalogueInstances once at load, BEFORE
		// /instances hydrate, and this recompose keeps existing meshes rather than re-instancing — so a
		// luminaire PLACED after boot (its row arrives on the hydrate) would never get geometry without
		// this. Idempotent + cheap: only fires when a registered anchor has no composed mesh yet, and
		// skips every already-composed anchor (builders + boot-time catalogue clones).
		await this._renderHydratedCatalogueNewcomers();
		// 🔴 AND AGAIN, AFTER THEM. The call above lands transforms on fixtures that ALREADY had
		// meshes; a newcomer composed one line earlier was not one of them, so it sat at its anchor
		// pose and the merchant's move was lost on every reload. MEASURED on e2e-multiroom once the
		// newcomer pass started running: persisted (-0.86, -4.22), composed (-0.00, -7.50) — a 3.39 m
		// drift, which is the seed position, i.e. the move never reached the mesh.
		//
		// Idempotent by construction, which is why calling it twice is the right fix rather than a
		// risky move of the first call: resolution is against the ORIGINAL shell-anchor pose captured
		// once in `_collectInstancesAsAnchors`, never against an already-overridden value.
		this.applyPersistedInstanceTransforms(this.currentScene?.instances || []);
		// Live hot-swap: re-apply fixture header signage so a per-anchor knob change
		// (headerSource / label) updates the header without a full scene reload. composeTemplate
		// above re-stashed the shop brand + signMode; the frame meshes persist across a recompose
		// (DECISION-053 Slice-2).
		for (const rb of (this._fixtureSignageRebinds || [])) {
			try { await applyHeaderSignage({ stage: this, scene: this.currentScene, ...rb }); }
			catch (err) { console.warn("[recompose] header signage re-apply failed:", err?.message || err); }
		}
		// ASK-085 — re-apply fixture library materials with the FRESH binding (recomputed here, so
		// knob/override changes repaint fixtures live; frame meshes persist across a recompose).
		for (const rb of (this._fixtureMaterialRebinds || [])) {
			try {
				const fxBinding = this._resolveFixtureBinding(rb.kind, rb.anchorName);
				if (!fxBinding || !this._materialLibraryCached) continue;
				const key = JSON.stringify(fxBinding);
				if (key === rb._appliedKey) continue;   // unchanged binding → keep the applied material (idempotent)
				const nApplied = await applyFixtureLibraryMaterials({
					meshes: rb.meshes, materialSlots: rb.materialSlots, binding: fxBinding,
					materialLibrary: this._materialLibraryCached, renderer: this.renderer, tier: this.renderTier,
					anisotropy: Math.min(8, this.renderer?.capabilities?.getMaxAnisotropy?.() || 8),
				});
				// Idempotence key is set AFTER a successful apply — never before the await, or a throw/
				// zero-match would poison it and permanently skip this rebind (Amendment-3 finding).
				if (nApplied > 0) rb._appliedKey = key;
			} catch (err) { console.warn("[recompose] fixture material re-apply failed:", err?.message || err); }
		}
		// If the recipe somehow doesn't include lighting, fall back to the
		// warm-interior helper rather than leaving the shop in the dark.
		if (!result.lightingHandled) this._applyShellInteriorLighting(this.currentScene);
		this._applyViewModes();   // freshly composed meshes inherit the active Edit view mode
		this._applyInstanceScales();   // ASK-094: re-apply persisted uniform scales after recompose
		// Reports whether the TEMPLATE composed — unchanged for callers. The instance work above now
		// happens either way (see the note at the composeTemplate result).
		return !!result.composed;
	}

	// ─── The ONE fixture-compose pass (DECISION-052 single pipeline) ─────────────────────────────
	// Extracted from setFullSceneContext so the boot compose AND the live re-seat primitive
	// (rehydrateSeatedProducts, ASK-099) run the IDENTICAL pipeline — priority order, per-kind
	// eligibility, manifest preflight, composeFixture. `onlyAnchorNames` (a Set) scopes a re-seat
	// to the anchors that were already composer-composed; null = the boot full pass.
	// Records the seated-products signature so re-seat calls can no-op on an unchanged pool.
	async _composeRegisteredFixtures({ scene, products, fixtureCatalogue, token, onlyAnchorNames = null }) {
		const composedAnchorIds = new Set();
		const registeredAnchors = this._shopAnchors?.registered;
		if (!registeredAnchors || registeredAnchors.size === 0) {
			// A shell with no fixture anchors can still SELL: zone stock seats on the shell's own floor
			// regions and needs no anchor at all. Returning here without the zone pass is what made a
			// space-first shop — the whole point of which is stock without baked fixtures — draw an
			// empty room. It is the same one pipeline; only the fixture half has nothing to do.
			// Bookkeeping first, for the reason spelled out at the end of this function: nothing may
			// await while another path can still read the previous pool.
			this._seatedSignature = this._seatingSignature(products);
			this._seatedProducts = Array.isArray(products) ? products : [];
			await this._composeZoneSeats({ scene, products, token });
			return composedAnchorIds;
		}
		// Compose order — stable for predictable scene-load timing + visual layering. With
		// GCG-driven per-anchor assignments this no longer affects who-gets-what.
		const FIXTURE_PRIORITY = {
			"featured-comic": 0, "wall-shelf": 1, "vinyl-wall-shelf": 1, "book-row": 2,
			"fan-table": 3, "stack-table": 3, "pile-table": 4, "spinner-rack": 5,
			"vinyl-bin": 5, "longbox": 9, "shortbox": 9,
		};
		const orderedKinds = [...registeredAnchors.keys()].sort(
			(a, b) => (FIXTURE_PRIORITY[a] ?? 50) - (FIXTURE_PRIORITY[b] ?? 50)
		);

		// Product-type filter comes from the catalogue sockets' acceptedProductTypes (the
		// registry's acceptsProductTypes retired with the builders); no sockets / no types →
		// pass all (the manifest slot-layout reads productType itself).
		// ASK-126 S3.3: matched through the SAME registry bridge `admits` uses (raw id | tagPath),
		// because a socket's `accepts` are tag paths and a raw-id compare refused every line whose
		// type wasn't ALSO in the legacy id list — a decor line typed under `product.decor.*` never
		// seated on a socket accepting `product`, while the panel's own admit said yes.
		const eligibilityRegistry = typeRegistry(this._productTypes);
		for (const kind of orderedKinds) {
			const anchorList = registeredAnchors.get(kind);
			if (!Array.isArray(anchorList) || anchorList.length === 0) continue;
			const catSockets = fixtureCatalogue?.[kind]?.sockets;
			const acceptedTypes = Array.isArray(catSockets)
				? [...new Set(catSockets.flatMap((s) => s.acceptedProductTypes || s.accepts || []))]
				: null;
			const eligibleProducts = Array.isArray(acceptedTypes) && acceptedTypes.length > 0
				? (products || []).filter((p) => lineMatchesAccepts(acceptedTypes, p, eligibilityRegistry))
				: (products || []);
			for (const anchor of anchorList) {
				if (token !== this.currentToken) return composedAnchorIds;
				if (onlyAnchorNames && !onlyAnchorNames.has(anchor.name)) continue;
				try {
					const manifest = await loadFixtureManifest({ catalogue: fixtureCatalogue, kind });
					if (!manifest) continue;
					// MOVE-PATH manifest adoption for BUILDER fixtures is HELD (compose-stash not re-enabled):
					// the -02 longbox manifest carries `minClearance.front: 0.4`, which is 8× the client
					// default and freezes the authored ~0.4m counter packing — re-breaking slice-2c's hand-
					// rearrange fix (assist can find no valid pose near the packed counter). The RULED surface
					// vocab (longbox → [floor, counter-top]) is instead reflected in the client DEFAULTS with
					// slice-2c's working clearance, so counter-longbox moves stay hand-placeable. Lift this +
					// drop the default override once Content reconciles the longbox front clearance (flagged).
					const composed = await composeFixture({
						anchor,
						scene,
						stage: this,
						products: eligibleProducts,
						fixtureCatalogue,
						token,
					});
					if (composed) composedAnchorIds.add(anchor.name);
				} catch (err) {
					console.error(`[composer] ${kind} (${anchor.name}) compose failed:`, err);
				}
			}
		}
		// 🔴 THE PASS'S BOOKKEEPING IS SETTLED BEFORE ANYTHING ELSE MAY AWAIT (F-108a).
		//
		// `_seatedSignature` and `_seatedProducts` are what OTHER paths read to answer "what pool was
		// this scene composed against" — the rehydrate no-op guard and the late-arriving-fixture
		// compose. They used to be assigned synchronously the instant the fixture loop ended. Putting
		// the zone pass in front of them opened a gap in which a concurrent caller could read the
		// PREVIOUS pool and re-seat it: MEASURED on the plane, `probe-stock-panel` CLEAR-BTN emptied
		// the shop (feed products 0) and polled 30 s with 7 comic meshes still standing, against a
		// control run on the untouched tree that was ALL GREEN 177/177.
		this._seatedSignature = this._seatingSignature(products);
		// The pool the composer last ran with, kept beside its own signature — the one place that
		// already owns "what was seated". A fixture that arrives AFTER this pass (a merchant places
		// one; the /instances hydrate lands it) has to be composed against the same pool, and the
		// stage had no way to answer "which pool?" without threading it through three call sites that
		// have no business knowing about seating.
		this._seatedProducts = Array.isArray(products) ? products : [];
		await this._composeZoneSeats({ scene, products, token });
		return composedAnchorIds;
	}

	/**
	 * 🔴 ASK-129 F-108a — ZONE-SEATED STOCK IS STOCK, AND THE COMPOSE PASS DRAWS IT.
	 *
	 * A `ZONE` seat addresses a socket the SHELL owns rather than a fixture, so it matched no anchor
	 * in the loop above and nothing drew it. The only code that ever did was the Studio's
	 * `_reseatZonesNow` — editor chunk, grant-gated — which made a shop's floor stock visible to its
	 * merchant and to nobody else. MEASURED on the deployed plane 2026-09-20, store-test-2, an honest
	 * customer identity (approved, no merchant role, no shop roles):
	 *
	 *     FEED     4 products, 4 served lines, all `fixtureAnchor:"ZONE"`, all carrying `at`
	 *     CUSTOMER productMeshes 0 · zoneMeshes 0 · `stage._zoneScene` never created
	 *     MERCHANT same feed, same shop — every line drawn
	 *
	 * So it goes here, in the ONE compose pass (DECISION-052), beside the fixture loop and sharing
	 * its pool, its token and its `products`: boot compose and `rehydrateSeatedProducts` both route
	 * through this function, so both sessions get the same floor from the same feed.
	 *
	 * WHAT IT PASSES, AND WHAT IT DELIBERATELY DOES NOT:
	 *
	 *   · `shape: { y }` — the floor's own height, read off the boot's `surfaceRegions[]`. A derived
	 *     selling-space frame is the PACKER's requirement (see `seatZoneNow`) and an addressed seat
	 *     is never packed, so asking the customer to re-derive one would be D-067 clause 2 in
	 *     reverse: re-deciding — here, refusing — what the server already resolved.
	 *   · `intent: null` — zone intents live behind `/admin/…/merch-zones` and a customer has no
	 *     business reading a merchant's knobs. `seatZoneNow` already resolves `accepts` from what is
	 *     actually seated when no intent is supplied, which is the right answer for redrawing a feed.
	 *
	 * An UNADDRESSED zone seat reaching a session with no frame is therefore not drawn, and says so
	 * by name on the console rather than being packed against a frame that is missing the merchant's
	 * own exclusions — two viewers standing in two different shops is worse than one empty patch.
	 * The Studio's own pass still supersedes all of this the moment a merchant derives (it clears the
	 * region and re-seats with the intent-aware frame); this is idempotent and last-writer-wins.
	 */
	/**
	 * 🔴 ONE QUEUE PER SUBJECT (ASK-129 F-108c, second pass).
	 *
	 * Zone seating CLEARS a region and redraws it, and there are two doors into this pass — the boot
	 * compose and `rehydrateSeatedProducts` — plus an `await` on a dynamic import in the middle of
	 * it. Two passes in flight over one region therefore interleave destructively: the later one's
	 * `clearZoneSeats` wipes what the earlier one is still drawing, and whatever it had already
	 * seated is gone. MEASURED on the deployed plane, `store-test-2`, three reads of ONE shop whose
	 * feed serves two addressed floor lines:
	 *
	 *     run A   zoneRootCount 0   nothing drawn at all
	 *     run B   wallComics 2      both drawn
	 *     Coord   1 of 2            one drawn
	 *
	 * Same code, same boot, three answers — which is a race, not a branch, and it is the same shape
	 * as the preview/commit pair that cost this repo a pass in ASK-129 Phase C. So the passes queue:
	 * a second caller waits for the first to finish rather than cutting into its teardown. The token
	 * check inside still abandons a pass whose scene has been replaced, so a queued stale pass costs
	 * one comparison and draws nothing.
	 */
	_composeZoneSeats(args) {
		const run = () => this._composeZoneSeatsNow(args);
		this._zoneSeatChain = (this._zoneSeatChain || Promise.resolve()).then(run, run);
		return this._zoneSeatChain;
	}

	async _composeZoneSeatsNow({ scene, products, token }) {
		// 🔴 THE STUDIO SUPERSEDES, AND IT IS NOT A SECOND PIPELINE.
		//
		// Both paths call the SAME `seatZone`; the Studio simply calls it with strictly richer inputs
		// (the merchant's own intents — exclusions, density, narrowed accepts — which a customer may
		// not read) and on its own schedule, around gestures. Once its zones module has pulled this
		// shop's intents it owns the region, and this pass must keep its hands off: re-seating a zone
		// clears and rebuilds its meshes, and doing that underneath a live drag destroys the very mesh
		// the gesture is holding. MEASURED on the plane the first time this ran without the claim:
		// `probe-zones-panel` Z6e "the real drag MOVED it in the scene — moved 0.00 m", and the seat
		// outline stranded 1.994 m from its item in `probe-editor-chrome` C2.
		if (this._zoneSeatOwner) return 0;

		const pool = Array.isArray(products) ? products : [];
		const regions = new Map();   // regionId → floor y, from the shell's own regions
		for (const r of (Array.isArray(scene?.surfaceRegions) ? scene.surfaceRegions : [])) {
			if (r?.kind === "floor" && r?.regionId) regions.set(r.regionId, Number.isFinite(r.y) ? r.y : 0);
		}
		const seated = new Set();
		for (const p of pool) {
			for (const a of (Array.isArray(p?.fixtureAssignments) ? p.fixtureAssignments : [])) {
				if (a?.fixtureAnchor !== "ZONE") continue;
				// A null socketId means the shop's only floor; anything else names its region.
				if (a.socketId == null) { for (const id of regions.keys()) seated.add(id); }
				else seated.add(a.socketId);
			}
		}

		// 🔴 ABSENCE NEEDS ITS OWN READ SET — a pass that only draws what the feed HAS cannot express
		// a removal, and the stock this pass drew would outlive the row that put it there. MEASURED:
		// with this pass seating but never clearing, `probe-stock-panel` CLEAR-BTN emptied the shop
		// (feed products 0) and found 7 comic meshes still standing, and `probe-zones-panel` reported
		// "the injected row and everything it drew are gone — 1 seat(s) standing" twice. So the region
		// set is the union of what the feed seats and what the SCENE currently holds, read off the
		// seat stamps rather than remembered.
		const drawnNow = new Set((this._zoneScene ? this.zoneSeatedItems() : []).map((s) => s.regionId).filter(Boolean));
		const stale = [...drawnNow].filter((id) => !seated.has(id));
		// A shop with no floor stock and nothing drawn never loads the zone chunk at all, so a template
		// store still pays nothing for any of this (`_ensureZoneScene` is a dynamic import).
		if (!seated.size && !stale.length) return 0;

		for (const regionId of stale) {
			if (token !== this.currentToken) return 0;
			this.clearZoneSeats(regionId);
		}

		let drawn = 0;
		for (const regionId of seated) {
			if (token !== this.currentToken) return drawn;
			if (!regions.has(regionId)) {
				console.warn(`[zone] ZONE:${regionId} — the feed seats stock on a region this shell does not derive; nothing drawn`);
				continue;
			}
			try {
				drawn += await this.seatZone({
					regionId, shape: { y: regions.get(regionId) }, intent: null,
					products: pool, productTypes: this._productTypes, token,
				});
			} catch (err) {
				console.error(`[zone] ZONE:${regionId} seat failed:`, err);
			}
		}
		return drawn;
	}

	// The seating-relevant fingerprint of a product pool: which product seats where (anchor/
	// socket/slot). Rehydrate no-ops when it's unchanged — the merchant-refetch SETTLE loop
	// refetches up to 4×, and only the fetch that actually lands new assignments should rebuild.
	_seatingSignature(products) {
		const rows = [];
		for (const p of (Array.isArray(products) ? products : [])) {
			for (const a of (Array.isArray(p.fixtureAssignments) ? p.fixtureAssignments : [])) {
				if (a?.fixtureAnchor) rows.push(`${a.fixtureAnchor}|${a.socketId ?? ""}|${a.slot ?? ""}|${p.id}`);
			}
		}
		return rows.sort().join(";");
	}

	// ─── rehydrateSeatedProducts — the live re-seat primitive (ASK-099, last WYSIWYG gap) ────────
	// The 3D scene's seated products re-compose IN-PLACE from a fresh feed: dispose every seated
	// instance (runtime_comics__* roots, socket comics, spinner rack contents — via the per-anchor
	// ownership registries, never spatial guesses) and re-run the ONE composer pipeline over the
	// same anchors with the fresh pool. Feed-grain change → feed-grain update (D-058 feed/bake
	// split): NO scene reload, NO camera movement — nothing in this path touches camera/controls.
	// Consumers: the merchant mutation refetch (refetchShopFeed → app.rehydrateSeatedProducts) so
	// Clear/carry-all is WYSIWYG; later, an idle-gated customer feed tick (ASK-080 etiquette —
	// flagged follow-on, not built).
	// Serialized: overlapping calls queue; each run is a full teardown+recompose so the last
	// snapshot wins. Signature-guarded: an unchanged pool is a no-op (the settle loop re-calls).
	async rehydrateSeatedProducts(products) {
		// 🔴 ASK-142 F-142b — A RESEAT NEVER INTERLEAVES WITH A FULL COMPOSE. The feed can land while the
		// first compose is still in its tail (catalogue instances, env bake, compileAsync, the frame hold);
		// a reseat started then tore down and rebuilt fixtures under the running compose, and the rebuilt
		// crate body stood in the scene graph — attached, visible, maps bound, program compiled — and drew
		// 0 GPU pixels (reproduced: reseat at +300 ms into a compose → 0 px; after it settles → 256 962 px).
		// Scene mutations go through one queue (Unity/Unreal serialize async scene operations): the reseat
		// waits for the compose to finish, then runs — and its signature guard makes it a no-op when the
		// compose already seated the same pool.
		const run = async () => { await this._composeSettled(); return this._rehydrateSeatedProductsNow(products); };
		this._reseatChain = (this._reseatChain || Promise.resolve()).then(run, run);
		this._reseatChain.then(() => this._notifyContents(), () => {});
		return this._reseatChain;
	}

	/** ASK-139 F-139h — the one sentence about what the scene holds, read from the stage NOW (not from
	 *  the report the first compose returned, which a fresh shop's first bake / re-seat left at 0). */
	/** ASK-139 F-139y — and it counts what is THERE: every holder that is holding something, by its own
	 *  kind, and every seated product by its seat. It counted every composed fixture as a "longbox" and
	 *  every product as a "comic", so a book row with a book on it read "2 longboxes, 20 total comics". */
	sceneSummary() {
		const seats = new Map();   // seat key → anchor
		this.scene?.traverse?.((n) => { const s = n.userData?.seat; if (s?.productId != null) seats.set(`${s.anchorName}|${s.socketId}|${s.slot}|${s.productId}`, s.anchorName); });
		const kinds = new Map();
		for (const a of new Set(seats.values())) {
			const t = this.boxEntries?.get?.(a)?.fixture?.templateKey;
			if (!t) continue;
			const k = t.replace(/-anchor$/, "").replace(/-/g, " ");
			kinds.set(k, (kinds.get(k) || 0) + 1);
		}
		const plural = (n, w) => `${n} ${w}${n === 1 ? "" : /(x|s|ch|sh)$/.test(w) ? "es" : "s"}`;
		const parts = [...kinds].map(([k, n]) => plural(n, k));
		parts.push(plural(seats.size, "product"));
		return `Scene loaded — ${parts.join(" · ")}.`;
	}

	/** Every path that changes what the scene holds (compose, re-seat, a placed row) ends here. */
	_notifyContents() {
		try { this.onContentsChanged?.(); } catch (err) { console.warn("[stage] contents listener:", err?.message || err); }
	}

	async _rehydrateSeatedProductsNow(products) {
		if (this.sceneKind !== "shop" || !this._shellRoot || !this.currentScene) return 0;
		const token = this.currentToken;                          // a real scene swap (token bump) aborts cleanly
		const sig = this._seatingSignature(products);
		if (sig === this._seatedSignature) return 0;              // seating unchanged — no-op
		const anchorsToReseat = new Set([...(this.boxEntries?.keys?.() || [])]);
		if (anchorsToReseat.size === 0) return 0;                 // nothing was ever composer-seated

		// 1. Close transient product interactions while their meshes still exist (open lid / hero out).
		try { if (this.activeBoxId !== null) this.forceCloseActiveBox(); } catch { /* state reset below */ }

		// 2. Teardown per composed fixture instance — interactions, animations, then every owned
		//    Object3D from the authoritative registries (_fixtureContentsFor enumerates the composed
		//    entry itself + the socket-ownership registry + name-declared __cat__ nodes; the adoption
		//    group carries moved fixtures). Dispose geometry + materials (textures stay — label/cover
		//    textures are cached and shared, mirroring _removeFixtureFromScene).
		for (const anchorName of anchorsToReseat) this._teardownComposedAnchor(anchorName);
		this._pruneDetachedGlobals();

		// 3. Reset the seated-product interaction registries (the resetStage subset that the
		//    composer pipeline rebuilds — pile/wall/book/spinner/featured/stack + box browse state).
		this.comicEntries = [];
		this.heroExtract = null; this.lidNode = null; this.lidPivotNode = null;
		this.lidMeshNode = null; this.lidHitZone = null;
		this.lidCurrentAngle = 0; this.lidTargetAngle = 0;
		this.boxState = "none"; this.browseIndex = 0; this.heroComicIndex = null;
		this.activeBoxId = null;
		this._exitBrowsePose();   // ASK-142 F-142e: a recompose closes the bin under the walker — blend home
		this._browseVerb = null;  // ASK-154 seam 2: the seat it was pulled from is gone
		this.wallComics = []; this.hoveredWallComic = null; this.activeWallComic = null; this.wallComicHeroEntry = null;
		this.pileComics = []; this.pileRightStack = []; this.pileLeftStack = [];
		this.pileState = "none"; this.pileArcAnim = null; this.pileHeroEntry = null; this.hoveredPileComic = null;
		this.bookEntries = []; this.hoveredBook = null; this.bookBrowseIndex = -1; this.bookState = "none"; this.bookHeroEntry = null;
		this.spinnerRack = null; this.featuredComics = []; this.stackTables = []; this.activeStackTable = null;
		// ASK-139 F-139an — bin sleeves are composer-seated now, so they re-seat like every other kind.
		if (this.hoveredVinyl) removeOutline(this.hoveredVinyl);
		this.vinylSleeves = []; this.hoveredVinyl = null; this.vinylHeroEntry = null; this.activeVinylBin = null;
		this.activeFixtureHero = null; this.focusedBoxComicRoot = null;
		this.updateBrowseControls?.();

		// 4. Re-seat: the SAME pipeline the boot runs, scoped to the anchors just torn down,
		//    with the fresh pool. Then the recompose tail — persisted transforms (a moved fixture
		//    recomposes at its shell pose; moveFixtureTo re-applies the merchant's move), scales,
		//    view modes. Camera untouched throughout.
		const composed = await this._composeRegisteredFixtures({
			scene: this.currentScene,
			products: Array.isArray(products) ? products : [],
			fixtureCatalogue: this._fixtureCatalogueCached,
			token,
			onlyAnchorNames: anchorsToReseat,
		});
		if (token !== this.currentToken) return 0;                // scene swapped mid-reseat — its compose owns the stage now
		// ③ finding 1 — the teardown above is unconditional and the compose below it is not: a
		// compose that fails or is skipped for one anchor (a transient GLB/manifest fetch, a null
		// bail) left that fixture DISPOSED with nothing downstream to notice — invisible until a
		// page refresh, while its seats kept arriving on the feed. The reseat now names every
		// anchor it tore down and could not rebuild, and runs the newcomer repair pass over the
		// registry (idempotent — a fully rebuilt scene is a no-op), so one bad fetch is a retried
		// compose rather than a vanished fixture.
		let lost = [...anchorsToReseat].filter((a) => !composed.has(a));
		if (lost.length) {
			console.warn(`[reseat] ${lost.length} fixture(s) torn down and NOT rebuilt — retrying compose: ${lost.join(", ")}`);
			try {
				const retried = await this._composeRegisteredFixtures({
					scene: this.currentScene,
					products: Array.isArray(products) ? products : [],
					fixtureCatalogue: this._fixtureCatalogueCached,
					token,
					onlyAnchorNames: new Set(lost),
				});
				for (const a of retried) composed.add(a);
				lost = lost.filter((a) => !retried.has(a));
			} catch (err) { console.error("[reseat] compose retry failed:", err?.message || err); }
			// Anything the composer still cannot draw goes to the newcomer pass — for a catalogue
			// kind that is at least its clone (a visible fixture whose grammar could not compose),
			// and for anything else a loud log rather than a silent hole.
			if (lost.length && token === this.currentToken) {
				console.warn(`[reseat] ${lost.length} fixture(s) still missing after retry — newcomer repair: ${lost.join(", ")}`);
				try { await this._renderHydratedCatalogueNewcomers(); }
				catch (err) { console.error("[reseat] repair pass failed:", err?.message || err); }
			}
		}
		if (token !== this.currentToken) return 0;
		this.applyPersistedInstanceTransforms(this.currentScene?.instances || []);
		this._applyInstanceScales();
		this._applyViewModes?.();
		// SELECTION SURVIVES A RE-SEAT (ASK-137 F-119d). A holder is recomposed under the SAME anchor
		// name, so the selection is held by that stable identity — the Unity/Unreal rule (selection keyed
		// by instance id / object path survives a reload of what it names). It used to be dropped at
		// teardown, so every holder-policy write (an Accepts chip, Auto-fill) closed the Inspector the
		// merchant was clicking in after ONE click. Released only if the fixture did not come back; a
		// seat focus drops to the holder (its slot may now hold another product).
		const sel = this._selectedAnchor;
		if (sel && anchorsToReseat.has(sel)) { if (composed.has(sel)) this.selectFixture(sel); else this.deselect(); }
		console.log(`[reseat] ${composed.size}/${anchorsToReseat.size} fixture(s) re-seated from the live feed`);
		return composed.size;
	}

	// Lowercased names of the shell's LIGHT_ZONE_<name> markers (stashed by
	// composeTemplate). The Studio per-zone-mood UI uses this to offer ONLY zones
	// that actually exist in the shop, so every picker visibly changes the render.
	getLightZoneNames() {
		return Object.keys(this._lightZoneMarkers || {});
	}

	// Studio legibility aid (WS4): briefly flash a glowing marker at a LIGHT_ZONE so
	// the merchant sees which physical zone a per-zone mood picker controls. A floor
	// ring under the zone + a small orb at the marker height, pulsing then fading.
	// No-op (returns false) if the zone has no marker.
	highlightLightZone(name, durationMs = 2200) {
		const key = String(name || "").toLowerCase();
		const pos = this._lightZoneMarkers?.[key];
		if (!pos || !this.scene) return false;
		this._clearLightZoneHighlight();

		const group = new THREE.Group();
		group.name = "studio__zone_highlight";
		const mat = new THREE.MeshBasicMaterial({
			color: 0xffd86b, transparent: true, opacity: 0.9,
			depthWrite: false, blending: THREE.AdditiveBlending,
		});
		const ring = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.04, 12, 48), mat);
		ring.rotation.x = -Math.PI / 2;
		ring.position.set(pos.x, 0.05, pos.z);          // flat on the floor under the zone
		const orb = new THREE.Mesh(new THREE.SphereGeometry(0.12, 16, 16), mat);
		orb.position.set(pos.x, pos.y, pos.z);          // at the marker height
		group.add(ring, orb);
		this.scene.add(group);
		this._zoneHighlight = group;

		const start = (typeof performance !== "undefined" ? performance.now() : 0);
		const tick = (now) => {
			if (this._zoneHighlight !== group) return;  // superseded / cleared
			const t = Math.min(1, ((now || 0) - start) / durationMs);
			const pulse = 0.6 + 0.4 * Math.sin(t * Math.PI * 4);   // a few pulses while it lives
			mat.opacity = (1 - t) * pulse;
			ring.scale.setScalar(1 + t * 0.8);          // expand outward as it fades
			if (t >= 1) { this._clearLightZoneHighlight(); return; }
			requestAnimationFrame(tick);
		};
		requestAnimationFrame(tick);
		return true;
	}

	_clearLightZoneHighlight() {
		const g = this._zoneHighlight;
		if (!g) return;
		this._zoneHighlight = null;
		this.scene?.remove(g);
		g.traverse((o) => { o.geometry?.dispose?.(); o.material?.dispose?.(); });
	}


	// ASK-085 part-2 — the ONE fixture-binding resolver (composer + rebinds both use it):
	// finish byKind (the FIXTURES dropdown's Material-Instance matrix, stashed by composeTemplate)
	// wins for kinds it names; omitted kinds keep their static identity (longbox stays cardboard
	// under a "warm wood" finish); no finish → static.
	_resolveFixtureBinding(kind, anchorName = null) {
		return resolveFixtureKindBinding({
			staticBinding: this._templateSurfaceMaterialsCached?.[this.currentScene?.template?.id]?.[kind] ?? null,
			finishPreset:  this._fixtureFinishPreset || null,
			kindFinishPreset: this._fixtureKindDefaultPresets?.[kind] ?? null,   // Amendment 3
			kind,
			// ASK-086 — per-instance cell (folded flat material.<anchor>.* knobs) is the top layer
			instanceCell:  anchorName ? (this._instanceMaterialKnobs?.[anchorName] ?? null) : null,
		});
	}

	// DECISION-052 — seat ONE world-composed socket's curated comics on the composer path
	// (called by composer.js for socket-driven fixtures, e.g. the pile-table). The composer
	// supplies the fixture geometry (Body clone); this seats the products the VERIFIED socket-
	// path way: layoutProducts → _buildSocketComic (qFlat×qYaw orientation) → the pile-comic/
	// wall-comic wiring. `socket` is already world-composed (socketsForAnchor) with its layout
	// mapped to an engine primitive. Returns the count seated.
	// ASK-087 slice-2 (DECISION-044 ruling): socket-seated products had NO ownership record — the
	// composer's boxEntries registry only covers the box family. `anchorName` is threaded through so
	// every seated product is registered against its host fixture at seat time. Ownership is then
	// STRUCTURAL (recorded where it's created), never inferred from spatial overlap.
	_seatTableSocket(socket, products, anchorName = null) {
		if (!socket || !Array.isArray(products) || !products.length) return 0;
		const dimsByType = {};
		for (const t of (this._productTypes || [])) if (t?.productTypeId) dimsByType[t.productTypeId] = t;
		// flat-pile: anchor the pile hero/arc-browse state to the socket's table-top world
		// position (not origin) — else _pileRestPos / the ←/→ arc send comics to the floor (y=0).
		if (socket.layout === "flat-pile") {
			this.pileTableSurfaceY = socket.position.y;
			this.pileCenterRight.set(socket.position.x, socket.position.y, socket.position.z);
			this.pileCenterLeft.set(socket.position.x + 0.24, socket.position.y, socket.position.z);
		}
		const plan = layoutProducts({ socket, products, productDims: dimsByType });
		if (plan.deferred) return 0;
		let placed = 0;
		for (const pl of plan.placements) {
			const product = products[pl.productIndex] || null;
			const mesh = this._buildSocketComic(product, pl, this._seatStampFor(anchorName, product, socket.socketId, pl.slot));
			this._wireSeatedComicInteraction(mesh, product, socket.layout);   // pile-comic / wall-comic
			// ASK-154 seam 2 — a legacy socket's derived verb (r55) rides its seats too. It publishes no approach, so
			// the motion leaves toward the shopper (`verbMotion`'s fallback); absent (r54) stamps nothing.
			const stamp = slotBrowse(socket);
			if (stamp) { mesh.userData.browse = stamp; this._loadBrowseVerbs().catch(() => {}); }
			this.scene.add(mesh);
			this.shopLights.push(mesh);
			if (anchorName) {
				this._fixtureContents ||= new Map();
				if (!this._fixtureContents.has(anchorName)) this._fixtureContents.set(anchorName, []);
				this._fixtureContents.get(anchorName).push(mesh);
			}
			placed++;
		}
		return placed;
	}

	// DECISION-049 — instance a fixture's catalogue GLB at the anchor's world transform
	// (position + yaw). The GLB is base-origined, so it sits correctly from the anchor
	// alone (e.g. longbox = counter-top bin, anchor at y≈0.86 on Shell_LongboxCounter).
	// Loaded via the shared catalogue loader (KTX2/Draco config) + cloned per instance.
	async _instanceFixtureGlb(anchor, fixtureCatalogue, token) {
		const kind = anchor?.fixtureKind;
		if (!kind || !fixtureCatalogue?.[kind]?.geometryAssetKey) return null;
		let gltf;
		try {
			gltf = await loadFixtureCatalogue({ catalogue: fixtureCatalogue, kind, gltfLoader: this.gltfLoader });
		} catch (err) {
			console.warn(`[socket-fixture] ${kind} GLB load failed — products seat without a frame:`, err?.message || err);
			return null;
		}
		if (token !== this.currentToken || !gltf?.scene) return null;
		const frame = gltf.scene.clone(true);
		frame.position.set(anchor.position.x, anchor.position.y, anchor.position.z);
		frame.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), anchor.rotationY || 0);
		frame.traverse((n) => { if (n.isMesh) { n.castShadow = true; n.receiveShadow = true; } });
		this.scene.add(frame);
		this.shopLights.push(frame);
		frame.updateMatrixWorld(true);
		return frame;
	}


	// Ordered baked SLOT_<socketId>_* nodes from the instanced frame (row-first then side
	// — matches the legacy spinner). The single source the explicit-slots paths read.
	_glbSlotNodes(frame, socketId) {
		if (!frame) return [];
		const re = new RegExp(`^SLOT_${socketId}_`, "i");
		const nodes = [];
		frame.traverse((n) => { if (re.test(n.name || "")) nodes.push(n); });
		nodes.sort((a, b) => { const A = this._parseSlotSR(a.name), B = this._parseSlotSR(b.name); return A.r !== B.r ? A.r - B.r : A.s - B.s; });
		return nodes;
	}

	_parseSlotSR(name) {
		const m = /_s(\d+)_r(\d+)/i.exec(name || "");
		return m ? { s: +m[1], r: +m[2] } : { s: 0, r: 0 };
	}

	// DECISION-050 Phase-B — wire a socket-seated comic's interaction by re-homing the SAME
	// userData.interactable the legacy builders set, so the existing _interactable-kinds
	// dispatchers (wall-comic / pile-comic) + the generic hero machinery drive it unchanged.
	//   featured-hero / fan-spread → "wall-comic" (E-to-inspect hero)
	//   flat-pile                  → "pile-comic" (click-to-hero + ←/→ arc-browse)
	// Self-contained: setPileHero/wall-comic read only stage.pileComics/wallComics + the
	// camera-based hero pos (no legacy pile-builder positional state needed). browse-bin
	// (longbox lid+browse) is a separate increment (open-close interaction).
	_wireSeatedComicInteraction(mesh, product, layout) {
		if (layout === "flat-pile") {
			const idx = this.pileComics.length;
			mesh.userData.isPileComic = true;
			mesh.userData.pileComicIdx = idx;
			mesh.userData.interactable = {
				kind: "pile-comic",
				product,
				// No `onClick` (same finding as boxed-comic): the pile-table dispatcher's hover + E-select own the
				// hero (`pileRightStack` below), and no caller of an interactable onClick exists since b6a2f65.
				describe: () => ({ kind: "pile-comic", productId: product?.id }),
			};
			this.pileComics.push({ mesh, product, jitterX: 0, jitterZ: 0 });
			// The pile-table dispatcher hovers/selects the TOP of pileRightStack — populate it
			// (socket flat-pile = one stack) so hover-outline + E-select activate (Coord bug B).
			this.pileRightStack.push(idx);
		} else if (layout === "featured-hero" || layout === "fan-spread") {
			this.wallComics = this.wallComics || [];
			const idx = this.wallComics.length;
			mesh.userData.isWallComic = true;
			mesh.userData.wallComicIdx = idx;
			mesh.userData.interactable = {
				kind: "wall-comic",
				product,
				describe: () => ({ kind: "wall-comic", productId: product?.id }),
			};
			this.wallComics.push({ mesh, product, worldPos: mesh.position.clone() });
		}
	}

	/**
	 * ASK-137 F-115k — the interaction for a product seated on a HOLDER'S SURFACE.
	 *
	 * Readable (a type that declares cover surfaces) → the seated-comic path, which registers it as a
	 * `wall-comic`: hover outline, E brings it to the camera, F flips it, R puts it back. That is the
	 * "existing inspect pattern" §74 asks for, and it is the exact call the zone floor makes, so the
	 * two surfaces cannot drift.
	 *
	 * Everything else → a `seated-item`: the first non-print open in this runtime. Registered here and
	 * nowhere else, so ownership is structural (the rule `_fixtureContents` and the seat stamp both
	 * follow) rather than inferred later from what happens to be standing on a table.
	 */
	_wireSeatedItemInteraction(root, product) {
		if (!root) return;
		if (this._isReadableProduct(product)) { this._wireSeatedComicInteraction(root, product, "featured-hero"); return; }
		this.seatedItems ||= [];
		const pid = product?.id ?? product?.kapowxProductId ?? null;
		// 🔴 ASK-137 §86c — ONE ENTRY PER PRODUCT, AND NONE FOR A ROOT THAT HAS LEFT THE SCENE.
		//
		// This pushed unconditionally, and a zone re-seat rebuilds its group — so the registry grew an
		// entry per re-seat while the old roots were removed from the scene underneath it. MEASURED by
		// Coord on the plane: `seatedItems` carried Vanessa's owl THREE times for one owl on the floor.
		//
		// That is not untidiness, it is the duplicate owl she photographed. `openSeatedItem(idx)` is
		// handed whichever entry the hover resolved; a STALE entry's root is a removed object, and
		// `scene.attach`-ing it to bring it to the camera puts a SECOND owl in the scene while the
		// live one still stands on the floor. Coord measured exactly that: two owl meshes during the
		// hero, the floor one unchanged at (−2.06, 0, −8.59), the held one at (0, 1.23, −1.59).
		//
		// So: drop any entry for this product, drop any entry whose root has been detached, then
		// re-stamp — the index is what the dispatcher resolves through, so it must follow the array.
		this.seatedItems = this.seatedItems.filter((e) => e && e.root && e.root.parent
			&& (e.product?.id ?? e.product?.kapowxProductId ?? null) !== pid);
		this.seatedItems.push({ root, product });
		this.seatedItems.forEach((e, i) => { if (e.root) e.root.userData.seatedItemIdx = i; });
		root.userData.interactable = { kind: "seated-item", product, describe: () => ({ kind: "seated-item", productId: pid }) };
	}

	/** ASK-139 F-139af — is this line a BOOK? Read off the registry's tag path (`product.media.print.book`
	 *  and below), never off the type string: `hardcover-book` and `paperback-book` are both books. */
	/** Is this line's type at or below tag `root` in the registry's tree? */
	_lineUnder(product, root) {
		const type = product?.kapowxProductType || product?.productType || product?.type || null;
		const tag = (type && this._productTypes?.find?.((p) => p?.productTypeId === type)?.tagPath) || "";
		return tag === root || tag.startsWith(`${root}.`);
	}

	/** A line of the vinyl family — a sleeve the vinyl hero flies (ASK-147 §5.4's wall panel). */
	_isVinylLine(product) { return this._lineUnder(product, "product.media.audio.vinyl"); }

	_isBookLine(product) { return this._lineUnder(product, "product.media.print.book"); }

	/**
	 * 🔴 ASK-139 F-139af — A BOOK ON A SHELF STANDS SPINE-OUT AND IS PICKED UP AS A BOOK.
	 *
	 * The grammar's `edge-out` pose fixes the item's up (h) and packing (d) axes and leaves its width
	 * pointing at the customer — but WHICH end of the width is the spine is a fact of the asset, not of
	 * the pose: `hardcover-book.glb` carries its `spine` material at local −X. So it is MEASURED, the way
	 * the base-sink measures the underside: the registry names the spine slot, the mesh says where it
	 * is, and the item turns half a revolution about its own vertical if the spine faces the wall.
	 *
	 * Then it joins `bookEntries` — the `book-row` interactable this kind has always had (hover outline,
	 * slide toward the shopper, ←/→ along the shelf, E → the book hero). Not a new verb grammar: the
	 * per-product-type pattern a shelf of books already owns. Anything else on a shelf takes the
	 * seated grain every holder surface uses (a comic → the readable hero, a statue → seated-item).
	 */
	_seatOnShelf(root, product, approach) {
		if (!root) return;
		if (!this._isBookLine(product)) { this._wireSeatedItemInteraction(root, product); return; }
		const type = product?.kapowxProductType || product?.productType || product?.type || null;
		const spineSlot = (this._lookupProductSurfaceSlots(type) || []).find((s) => s?.face === "spine")?.name || "spine";
		root.updateMatrixWorld(true);
		// The spine's centre from the vertices its OWN triangles use. A glTF mesh's primitives share one
		// POSITION accessor, so a per-material bounding box is the whole book and says nothing.
		const spine = new THREE.Vector3(), v = new THREE.Vector3();
		let count = 0;
		root.traverse((n) => {
			if (!n.isMesh || (Array.isArray(n.material) ? n.material : [n.material])[0]?.name !== spineSlot) return;
			const pos = n.geometry?.attributes?.position, idx = n.geometry?.index;
			if (!pos) return;
			const len = idx ? idx.count : pos.count;
			for (let i = 0; i < len; i++) { spine.add(v.fromBufferAttribute(pos, idx ? idx.getX(i) : i).applyMatrix4(n.matrixWorld)); count++; }
		});
		const itemBox = new THREE.Box3().setFromObject(root);
		if (approach && count) {
			const toSpine = spine.divideScalar(count).sub(itemBox.getCenter(new THREE.Vector3()));
			toSpine.y = 0;
			if (toSpine.dot(approach) < 0) {
				root.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI));
				root.updateMatrixWorld(true);
			}
		}
		// One shelf per anchor for ←/→ — the browse keeps to the shelf under the crosshair.
		const anchorName = root.userData.seat?.anchorName ?? null;
		const same = this.bookEntries.find((e) => e.anchorName === anchorName);
		const shelfIndex = same ? same.shelfIndex : this.bookEntries.reduce((m, e) => Math.max(m, e.shelfIndex ?? -1), -1) + 1;
		this.bookEntries.push({
			mesh: root, product, anchorName, shelfIndex,
			basePosition: root.position.clone(), slideZ: 0,
			faceDir: (approach || new THREE.Vector3(0, 0, 1)).clone().setY(0).normalize(),
			heightM: itemBox.max.y - itemBox.min.y,
			coverAxis: new THREE.Vector3(0, 0, 1),   // the type card's `face: d` → the GLB's local +Z (the F-139aj mirror keeps it there)
			binding: bookBinding(product),           // ASK-139 F-139aj — said on the entry so an instrument can read it off the scene
		});
		root.userData.binding = bookBinding(product);
		const interactable = { kind: "book-row", product };
		root.traverse((n) => { if (n === root || n.isMesh) n.userData.interactable = interactable; });
	}

	/** ASK-139 F-139af — a book whose feed carries no spine image gets one made from what it does carry:
	 *  the cover's dominant tone, with the title set along it. Lazy — it is a few hundred bytes of
	 *  canvas work that only a book ever needs (the `_item-hero.js` split). */
	_dressBookSpine(root, product) {
		if (product?.spineImage || product?.media?.spine) return;
		import("./_book-spine.js").then((m) => m.dressBookSpine({ root, product, stage: this })).catch((err) => console.warn("[book] spine not dressed:", err?.message || err));
	}

	/**
	 * Open a seated item — bring it to the camera, orbit it, open the product panel. The PATTERN is lazy
	 * (`_item-hero.js`); this is the branch that decides to call it, which is the half that has to be
	 * eager. Awaited by nobody: the hover kind fires it and the frame carries on.
	 */
	async openSeatedItem(idx) {
		const entry = this.seatedItems?.[idx];
		if (!entry || this.activeFixtureHero) return false;
		// A root with no parent has been removed from the scene by a re-seat — bringing it to the
		// camera would draw a second copy of a thing that is already standing there (§86c). Belt to
		// the registration's braces: the registry should never hold one, and if it does, say so.
		if (!entry.root?.parent) {
			console.warn(`[item] open refused for ${entry.product?.id ?? "?"} — its mesh is no longer in the scene (stale seat registration)`);
			return false;
		}
		try {
			const mod = await import("./_item-hero.js");
			return !!mod.openItemHero(this, entry);
		} catch (err) {
			// A lazy chunk that will not load must say so by name — a silent return here is a shopper
			// pressing E on a statue and getting nothing, with no way to tell that from "not
			// interactable" (the exact ambiguity F-115k exists to remove).
			console.warn(`[item] open failed for ${entry.product?.id ?? "?"} — ${err?.message || err}`);
			return false;
		}
	}

	// ─── Seat grain (ASK-120 S3.5) ───────────────────────────────────────────────────────────────
	// A SEAT is `{anchorName, socketId, slot, productId, via}` — the address of one place in one
	// holder, stamped on the mesh that occupies it. Until now the finest thing the editor could
	// select was the fixture, so "pin THIS issue to THAT spot" had no noun in the scene to point at.
	//
	// Read from the SAME `fixtureAssignments` row the seating itself came from, so the stamp can
	// never disagree with the placement. `via` is additive and absent on every feed shipped to date
	// (measured on gcg-dev `test-shop`, 2026-08-26) — it reads null, the panel reads "Auto", and the
	// whole surface activates on the first feed that carries it (D-061 additive-and-inert).
	//
	// D-063 guard 1: the seat's subject is named `productId` here ONLY because that is the field on
	// today's assignment row; every CONSUMER addresses it as `itemRef` (an asset reference). Nothing
	// downstream of this stamp reads `sellable` or any commerce field (guard 4).
	// ASK-121: `atFallback` is WHERE THE RENDERER PUT IT — the `{u,v}` a surface region resolved for an
	// AUTO seat. It never overrides the feed's `at` (a pin's address is the merchant's, and the feed is
	// its only source); it fills the gap for an unpinned item on a continuous domain, which is what
	// lets the non-drag path pin a seated item exactly where it already stands rather than "somewhere
	// on that table". Without it, `Pin…` on a scattered item could only write `at: null` and the item
	// would jump on the next re-resolve — a pin gesture that visibly moves the thing it pinned.
	_seatStampFor(anchorName, product, socketId = null, slotFallback = null, atFallback = null) {
		if (!anchorName) return null;
		const assigns = Array.isArray(product?.fixtureAssignments) ? product.fixtureAssignments : [];
		const hit = assigns.find((a) => a && a.fixtureAnchor === anchorName
			&& (socketId == null || a.socketId == null || a.socketId === socketId)) || null;
		return {
			anchorName,
			socketId: hit?.socketId ?? socketId ?? null,
			slot: Number.isFinite(hit?.slot) ? hit.slot : (Number.isFinite(slotFallback) ? slotFallback : 0),
			productId: product?.id ?? null,
			via: hit?.via ?? null,          // additive — null until GCG's S2 feed carries provenance
			at: hit?.at ?? atFallback ?? null,   // the feed's address verbatim, else where the renderer put it
			atSource: hit?.at != null ? "feed" : (atFallback != null ? "rendered" : null),
		};
	}

	// Stamp a seat onto a mesh (and every mesh beneath it, so a template CLONE whose meshes are
	// grandchildren still answers a raycast hit). Idempotent; safe on any Object3D.
	_stampSeat(root, seat) {
		if (!root || !seat) return root;
		root.userData.seat = seat;
		if (root.traverse) root.traverse((n) => { if (n !== root) n.userData.seat = seat; });
		return root;
	}

	// The seat a raycast hit belongs to — walk the ancestor chain, nearest wins. Returns null for
	// anything that is not a seated product, which is what keeps the pointerup ordering honest:
	// "a seat, else the fixture" is a real test, not a heuristic about what was clicked.
	_seatForHit(object) {
		for (let n = object; n; n = n.parent) if (n.userData?.seat) return n.userData.seat;
		return null;
	}

	// Every seated mesh whose stamp matches an address — the Inspector's "show me that seat" and the
	// drag's drop-target resolution both need to go from an address back to the pixels.
	_meshesForSeat({ anchorName, socketId = null, slot = null } = {}) {
		const out = [];
		this.scene?.traverse?.((o) => {
			const s = o.userData?.seat;
			if (!s || s.anchorName !== anchorName) return;
			if (socketId != null && s.socketId !== socketId) return;
			if (slot != null && s.slot !== slot) return;
			if (o.isMesh) out.push(o);
		});
		return out;
	}

	// Build one comic mesh at a placement (mirrors the featured/fan comic mesh: 6-face
	// box, cover=+Z, front/back textures). rotationX=-π/2 lays it flat; rotationY orients
	// the facing. Quaternion-composed (flat then yaw) to match the legacy builders.
	_buildSocketComic(product, pl, seat = null) {
		const W = 0.170, H = 0.260, T = 0.0032;
		const frontMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85, metalness: 0 });
		const backMat  = new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.85, metalness: 0 });
		const edgeMat  = new THREE.MeshStandardMaterial({ color: 0x1a1410, roughness: 0.95, metalness: 0 });
		// F-CU16b — glTF UVs (v=0 = image top), the convention `_loadTextureCached` binds to (ASK-129).
		const mesh = new THREE.Mesh(toGltfUv(new THREE.BoxGeometry(W, H, T)), [edgeMat, edgeMat, edgeMat, edgeMat, frontMat, backMat]);
		mesh.position.set(pl.x, pl.y, pl.z);
		if (pl.quaternion) {
			// explicit-slots: adopt the baked slot pose verbatim (full orientation).
			mesh.quaternion.set(pl.quaternion.x, pl.quaternion.y, pl.quaternion.z, pl.quaternion.w);
		} else {
			const qFlat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), pl.rotationX || 0);
			const qYaw  = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), pl.rotationY || 0);
			mesh.quaternion.copy(qYaw).multiply(qFlat);   // lay flat (local) then orient by world yaw
		}
		mesh.castShadow = true;
		mesh.receiveShadow = true;
		// ASK-142 R1 — the shelf LOD at compose, the hero on hold (`_coverBindings`).
		const bindFace = (rawUrl, mat, face) => {
			const binding = { face, rawUrl, transform: null, node: mesh,
				apply: (tex) => { mat.map = tex; mat.needsUpdate = true; },
				placeholder: () => {} };
			this._recordCoverBinding(product, binding);
			this._loadTextureCached(this._lodUrl(product, rawUrl, "shelf"), (tex) => { if (!binding.heroUrl) binding.apply(tex); });
		};
		if (product?.frontImage) bindFace(product.frontImage, frontMat, "front");
		else frontMat.color = new THREE.Color().setHSL(((pl.slot * 37) % 360) / 360, 0.55, 0.52);
		if (product?.backImage) bindFace(product.backImage, backMat, "back");
		if (seat) this._stampSeat(mesh, seat);       // ASK-120 S3.5 — the seat grain
		return mesh;
	}

	// Optional flat-and-high mesh hide pass for shell GLBs that ship with a
	// mezzanine slab or ceiling we don't want at runtime. Toggled per-scene via
	//   shopGeometryBinding.hideUpperFloor: hide all flat meshes above 0.5m
	//   shopGeometryBinding.hideCeiling:    hide flat meshes above 2.0m only
	_applyShellVisibilityFilters(root, binding) {
		const hideUpper   = binding.hideUpperFloor === true;
		const hideCeiling = binding.hideCeiling === true;
		if (!hideUpper && !hideCeiling) return;
		const tmpBox = new THREE.Box3();
		let hidden = 0;
		root.traverse((node) => {
			if (!node.isMesh) return;
			tmpBox.setFromObject(node);
			const sizeY = tmpBox.max.y - tmpBox.min.y;
			const centerY = (tmpBox.min.y + tmpBox.max.y) / 2;
			if (sizeY >= 0.4) return;
			const matchesUpper = hideUpper && centerY > 0.5;
			const matchesCeil  = hideCeiling && centerY > 2.0;
			if (matchesUpper || matchesCeil) {
				node.visible = false;
				hidden++;
			}
		});
		console.log(`[shop-shell] hid ${hidden} flat-high meshes (upper:${hideUpper}, ceiling:${hideCeiling})`);
	}

	// _applyShellFlatShade RETIRED (DECISION-053 Slice-2 Step 6) — the flat MeshBasicMaterial
	// placeholder path is superseded by real library materials. Unbound/unfitted shells keep their
	// authored GLB materials (no placeholder). (_applyShellInteriorLighting is KEPT — it is the
	// LIGHTING fallback for scenes without a lighting recipe, not a material fallback.)

	// Shell-mode interior lighting — used when a scene has no procedural
	// `scene.room` for `applyAtmosphere` to fill. Warm key spot + hemi + two
	// corner point lights, all tracked in `shopLights` for cleanup.
	// Future: drive from a `scene.lighting` config block.
	// ─────── ASK-084 Part 1 — framework shadow standard (per-mode owner) ───────
	// A single owner configures shadows for WHICHEVER lights cast (Part 2). Called by each render
	// mode (recipe / interior-fallback / hub). No caster is ever left at THREE defaults: every
	// shadow camera is FITTED to the shell AABB (_shellRoomBounds) and sized/filtered from the
	// render tier (VSM). Shells receive on REAL geometry (floor/walls/counter enrolled) — the WS4
	// studio__shadow_catcher stopgap is retired (kept only as a tier-`low` fallback). ORIGIN-
	// AGNOSTIC: pass ANY casting light (recipe, zone marker, future Studio luminaire) — all get the
	// same fitted+tiered setup, so light-placement (next slice) drops in with zero rework.
	_configureShadows({ mode = "recipe", casters = [], model = null, shadowIntensity = SHADOW_INTENSITY_DEFAULT } = {}) {
		const tp = this.tierProfile || getTierProfile("low");
		this.renderer.shadowMap.enabled = tp.shadowsEnabled !== false;
		// Shadow FILTER is caster-shaped, not tier-shaped (ASK-089, real-GPU bisected on ANGLE Metal):
		//  • Model A's directional key uses a large ORTHO frustum. VSM's variance test bleeds light over
		//    that depth range and WASHES OUT open-floor shadows — a floor caster toggled 0.007% under VSM
		//    vs 1.06% under PCFSoft (150×). Tight counter-contact shadows survived VSM; the open floor did
		//    not. PCFSoft renders the directional's floor contact cleanly (no bleed, no acne at bias
		//    −0.0005 / normalBias 0.02). So DIRECTIONAL casters → PCFSoft.
		//  • Model B's overhead contact SPOT keeps VSM — its soft contact look + the LAW-1/2 depth tuning
		//    (see computeShadowFit) were bisected against VSM; PCF would change both.
		// 🔴 ASK-139 §94.14-2 (Shaun's re-entry picture: every product drawn, NO fixture) — PCF, never the deprecated
		// PCFSoft. three r183 swaps PCFSoft → PCF inside the first shadow render, AFTER compose's compileAsync has baked
		// `shadowMapType 2` into the programs; on a first entry the swap reads as a type change (VSM → PCF) and three
		// recompiles every material, but on a RE-entry the previous shadow render was already PCF, so nothing recompiles
		// and the cached fixture materials draw with a PCFSoft program against a PCF depth map ("Mismatch between texture
		// format and sampler type (shadow)") — nothing on screen. Measured: program cacheKey …,2,4… vs …,1,4…
		// (shadowMapType), and `material.needsUpdate` alone brings the fixtures back. PCF is what has rendered all along.
		const hasDirectional = casters.some((c) => c && c.isDirectionalLight);
		this.renderer.shadowMap.type = hasDirectional ? THREE.PCFShadowMap : THREE.VSMShadowMap;
		this._enrolShellShadowReceivers();
		const bounds = this._shellRoomBounds || DEFAULT_ROOM_BOUNDS;
		let n = 0;
		for (const c of casters) { if (c) { this._configureCaster(c, bounds, tp, shadowIntensity); n++; } }
		this._retireShadowCatcher(this.renderTier === "low");
		this.activeShadowModel = model || this.activeShadowModel || null;
		console.log(`[shadows] mode=${mode} model=${model || "?"} tier=${this.renderTier} → ${n} fitted caster(s), map=${tp.shadowMapSize}, ${hasDirectional ? "PCF" : "VSM"} (bounds ${(bounds.width||0).toFixed(1)}×${(bounds.depth||0).toFixed(1)}×${(bounds.height||0).toFixed(1)}m)`);
		return n;
	}

	// Fully configure ONE casting light's shadow: fit its camera to the room AABB + tier res/blur/
	// bias. Directional → ortho; spot → overhead contact placement + perspective. Marks
	// userData.shadowConfigured for the render-truth probe / verify gate (no default survives).
	_configureCaster(light, bounds, tp, shadowIntensity = SHADOW_INTENSITY_DEFAULT) {
		if (!light?.shadow) return;
		const isDir = !!light.isDirectionalLight;
		const fit = computeShadowFit(bounds, isDir ? "directional" : "spot");
		light.castShadow = true;
		light.shadow.mapSize.set(tp.shadowMapSize, tp.shadowMapSize);
		light.shadow.radius = tp.shadowRadius;
		light.shadow.blurSamples = tp.shadowBlurSamples;
		light.shadow.bias = -0.0005;
		light.shadow.normalBias = 0.02;
		// shadowIntensity knob → shadow DARKNESS (THREE LightShadow.intensity, r166+; guarded).
		if ("intensity" in light.shadow) light.shadow.intensity = shadowDarkness(shadowIntensity);
		const cam = light.shadow.camera;
		if (isDir) {
			// Model A sun — angled overhead key (crisp directional form), aimed at room centre.
			light.position.set(fit.posX, fit.posY, fit.posZ);
			if (light.target) { light.target.position.set(0, 0, 0); light.target.updateMatrixWorld?.(); }
			cam.left = fit.left; cam.right = fit.right; cam.top = fit.top; cam.bottom = fit.bottom;
			cam.near = fit.near; cam.far = fit.far;
		} else {
			// overhead contact caster — above room centre, aim down, fit cone + near/far.
			// intensityScale compensates the Metal-safe raised height (LAW 2) so the knob's floor
			// contribution stays height-invariant — the configurer moved the light, so it owns this.
			light.position.set(0, fit.posY, 0);
			if (light.target) { light.target.position.set(0, 0, 0); light.target.updateMatrixWorld?.(); }
			light.angle = fit.angle;
			light.distance = fit.distance;
			if (Number.isFinite(fit.intensityScale)) light.intensity *= fit.intensityScale;
			cam.near = fit.near; cam.far = fit.far;
		}
		cam.updateProjectionMatrix?.();
		light.userData.shadowConfigured = true;
		light.userData.shadowFar = fit.far;
	}

	// Enrol the shell's real surfaces (floor + walls + ceiling + counter) as shadow RECEIVERS —
	// AAA shadows land on real geometry, retiring the flat catcher plane. Uses the order-safe
	// slot resolver.
	_enrolShellShadowReceivers() {
		const root = this._shellRoot;
		if (!root) return 0;
		let n = 0;
		root.traverse((node) => {
			if (!node.isMesh) return;
			const slot = resolveShellSlot(node.name || "");
			if (slot === "floor" || slot === "wall" || slot === "ceiling" || slot === "counter") { node.receiveShadow = true; n++; }
		});
		return n;
	}

	// Retire the WS4 studio__shadow_catcher stopgap plane (real surfaces receive now). Kept only
	// as a tier-`low` fallback where enrolling every wall could cost too much.
	_retireShadowCatcher(keepForLow) {
		if (keepForLow) return;
		const c = this.scene.getObjectByName?.("studio__shadow_catcher");
		if (c) { c.parent?.remove(c); c.geometry?.dispose?.(); c.material?.dispose?.(); }
	}

	/**
	 * 🔴 …AND IT CARRIES AN IBL. `this.scene.environment = null` was this line for as long as the
	 * fallback has existed, and it was invisible while every shop on the plane had a template.
	 *
	 * MEASURED 2026-09-11, triaging the nightly sweep's `probe-shop-env` red: `e2e-multiroom` is the
	 * first `kind:"shop"` on the boot with **no template block** (`scene.template.id === null`, where
	 * demo-store/ci-probe-shop carry `comic-shop` and spider-web carries `platform-shell`). No template
	 * → `resolved.light.lighting` is absent → `template-composition.js:204` never runs the recipe →
	 * this fallback takes over → and it set the environment to nothing at all.
	 *
	 * The analytic rig below is fine for diffuse and it is NOT the shortfall. The shortfall is
	 * specular: glTF PBR resolves reflections from `scene.environment`, so with none, every metal and
	 * every gloss in the shop reflects black. That is what "renders under-spec" means and it is why the
	 * gate asserts an environment rather than a light count.
	 *
	 * So the fallback finishes the job it starts: it already commits to a warm interior palette, and
	 * this is the same palette as an environment. Procedural + PMREM, the technique
	 * `_getOrBuildHubEnvMap` already uses — no asset, no fetch, no pin. A gentle vertical gradient
	 * (warm ceiling bounce over a darker floor) is what a room without a window actually looks like,
	 * and it is the default every engine ships rather than none: Unity's default environment lighting,
	 * Unreal's default SkyLight. A shop with no recipe is under-dressed; it is never unlit.
	 */
	_applyShellInteriorLighting(_scene) {
		this.scene.background = new THREE.Color(0x1a1410);
		this.scene.fog = new THREE.FogExp2(0x1a1410, 0.04);
		this.scene.environment = this._getOrBuildInteriorEnvMap();
		this.ambientLight.color.set(0xffe1b3);
		this.ambientLight.intensity = 0.85;
		this.fillLight.color.set(0xffffff);
		this.fillLight.intensity = 0.45;
		this.fillLight.castShadow = false;   // fill never casts — the owner's dedicated caster does
		this.floor.visible = false;
		this.renderer.toneMappingExposure = 1.0;

		const keyLight = new THREE.SpotLight(0xffeecc, 5.0);
		keyLight.position.set(0, 3.2, 0);
		keyLight.target.position.set(0, 0, -1);
		keyLight.penumbra = 0.55;
		this.scene.add(keyLight);
		this.scene.add(keyLight.target);
		this.shopLights.push(keyLight, keyLight.target);
		// Shadow config owned by the framework standard (fit-to-bounds + tier VSM), not inline.
		this._configureShadows({ mode: "interior", casters: [keyLight] });

		const hemi = new THREE.HemisphereLight(0xfff1d6, 0x4a3a30, 0.6);
		hemi.position.set(0, 4, 0);
		this.scene.add(hemi);
		this.shopLights.push(hemi);

		for (const px of [-2.5, 2.5]) {
			const pt = new THREE.PointLight(0xffd5a0, 1.6, 8, 1.5);
			pt.position.set(px, 3.0, -1.0);
			this.scene.add(pt);
			this.shopLights.push(pt);
		}
	}

	// Decor panel — supports `imagePanel` (image-textured, optional UV crop,
	// optionally emissive) or the older `placeholderPanel` (canvas text fallback).
	// No-op when neither is configured.
	_applyDecorPanel(scene, projectAssets) {
		const imageCfg = scene?.decorations?.imagePanel;
		const textCfg  = scene?.decorations?.placeholderPanel;
		const panelCfg = imageCfg || textCfg;
		if (!panelCfg) return;
		let panel = null;
		if (imageCfg) {
			const url = resolveProjectAssetUrl(projectAssets, imageCfg.assetKey);
			if (url) {
				panel = makeImagePanel(this.textureLoader, url, {
					width:             imageCfg.size?.width  ?? 3.0,
					height:            imageCfg.size?.height ?? 2.0,
					uv:                imageCfg.uv,
					emissive:          imageCfg.emissive !== false,
					emissiveIntensity: imageCfg.emissiveIntensity ?? 1.0,
				});
			} else {
				console.warn(`[shop-shell] imagePanel missing asset for key '${imageCfg.assetKey}'`);
			}
		} else {
			panel = makeCanvasTextPanel(
				textCfg.text || "Coming Soon",
				textCfg.size?.width  ?? 3.0,
				textCfg.size?.height ?? 1.5,
			);
		}
		if (!panel) return;
		const pp = panelCfg.position || { x: 0, y: 2.0, z: -3.0 };
		panel.position.set(pp.x, pp.y, pp.z);
		switch (panelCfg.facing) {
			case "-Z": panel.rotation.y = Math.PI; break;
			case "+X": panel.rotation.y = -Math.PI / 2; break;
			case "-X": panel.rotation.y =  Math.PI / 2; break;
			default:   /* +Z, no rotation */ break;
		}
		this.stageRoot.add(panel);
	}

	// Walk the shell GLB and harvest empties matching the contract:
	//   • EXIT_ANCHOR              — exit trigger (stage-level singleton)
	//   • SPAWN / SPAWN_LOOK       — default spawn (stage-level singleton)
	//   • SPAWN_<REF> / _LOOK      — named spawn (e.g. SPAWN_FROM_PORTAL_1)
	//   • <KIND>_ANCHOR_NN         — any pattern registered via registerFixture()
	//                                (longbox, shortbox, wall-shelf, book-row,
	//                                 pile-table, vinyl-wall-shelf, vinyl-bin,
	//                                 listening-station, future ?)
	// Each entry captures world position/rotation and the empty's local scale.
	_collectShopAnchors(root) {
		const anchors = {
			exit: null,
			spawns: { default: null, named: {} },
			registered: new Map(),
		};
		root.updateMatrixWorld(true);
		const exitRe = /^EXIT_ANCHOR$/i;
		const parseSpawnName = (rawName) => {
			const upper = rawName.toUpperCase();
			if (upper !== "SPAWN" && !upper.startsWith("SPAWN_")) return null;
			const isLook = upper.endsWith("_LOOK");
			const core   = isLook ? upper.slice(0, -5) : upper;
			if (core === "SPAWN") return { ref: "default", isLook };
			if (!core.startsWith("SPAWN_")) return null;
			const ref = core.slice(6).toLowerCase().replace(/_/g, "-");
			return ref ? { ref, isLook } : null;
		};
		const rawSpawns = { default: null, lookDefault: null, named: {}, namedLook: {} };

		root.traverse((node) => {
			if (!node.name) return;
			const wp = new THREE.Vector3();
			const wq = new THREE.Quaternion();
			const ws = new THREE.Vector3();
			node.matrixWorld.decompose(wp, wq, ws);

			if (exitRe.test(node.name)) {
				anchors.exit = {
					name: node.name, index: 0,
					position: wp.clone(), scale: ws.clone(),
					rotationY: new THREE.Euler().setFromQuaternion(wq, "YXZ").y,
				};
				return;
			}

			const sp = parseSpawnName(node.name);
			if (sp) {
				const point = { x: wp.x, y: wp.y, z: wp.z };
				if (sp.ref === "default") {
					if (sp.isLook) rawSpawns.lookDefault = point; else rawSpawns.default = point;
				} else {
					if (sp.isLook) rawSpawns.namedLook[sp.ref] = point; else rawSpawns.named[sp.ref] = point;
				}
				return;
			}

			const reg = matchFixture(node.name);
			let kind = reg?.entry.kind || null;
			let index = reg ? parseInt(reg.match[1] || "0", 10) : 0;
			if (!kind) {
				// DECISION-052 — register composer-converged catalogue kinds that have NO
				// registry pattern (e.g. longbox/shortbox, composer-retired) so PASS 1 composes
				// them via the socket→slot-layout mapping. Unconverged catalogue kinds stay on
				// the socket render path (this returns false for them).
				const derived = deriveFixtureKind(node.name);
				if (derived && isComposerConvergedKind(derived, this._fixtureCatalogueCached)) {
					kind = derived;
					const m = /_(\d+)$/.exec(node.name);
					index = m ? parseInt(m[1], 10) : 0;
				}
			}
			if (kind) {
				const entry = {
					name: node.name,
					index,
					position: wp.clone(),
					scale: ws.clone(),
					rotationY: new THREE.Euler().setFromQuaternion(wq, "YXZ").y,
					fixtureKind: kind,
					// glTF extras carry Core-tier defaults authored in the shell GLB.
					// Used by per-fixture builders (e.g. longbox/shortbox read
					// facingDeg + hinge from here). Storefront-tier knobs
					// override these at the fixture-builder layer.
					extras: { ...(node.userData || {}) },
				};
				if (!anchors.registered.has(kind)) {
					anchors.registered.set(kind, []);
				}
				anchors.registered.get(kind).push(entry);
			}
		});

		if (rawSpawns.default) {
			anchors.spawns.default = {
				position: rawSpawns.default,
				lookAt:   rawSpawns.lookDefault || rawSpawns.default,
			};
		}
		for (const [ref, pos] of Object.entries(rawSpawns.named)) {
			anchors.spawns.named[ref] = {
				position: pos,
				lookAt:   rawSpawns.namedLook[ref] || pos,
			};
		}

		const byIndex = (a, b) => a.index - b.index;
		for (const arr of anchors.registered.values()) arr.sort(byIndex);
		return anchors;
	}

	// Studio v2 — per DECISION-007 (in galleria-coordination/AGREED_DECISIONS.md).
	// Fixture instances authored via Studio v2 (drag-place + transform inspector)
	// land here as records GCG stores, Forge composes into scene packages, and
	// the runtime renders. Each instance carries its own
	// `(fixtureKind, position, rotation, scale, knobs?)` set; Forge bakes them
	// into `scene.instances[]` of the published scene package.
	//
	// This method converts those instance records into anchor entries with the
	// same shape `_collectShopAnchors` produces — so fixture builders
	// (`fixtures/*-anchor.js`) consume them identically to GLB-baked anchors.
	// The unified anchor surface is the whole point of DECISION-007: anchors
	// don't disappear; they gain a second source.
	//
	// Anchor-name convention: instances carry server-generated `anchorName`
	// strings (e.g. `LONGBOX_ANCHOR_INST_<uuid>`), collision-free with the
	// `<KIND>_ANCHOR_NN` pattern used by GLB-authored anchors. Indexed off
	// the instance's `instanceId` so duplicates re-emerge cleanly post-edit.
	//
	// Mutates `anchorsOut.registered` (a `Map<kind, AnchorEntry[]>`) in place.
	// No-op when `instances` is empty or absent — which is the current state
	// (no shops have authored instances yet; Studio v2 UI is gated on Forge
	// being live per DECISION-014's Phase 5).
	_collectInstancesAsAnchors(instances, anchorsOut) {
		if (!Array.isArray(instances) || instances.length === 0) return;
		if (!anchorsOut?.registered) return;

		// MINIMAL DUAL-EMIT HANDLING (DECISION-019 Phase B Step 1, kapowx PR #24 fix).
		//
		// First attempt (kapowx PR #23) augmented the existing GLB-derived entry
		// in place — overwrote `fixtureKind`, replaced `extras` with a new
		// object via spread, sometimes moved entries between kind-buckets, then
		// re-sorted every kind array. Empirically this broke interactions on
		// populated fixtures (shortbox lid wouldn't open, spinner-rack arrows
		// stopped responding); Shaun isolated it 2026-05-26 via a debug preview
		// channel that disabled this whole function. Root cause unconfirmed but
		// almost certainly the `existing.extras = { ...existing.extras, ... }`
		// reassignment dropping a non-enumerable / symbol-keyed property the
		// GLTFLoader populates on node.userData (which `_collectShopAnchors`
		// shallow-spreads into `entry.extras`), OR the sort reordering entries
		// in a way downstream interactable wiring depended on.
		//
		// Step 1 emit (instances carry `{instanceId, fixtureKind, anchorName, knobs:{}}`
		// only) genuinely doesn't need any of those mutations — the GLB-derived
		// entry already has the right kind, transform, and extras. The only
		// new thing instances give us in Step 1 is the stable `instanceId`,
		// useful for Studio-v2-side cross-reference later. So this minimum
		// version JUST attaches instanceId on the existing entry, full stop.
		//
		// Steps 3+ (transforms in instance), Step 5+ (legacy fixtures dropped
		// from boot.json), and future Studio v2 drag-place authoring will need
		// more handling. Re-introduce the additional augmentation logic step
		// by step as those phases ship, each time verifying interactions
		// don't regress on a preview channel before promoting to main dev.
		for (const inst of instances) {
			if (!inst || !inst.anchorName || !inst.fixtureKind) continue;
			let found = false;
			for (const entries of anchorsOut.registered.values()) {
				const entry = entries.find((e) => e.name === inst.anchorName);
				if (entry) {
					entry.instanceId = inst.instanceId;
					// The ORIGINAL shell-anchor pose, captured the first time this entry is seen (at
					// boot, before any instance override touches it). Every later resolution reads
					// this rather than entry.position, which may already BE an applied override —
					// that's what makes re-running this on each recompose idempotent, and what lets a
					// row that loses its transform fall back to the shell instead of sticking.
					// Only a SHELL-declared anchor has an authored pose to fall back to. Stamping this on
					// an entry that was created from a row (the add-fresh branch below) made the stamp
					// mean "an instance row matched this" instead of "the shell authored this" — see
					// `_fromInstanceRow` there for the two guards that quietly inverted because of it.
					if (!entry._fromInstanceRow) {
						entry._shellPose ||= { position: { x: entry.position.x, y: entry.position.y, z: entry.position.z }, rotationY: entry.rotationY };
					}
					// DECISION-050 §2 (ASK-087 slice-2, runtime-side ahead of Forge's Phase-B mint):
					// apply the ratified precedence `row.<field> ?? shellAnchor.<field>` PER FIELD, so a
					// deliberately free-placed (flagged) instance owns its transform and a transform-less
					// one keeps tracking the shell anchor. §3: scale is NOT applied (render ignores it).
					// Same rule at two points is idempotent — no collision when the mint starts composing it.
					const t = resolveInstanceTransform(inst, entry._shellPose);
					if (t.unflagged) {
						console.warn(`[instances] ${inst.anchorName}: transform present without a free-place provenance flag — ignoring it and tracking the shell anchor (DECISION-050 §2).`);
					}
					// Assign in BOTH directions: "instance" applies the override, "anchor" restores the
					// shell pose. A one-way apply left a reverted row frozen at its last moved position
					// until a full page load.
					if (t.position) entry.position.set(t.position.x, t.position.y, t.position.z);
					if (t.rotationY !== null) entry.rotationY = t.rotationY;
					// ASK-094: carry the persisted uniform scale (default 1.0) so the post-compose pass
					// (_applyInstanceScales) re-applies it — a scaled fixture holds its size on reload.
					entry.uniformScale = Number.isFinite(inst?.uniformScale) && inst.uniformScale > 0 ? inst.uniformScale : 1;
					// DECISION-061 §3: carry the placement HOST onto the anchor entry so compose mounts
					// the fixture group under it. Absent host = shell root, so every row written before
					// D-061 lands here as `null` and composes exactly as it always did — that is the
					// zero-migration clause, not a compatibility shim.
					entry.host = hostOf(inst);
					found = true; break;
				}
			}
			if (found) continue;

			// No matching GLB entry — fall through to add-fresh. Two paths
			// hit here: (a) Studio v2 truly-novel instance path (no GLB
			// empty pre-exists), (b) kinds whose per-kind anchor file was
			// retired (DECISION-024 Phase 6+) so `_collectShopAnchors`'
			// `matchFixture()` returns null for the GLB empty and the
			// pattern-derived index is lost. Recover the index by parsing
			// the trailing `_NN` suffix from `inst.anchorName` (matches
			// the `<KIND>_ANCHOR_NN` convention all GLB-authored anchors
			// use). Falls back to 0 for Studio v2 instances which lack
			// the suffix entirely.
			const p = inst.position || { x: 0, y: 0, z: 0 };
			const s = inst.scale    || { x: 1, y: 1, z: 1 };
			const r = inst.rotation || { y: 0 };
			const indexMatch = typeof inst.anchorName === "string"
				? inst.anchorName.match(/_(\d+)$/)
				: null;
			const anchorIndex = indexMatch ? parseInt(indexMatch[1], 10) : 0;
			const entry = {
				name:        inst.anchorName,
				index:       anchorIndex,
				position:    new THREE.Vector3(p.x ?? 0, p.y ?? 0, p.z ?? 0),
				scale:       new THREE.Vector3(s.x ?? 1, s.y ?? 1, s.z ?? 1),
				rotationY:   typeof r.y === "number" ? r.y : 0,
				// ASK-094: a PLACED (net-new) fixture hydrates through THIS add-fresh branch, not the
				// matched-anchor branch above — so carry uniformScale here too, or a scaled placed
				// fixture would reload at size 1. (Coord gate-hole catch, 2026-08-07.)
				uniformScale: Number.isFinite(inst.uniformScale) && inst.uniformScale > 0 ? inst.uniformScale : 1,
				fixtureKind: inst.fixtureKind,
				extras:      { ...(inst.knobs || {}) },
				instanceId:  inst.instanceId,
				// 🔴 ASK-128 §confirming-walk — THIS ENTRY EXISTS BECAUSE A ROW ASKED FOR IT.
				//
				// Two predicates need that fact and both were inferring it from `_shellPose`'s ABSENCE,
				// which stopped being reliable the moment the matched branch above started stamping
				// `_shellPose` on any entry a row matches: `removeSelectedFixture` refused a merchant's
				// own rocket, and `isMintedInstanceAnchor` (pack portability) called a hand-placed
				// fixture a portable target. An inference that used to hold is worse than a fact that
				// always does, so the fact is recorded where it is known.
				_fromInstanceRow: true,
			};
			if (!anchorsOut.registered.has(inst.fixtureKind)) {
				anchorsOut.registered.set(inst.fixtureKind, []);
			}
			anchorsOut.registered.get(inst.fixtureKind).push(entry);
		}
		// Sort is intentionally skipped — original sort in PR #23 may have
		// contributed to the interaction breakage by reordering entries from
		// GLB-traversal order to (index, name) order. Re-introduce when add-
		// fresh entries actually appear (Step 5+) and need ordering reconciled
		// with existing GLB-derived entries.
	}

	// Generic catalogue-instance renderer — DECISION-023's AAA-canonical
	// static-mesh-instance pipeline.
	//
	// For each kind in registeredAnchors that has a catalogue entry but
	// NO per-kind fixture builder, fetch the catalogue GLB (shared cache
	// across anchors of the same kind), then for each anchor: clone the
	// Body node (or scene root if Body is absent), apply a bbox-derived
	// offset so the mesh's base-centre lands at anchor.position, apply
	// anchor.rotationY around the anchor, and parent into _shellRoot.
	//
	// Today's consumer is decor (stained-glass-lamp + the 16 from the
	// 2026-05-26-6 library batch as they get instanced). Tomorrow:
	// product-holder builders converge into this base path once
	// DECISION-023 Phase 5 retires per-kind procedural geometry — the
	// builders keep their interaction + product-placement logic but
	// delegate mesh-rendering to this method.
	async _renderCatalogueInstances({ registeredAnchors, kindsWithBuilders, fixtureCatalogue, skipAnchorIds, token }) {
		if (!this._shellRoot || !registeredAnchors || !fixtureCatalogue) return;
		const skip = skipAnchorIds instanceof Set ? skipAnchorIds : null;
		for (const [kind, anchors] of registeredAnchors.entries()) {
			if (kindsWithBuilders.has(kind)) continue;
			if (!fixtureCatalogue[kind]) continue;
			if (!anchors || anchors.length === 0) continue;
			// Only touch kinds that ACTUALLY get catalogue-rendered here. builder-composed anchors are all
			// in skipAnchorIds — loading their GLB/manifest was wasteful, and (since the manifest stash
			// below drives move-path placementRules) stashing a builder manifest would silently re-enable
			// the HELD longbox move-adoption (front:0.4 clearance). So bail when every anchor is skipped.
			if (skip && anchors.every((a) => skip.has(a.name))) continue;
			if (token !== this.currentToken) return;

			let gltf;
			try {
				gltf = await loadFixtureCatalogue({ catalogue: fixtureCatalogue, kind, gltfLoader: this.gltfLoader });
			} catch (err) {
				console.warn(`[catalogue-instance] failed to load ${kind}:`, err);
				continue;
			}
			if (token !== this.currentToken) return;
			// Stash the manifest so _placeCatalogueClone reads the authored castShadow (ASK-084) — cached.
			await this._ensureFixtureManifest(kind);
			if (token !== this.currentToken) return;
			// srcBody + base-centre reference, computed ONCE per kind (not per anchor).
			const ref = this._catalogueSourceRef(gltf);
			if (!ref) continue;

			let placed = 0;
			for (const anchor of anchors) {
				if (token !== this.currentToken) return;
				if (skip?.has(anchor.name)) continue;
				this._placeCatalogueClone({ kind, anchor, ...ref, fixtureCatalogue });
				placed++;
			}
			console.log(`[catalogue-instance] ${kind}: rendered ${placed} instance(s)`);
		}
	}

	// The catalogue GLB's placement reference — the `Body` node (or scene root for simple decor GLBs
	// that don't follow the DECISION-022 named-node convention) plus its bbox base-centre. Computed
	// once per kind so the per-anchor work is a single clone + position/rotation set.
	_catalogueSourceRef(gltf) {
		const src = gltf?.scene;
		if (!src) return null;
		const srcBody = src.getObjectByName("Body") || src;
		srcBody.updateMatrixWorld(true);
		const srcBbox = new THREE.Box3().setFromObject(srcBody);
		const cataRef = new THREE.Vector3(
			(srcBbox.min.x + srcBbox.max.x) * 0.5,
			srcBbox.min.y,
			(srcBbox.min.z + srcBbox.max.z) * 0.5,
		);
		return { srcBody, cataRef };
	}

	// Clone ONE catalogue instance at an anchor: base-centre → anchor.position, yaw about the anchor,
	// register it as a first-class movable fixture (ASK-088 slice-3a), and materialise its lights.
	// Returns the clone. Shared by the batch loader and live placement (ASK-088 slice-3b) — one path,
	// so a placed luminaire is byte-identical to a hydrated one.
	_placeCatalogueClone({ kind, anchor, srcBody, cataRef, fixtureCatalogue }) {
		const clone = cloneInWorldFrame(srcBody);   // the node's place in its own asset (a nested Body keeps its parent's transform — ASK-159 §2.18gb)
		clone.name = `${kind}__cat__${anchor.name}`;
		// MUST `.add(offsetVec)` rather than `.copy(offsetVec)` — catalogue nodes may carry authored
		// translations that position the mesh within the catalogue scene; replacing them with offsetVec
		// alone drops that authoring layer. See longbox-anchor.js; the shortbox dormant-then-surfaced
		// regression caught the equivalent bug in the per-fixture resolver 2026-05-27.
		const offsetVec = new THREE.Vector3().subVectors(anchor.position, cataRef);
		clone.position.add(offsetVec);
		// Apply anchor's Y rotation around anchor.position (not the mesh local origin). Matches the
		// rotate-around-pivot formula used by longbox-anchor.js for body+lid rotation.
		const yaw = anchor.rotationY || 0;
		if (Math.abs(yaw) > 0.0001) {
			const rotQ = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
			const delta = clone.position.clone().sub(anchor.position).applyQuaternion(rotQ);
			clone.position.copy(anchor.position).add(delta);
			clone.quaternion.premultiply(rotQ);
		}
		// Fixtures cast onto the contact-shadow catcher so they read as grounded (WS4). Catalogue clones
		// default to castShadow=false, so set it explicitly. ASK-084 (Content, release-2026-07-24-01):
		// consume the authored top-level manifest `castShadow` — absent = true (back-compat, the ASK-080
		// blanket), false only for the two flush coplanar kinds (floor-mat / wall-poster) whose shadow
		// would be acne against their own support surface (a real pixel-gate variance source). Inert
		// until the -24-01 pin lands (all default true today); rides Coord's queued re-capture then.
		const castShadow = fixtureCatalogue?.[kind]?.manifest?.castShadow !== false;
		clone.traverse((node) => {
			if (node.isMesh) { node.castShadow = castShadow; node.receiveShadow = true; }
		});
		// ASK-088 slice-3a — catalogue decor becomes a first-class fixture INSTANCE: registered in the
		// rebind registry (so selection / move / Inspector key on it, exactly like a builder fixture)
		// and parented into the per-anchor transform group (so a move translates the whole clone —
		// mesh AND its lights — as one). The group sits at the origin under _shellRoot, so placement is
		// byte-identical to the previous `_shellRoot.add(clone)`; nothing moves until a move asks it to.
		// The clone MUST live inside the group (not just its leaf meshes) or a move would attach the
		// meshes out and strand the clone-parented lights at the old pose.
		const group = this._ensureFixtureGroup(anchor.name, anchor.host || null) || this._shellRoot;
		// D-061: the group is the mount AND the host handle — a fixture placed on this one keys on the
		// instanceId read straight off it (hostForObject → hostFromAncestors).
		if (anchor.instanceId) group.userData.instanceId = anchor.instanceId;
		group.add(clone);
		const meshes = [];
		clone.traverse((node) => { if (node.isMesh) meshes.push(node); });
		(this._fixtureMaterialRebinds ||= []).push({ kind, anchorName: anchor.name, meshes });
		this._instantiateLuminaireLights(anchor, clone, fixtureCatalogue);
		this._noteComposedPose(anchor.name, "catalogue-clone", anchor.rotationY || 0);
		return clone;
	}

	// Render ONE catalogue instance live, for a just-placed fixture (ASK-088 slice-3b). Loads the
	// kind's GLB (cache-hit after first paint) then places a single clone at the anchor entry — the
	// live-render path recomposeTemplate deliberately does NOT cover (it keeps existing meshes and
	// only re-skins). `anchor` is a compose-shaped entry: {name, position:Vector3, rotationY, extras}.
	async _renderSingleCatalogueInstance({ kind, anchor, fixtureCatalogue, token }) {
		if (!this._shellRoot || !kind || !anchor?.name || !fixtureCatalogue?.[kind]) return null;
		// 🔴 ASK-177 §6 item 4 (5g-hub 7c, ALPHA023) — A PLACED HOLDER IS COMPOSED, NOT CLONED. This live path cloned every
		// kind as decor, so a just-placed `display-pedestal` (a derived-surface grammar socket — `composerOwnsKind` true)
		// had a rebind but no composed entry and no registry row: the pin's re-seat (`_rehydrateSeatedProductsNow`,
		// scoped to `boxEntries`) never visited it, and the newcomer pass read the rebind as "already has geometry".
		// MEASURED on the deployed plane (probe-k1-studio-sheets A4, ci-probe-shop): PUT 200, the feed seated the figurine
		// on DISPLAY_PEDESTAL_ANCHOR_INST_* · SUPPORT_TOP_01, the scene drew nothing on the pedestal — until a reload, where
		// the hydrate registers the row and the boot pass composes it. Now the live path asks the SAME declaration the
		// newcomer pass asks (ruling 7): a composer-owned kind is registered like a hydrated row and composed through the
		// one pipeline with the pool the composer last ran with; decor (or a holder the composer declines) keeps the clone.
		if (await this._composePlacedHolder({ kind, anchor, fixtureCatalogue, token })) return this._fixtureGroups?.get?.(anchor.name) || null;
		let gltf;
		try {
			gltf = await loadFixtureCatalogue({ catalogue: fixtureCatalogue, kind, gltfLoader: this.gltfLoader });
		} catch (err) {
			console.warn(`[catalogue-instance] failed to load ${kind} for live placement:`, err);
			return null;
		}
		if (token != null && token !== this.currentToken) return null;
		await this._ensureFixtureManifest(kind);   // authored castShadow (ASK-084) available to the clone
		const ref = this._catalogueSourceRef(gltf);
		if (!ref) return null;
		const clone = this._placeCatalogueClone({ kind, anchor, ...ref, fixtureCatalogue });
		console.log(`[catalogue-instance] ${kind}: rendered 1 live instance at ${anchor.name}`);
		return clone;
	}

	/** ASK-177 §6 item 4 — a just-placed composer-owned kind (grammar socket · manifest slot · declared interaction) is
	 *  registered as a row-born anchor and composed through `_composeRegisteredFixtures`. → true when it composed. */
	async _composePlacedHolder({ kind, anchor, fixtureCatalogue, token }) {
		const registered = this._shopAnchors?.registered;
		if (!registered) return false;
		const manifest = await this._kindManifest(kind);
		if (!composerOwnsKind({ manifest, catalogueEntry: fixtureCatalogue?.[kind], isGrammarSocket })) return false;
		if (token != null && token !== this.currentToken) return false;
		if (!registered.has(kind)) registered.set(kind, []);
		const list = registered.get(kind);
		if (!list.some((e) => e?.name === anchor.name)) list.push({ ...anchor, uniformScale: 1, fixtureKind: kind, _fromInstanceRow: true });   // the commit's entry IS the hydrate's shape
		let composed = new Set();
		try {
			composed = await this._composeRegisteredFixtures({ scene: this.currentScene, products: this._seatedProducts || [], fixtureCatalogue, token: token ?? this.currentToken, onlyAnchorNames: new Set([anchor.name]) });
		} catch (err) { console.warn(`[place] ${anchor.name} compose:`, err?.message || err); }
		if (composed.has(anchor.name)) { console.log(`[place] ${anchor.name}: composed (live)`); return true; }
		console.warn(`[place] ${anchor.name}: declined — clone`);
		return false;
	}

	// Render any registered anchor that doesn't yet have composed geometry — the post-boot hydrate
	// newcomers (ASK-088 slice-3b). Called from recomposeTemplate. No-op unless a genuinely new
	// anchor exists, so it's free on the per-frame knob-drag recompose path; skips every
	// already-composed anchor so it can never double-render.
	//
	// 🔴 A NEWCOMER CAN BE A PRODUCT HOLDER NOW, AND UNTIL ASK-121 IT COULD NOT (fixed 2026-09-01).
	// This path was written for luminaires and decor, so it cloned the GLB and stopped. That was
	// complete while every holder was baked into the shell: nothing you could place post-boot took
	// stock. D-061 free placement plus P2-SOCKETS' derived `surface` sockets ended that — ANY
	// catalogue fixture with a support top is a holder the moment a merchant puts it down.
	//
	// MEASURED on the deployed plane before the fix: a `display-pedestal` placed on vanessa hydrated,
	// cloned, and appeared — with NO composed entry, NO registered surface region and NO seats. GCG
	// seated five real pins onto its socket and the feed carried them; the renderer drew none of them
	// and the merchant could not even drop onto it, because the region a pointer resolves against is
	// registered by `composeFixture` and `composeFixture` never ran. Panel ≠ pixels, at our door.
	//
	// This is the same shape GCG closed on the server the same day (its rule validator read what the
	// SHELL declares and ignored what the shop COMPOSES). Both halves had one door each that still
	// believed a shop's holders are fixed at bake time.
	/** The kind's manifest through the memoised loader — WITHOUT stashing it on the catalogue entry (a stash drives the
	 *  move-path placementRules; see `_renderCatalogueInstances`). Records the kind's declared interactions as it
	 *  goes, so `declaredInteractionsFor` can answer synchronously once any caller has read the manifest. */
	async _kindManifest(kind) {
		if (!kind || !this._fixtureCatalogueCached?.[kind]) return null;
		let manifest = null;
		try { manifest = await loadFixtureManifest({ catalogue: this._fixtureCatalogueCached, kind }); }
		catch { manifest = null; }
		(this._kindInteractions ||= new Map()).set(kind, declaredInteractions(manifest));
		return manifest;
	}

	/** ASK-159 §2.18gb ruling 7 — the interactions a kind DECLARES (its manifest), or null while its manifest is unread.
	 *  The Studio's hinge control reads `has("open-close")` here instead of a name list. */
	declaredInteractionsFor(kind) {
		return this._kindInteractions?.get(kind) ?? null;
	}

	/** Read the manifests of these kinds (memoised); resolves to how many kinds became known by this call. */
	async resolveKindDeclarations(kinds) {
		const todo = [...new Set(kinds || [])].filter((k) => k && !this._kindInteractions?.has(k) && this._fixtureCatalogueCached?.[k]);
		await Promise.all(todo.map((k) => this._kindManifest(k)));
		return todo.filter((k) => this._kindInteractions?.has(k)).length;
	}

	async _renderHydratedCatalogueNewcomers() {
		const registered = this._shopAnchors?.registered;
		if (!registered || !this._fixtureCatalogueCached) return;
		// "Already has geometry" needs a witness BOTH paths write, and the rebind registry is not one.
		// The decor clone registers a rebind unconditionally; `composeFixture` registers one only when
		// the kind declares `materialSlots`, and `display-pedestal` declares none. Keying on rebinds
		// alone therefore reads a composed materialSlot-less holder as a newcomer on every subsequent
		// recompose and composes it again — found by measuring, one recompose after the fix above.
		// `boxEntries` is set for every composed fixture, so the union is the honest answer.
		const already = new Set([
			...(this._fixtureMaterialRebinds || []).map((r) => r.anchorName),
			...(this.boxEntries?.keys?.() || []),
		]);
		const newcomers = [];
		for (const entries of registered.values()) {
			for (const a of entries) if (a?.name && !already.has(a.name)) newcomers.push(a);
		}
		if (!newcomers.length) return;

		// A newcomer whose catalogue entry carries a D-062 grammar socket goes through the ONE
		// composer pipeline (DECISION-052), exactly as a baked holder does — same priority order,
		// same manifest preflight, same `composeFixture`. It is not a second rendering path: it is
		// the existing one, scoped to the anchors that arrived late.
		//
		// 🔴 ASK-159 §2.18gb ruling 7 — BY DECLARATION, the boot pass's own rule. This read "a kind carrying a D-062
		// GRAMMAR socket", so every manifest-slot holder (longbox-tall · shortbox · featured-comic · the tables · the
		// spinner · the shelves — `grammarSource: "derived"`) fell to the decor clone below: MEASURED on ci-probe-shop
		// under the longbox-tall override, the seeded `LONGBOX_TALL_ANCHOR_INST_8B1A13F2` registered (bucket longbox-tall,
		// _fromInstanceRow) and drew as "[catalogue-instance] longbox-tall: rendered 1 instance(s)" — a body with no lid,
		// no composed entry, no hinge control. The boot pass composes every kind whose manifest declares what the
		// composer builds; a newcomer now asks the same declaration (a grammar socket, a manifest slot, or a declared
		// interaction — `composerOwnsKind`). Plain decor declares none of the three and keeps the clone path.
		const owns = new Map();
		for (const kind of new Set(newcomers.map((a) => a.fixtureKind))) {
			const manifest = await this._kindManifest(kind);
			owns.set(kind, composerOwnsKind({ manifest, catalogueEntry: this._fixtureCatalogueCached?.[kind], isGrammarSocket }));
		}
		const holders = newcomers.filter((a) => owns.get(a.fixtureKind));
		let composed = new Set();
		if (holders.length) {
			// ASK-142 F-142c instrument — what the newcomer pass composes AGAINST, named per holder: the
			// pool it inherits and how many of its lines are assigned to each newcomer. The hub run read
			// "0 products" on a crate whose feed seated two; this line says which half was empty.
			const pool = this._seatedProducts || [];
			const assigned = holders.map((a) => `${a.name}=${pool.filter((p) => (p?.fixtureAssignments || []).some((x) => x?.fixtureAnchor === a.name)).length}`);
			console.log(`[composer] hydrated newcomer pool: ${pool.length} line(s) · assigned ${assigned.join(" · ")} · signature ${this._seatedSignature ? String(this._seatedSignature).length : "none"}`);
			composed = await this._composeRegisteredFixtures({
				scene:            this.currentScene,
				// The pool the composer last ran with. A newcomer placed before any stock is routed to
				// it composes EMPTY and registers its region — which is exactly right, and is what
				// makes it droppable; the pin write's own re-seat then fills it, because it now has a
				// composed entry to be re-seated.
				products:         this._seatedProducts || [],
				fixtureCatalogue: this._fixtureCatalogueCached,
				token:            this.currentToken,
				onlyAnchorNames:  new Set(holders.map((a) => a.name)),
			});
			if (composed.size) console.log(`[composer] ${composed.size} hydrated newcomer(s) composed as product holders: ${[...composed].join(", ")}`);
		}
		// Everything else — luminaires, busts, decor — keeps the clone path it always had. A holder
		// the composer declined (an unported layout, a missing Body node) falls through to it too,
		// so a fixture is never invisible because its grammar could not be drawn.
		await this._renderCatalogueInstances({
			registeredAnchors: registered,
			kindsWithBuilders: new Set(),
			fixtureCatalogue:  this._fixtureCatalogueCached,
			skipAnchorIds:     new Set([...already, ...composed]),
			token:             this.currentToken,
		});
	}

	// ASK-088 slice-3a — materialise a luminaire's typed emitters (catalogue lights[]) into THREE
	// lights, PARENTED to the fixture clone. The emitter position is authored LOCAL to Body (socket
	// precedent), so a child of the clone lands at the correct world pose for free — whatever the
	// clone's placement/yaw — and travels with the fixture on a move (the clone is adopted into the
	// per-anchor group). Per-instance knobs tune intensity/colour/range; the emitter POSE stays
	// authoring-owned. castShadow defaults OFF (tier-lean, Model-B baseline per Coord guidance 2) —
	// a per-light knob opts a hero luminaire into casting. Returns the count materialised.
	_instantiateLuminaireLights(anchor, clone, fixtureCatalogue) {
		if (!anchor || !clone || !fixtureCatalogue) return 0;
		let specs;
		try { specs = lightsForAnchor(anchor, fixtureCatalogue); }
		catch (err) { console.warn(`[luminaire] ${anchor?.name}: light resolve failed:`, err?.message || err); return 0; }
		if (!specs.length) return 0;
		let n = 0;
		for (const s of specs) {
			const color = new THREE.Color(s.color);
			const distance = s.range != null ? s.range : 0;   // 0 = THREE "no distance cutoff"
			let light;
			if (s.type === "spot") {
				light = new THREE.SpotLight(color, s.intensity, distance, s.angle, s.penumbra, 2);
				if (s.aim) {
					// Aim is Body-local too → the target rides the clone, so the cone tracks the fixture.
					light.target.position.set(s.aim.x, s.aim.y, s.aim.z);
					clone.add(light.target);
				}
			} else {
				light = new THREE.PointLight(color, s.intensity, distance, 2);
			}
			light.name = `__luminaire__${anchor.name}__${s.name}`;
			light.position.set(s.position.x, s.position.y, s.position.z);
			light.castShadow = s.castShadow === true;
			light.userData.luminaire = { anchorName: anchor.name, lightName: s.name };
			clone.add(light);
			n++;
		}
		if (n) console.log(`[luminaire] ${anchor.name} (${anchor.fixtureKind}): ${n} light(s) cast at emitter pose`);
		return n;
	}

	// ASK-088 slice-3c — the Inspector's VIEWPORT half: live-repaint a placed luminaire's light(s)
	// from a resolved patch, mutating the existing THREE light(s) in place (no re-instantiation), so a
	// slider drag reads instantly. patch fields: intensity, color (hex), range (null → no cutoff),
	// castShadow. POSE is never touched here (authoring-owned). Persistence is the Inspector's other
	// half (row knobs → PUT). Returns the count updated.
	updateLuminaireLight(anchorName, lightName, patch) {
		if (!anchorName || !patch) return 0;
		let n = 0;
		(this._shellRoot || this.scene)?.traverse((o) => {
			const lum = o.userData?.luminaire;
			if (!lum || lum.anchorName !== anchorName || (lightName && lum.lightName !== lightName)) return;
			if (typeof patch.intensity === "number" && Number.isFinite(patch.intensity)) o.intensity = Math.max(0, Math.min(50, patch.intensity));
			if (typeof patch.color === "string") { try { o.color.set(patch.color); } catch { /* bad hex → keep */ } }
			if (patch.range !== undefined) o.distance = (patch.range == null ? 0 : Math.max(0, patch.range));
			if (typeof patch.castShadow === "boolean") { o.castShadow = patch.castShadow; if (patch.castShadow) this._configureLuminaireShadow(o); }
			n++;
		});
		return n;
	}

	// Minimal shadow config when a merchant opts a luminaire into casting (tier-lean default is OFF).
	// A point light gets a modest cube shadow fitted to its range; best-effort (shadows may be tier-
	// gated off at the renderer, in which case this is inert until the renderer enables them).
	_configureLuminaireShadow(light) {
		if (!light?.castShadow || !light.shadow) return;
		light.shadow.mapSize?.set?.(512, 512);
		if (light.shadow.camera) {
			light.shadow.camera.near = 0.1;
			light.shadow.camera.far = Math.max(4, light.distance || 8);
			light.shadow.camera.updateProjectionMatrix?.();
		}
		light.shadow.bias = -0.002;
		light.shadow.needsUpdate = true;
	}

	// The live state of a placed luminaire's lights (what the Inspector reads back to verify a repaint).
	getLuminaireLightState(anchorName) {
		const out = [];
		(this._shellRoot || this.scene)?.traverse((o) => {
			const lum = o.userData?.luminaire;
			if (!lum || lum.anchorName !== anchorName) return;
			out.push({ name: lum.lightName, type: o.isSpotLight ? "spot" : "point",
				intensity: o.intensity, color: "#" + o.color.getHexString(), range: o.distance || null, castShadow: !!o.castShadow });
		});
		return out;
	}

	// Hub scene loader: outdoor dusk atmosphere with fog, env-mapped reflections,
	// glossy wet ground, and post-load emissive neon signs. GLB geometry only —
	// no fixtures, no comic-shop builders. scene.spawn controls camera placement.
	async _setHubSceneContext(scene, projectAssets, token) {
		// v2 hub — stylized HK night. Black sky + emissive starfield + reflective
		// wet-asphalt road. Brand emissive signs pop against dark surrounds. The
		// daylight HDRI path is skipped (would lift the scene to flat overcast);
		// instead we use the canvas starfield PMREM, then `_bakeHubReflectionEnv`
		// captures the loaded GLB scene (sky cyclorama stars + brand signage) into
		// the final env so the wet road reflects bright pinpoints + sign colour.
		const skyColor = 0x000000;        // pure black night sky
		const horizonColor = 0x000000;    // black horizon (fog barely visible)
		this.scene.background = new THREE.Color(skyColor);
		this.scene.fog = new THREE.FogExp2(horizonColor, 0.002);
		this.ambientLight.intensity = 0.18;
		this.ambientLight.color.set(0x2a3045);  // cool deep-blue ambient (night)
		this.fillLight.intensity = 0.1;
		this.fillLight.color.set(0x4a5070);     // dim cool fill
		this.fillLight.castShadow = false;
		this.floor.visible = false;
		this.renderer.toneMappingExposure = 1.2; // slight lift for night dynamic range
		// Hemisphere — dim cool night sky overhead, warm-ish ground bounce
		const hemi = new THREE.HemisphereLight(0x303a55, 0x1a1812, 0.25);
		hemi.position.set(0, 30, 0);
		this.scene.add(hemi);
		this.shopLights.push(hemi);

		// Moonlight — cool-white directional, high overhead, slight south-east
		// drift so shadows fall gently into the arcade. Modest intensity so the
		// brand emissive signs still dominate visual hierarchy.
		const moon = new THREE.DirectionalLight(0xc8d4e8, 1.0);
		moon.position.set(8, 40, 18);
		moon.target.position.set(0, 0, 0);
		this.scene.add(moon);
		this.scene.add(moon.target);
		this.shopLights.push(moon);
		this.shopLights.push(moon.target);

		// Skip the daylight HDRI in night mode — it would clobber the starfield
		// PMREM baked from the scene below. Use the canvas-built starfield env as
		// the initial environment; `_bakeHubReflectionEnv` upgrades it once the
		// hub GLB is in the scene (so reflections include cyclorama stars + signs).
		this.scene.environment = this._getOrBuildHubEnvMap();

		// Backdrop ground plane — sits 10cm BELOW the GLB's ground layers (which
		// the runtime now keeps visible). 400×400m matte dark, fills the void
		// beyond the arcade's GLB extent (visible through the gateway, past walls).
		// Same matte dark character as walls so road + walls read as one continuous
		// dark wrap, with the GLB's wet walkway + brand signs as the bright contrast.
		const ground = new THREE.Mesh(
			new THREE.PlaneGeometry(400, 400),
			new THREE.MeshStandardMaterial({
				color: 0x0a0a0c,
				roughness: 0.85,
				metalness: 0.0,
			}),
		);
		ground.rotation.x = -Math.PI / 2;
		ground.position.y = -0.10;
		this.roomRoot.add(ground);
		this._hubReflector = null;

		const spawnRef = scene?._activeSpawnRef;
		const spawn = (spawnRef && scene?.spawns?.[spawnRef]) || scene?.spawn || {
			position: { x: 0, y: 1.7, z: 10 },
			lookAt:   { x: 0, y: 1.4, z: 0 },
		};
		this._applyHubSpawn(spawn);

		// Stage 4: register portal triggers (XZ distance check, debounced via arming)
		this.activeTriggers = Array.isArray(scene?.portals) ? scene.portals.map(p => ({
			id: p.id,
			position: p.position,
			radius: p.radius ?? 1.5,
			target: p.target,
			spawnRef: p.spawnRef,
			label: p.label || p.id,
		})) : [];
		this._armedTriggers = new Set(); // populated as player moves outside zones

		// D-086 cl.1 (ASK-177 §10): the street carries no rings, beams or arrows for doors — the waypoint survives only as a
		// debug overlay (`?waypoints=debug`); the entrance is the door itself (`_hub-entrance.js`: the prompt + the box).
		if (/[?&]waypoints=debug\b/.test(globalThis.location?.search || "")) this._buildPortalWaypoints(Array.isArray(scene?.portals) ? scene.portals : [], "#ffffff", 0.6);

		// Criterion 15 — TAKEN merchant storefront door-slots become portals (+ gold waypoints), built once the street is
		// mounted, where the street declares each door (`_hub-door-arrival.js placeSlotDoors`, ASK-177 §9 item 1).
		const takenSlots = Array.isArray(scene?.doorSlotsTaken) ? scene.doorSlotsTaken : [];
		const freeRows = Array.isArray(scene?.doorSlotsFreeRows) ? scene.doorSlotsFreeRows : [];   // D-086 cl.5: a TO LET door's enquiry

		let hubAssetUrl = resolveProjectAssetUrl(projectAssets, "hub");
		if (hubAssetUrl) {
			console.log(`[hub-shell] loading hub from ${hubAssetUrl} ${describeAssetSource(projectAssets, "hub")}`);
		}
		const errors = [];
		let summary = "Hub loaded.";

		// Hub note: the optimised HK street GLB is in metres already, so no
		// per-asset scale is applied (matching the canonical-GLB convention).
		if (!hubAssetUrl) {
			errors.push("No hub.assetUrl in projectAssets — falling back to bare ground.");
		} else {
			try {
				// ASK-177 S1 — the split pair (layout, then the active theme) when the boot declares `scene.layout` (lazy chunk).
				// D-K2-4 (coord ASK-180 §3m, ruled): the merged slot is RETIRED — on the plane `projectAssets.hub` is the published
				// row's LAYOUT alone, so a pair that fails (a failed load, or no usable theme) composes NOTHING rather than a
				// theme-less street (measured before: 56 layout nodes drawn, no street life, nothing said). The failure is recorded
				// (`_hubBuildFailure`, read by the threshold card and the Report draft); a boot with no `scene.layout` keeps its one GLB.
				this._hubBuildFailure = null;
				let splitErr = null;
				const split = scene?.layout ? await import("./_hub-split.js").then((m) => m.loadHubSplit(this, { scene, projectAssets, pick: scene.hubThemePick })).catch((err) => { splitErr = err; return null; }) : null;
				if (scene?.layout && !split) {
					if (token !== this.currentToken) return createEmptyReport();
					const reason = splitErr ? String(splitErr?.message || splitErr) : "no theme of this street is usable on this device";
					this._hubSplit = null;
					this._hubBuildFailure = { what: "the street (layout + theme)", reason, at: new Date().toISOString() };
					console.error(`[hub] the street could not be built — ${reason}`);
					errors.push(`The street could not be built: ${reason}`);
					return { summary: "The street could not be built.", meta: ["sceneKind=hub"], fixture: { label: "Hub", mode: "scene", errors, warnings: [] }, content: { label: "Hub", mode: "hub", errors, warnings: [] } };
				}
				if (split) { hubAssetUrl = split.glbUrl; projectAssets = split.projectAssets; this._hubSplit = split.state; console.log(`[hub-shell] split pair: layout + theme ${split.state.theme} (${split.state.source})`); } else this._hubSplit = null;
				// D-088 Slice 0 (ASK-178 §3 item 2): the merged street's decoders + sky pair start beside its GLB (the split pair does it itself)
				const cp = !split && projectAssets?.hubSky ? await import("./_hub-critical-path.js").catch(() => null) : null;
				if (cp) { cp.warmHubDecoders(this); cp.prefetchHubSky(this, projectAssets); }
				const gltf = split ? split.gltf : cp ? await cp.loadStreetGlb(this, hubAssetUrl) : await this.gltfLoader.loadAsync(hubAssetUrl);
				if (token !== this.currentToken) return createEmptyReport();
				const root = gltf.scene || gltf.scenes?.[0];
				// ASK-146 §16 — a hub GLB carrying the hub-v3 contract (its OWN baked light + dusk sky) is
				// bound by a LAZY chunk only a v3 hub downloads; the v2 night dressing below is skipped for it.
				// The marker is the street's probe grid (scene extras → `root.userData`, GLTFLoader's own
				// mapping); `_hub-v3-contract.js` reads the full contract. Kept to one terse line on purpose:
				// this is the only hub-v3 code in the customer's initial bundle (check-bundle-budget).
				// `this._hubLife` (unset elsewhere) is the street's life object: ticked, drawn, disposed with the scene.
				const v3 = root && root.userData.lightProbes && await import("./_hub-v3.js");
				// The v2 night dressing is lazy too (ASK-159 §1, a customer-bundle reduction): only a pre-v3 hub GLB reads it.
				const v2 = root && !v3 ? await import("./_hub-v2-dressing.js") : null;
				if (root) {
					if (v2) v2.applyHubMaterialEnhancements(this, root);
					this.roomRoot.add(root);
					if (takenSlots.length || freeRows.length) await (await import("./_hub-door-arrival.js")).placeSlotDoors(this, root, takenSlots, { scene, freeRows });
					if (v3) this._hubLife = await v3.mountHubV3(this, { gltf, ground, scene, projectAssets, glbUrl: hubAssetUrl });
					if (this._hubLife && typeof performance !== "undefined") try { performance.mark("gg:hub-mounted"); } catch { /* User Timing absent */ }
					if (token !== this.currentToken) return createEmptyReport();
					// Brand signage at portals (Kapow / Nocturn) — emissive, baked into env
					if (v2) v2.buildHubSignage(this, scene, projectAssets, { resolveProjectAssetUrl, makeImagePanel });   // v2 only (lazy since ASK-177 §10)
					// ASK-129 §vacant-sign — a TAKEN door wears its shop's name. Before the env bake, so
					// the wet ground reflects the sign a merchant actually has rather than the one the
					// GLB was published with. Loaded on demand: it is customer-facing (so it cannot live
					// in the editor chunk) but it runs on exactly one scene, and `check-bundle-budget`
					// measured the difference — inline it put the customer's initial JS over the ceiling.
					// D-086 cl.4 as amended (ASK-177 §13a): every slot's lightbox nameplate — taken: its name; free/unlisted: TO LET
					// (`free` null = the registry was not read → only its taken rows speak).
					// ASK-178 §6 item 1 (the xb8qp seam): the nameplates are part of the entrance, so they are AWAITED — the street
					// reports ready (and its loader hides) only once every declared slot's door, prompt and plate is live.
					if (takenSlots.length || scene?.doorSlotsFree) {
						await import("./_hub-slot-signs.js")
							.then((m) => m.applySlotSigns({ stage: this, takenSlots, free: scene?.doorSlotsFree ?? null, hubRoot: root }))
							.catch((err) => console.warn("[hub] slot signage unavailable:", err?.message || err));
						if (token !== this.currentToken) return createEmptyReport();
					}
					this._markHubReady();
					// Bake the loaded scene (with glowing neons + signage) into a real env
					// map so the wet ground reflects actual sign colours, not just sky tint.
					if (v2) {
						v2.bakeHubReflectionEnv(this, ground);
						// v2 kit windows — wrap textures in a parallax shader so they read as
						// looking-into-an-interior rather than a flat decal.
						v2.applyV2KitWindowParallax(this, root);
						// v2 kit doors — find each `KIT_*_DOOR_HINGE` empty + its door child;
						// each frame we'll animate hinge.rotation.y based on player proximity.
						v2.collectV2KitDoors(this, root);
					}
					summary = `Hub loaded — ${hubAssetUrl.split("/").pop()}.`;
				} else {
					errors.push("GLB loaded but contained no scene root.");
				}
			} catch (err) {
				console.error("[kapowx] Hub GLB load failed:", err);
				errors.push(String(err?.message || err));
			}
		}

		return {
			summary,
			meta: ["sceneKind=hub"],
			fixture: { label: "Hub", mode: "scene", errors, warnings: [] },
			content: { label: "Hub", mode: "hub", errors, warnings: [] },
		};
	}


	// M7 Chunk 3 / WS4 (ASK-080 §A) — shop IBL. Load the recipe's HDRI → PMREM →
	// scene.environment (glTF metals need an env; null today = under-spec). LOD (1K)
	// first for fast paint, then swap to the 2K. Token-guarded against scene swaps;
	// cached by 2K url; graceful on load failure (keeps prior env). Sets environment
	// only (not background — the shop interior keeps the recipe's tone.background).
	// ASK-124 B3 — `url`/`lodUrl` may be a single URL or an ORDERED CANDIDATE LIST; the first
	// that bakes wins. Since B3 closed (2026-08-30) the list holds exactly one entry, the
	// shared template-family key — the per-shop leg is deleted. The multi-candidate handling
	// stays because it is the loader's contract, and it is what let that swap land without a
	// flag day. See `envAssetCandidates()` in _asset-url-resolver.js for the measurement that
	// motivated it (907 HDR objects / 3.909 GB / 129x duplication).
	async applyShopEnvironment(url, lodUrl, fallbackUrl) {
		let urls = (Array.isArray(url) ? url : [url]).filter(Boolean);
		let lodUrls = (Array.isArray(lodUrl) ? lodUrl : [lodUrl]).filter(Boolean);
		// ASK-142 R4 — a tier that stops at the LOD (`envLodOnly`, phones) takes the 1K as the FINAL env.
		if (this.tierProfile?.envLodOnly && lodUrls.length) { urls = lodUrls; lodUrls = []; }
		const cacheKey = urls[0] || fallbackUrl;
		if (!cacheKey) return;
		// The cache hits when the ALREADY-BAKED env is any of this call's candidates, not
		// only the first. Otherwise a shop still served by the per-shop key would re-bake
		// its PMREM on every recompose the moment a shared candidate was put in front of it.
		if (this._shopEnvMap && (urls.includes(this._shopEnvUrl) || this._shopEnvUrl === fallbackUrl)) { this.scene.environment = this._shopEnvMap; return; }
		// Dedupe re-entrant calls for the SAME env: a facing commit / knob change re-runs
		// applyLightingRecipe → applyShopEnvironment while the first load is still in flight.
		// Without this the recompose spawns a DUPLICATE HDR GET; when the winning load supersedes
		// it the loser is orphaned and the browser aborts it (net::ERR_ABORTED) — which trips the
		// probe's asset-load gate even though the shop lights correctly (ASK-099 item 1).
		if (this._shopEnvPending && this._shopEnvPending.cacheKey === cacheKey) return this._shopEnvPending.promise;
		const token = (this._shopEnvToken = (this._shopEnvToken || 0) + 1);
		// ASK-142 R2 — bind a stand-in env NOW, the same CubeUV size the 1K LOD bakes to (256), so every
		// PBR shader compiles WITH its envMap define during compose. Without it the HDR's arrival seconds
		// after the first frame changed every program's key at once and recompiled the whole shop in one
		// frame (133–200 ms, measured). The real env then swaps in under the same programs.
		if (!this.scene.environment) this.scene.environment = this._envPlaceholder();
		// bake a URL → PMREM env texture, or null on any failure (graceful → caller falls back).
		// NO HEAD pre-flight: the primary URLs are real CDN HDR that GET cross-origin exactly like the
		// shell GLBs (the bucket sends ACAO:*), and the fallback is the bundled SAME-ORIGIN hub HDRI —
		// neither can be the SPA-fallback HTML that once crashed HDRLoader (that only happened when the
		// env resolved against the page origin, which it no longer does). A HEAD round-trip only added a
		// second request per URL — the extra in-flight request that got aborted. onError + the tex.image
		// guard still cover a malformed body.
		const bake = (u) => {
			if (!u) return Promise.resolve(null);
			return new Promise((resolve) => {
				try {
					new HDRLoader().load(u, (tex) => {
						try {
							if (!tex?.image) { console.warn(`[shop-env] HDRI decoded without an image → skip ${u}`); resolve(null); return; }
							tex.mapping = THREE.EquirectangularReflectionMapping;
							const pmrem = new THREE.PMREMGenerator(this.renderer);
							const env = pmrem.fromEquirectangular(tex).texture;
							tex.dispose(); pmrem.dispose();
							resolve(env);
						} catch (e) { console.warn(`[shop-env] PMREM failed ${u}:`, e?.message || e); resolve(null); }
					}, undefined, (err) => { console.warn(`[shop-env] HDRI load failed ${u}:`, err?.message || err); resolve(null); });
				} catch (e) { console.warn(`[shop-env] loader error ${u}:`, e?.message || e); resolve(null); }
			});
		};
		// Try candidates in order; the first that bakes wins. A candidate that has already
		// failed THIS SESSION is skipped without a request: during the B3 migration the
		// shared key 404s for every shop on an un-migrated pin, and re-issuing that same
		// dead GET once per shop per recompose is exactly the wasted-round-trip class the
		// HEAD pre-flight was removed for. One request per distinct dead URL, then silence.
		this._envDeadUrls ??= new Set();
		const bakeFirst = async (candidates) => {
			for (const u of candidates) {
				if (this._envDeadUrls.has(u)) continue;
				const env = await bake(u);
				if (env) return { env, url: u };
				this._envDeadUrls.add(u);
			}
			return null;
		};
		const run = (async () => {
			// LOD first (fast paint) — applied only if the 2K hasn't already landed.
			if (lodUrls.length) {
				const lod = await bakeFirst(lodUrls);
				if (lod) {
					if (token === this._shopEnvToken && !urls.includes(this._shopEnvUrl)) { this.scene.environment = lod.env; this._shopEnvLod = lod.env; }
					else lod.env.dispose();
				}
			}
			let won = urls.length ? await bakeFirst(urls) : null;
			// ASK-099 item 1 — when the recipe HDRI can't load (missing curated HDRI), fall back to the
			// bundled hub HDRI so the shop is LIT, not dark. Skipped if it IS the same URL.
			if (!won && fallbackUrl && !urls.includes(fallbackUrl)) {
				console.warn(`[shop-env] recipe HDRI unavailable → falling back to bundled hub HDRI (${String(fallbackUrl).split("/").pop()})`);
				const fb = await bake(fallbackUrl);
				if (fb) won = { env: fb, url: fallbackUrl };
			}
			if (!won) return;
			const full = won.env;
			if (token !== this._shopEnvToken) { full.dispose(); return; }   // a newer scene swapped in
			if (this._shopEnvMap && this._shopEnvMap !== full) this._shopEnvMap.dispose();
			if (this._shopEnvLod) { this._shopEnvLod.dispose(); this._shopEnvLod = null; }
			this._shopEnvMap = full;
			// The WINNING url, not the first candidate — so the cache check above can tell
			// "this env is already baked" from "a different candidate won last time".
			this._shopEnvUrl = won.url;
			this.scene.environment = full;
		})();
		this._shopEnvPending = { cacheKey, promise: run };
		try { await run; } finally { if (this._shopEnvPending && this._shopEnvPending.promise === run) this._shopEnvPending = null; }
	}

	/** A neutral PMREM env at the 1K LOD's cube size — the shop's lighting stand-in until its HDRI bakes. */
	_envPlaceholder() {
		if (this._envPlaceholderMap) return this._envPlaceholderMap;
		const room = new THREE.Scene();
		room.background = new THREE.Color("#8a8680");
		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this._envPlaceholderMap = pmrem.fromScene(room, 0, 0.1, 100, { size: 256 }).texture;
		pmrem.dispose();
		return this._envPlaceholderMap;
	}

	_getOrBuildHubEnvMap() {
		if (this._hubEnvMap) return this._hubEnvMap;

		// v2 hub — starfield night env. Procedural canvas with random bright
		// pinpoints on black, PMREM-baked. Acts as the initial env; the later
		// `_bakeHubReflectionEnv` cube-cam pass overlays this with the actual
		// loaded scene (cyclorama + brand signage), giving the wet road real
		// reflections of the sky stars + sign colour.
		const W = 512;
		const H = 256;
		const canvas = document.createElement("canvas");
		canvas.width = W;
		canvas.height = H;
		const ctx = canvas.getContext("2d");

		// Black background
		ctx.fillStyle = "#000000";
		ctx.fillRect(0, 0, W, H);

		// Deterministic-ish "random" stars (mulberry32 seeded)
		let seed = 0x9e3779b1;
		const rand = () => {
			seed = (seed + 0x6d2b79f5) | 0;
			let t = seed;
			t = Math.imul(t ^ (t >>> 15), t | 1);
			t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
			return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
		};

		// Faint stars (single pixels)
		const NUM_FAINT = 300;
		for (let i = 0; i < NUM_FAINT; i++) {
			const x = Math.floor(rand() * W);
			const y = Math.floor(rand() * H);
			const b = Math.floor(60 + rand() * 120);   // 60–180 grey
			ctx.fillStyle = `rgb(${b},${b},${Math.min(255, b + 5)})`;
			ctx.fillRect(x, y, 1, 1);
		}
		// Bright stars (1–2 px with subtle bloom)
		const NUM_BRIGHT = 40;
		for (let i = 0; i < NUM_BRIGHT; i++) {
			const x = Math.floor(rand() * W);
			const y = Math.floor(rand() * H);
			const r = rand() < 0.3 ? 2 : 1;
			ctx.fillStyle = "rgba(220,225,240,1.0)";
			ctx.fillRect(x, y, r, r);
			// faint halo
			ctx.fillStyle = "rgba(180,190,210,0.35)";
			ctx.fillRect(x - 1, y - 1, r + 2, r + 2);
		}

		const tex = new THREE.CanvasTexture(canvas);
		tex.mapping = THREE.EquirectangularReflectionMapping;
		tex.colorSpace = THREE.SRGBColorSpace;

		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this._hubEnvMap = pmrem.fromEquirectangular(tex).texture;
		tex.dispose();
		pmrem.dispose();

		return this._hubEnvMap;
	}

	/**
	 * The neutral warm-interior IBL the no-recipe fallback lights with (see
	 * `_applyShellInteriorLighting` for the measurement that put it here). Built once per session and
	 * cached like the hub's, because a PMREM bake is not free and every template-less shop wants the
	 * same one.
	 *
	 * A vertical gradient, not a starfield: warm ceiling bounce at the top, a darker floor below, and
	 * a soft bright band where a ceiling fitting would be. That is the character the analytic rig
	 * beneath it already chose (0xffe1b3 ambient, 0xffeecc key), so the reflections agree with the
	 * lights instead of arguing with them.
	 */
	_getOrBuildInteriorEnvMap() {
		if (this._interiorEnvMap) return this._interiorEnvMap;
		if (typeof document === "undefined" || !this.renderer) return null;
		const W = 256, H = 128;
		const canvas = document.createElement("canvas");
		canvas.width = W; canvas.height = H;
		const ctx = canvas.getContext("2d");
		// Equirectangular: v=0 is up. Warm above, dim below — a lit ceiling over a matte floor.
		const g = ctx.createLinearGradient(0, 0, 0, H);
		g.addColorStop(0.00, "#fff0d8");   // ceiling bounce
		g.addColorStop(0.35, "#b99b78");
		g.addColorStop(0.62, "#5d4c3c");   // horizon — the walls
		g.addColorStop(1.00, "#241c16");   // floor
		ctx.fillStyle = g;
		ctx.fillRect(0, 0, W, H);
		// A soft bright band just off the top: the fitting the key light is standing in for. Without
		// it the gradient reflects as a flat wash and nothing on a glossy surface reads as a highlight.
		const band = ctx.createRadialGradient(W * 0.5, H * 0.12, 0, W * 0.5, H * 0.12, W * 0.42);
		band.addColorStop(0, "rgba(255,244,222,0.95)");
		band.addColorStop(1, "rgba(255,244,222,0)");
		ctx.fillStyle = band;
		ctx.fillRect(0, 0, W, H * 0.5);

		const tex = new THREE.CanvasTexture(canvas);
		tex.mapping = THREE.EquirectangularReflectionMapping;
		tex.colorSpace = THREE.SRGBColorSpace;
		const pmrem = new THREE.PMREMGenerator(this.renderer);
		this._interiorEnvMap = pmrem.fromEquirectangular(tex).texture;
		tex.dispose();
		pmrem.dispose();
		return this._interiorEnvMap;
	}

	// Waypoint markers — built by the lazy `_portal-waypoint.js` (ASK-177 §10: D-086 cl.1 retired them from the street's
	// doors, so the builder left the customer's initial bundle). `waypointPosition` lets the marker sit apart from the
	// trigger. Resolves once built; a scene swapped meanwhile builds nothing (the compose token).
	/** ASK-178 §6 item 1 — the street is READY when its entrance is live: every declared slot's door model, the prompt's tick
	 *  (one run now, so the prompt already answers the spawn pose) and the nameplates. `_hubReady` is the one signal the loader,
	 *  the timing marks and probe-hub-walk wait on; `ticks` counts the prompt's frames (a probe waits on frames, never a clock). */
	_markHubReady() {
		this._hubProxyHandle?.lift(); this._hubProxyHandle = null;   // §6 item 6: the proxy street leaves as the card lifts
		this._doorTick?.(0);
		this._hubReady = { at: Math.round(performance.now()), doors: this._hubDoors?.models?.length ?? 0, entrance: !!this._entrance, plates: this._hubNameplates?.length ?? 0 };
		try { performance.mark("gg:hub-ready"); } catch { /* User Timing absent */ }
	}

	_buildPortalWaypoints(items, fallbackColor = "#ffffff", scale = 1.0, options = {}) {
		this._portalWaypoints = this._portalWaypoints || [];
		if (!items?.length) return Promise.resolve();
		const tok = this.currentToken;
		return import("./_portal-waypoint.js").then(({ createPortalWaypoint }) => {
			if (tok !== this.currentToken) return;
			for (const item of items) {
				const wp = createPortalWaypoint(item.waypointPosition || item.position, new THREE.Color(item.waypointColor || fallbackColor), options);
				wp.scale.setScalar(scale);
				wp.userData.portalId = item.id;
				this.stageRoot.add(wp);
				this._portalWaypoints.push(wp);
			}
		}).catch((err) => console.warn("[waypoint] unavailable:", err?.message || err));
	}

	_animatePortalWaypoints() {
		if (!this._portalWaypoints?.length) return;
		const t = performance.now() / 1000;
		const pulse  = (Math.sin(t * 2.5) + 1) * 0.5;          // 0..1
		const cpulse = (Math.cos(t * 2.5) + 1) * 0.5;          // 0..1
		for (const wp of this._portalWaypoints) {
			const { ring, fill, beam, skyBeam, arrow, stem } = wp.userData.parts;
			ring.scale.setScalar(1 + pulse * 0.10);
			ring.material.opacity = 0.45 + pulse * 0.45;
			fill.material.opacity = 0.06 + cpulse * 0.10;
			if (beam)    beam.material.opacity    = 0.06 + pulse * 0.10;
			if (skyBeam) skyBeam.material.opacity = 0.05 + pulse * 0.06;
			arrow.position.y = wp.userData.arrowRestY + Math.sin(t * 1.6) * 0.18;
			arrow.rotation.y = t * 0.9;
			if (stem) {
				stem.position.y = arrow.position.y + wp.userData.stemOffset;
				stem.rotation.y = arrow.rotation.y;
			}
		}
	}


	_applyHubSpawn(spawn) {
		const p  = spawn?.position || { x: 0, y: 1.7, z: 10 };
		const lt = spawn?.lookAt   || { x: 0, y: 1.4, z: 0 };
		const startPos    = new THREE.Vector3(p.x, p.y, p.z);
		const startTarget = new THREE.Vector3(lt.x, lt.y, lt.z);
		// ASK-177 §6 (Client 5h, measured on the v5 preview): the spawn's lookAt is a DIRECTION as much as a point, and the v5
		// street looks down its axis at the landmark 28.5 m away — past OrbitControls' maxDistance (24), so `controls.update()`
		// pulled the CAMERA 4.5 m toward it: every arrival stood at x −14.975 instead of SPAWN's −19.5 (W1 red). The orbit
		// target keeps the look direction exactly and sits inside the controls' distance range; the camera stays where it spawns.
		if (this.controls) {
			const d = startTarget.distanceTo(startPos), lo = this.controls.minDistance ?? 0, hi = this.controls.maxDistance ?? Infinity;
			if (d > 1e-6 && (d > hi || d < lo)) startTarget.sub(startPos).multiplyScalar(THREE.MathUtils.clamp(d, lo, hi) / d).add(startPos);
		}
		this.closedCameraPos    = startPos.clone();
		this.closedCameraTarget = startTarget.clone();
		this.camera.position.copy(startPos);
		if (this.controls) {
			this.controls.target.copy(startTarget);
			this.controls.update();
		}
	}

	// Comic anchor builders moved to fixtures/{longbox,wall-shelf,book-row,
	// pile-table}-anchor.js — self-registering modules. Stage no longer
	// hardcodes per-fixture-type knowledge.

	// Returns the products GCG has assigned to a specific anchor, sorted by
	// the `slot` field so positional ordering (slot 1 = front, slot 2 = next,
	// etc.) is honoured. GCG is the source of truth for placement: anchors
	// without an assignment render empty. No fallback to "all typed products"
	// — that previously masked GCG's allocation decisions and made the
	// per-fixture capacity contract meaningless.
	//
	// Wire shape (per GCG feed):
	//   product.fixtureAssignments = [{ fixtureAnchor: "LONGBOX_ANCHOR_01", slot: 1 }, ...]
	//
	// typeFilter ("comic" | "book" | "vinyl") guards against cross-type
	// mis-assignment from GCG. A book assigned to a comic fixture is
	// dropped client-side rather than rendered.
	_getProductsForAnchor(anchorName, allProducts, typeFilter) {
		if (!anchorName) return [];
		const list = Array.isArray(allProducts) ? allProducts : [];
		const matched = [];
		for (const p of list) {
			if (typeFilter && p.type !== typeFilter) continue;
			const assigns = Array.isArray(p.fixtureAssignments) ? p.fixtureAssignments : [];
			const hit = assigns.find((a) => a && a.fixtureAnchor === anchorName);
			if (hit) matched.push({ product: p, slot: Number.isFinite(hit.slot) ? hit.slot : 0 });
		}
		matched.sort((a, b) => a.slot - b.slot);
		return matched.map((m) => m.product);
	}

	// Returns the computed capacity for a fixture instance, derived from the
	// anchor's authored scale + the canonical product dimensions for the
	// fixture's accepted product type. Mirrors the maths in each fixture
	// builder so GCG's curator sees the same number the runtime would honour.
	_computeFixtureCapacity(fixtureKind, anchor) {
		const s = anchor?.scale || { x: 0, y: 0, z: 0 };
		const COMIC_THICKNESS = 0.0032;
		const COMIC_W         = 0.170;
		switch (fixtureKind) {
			case "longbox":
			case "shortbox": {
				const slotSpacing = Math.max(COMIC_THICKNESS * 1.2, 0.006);
				const slotMargin  = Math.max(COMIC_THICKNESS * 0.7, 0.016);
				const laneLength  = Math.max(0.12, s.x);
				const usable = Math.max(0, laneLength - slotMargin * 2);
				return Math.max(1, Math.floor(usable / slotSpacing) + 1);
			}
			case "wall-shelf": {
				const AVG_GAP     = 0.010;
				const WALL_MARGIN = 0.05;
				const shelfLength = Math.max(0.20, s.x);
				const usable      = Math.max(0, shelfLength - WALL_MARGIN * 2);
				return Math.max(0, Math.floor((usable + AVG_GAP) / (COMIC_W + AVG_GAP)));
			}
			case "book-row": {
				const SPINE_W    = 0.020;
				const ROW_MARGIN = 0.01;
				const rowLength  = Math.max(0.20, s.x);
				return Math.max(0, Math.floor((rowLength - 2 * ROW_MARGIN) / SPINE_W));
			}
			case "pile-table":       return 20;
			case "fan-table":        return 3;
			case "stack-table":      return 20;
			case "featured-comic":   return 1;
			case "spinner-rack":     return 16;
			case "vinyl-wall-shelf": {
				const LP_W = 0.32;
				return Math.max(0, Math.floor(Math.max(0.40, s.x) / LP_W));
			}
			case "vinyl-bin":        return 100;
			default:                 return 0;
		}
	}

	// At scene-load time, walks every registered anchor and computes both
	// runtime-observed (capacity, spatial) and merchant-authored (passed
	// through from boot.json scene.semantic.fixtures[]) fields into a single
	// publishable record. The mutated array is the runtime's contract surface
	// for GCG — same shape the curator will read from a GCG endpoint once
	// the round-trip is wired up.
	_publishRuntimeMetadata(scene) {
		if (!this._shopAnchors) return;
		const registered = this._shopAnchors.registered;
		if (!registered || registered.size === 0) return;
		const spawn = this._shopAnchors.spawns?.default;
		const spawnPos = spawn
			? new THREE.Vector3(spawn.position.x, spawn.position.y, spawn.position.z)
			: null;

		// Flatten all anchors with their fixture kinds for cross-anchor
		// distance lookups.
		const all = [];
		for (const [kind, list] of registered.entries()) {
			for (const anchor of list) all.push({ kind, anchor });
		}

		const publishable = [];
		for (const { kind, anchor } of all) {
			const pos = anchor.position;
			const proximity = spawnPos ? pos.distanceTo(spawnPos) : null;

			const NEAREST_RADIUS = 2.0;
			const nearest = all
				.filter((o) => o.anchor.name !== anchor.name)
				.map((o) => ({ name: o.anchor.name, dist: pos.distanceTo(o.anchor.position) }))
				.filter((d) => d.dist <= NEAREST_RADIUS)
				.sort((a, b) => a.dist - b.dist)
				.slice(0, 3)
				.map((d) => d.name);

			// Label resolution: shared resolver (Storefront knob → GLB extras
			// → fixture-kind default). Default-labeled anchors still get
			// surfaced to GCG so the curator has something human-readable.
			const label = resolveAnchorLabel(scene, anchor, kind);

			publishable.push({
				anchorName:        anchor.name,
				fixtureKind:       kind,
				worldPosition:     { x: pos.x, y: pos.y, z: pos.z },
				capacity:          this._computeFixtureCapacity(kind, anchor),
				proximityToSpawn:  proximity != null ? Math.round(proximity * 1000) / 1000 : null,
				visibleFromSpawn:  true,   // v1: single-room assumption; raycast pass arrives with multi-room shells
				nearestFixtures:   nearest,
				label,
			});
		}

		// Mutate the in-memory scene.semantic.fixtures[] — preserves any
		// merchant-authored fields already present (label, overflow, etc.)
		// and adds/overwrites the runtime-published ones. Creates entries
		// for anchors that boot.json hadn't yet declared.
		if (!scene.semantic) scene.semantic = {};
		if (!Array.isArray(scene.semantic.fixtures)) scene.semantic.fixtures = [];
		for (const meta of publishable) {
			let entry = scene.semantic.fixtures.find((f) => f.anchorName === meta.anchorName);
			if (!entry) {
				entry = { anchorName: meta.anchorName, semanticType: meta.fixtureKind };
				scene.semantic.fixtures.push(entry);
			}
			Object.assign(entry, {
				worldPosition:    meta.worldPosition,
				capacity:         meta.capacity,
				proximityToSpawn: meta.proximityToSpawn,
				visibleFromSpawn: meta.visibleFromSpawn,
				nearestFixtures:  meta.nearestFixtures,
			});
			if (meta.label != null) entry.label = meta.label;
		}

		// Stash on stage for downstream consumers (Studio preview, future
		// POST to GCG endpoint, etc.) and log a one-line summary.
		this._runtimeMetadata = publishable;
		console.log(`[runtime-metadata] published ${publishable.length} anchors (capacity + spatial)`);
	}

	// Notifies listeners (the GCG curator, ultimately) that a fixture
	// received more products than its capacity. v1: dispatched as a
	// CustomEvent on the window for any consumer to pick up; v2 will POST
	// to a GCG endpoint when one is available.
	_publishCapacityOverflow(anchorName, surplusProductIds) {
		if (!Array.isArray(surplusProductIds) || surplusProductIds.length === 0) return;
		const detail = { anchorName, surplusProductIds: surplusProductIds.slice() };
		if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
			window.dispatchEvent(new CustomEvent("kapowx:capacityOverflow", { detail }));
		}
		console.log(`[runtime-overflow] ${anchorName}: ${surplusProductIds.length} surplus product(s)`);
	}

	// ─── Spinner rack ────────────────────────────────────────────────────────────

	_crosshairHitsSpinner() {
		const rack = this.spinnerRack;
		if (!rack || rack.state === 'hero') return false;
		const meshes = rack.comics.map(c => c.mesh);
		if (!meshes.length) return false;
		return this.raycaster.intersectObjects(meshes, false).length > 0;
	}

	advanceSpinnerBrowse(delta) {
		const rack = this.spinnerRack;
		if (!rack || rack.state === 'hero') return;
		rack.velocity += delta * 3.0; // rad/s impulse; multiple presses stack
		rack.velocity = Math.max(-Math.PI * 4, Math.min(Math.PI * 4, rack.velocity));
	}

	setSpinnerHero(comicEntry) {
		const rack = this.spinnerRack;
		if (!rack || rack.state === 'hero') return;

		const mesh = comicEntry.mesh;
		const fromPos  = new THREE.Vector3();
		const fromQuat = new THREE.Quaternion();
		mesh.getWorldPosition(fromPos);
		mesh.getWorldQuaternion(fromQuat);

		// Hero position: in front of camera at 2/3 screen height
		const heroPos  = this._computeHeroPos(0.260, mesh);
		const heroQuat = this._faceCameraQuat(heroPos);

		// Detach from spinning rack, place in world space
		rack.spinRoot.remove(mesh);
		this.scene.add(mesh);
		mesh.position.copy(fromPos);
		mesh.quaternion.copy(fromQuat);

		removeOutline(mesh);
		rack.hoveredComic = null;
		rack.velocity     = 0;
		rack.state        = 'hero';
		rack.heroEntry    = { comicEntry, fromPos, fromQuat, heroPos, heroQuat, animProgress: 0, returning: false };

		this._setActiveHero("spinner", comicEntry.product, comicEntry.mesh);
		this.onComicSelect?.({ index: comicEntry.spinnerComicIdx ?? 0, product: comicEntry.product });
	}

	exitSpinnerHero() {
		const rack = this.spinnerRack;
		if (!rack?.heroEntry || rack.heroEntry.returning) return;
		rack.heroEntry.animProgress = 0;
		rack.heroEntry.returning    = true;
		this._clearActiveHero();
		this.onComicSelect?.(null);
	}

	// Load a texture by URL, returning the cached copy if already loaded.
	_loadTextureCached(url, callback) {
		if (this.textureCache.has(url)) {
			callback(this.textureCache.get(url));
			return;
		}
		// ASK-142 R2 — through the cover stream (off-thread decode, pooled), at top priority: every caller
		// here is a hold/open/page-turn the customer just asked for.
		this._coverStream.load(url).then(
			(tex) => {
				tex.colorSpace = THREE.SRGBColorSpace;
				// 🔴 ONE TEXTURE CONVENTION, AND IT IS glTF's (ASK-129 cover-orientation ruling).
				//
				// `v = 0` is the image TOP, and the runtime binds product images with `flipY = false`
				// — what `GLTFLoader` already does for embedded textures, and what every glTF pipeline
				// assumes. This loader left three.js's `flipY = true` default in place while the
				// registry-driven binder set it false, so the platform carried TWO conventions and
				// which one a cover got depended on which path its type took: comics compensated into
				// looking right against non-conformant UVs, and the new book/vinyl geometry authored
				// "to match" came out upside down. Every caller here binds FEED imagery — a socket
				// comic's front/back cover and the interior spreads — so they all take the one rule.
				// No per-type compensation: the assets are conformant and the runtime does not argue
				// with them.
				tex.flipY = false;
				if (!this.textureCache.has(url)) this.textureCache.set(url, tex);
				callback(tex);
			},
			// DETERMINISM (CI content-load, Coord 2026-07-23): a cover that fails to load (e.g. 401 in
			// the capture environment's egress) must render a STABLE placeholder, not leave the material
			// unresolved — an unresolved cover renders differently depending on which loads happened to
			// finish before the frame was captured, which is the per-capture pixel non-determinism. The
			// fallback is a single shared 1×1 neutral texture, so every failed cover reads identically
			// across captures. Cached under the URL so a retry doesn't re-fetch a known-bad cover.
			(err) => {
				console.warn(`[texture] load failed, using deterministic fallback: ${String(url).slice(0, 120)}`);
				const fb = this._fallbackCoverTexture();
				if (!this.textureCache.has(url)) this.textureCache.set(url, fb);
				callback(fb);
			},
		);
	}

	/** A single shared, deterministic placeholder texture for covers that fail to load (CI 401s etc.). */
	/** ASK-142 R2 — the stand-in a streaming cover binds while its picture is on the way: 1×1 white,
	 *  so `color` (the slot's fallback) shows exactly as with no map, under the shader the real cover uses. */
	_coverPlaceholderTexture() {
		if (this._coverPlaceholderTex) return this._coverPlaceholderTex;
		const tex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.needsUpdate = true;
		this._coverPlaceholderTex = tex;
		return tex;
	}

	_fallbackCoverTexture() {
		if (this._coverFallbackTex) return this._coverFallbackTex;
		const data = new Uint8Array([206, 197, 176, 255]);   // #cec5b0 — neutral parchment, opaque
		const tex = new THREE.DataTexture(data, 1, 1, THREE.RGBAFormat);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.needsUpdate = true;
		this._coverFallbackTex = tex;
		return tex;
	}

	// Compute world rest position for a comic in a given pile at a given stack index
	_pileRestPos(side, stackIdx, comicIdx) {
		const center = side === "right" ? this.pileCenterRight : this.pileCenterLeft;
		const c = this.pileComics[comicIdx];
		return new THREE.Vector3(
			center.x + c.jitterX,
			this.pileTableSurfaceY + stackIdx * 0.0025 + 0.001,
			center.z + c.jitterZ,
		);
	}

	advancePileBrowse(delta) {
		if (this.pileArcAnim) return;  // animation in flight — ignore input
		if (this.pileState === "hero") return;

		// Clear hover outline before the top comic changes
		if (this.hoveredPileComic) {
			removeOutline(this.hoveredPileComic);
			this.hoveredPileComic = null;
		}

		if (delta > 0 && this.pileRightStack.length > 0) {
			// Move top of right pile to left pile
			const comicIdx = this.pileRightStack.pop();
			const fromPos  = this._pileRestPos("right", this.pileRightStack.length, comicIdx);
			this.pileLeftStack.push(comicIdx);
			const toPos    = this._pileRestPos("left", this.pileLeftStack.length - 1, comicIdx);
			this.pileArcAnim = { comicIdx, fromPos, toPos, progress: 0 };
		} else if (delta < 0 && this.pileLeftStack.length > 0) {
			// Move top of left pile back to right pile
			const comicIdx = this.pileLeftStack.pop();
			const fromPos  = this._pileRestPos("left", this.pileLeftStack.length, comicIdx);
			this.pileRightStack.push(comicIdx);
			const toPos    = this._pileRestPos("right", this.pileRightStack.length - 1, comicIdx);
			this.pileArcAnim = { comicIdx, fromPos, toPos, progress: 0 };
		}
	}

	setPileHero(comicIdx) {
		if (this.pileState === "hero") return;
		if (this.pileArcAnim) return;
		const entry = this.pileComics[comicIdx];
		if (!entry) return;
		const fromPos  = entry.mesh.position.clone();
		const fromQuat = entry.mesh.quaternion.clone();
		// midPos: lifted above original position — rotation phase completes here before translation
		const midPos = fromPos.clone();
		midPos.y += 0.18;

		// Clear pile hover outline before lifting
		if (this.hoveredPileComic) {
			removeOutline(this.hoveredPileComic);
			this.hoveredPileComic = null;
		}

		const heroPos = this._computeHeroPos(0.260, entry.mesh);
		const toCam   = new THREE.Vector3()
			.subVectors(this.camera.position, heroPos)
			.setY(0).normalize();
		const heroQuat = new THREE.Quaternion()
			.setFromEuler(new THREE.Euler(0, Math.atan2(toCam.x, toCam.z), 0));

		this.pileState = "hero";
		this.pileHeroEntry = { comicIdx, fromPos, fromQuat, midPos, heroPos, heroQuat, animProgress: 0, returning: false };
		this._setActiveHero("pile", entry.product, entry.mesh);
		this.onComicSelect?.({ index: comicIdx, product: entry.product });

		if (this.heroSpotLight) {
			this.heroSpotLight.position.set(
				heroPos.x,
				heroPos.y + 0.25,
				heroPos.z + 0.30,
			);
			this.heroSpotLight.intensity = 3.5;
			this.heroSpotLight.visible = true;
		}
	}

	exitPileHero() {
		if (this.pileState !== "hero" || !this.pileHeroEntry) return;
		this._snapUnflip();
		this.pileHeroEntry.returning = true;
		this.pileHeroEntry.animProgress = 0;
		this._clearActiveHero();
		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible = false;
		}
	}

	// Shared hero animator used by every product type (wall comic, vinyl, pile,
	// book, spinner). Handles both single-phase lerp and two-phase arc (pile/book).
	//
	// h:    { fromPos, fromQuat, heroPos, heroQuat, midPos?, animProgress, returning }
	// opts: {
	//   mesh,                 // required — the THREE.Mesh to animate
	//   duration,             // default 0.55s
	//   split,                // if set, two-phase split point (default 0.40 if midPos)
	//   arcLift,              // sine-arc Y boost on second phase (default 0.05)
	//   returnPos, returnQuat,// override return target (default fromPos / fromQuat)
	//   onComplete,           // called when animProgress reaches 1
	//   onCompleteReturn,     // called specifically when returning completes
	// }
	_animateHero(h, dt, opts) {
		if (!h || h.animProgress >= 1 || !opts?.mesh) return;
		const mesh = opts.mesh;
		const duration   = opts.duration ?? 0.55;
		const isTwoPhase = !!h.midPos;
		const split      = opts.split ?? 0.40;
		const arcLift    = opts.arcLift ?? 0.05;
		const returnPos  = opts.returnPos || h.fromPos;
		const returnQuat = opts.returnQuat || h.fromQuat;

		h.animProgress = Math.min(1, h.animProgress + dt / duration);

		if (isTwoPhase) {
			if (h.returning) {
				if (h.animProgress < 1 - split) {
					const t1 = easeInOutQuad(h.animProgress / (1 - split));
					mesh.position.lerpVectors(h.heroPos, h.midPos, t1);
					mesh.quaternion.copy(h.heroQuat);
				} else {
					const t2 = easeInOutQuad((h.animProgress - (1 - split)) / split);
					mesh.position.lerpVectors(h.midPos, returnPos, t2);
					mesh.quaternion.slerpQuaternions(h.heroQuat, returnQuat, t2);
				}
			} else {
				if (h.animProgress < split) {
					const t1 = easeInOutQuad(h.animProgress / split);
					mesh.position.lerpVectors(h.fromPos, h.midPos, t1);
					mesh.quaternion.slerpQuaternions(h.fromQuat, h.heroQuat, t1);
				} else {
					const t2 = easeInOutQuad((h.animProgress - split) / (1 - split));
					mesh.position.lerpVectors(h.midPos, h.heroPos, t2);
					mesh.position.y += Math.sin(t2 * Math.PI) * arcLift;
					mesh.quaternion.copy(h.heroQuat);
				}
			}
		} else {
			const t = easeInOutQuad(h.animProgress);
			if (h.returning) {
				mesh.position.lerpVectors(h.heroPos, returnPos, t);
				mesh.quaternion.slerpQuaternions(h.heroQuat, returnQuat, t);
			} else {
				mesh.position.lerpVectors(h.fromPos, h.heroPos, t);
				mesh.quaternion.slerpQuaternions(h.fromQuat, h.heroQuat, t);
			}
		}

		if (h.animProgress >= 1) {
			if (h.returning) {
				mesh.position.copy(returnPos);
				mesh.quaternion.copy(returnQuat);
				opts.onCompleteReturn?.();
			} else {
				mesh.position.copy(h.heroPos);
				mesh.quaternion.copy(h.heroQuat);
			}
			opts.onComplete?.();
		}
	}

	/**
	 * The print hero's framing. D-075 cl.5's sphere framer stays in the LAZY `_item-hero.js` (§84's
	 * budget argument still holds for the sphere itself); print items now measure their OWN box here,
	 * which is the part that mattered. The §84 note argued the height constant agreed with the sphere
	 * "to within centimetres" — true of the DISTANCE, and beside the point: the constant put the base
	 * on the ray, which is what cut the top off (measured §91b).
	 */
	_computeHeroPos(itemHeightM, obj = null) {
		// 🔴 ASK-137 §91b — THE ITEM'S OWN SIZE, AND ITS CENTRE ON THE RAY. Every print hero passed a
		// constant (a comic's 0.26 m) and the formula put the ORIGIN on the eye ray — and a print GLB is
		// base-origined. Measured on the plane through the live camera: Vanessa's 0.34 m wall magazine
		// held at free-area NDC y −0.004 … +1.72, base on the crosshair and the top cut off — the same
		// defect §86b fixed for 3D items, still standing on print. So: measure it (in its own frame —
		// a hero stands every print item upright, local +Y up, whatever its rest pose), and lower the
		// origin by its centre's height so the CENTRE lands on the ray. The constant stays the fallback
		// for a thing that cannot be measured.
		let h = itemHeightM, w = itemHeightM * 0.7, cy = 0, minY = -itemHeightM * 0.5;
		const b = obj ? this._localBounds(obj) : null;
		if (b) { h = b.max.y - b.min.y; w = Math.max(b.max.x - b.min.x, b.max.z - b.min.z); cy = (b.min.y + b.max.y) / 2; minY = b.min.y; }
		// D-076 cl.2 — sized against the FREE area beside the product panel, not the whole canvas: 3/4
		// of the free height, and never wider than ~85 % of the free width (a portrait phone under a
		// bottom sheet is width-bound).
		const t = Math.tan(this.camera.fov * Math.PI / 360), { fx, fy } = this._freeFrac();
		const dist = Math.max((h * 3) / (4 * t * fy), w / (1.7 * t * this.camera.aspect * fx));
		const dir    = new THREE.Vector3();
		this.camera.getWorldDirection(dir);
		const pos = this.camera.position.clone().addScaledVector(dir, dist);
		pos.y -= cy;
		// Clamp so the item never clips the floor, but otherwise follow full 3D look direction.
		pos.y = Math.max(0.05 - minY, pos.y);
		return pos;
	}

	/** An object's bounds in its OWN frame (metres), over its meshes and not their hover shells. */
	_localBounds(obj) {
		const box = new THREE.Box3(), part = new THREE.Box3(), m = new THREE.Matrix4(), inv = new THREE.Matrix4();
		obj.updateMatrixWorld(true);
		inv.copy(obj.matrixWorld).invert();
		obj.traverse((o) => {
			if (!o.isMesh || !o.geometry || o.userData?.isOutline) return;
			if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
			box.union(part.copy(o.geometry.boundingBox).applyMatrix4(m.multiplyMatrices(inv, o.matrixWorld)));
		});
		if (box.isEmpty()) return null;
		const s = obj.getWorldScale(new THREE.Vector3());
		box.min.multiply(s); box.max.multiply(s);
		return box;
	}

	/**
	 * The hero position for a specific thing: its bounding sphere when it can be measured, else the
	 * old height-based formula. ONE reader, so a hero that cannot be measured degrades to exactly
	 * today's framing rather than to a guess — absent is not the same branch as wrong.
	 */
	/** The quaternion that turns a flat item's +Z to face the camera, about Y only — SIX hero sites
	 *  each carried this same four-line pair (ASK-137 §84, the second duplicate this pass paid with).
	 *  Yaw-only, deliberately: a hero that pitched to face a camera looking down would lie on its back. */
	_faceCameraQuat(atPos) {
		const toCam = new THREE.Vector3().subVectors(this.camera.position, atPos).setY(0).normalize();
		return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), toCam);
	}

	_isAnyHeroActive() {
		return this.pileState === "hero" ||
		       this.bookState === "hero" ||
		       this.spinnerRack?.state === "hero" ||
		       this.boxState === "wallComic" ||
		       this.boxState === "hero";
	}

	setBookHero(entry) {
		if (this.bookState === "hero") return;

		// Snap slide back so fromPos equals the base shelf position
		entry.slideZ = 0;
		entry.mesh.position.copy(entry.basePosition);
		if (this.bookBrowseIndex === this.bookEntries.indexOf(entry)) this.bookBrowseIndex = -1;
		const fromPos  = entry.basePosition.clone();
		const fromQuat = entry.mesh.quaternion.clone();

		// Phase 1 midpoint: pull book straight out of the shelf along its
		// outward face direction. Books on side walls (east/west) used to fly
		// in world -Z which moved them along the wall, not away from it.
		const outward = entry.faceDir || new THREE.Vector3(0, 0, -1);
		const midPos = fromPos.clone().addScaledVector(outward, 0.32);

		// Phase 2 hero position: in front of camera at 2/3 screen height
		const bookH  = entry.heightM || 0.229;
		const heroPos = this._computeHeroPos(bookH, entry.mesh);

		// Orientation: front cover (+X face) points toward camera. The +X face
		// now carries frontMat after the material swap in book-row-anchor.js
		// (previously -X did, hence the old -1 vector).
		const toCamera = new THREE.Vector3(
			this.camera.position.x - heroPos.x,
			0,
			this.camera.position.z - heroPos.z,
		).normalize();
		// The entry names its own cover axis (a catalogue book: local +Z, `_seatOnShelf`); +X is the
		// retired procedural box's convention and stays the default for anything that does not say.
		const heroQuat = new THREE.Quaternion().setFromUnitVectors(entry.coverAxis || new THREE.Vector3(1, 0, 0), toCamera);

		if (this.hoveredBook) {
			removeOutline(this.hoveredBook.mesh);
			this.hoveredBook = null;
		}

		this.bookState = "hero";
		this.bookHeroEntry = { entry, fromPos, fromQuat, midPos, heroPos, heroQuat, animProgress: 0, returning: false };
		this._setActiveHero("book", entry.product, entry.mesh);
		this.onComicSelect?.({ index: 0, product: entry.product });

		if (this.heroSpotLight) {
			this.heroSpotLight.position.set(heroPos.x, heroPos.y + 0.25, heroPos.z + 0.20);
			this.heroSpotLight.intensity = 3.0;
			this.heroSpotLight.visible = true;
		}
	}

	exitBookHero() {
		if (this.bookState !== "hero" || !this.bookHeroEntry) return;
		this._snapUnflip();
		this.bookHeroEntry.returning = true;
		this.bookHeroEntry.animProgress = 0;
		this._clearActiveHero();
		this.onComicSelect?.(null);
		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible = false;
		}
	}

	handlePointerMove(event) {
		// Crosshair-only contract: hover only updates while the pointer is
		// locked. Locked mode drives hover via `_updateHoverFromCenter()` each
		// frame; unlocked (Esc cursor) doesn't interact with the world at all.
		// Previously the unlocked cursor was the source of hover state, which
		// is why Esc'd users saw objects "respond" to the free cursor.
		if (!this._mouseLook?.locked) return;
		const bounds = this.renderer.domElement.getBoundingClientRect();
		this.pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
		this.pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
		this._updateHoverAtPointer();
	}

	// Per-frame hover via crosshair when pointer is locked. Reuses the same
	// raycast logic as the cursor-driven path but with NDC (0, 0).
	_updateHoverFromCenter() {
		this.pointer.x = 0;
		this.pointer.y = 0;
		this._updateHoverAtPointer();
	}

	_updateHoverAtPointer() {
		this.raycaster.setFromCamera(this.pointer, this.camera);

		// Generic interactable dispatcher — every fixture kind's hover +
		// modal claim logic lives in `_interactable-kinds/*.js`. Kinds are
		// iterated by precedence (highest first), so modal kinds claim
		// `stage.activeVinylBin / activeStackTable / hoveredPileComic /
		// hoveredBook / spinnerRack.hoveredComic` before lower-precedence
		// kinds (wall-comic, vinyl-wall-shelf, longbox-lid) read them in
		// their `suppressedBy`. Bailey's custom AABB hit-test stays inline
		// below (Stage 3 follow-up).
		runDispatcher(this);

		// Browse-focused box comic outline. While the box is open/browsing,
		// the comic at browseIndex wears the highlight. Not a fixture hover —
		// it's a browse-state effect, kept inline rather than as a registered
		// kind. Hero state is excluded because the hero comic already has a
		// spotlight + camera-relative position making the outline redundant.
		//
		// addOutline adds an outline mesh as a CHILD of the target. Naively
		// calling it during traverse() makes traverse visit the new outline
		// child on its next iteration → infinite recursion. Collect mesh
		// references first, filter out any existing outline children, then
		// mutate the tree.
		let newFocusedBoxComic = null;
		if (this.boxState === "open" || this.boxState === "browsing") {
			newFocusedBoxComic = this.comicEntries.find((e) => e.index === this.browseIndex)?.root ?? null;
		}
		if (newFocusedBoxComic !== this.focusedBoxComicRoot) {
			const collectMeshes = (root) => {
				const out = [];
				root.traverse((node) => {
					if (node.isMesh && !node.userData.isOutline) out.push(node);
				});
				return out;
			};
			if (this.focusedBoxComicRoot) {
				for (const m of collectMeshes(this.focusedBoxComicRoot)) removeOutline(m);
			}
			this.focusedBoxComicRoot = newFocusedBoxComic;
			if (newFocusedBoxComic) {
				for (const m of collectMeshes(newFocusedBoxComic)) addOutline(m);
			}
		}

		// Cursor: pointer if any registered kind has active hover state, OR
		// if the focused-box-comic outline is up.
		const anyHover = !!(
			this.hoveredWallComic ||
			this.hoveredPileComic ||
			this.hoveredBook ||
			this.spinnerRack?.hoveredComic
		);
		this.renderer.domElement.style.cursor = anyHover ? "pointer" : "";

		// Bailey hover is a registered kind (_interactable-kinds/bailey.js,
		// precedence 10 — runs last, after every fixture kind has set its
		// state). Engagement via E is dispatched alongside fixture E. The
		// R release + digit-1-8 command handling stays inline below since
		// they're Bailey-specific verbs without an obvious analogue in the
		// fixture vocabulary; they'll register via the registry's
		// interactR / interactDigit extension when Bailey's command-menu
		// session lands its first verbs.

		this._updateEHint();
	}

	activateBox(fixtureId) {
		const entry = this.boxEntries.get(fixtureId);
		if (!entry) return;
		this.activeBoxId = fixtureId;
		this.lidNode = entry.lidNode;
		this.lidBaseQuat.copy(entry.lidBaseQuat || new THREE.Quaternion());
		this.lidPivotNode = entry.lidPivotNode || null;
		this.lidPivotBaseQuat.copy(entry.lidPivotBaseQuat || new THREE.Quaternion());
		this.lidMeshNode = entry.lidMeshNode;
		this.lidMeshBaseY = entry.lidMeshBaseY;
		this.lidHitZone = entry.lidHitZone;
		this.heroExtract = entry.heroExtract;
		this.activeLaneCenter.copy(entry.anchors.laneCenter);
		this.activeBoxForward.copy(boxForwardFromAnchors(entry.anchors.heroExtract, entry.anchors.laneCenter));
		this.lidCurrentAngle = 0;
		this.lidTargetAngle = 0;
		this._lidDebugLogged = false;
		if (this.lidMeshNode) {
			this.lidMeshNode.visible = true;
			this.applyLidAngle(0);
		}
		this.comicEntries = entry.comicEntries;
		this.interactiveMeshes = entry.interactiveMeshes;
		this.browseIndex = 0;
		this.heroComicIndex = null;
		for (const comic of this.comicEntries) {
			comic.root.visible = true;
			comic.leanAngle = COMIC_LEAN_BACK;
			comic.targetLeanAngle = COMIC_LEAN_BACK;
			comic.isHero = false;
			comic.returning = false;
			comic.root.position.copy(comic.basePosition);
			this.applyComicLean(comic);
		}
		this.boxState = "closed";
		this.updateBrowseControls();
	}

	forceCloseActiveBox() {
		if (this.activeBoxId === null) return;
		if (this.heroComicIndex !== null) {
			const hero = this.comicEntries.find((e) => e.index === this.heroComicIndex);
			if (hero) {
				hero.isHero = false;
				hero.returning = false;
				hero.root.position.copy(hero.basePosition);
				hero.leanAngle = COMIC_LEAN_BACK;
				this.applyComicLean(hero);
			}
			this.heroComicIndex = null;
			this._clearActiveHero();
			this.onComicSelect?.(null);
		}
		for (let i = 1; i < this.comicEntries.length; i++) this.comicEntries[i].root.visible = false;
		if (this.comicEntries[0]) this.comicEntries[0].root.visible = true;
		if (this.lidMeshNode) {
			this.lidMeshNode.visible = true;
			this.applyLidAngle(0);
		}
		this.lidCurrentAngle = 0;
		this.lidTargetAngle = 0;
		if (this.lidHitZone && !this.lidMeshes.includes(this.lidHitZone)) {
			this.lidMeshes.push(this.lidHitZone);
		}
		this.comicEntries = [];
		this.interactiveMeshes = [];
		this.browseIndex = 0;
		this.heroComicIndex = null;
		this.boxState = "none";
		this.lidNode = null;
		this.lidPivotNode = null;
		this.lidMeshNode = null;
		this.lidHitZone = null;
		this.heroExtract = null;
		this.activeBoxId = null;
		this.activeLaneCenter.set(0, 0, 0);
		this.activeBoxForward.set(0, 0, 1);
		this._exitBrowsePose();   // ASK-142 F-142e
		this.updateBrowseControls();
	}

	setSceneOverviewCamera() {
		const bbox = new THREE.Box3();
		for (const entry of this.boxEntries.values()) {
			// Anchor-driven boxes have no worldGroup (box mesh lives in the
			// shell GLB, not a procedurally-built group).
			if (entry.worldGroup) bbox.expandByObject(entry.worldGroup);
		}
		if (this.roomRoot.children.length) {
			bbox.expandByObject(this.roomRoot);
		}
		if (bbox.isEmpty()) return;

		const center = bbox.getCenter(new THREE.Vector3());
		const size = bbox.getSize(new THREE.Vector3());
		const span = Math.max(size.x, size.z, 1);

		// Centered directly above the room/scene, tilted slightly forward to show the floor
		const toTarget = new THREE.Vector3(center.x, 0, center.z);
		const toPos = new THREE.Vector3(center.x, span * 0.6 + size.y * 0.8, center.z + span * 0.55);

		this.camera.position.copy(toPos);
		this.controls.target.copy(toTarget);
		this.controls.update();
		this.closedCameraPos = toPos.clone();
		this.closedCameraTarget = toTarget.clone();
	}

	focusFixture(fixtureId) {
		const entry = this.boxEntries.get(fixtureId);
		if (entry) {
			const bbox = new THREE.Box3().setFromObject(entry.worldGroup);
			const center = bbox.getCenter(new THREE.Vector3());
			const size = bbox.getSize(new THREE.Vector3());
			const dist = Math.max(size.x, size.z) * 1.6;
			this.startCameraAnimation(
				new THREE.Vector3(center.x + dist, center.y + size.y * 0.4, center.z + dist * 0.25),
				center.clone(),
				0.55,
			);
			return;
		}
		if (this.closedCameraPos) {
			this.startCameraAnimation(this.closedCameraPos, this.closedCameraTarget, 0.55);
		}
	}

	// ─── Stage reset ─────────────────────────────────────────────────────────────

	resetStage() {
		this.interactiveMeshes = [];
		this.lidMeshes = [];
		this.comicEntries = [];
		this.heroExtract = null;
		this.lidNode = null;
		this.lidCurrentAngle = 0;
		this.lidTargetAngle = 0;
		this.lidMeshNode = null;
		this.lidMeshBaseY = 0;
		this._lidDebugLogged = false;
		this.boxState = "none";
		this._browse = null;   // ASK-142 F-142e: a new scene has no bin to come back from
		this._browseVerb = null;   // ASK-154 seam 2: nor a verb in play
		this.browseIndex = 0;
		this.heroComicIndex = null;
		this.boxEntries.clear();
		this.activeBoxId = null;
		this.wallComics = [];
		this.vinylSleeves = [];
		this.hoveredVinyl = null;
		this.vinylHeroEntry = null;
		this.hoveredWallComic = null;
		this.activeWallComic  = null;
		this.wallComicHeroEntry = null;
		this.pileComics = [];
		this.pileRightStack = [];
		this.pileLeftStack  = [];
		this.pileState      = "none";
		this.pileArcAnim    = null;
		this.pileHeroEntry  = null;
		this.hoveredPileComic = null;
		this.bookEntries     = [];
		this.hoveredBook     = null;
		this.bookBrowseIndex = -1;
		this.bookState       = "none";
		this.bookHeroEntry   = null;
		this.spinnerRack     = null;
		this.activeFixtureHero = null;
		this.focusedBoxComicRoot = null;
		this.activeVinylBin = null;
		this.stackTables = [];
		this.activeStackTable = null;
		this.featuredComics = [];
		// E-Open scaffold — if the player teleports while opened, the
		// procedural mesh would otherwise leak into the next scene.
		if (this.openBookGroup) {
			this.scene.remove(this.openBookGroup);
			this.openBookGroup.traverse((n) => {
				if (n.geometry?.dispose) n.geometry.dispose();
				if (n.material?.dispose) n.material.dispose();
			});
		}
		this.openBookGroup = null;
		this.openBookLeftMat = null;
		this.openBookRightMat = null;
		this.openBookLeftWing  = null;
		this.openBookRightWing = null;
		this.openBookFlipWing  = null;
		this.openBookFlipFrontMat = null;
		this.openBookFlipBackMat  = null;
		this._flipCurlMeshes = null;
		this.pageFlipAnim   = null;
		this.inspectOpened = false;
		this.inspectPageIdx = 0;
		this.comicFlipped  = false;
		this.comicFlipAnim = null;
		this.cameraAnim.active = false;
		this.renderer.domElement.style.cursor = "";
		this.onComicSelect?.(null);
		this.updateBrowseControls();

		while (this.stageRoot.children.length) {
			this.stageRoot.remove(this.stageRoot.children[0]);
		}

		// v2 kit window parallax materials — dispose on scene swap so we don't leak
		// shader programs / uniform allocations across hub→shop→hub hops
		if (this._parallaxMaterials && this._parallaxMaterials.length) {
			for (const m of this._parallaxMaterials) m.dispose?.();
			this._parallaxMaterials = [];
		}
		// v2 kit doors — drop tracked references; the meshes themselves get cleared
		// when roomRoot is wiped below
		this._v2KitDoors = []; this._v2DoorTick = null; this._hubDoors = null; this._hubReady = null; this._hubBuildFailure = null; this._hubProxyHandle?.lift(); this._hubProxyHandle = null; this._hubProxy = null;
		// ASK-146 §16 — the hub-v3 street's life (audio, animators, bloom, tone mapping) ends with the scene.
		if (this._hubLife) { this._hubLife.dispose(); this._hubLife = null; }
		// Shop GLB shell anchors — re-collected on next hybrid load
		this._shopAnchors = null;
		this._effectiveExits = [];
		this._currentComicAssets = null;
		if (this._hubReflector) {
			this._hubReflector.dispose?.();
			this._hubReflector = null;
		}
		while (this.roomRoot.children.length) {
			this.roomRoot.remove(this.roomRoot.children[0]);
		}
		this.floor.visible = true;

		for (const light of this.shopLights) this.scene.remove(light);
		this.shopLights = [];
		this.shopFlickers = [];
		this.scene.fog = null;
		this.scene.environment = null;
		this.scene.background = new THREE.Color("#f3eadc");
		this.renderer.toneMappingExposure = 1.0;
		this.ambientLight.intensity = 0.32;
		this.ambientLight.color.set(0xffe8cc);
		this.fillLight.intensity = 0.22;
		this.fillLight.castShadow = false;   // ASK-084: fill never casts (see constructor); owner's fitted caster does

		if (this.dustParticles) {
			this.scene.remove(this.dustParticles);
			this.dustParticles.geometry.dispose();
			this.dustParticles.material.dispose();
			this.dustParticles     = null;
			this.dustVelocities    = null;
			this.dustHomePositions = null;
			this.dustRoomBounds    = null;
		}

		this.hoveredBoxId = null;
		this._boxHintKey = "";
		this.onBoxHover?.(null);

		// Reset generic interactable dispatcher state — fires each migrated
		// kind's commitHover with prev → null so any per-stage references it
		// holds are released alongside the scene tear-down above.
		resetDispatcher(this);

		this.activeTriggers = [];
		this._armedTriggers.clear();
		this._trigPrev = null;
		this._portalWaypoints = []; // stageRoot was already cleared above
		this._entrance?.dispose();   // ASK-177 §10 — the street's door prompts / the shop's exit nameplate leave with their scene

		// Reset headbob so the new scene's spawn isn't carrying over a Y offset
		this._bobPhase = 0;
		this._bobAmp = 0;
		this._lastBobY = 0;

		// Reset step-climbing — prime baseline on first frame after spawn
		this._lastGroundY = undefined;
		this._stepClimbDebt = 0;
	}

	/** 🔴 ASK-106 (shortbox lid, measured 2026-10-16) — the lid opens to the angle its MANIFEST authors.
	 *  `open-close` wrote `hinge.openAngleRad` onto the composed entry (`lidOpenAngle`) and its header
	 *  promised the animator read it; nothing did — every box opened to the stage constant 0.8π (144°)
	 *  while longbox, longbox-tall and shortbox all author 1.7 rad (97°). Measured in the real render on
	 *  demo-store SHORTBOX_ANCHOR_01: pivot turn 2.5133 rad at rest. The same write-only shape as the
	 *  hinge knob (ASK-129 §E2E104). The constant stays the fallback for a box that authors nothing. */
	_lidOpenAngle() {
		const a = this.boxEntries?.get?.(this.activeBoxId)?.lidOpenAngle;
		return lidOpenAngleFor(a, LID_OPEN_ANGLE);
	}

	openLid() {
		console.log("[KapowX] openLid called — current boxState:", this.boxState);
		if (this.boxState !== "closed") return;
		// Lid animates open via the per-frame lerp on lidCurrentAngle →
		// lidTargetAngle (~line 3430). Animation loop flips boxState to "open"
		// when the lerp lands. Mirrors the symmetric close-lid path. The old
		// instant-snap path hid the proxy-GLB lidMeshNode; with the shell-GLB
		// lid (Shell_*_Lid) the mesh stays visible and just rotates.
		this.lidTargetAngle = this._lidOpenAngle();
		this.lidMeshes = this.lidMeshes.filter((m) => m !== this.lidHitZone);
		this.boxState = "opening";
		this.cue("lid-open", { role: "self", pos: this._cuePosOf(this.lidMeshNode || this.lidPivotNode) || this._handPos() });   // ASK-174: the lid, from the box
		this.updateBrowseControls();
		// ASK-142 F-142e (RULED 2026-10-15) — the camera no longer stays where the player stood: it blends
		// to the browse pose while the lid swings, so the bin fills the view (was ≈ 20 % of the frame).
		this._enterBrowsePose();
	}

	advanceBrowse(delta) {
		const count = this.comicEntries.length;
		if (!count) return;

		// When a comic is popped up, arrows step through heroes directly.
		if (this.boxState === "hero") {
			const newIndex = Math.max(0, Math.min(count - 1, (this.heroComicIndex ?? this.browseIndex) + delta));
			if (newIndex === this.heroComicIndex) return;
			this.browseIndex = newIndex;
			for (let i = 0; i < count; i++) {
				if (this.comicEntries[i]) {
					this.comicEntries[i].targetLeanAngle = i < this.browseIndex
						? COMIC_LEAN_FORWARD
						: COMIC_LEAN_BACK;
				}
			}
			this.setSelectedComic(newIndex);
			return;
		}

		if (this.boxState !== "open" && this.boxState !== "browsing") return;
		this.boxState = "browsing";

		this.browseIndex = Math.max(0, Math.min(count - 1, this.browseIndex + delta));

		// Comics before the current index have been passed — lean them forward.
		// Comics at or after the current index are still in the queue — lean them back.
		for (let i = 0; i < count; i++) {
			if (this.comicEntries[i]) {
				this.comicEntries[i].targetLeanAngle = i < this.browseIndex
					? COMIC_LEAN_FORWARD
					: COMIC_LEAN_BACK;
			}
		}

		this.updateBrowseControls();
	}

	setSelectedComic(index) {
		if (!this.heroExtract) return;

		const entry = this.comicEntries.find((e) => e.index === index);
		if (!entry) return;

		if (this.heroComicIndex !== null && this.heroComicIndex !== index) {
			const prev = this.comicEntries.find((e) => e.index === this.heroComicIndex);
			if (prev) {
				prev.isHero = false;
				prev.returning = true;
				prev.returnProgress = 0;
				prev.returnFrom = prev.root.position.clone();
				prev.returnQuatFrom = prev.root.quaternion.clone();
			}
		}

		// Camera-relative hero target — same calc the wall-comic / pile / book
		// / spinner / vinyl heroes use. Previously the longbox flew the comic
		// up to a static `heroExtract` anchor inside the box, which felt
		// inconsistent next to every other fixture's "flies to in front of
		// you" behaviour.
		const heroPos = this._computeHeroPos(0.26, entry.root);
		const heroQuat = this._faceCameraQuat(heroPos);

		entry.isHero = true;
		entry.heroProgress = 0;
		entry.heroFrom = entry.root.position.clone();
		entry.heroQuatFrom = entry.root.quaternion.clone();
		entry.heroPos  = heroPos;
		entry.heroQuat = heroQuat;
		this.heroComicIndex = index;
		this.boxState = "hero";

		// Spotlight tracks the hero comic's new in-front-of-player position.
		if (this.heroSpotLight) {
			this.heroSpotLight.position.set(heroPos.x, heroPos.y + 0.25, heroPos.z + 0.20);
			this.heroSpotLight.intensity = 3.5;
			this.heroSpotLight.visible = true;
		}

		this._setActiveHero("longbox", entry.product, entry.root);
		this.onComicSelect?.({ index, product: entry.product });
		this.updateBrowseControls();
	}

	putHeroBack() {
		if (this.heroComicIndex === null) return;
		this._snapUnflip();
		const entry = this.comicEntries.find((e) => e.index === this.heroComicIndex);
		if (entry) {
			entry.isHero = false;
			entry.returning = true;
			entry.returnProgress = 0;
			entry.returnFrom = entry.root.position.clone();
			entry.returnQuatFrom = entry.root.quaternion.clone();
		}
		this.heroComicIndex = null;
		this._clearActiveHero();
		this.boxState = this.browseIndex > 0 ? "browsing" : "open";
		this.onComicSelect?.(null);
		this.updateBrowseControls();

		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible = false;
		}
		// Camera stays where the player is — no forced return animation.
	}

	closeLid() {
		// Return any hero comic to its slot
		if (this.heroComicIndex !== null) {
			const hero = this.comicEntries.find((e) => e.index === this.heroComicIndex);
			if (hero) {
				hero.isHero = false;
				hero.returning = true;
				hero.returnProgress = 0;
				hero.returnFrom = hero.root.position.clone();
				hero.returnQuatFrom = hero.root.quaternion.clone();
			}
			this.heroComicIndex = null;
			this._clearActiveHero();
			this.onComicSelect?.(null);
		}

		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible = false;
		}

		// Reset all comics to lean-back so they sit tidy inside the closing box
		for (const entry of this.comicEntries) {
			entry.targetLeanAngle = COMIC_LEAN_BACK;
		}
		this.browseIndex = 0;

		// Lid reappears at the open position and animates back down onto the box
		if (this.lidMeshNode) {
			this.lidMeshNode.visible = true;
			this.applyLidAngle(this._lidOpenAngle());
		}
		this.lidCurrentAngle = this._lidOpenAngle();

		// Drive lid back down
		this.boxState = "closing";
		this.cue("lid-close", { role: "self", pos: this._cuePosOf(this.lidMeshNode || this.lidPivotNode) || this._handPos() });
		this.lidTargetAngle = 0;
		this._exitBrowsePose();   // ASK-142 F-142e — back to where the walker stood, over the same 260 ms

		this.updateBrowseControls();
	}

	/** The shared comic template, resolved from the scene binding's `assetKey` through the CDN — exactly as a
	 *  `projectAssets` slot is (RULING 32; Client 4ad, ASK-159 §2.18hb 🟣 2 step 1). The binding's `assetUrl`
	 *  is NEVER read: it was fetched against the runtime ORIGIN, where test-shop's key-shaped URL answered the
	 *  SPA's index.html and the comic fell to the procedural template unseen. Two named fallbacks, both the
	 *  procedural comic contract: a binding with NO `assetKey` (nothing is bound — a warning), and a key that
	 *  cannot resolve because the build has no CDN base (a misconfiguration — an error; vite refuses that build). */
	async resolveComicTemplate(binding, token, assetLabel = "shared comic", metrics = boxedComicReferenceMeters) {
		const assetKey = String(binding?.assetKey || "").trim();
		const assetUrl = resolveBindingAssetUrl(binding) || "";

		if (!assetKey || !assetUrl) {
			return {
				mode: "procedural",
				assetKey,
				assetUrl: "",
				errors: assetKey ? [`The ${assetLabel} key "${assetKey}" has no CDN base to resolve against (VITE_SCENE_CDN_URL unset).`] : [],
				warnings: assetKey ? [] : [`No ${assetLabel} assetKey is bound, so runtime is using the procedural comic contract.`],
				template: createProceduralComicTemplate(metrics),
			};
		}

		try {
			const gltf = await this.gltfLoader.loadAsync(assetUrl);

			if (token !== this.currentToken) {
				throw new Error("stale-runtime-request");
			}

			const report = inspectComicAsset(gltf);

			if (!report.valid) {
				return {
					mode: "procedural",
					assetKey,
					assetKey,
				assetUrl,
					errors: [...report.errors],
					warnings: [...report.warnings],
					template: createProceduralComicTemplate(metrics),
				};
			}

			const clone = gltf.scene.clone(true);
			stripGuideNodes(clone);
			const template = getContractNode(clone, "comic_root") || clone;

			return {
				mode: "mesh",
				assetKey,
				assetUrl,
				errors: [],
				warnings: [...report.warnings],
				template,
			};
		} catch (error) {
			const message = error?.message === "stale-runtime-request"
				? "Runtime stage changed before the comic asset finished loading."
				: `${capitalizeLabel(assetLabel)} asset failed to load from ${assetUrl}.`;

			return {
				mode: "procedural",
				assetKey,
				assetUrl,
				errors: [message],
				warnings: [],
				template: createProceduralComicTemplate(metrics),
			};
		}
	}

	async populateComics({ fixture, anchors, comicTemplate, products, materialSlots, token, comicStore = null, maxSlots = null, interaction = "in-box", browseVerbs = true }) {
		const targetEntries = comicStore ? comicStore.comicEntries : this.comicEntries;
		const targetMeshes = comicStore ? comicStore.interactiveMeshes : this.interactiveMeshes;
		// CONTAINMENT, not enumeration (slice-2b ruling). This group used to be a single anonymous
		// `runtime_comics` bucket under stageRoot, so everything inside it was owned by nobody: the
		// comics were separately recorded in comicEntries and travelled, while the box BACKDROP — added
		// below and recorded nowhere — stayed behind as a dark square at the old position when the
		// fixture moved. That was the third family-enumeration miss in this workstream.
		// The group is now per-anchor and registered ONCE, so anything added to it afterwards belongs
		// to the fixture by construction and moves with it, with no registry edit needed here or later.
		const ownerAnchor = comicStore?.fixtureId || null;
		const comicsRoot = new THREE.Group();
		comicsRoot.name = ownerAnchor ? `runtime_comics__${ownerAnchor}` : "runtime_comics";
		this.stageRoot.add(comicsRoot);
		if (ownerAnchor) {
			this._fixtureContents ||= new Map();
			if (!this._fixtureContents.has(ownerAnchor)) this._fixtureContents.set(ownerAnchor, []);
			this._fixtureContents.get(ownerAnchor).push(comicsRoot);
		}
		// ASK-139 F-139an — sleeves standing edge-out in a VOLUME are a browse bin (`_flip-bin.js`, lazy).
		// ASK-147 §5.4 — sleeves face-out on a wall PANEL (`surface · grid` on a non-level surface) are the
		// same sleeve mesh, registered as a wall display rather than a bin (`arrangeWallSleeves`).
		const panelSeat = interaction === "panel";
		const sleeveKit = (interaction === "flip-bin" || panelSeat) ? (this._flipBin ||= await import("./_flip-bin.js").catch((err) => { console.warn("[compose] flip-bin did not load:", err?.message || err); return null; })) : null;
		const flipBin = interaction === "flip-bin" ? sleeveKit : null;
		if (token !== this.currentToken) return;
		const flipSeats = [];
		const wallSleeves = [];

		for (let index = 0; index < products.length; index += 1) {
			if (token !== this.currentToken) {
				return;
			}

			const product = products[index];
			// P2-E2 ruling 2 — which mesh does this seat draw? Concrete-type catalogue key, else
			// the line's `renderAssetKey` (GCG emits it from product_asset_binding), else the comic
			// family's own template — and a typed non-comic line with no resolvable asset renders
			// NOTHING and says why by name. Never a guessed placeholder: a statue drawn as a comic
			// box would look supported and would have to be un-shipped later.
			const renderSource = resolveProductRenderSource({ catalogue: this._productCatalogue, fixtureCatalogue: this._fixtureCatalogueCached, productTypes: this._productTypes, product });
			const refuseSeat = (detail) => {
				console.warn(`[compose] ${comicsRoot.name} seat ${index} (${product?.id ?? "?"}): no-render-asset — ${detail}; the seat renders nothing`);
				const store = comicStore || this;
				(store.renderRefusals ||= []).push({ index, productId: product?.id ?? null, reason: "no-render-asset", detail });
			};
			if (renderSource.kind === "refused") { refuseSeat(renderSource.detail); continue; }
			let comic;
			const catalogueMesh = renderSource.kind === "catalogue";
			if (catalogueMesh) {
				// ASK-126: the key resolves in whichever catalogue owns it (a decor asset IS a
				// fixture-catalogue key) — the loader is key→GLB either way, one renderer, one path.
				const sourceMap = renderSource.source === "fixture" ? this._fixtureCatalogueCached : this._productCatalogue;
				const gltf = await loadProductCatalogue({ catalogue: sourceMap, productType: renderSource.key, gltfLoader: this.gltfLoader });
				if (token !== this.currentToken) return;
				// A load failure refuses too — the rule forbids a placeholder in both directions.
				if (!gltf?.scene) { refuseSeat(`catalogue GLB for '${renderSource.key}' did not load`); continue; }
				comic = gltf.scene.clone(true);
			} else {
				// A comic-family line with no template loaded is a refusal, not a crash — this
				// branch is unreachable on shop scenes (the template loads with the scene) but a
				// compose without it must say so by name rather than throw mid-populate.
				if (!comicTemplate) { refuseSeat("comic template unavailable on this compose"); continue; }
				comic = comicTemplate.clone(true);
			}
			// A bin sleeve is ONE mesh (the vinyl browse raycasts, outlines and flies its target as a
			// mesh) — the catalogue asset's parts merged, same geometry, same named materials.
			const wallSleeve = panelSeat && !!sleeveKit && this._isVinylLine(product);
			if (flipBin || wallSleeve) comic = sleeveKit.sleeveMeshOf(comic, (this._sleeveGeometry ||= new WeakMap())) || comic;
			// ASK-139 F-139aj — A RIGHT-BOUND BOOK IS THE LEFT-BOUND ASSET MIRRORED ABOUT ITS SPINE PLANE.
			// The catalogue ships one book per type, bound on the left (spine at −X). A manga's spine
			// is on the right of the cover you read first, so the clone's geometry is mirrored (spine
			// to +X, front still +Z, print kept readable — `_book-mirror.js`) BEFORE it is sized and
			// seated, and from here on it is simply a mesh: the spine-out turn below measures where
			// the spine is, so the front cover lands on the other side of a left-bound neighbour by
			// the same rule that stands the neighbour. Only a line the reader can open has a binding
			// (a statue has no spine); the procedural comic template is left alone — a comic's
			// binding shows in E-Open's flip direction, which the contract defers.
			if (catalogueMesh && bookBinding(product) === "right" && this._isReadableProduct(product)) {
				try {
					const m = await import("./_book-mirror.js");
					if (token !== this.currentToken) return;
					m.mirrorRootAcrossSpinePlane(comic, (this._mirroredGeometry ||= new WeakMap()));
					comic.userData.binding = "right";
				} catch (err) {
					console.warn(`[compose] ${comicsRoot.name} seat ${index} (${product?.id ?? "?"}): right binding not mirrored — ${err?.message || err}; the book stands as left-bound`);
				}
			}
			// Per-slot placement (DECISION-052) — when the slot-layout supplies explicit
			// per-slot poses (anchors.slots[i], for non-linear layouts: fan / pile / spinner /
			// explicit-slots), use them; else the linear lane (slotOne + step·i) for the
			// longbox/shelf families. Backward-compatible: linear layouts' slots ARE the lane,
			// so the result is identical for them.
			const slotPose = Array.isArray(anchors.slots) ? anchors.slots[index] : null;
			// ASK-121 — a slot carrying a {u,v} address is a SURFACE placement: the packer's basis
			// (base-down, possibly turned 90° to fit) IS the presentation, and the box-family lean
			// must not touch it. Measured on the deployed plane: every item on a derived surface
			// socket was drawn at lean × slots[0].quaternion — the per-slot pose (and with it the
			// packer's turn) erased by applyComicLean reading the SHARED slotQuaternion as its base.
			const surfaceSeat = Number.isFinite(slotPose?.at?.u);
			if (slotPose?.position) {
				comic.position.copy(slotPose.position);
				comic.quaternion.copy(slotPose.quaternion || anchors.slotQuaternion);
			} else {
				comic.position.copy(anchors.slotOne.clone().addScaledVector(anchors.slotStepVector, index));
				comic.quaternion.copy(anchors.slotQuaternion);
			}
			comic.userData.packIndex = index;
			// ASK-154 seam 2 — the socket's DERIVED verb rides the seat (r55). Absent (r54) stamps nothing and every
			// line below runs exactly as before; a lidded box (`browseVerbs: false`) keeps its shipped riffle.
			const verbSeat = isVerbSeat({ slot: slotPose, browseVerbs });
			const verbGrain = seatedGrainForVerb({ interaction, verbSeat });
			if (verbSeat) { comic.userData.browse = slotPose.browse; this._loadBrowseVerbs().catch(() => {}); }
			// ASK-120 S3.5 — the seat grain, on the box-family path. `ownerAnchor` is the host anchor
			// (the same key the containment group is registered under), so a boxed comic answers "which
			// seat am I?" with the address a pin would name. Stamped on the template CLONE and every
			// mesh under it, because the raycast hits a grandchild.
			const seat = ownerAnchor
				? this._seatStampFor(ownerAnchor, product, slotPose?.socketId ?? null, index, slotPose?.at ?? null)
				: null;
			if (seat) this._stampSeat(comic, seat);
			if (catalogueMesh) {
				// 🔴 D-074 (ASK-137 §75) — THE MESH DRAWS AT ITS WORLD SIZE.
				//
				// The GLB is the MODEL at real scale, so the world size is the model times the
				// merchant's `displayScale`, and applying it here is the whole of "seated products
				// render at world". One multiply, before the base-sink below, so a scaled item still
				// rests ON the surface rather than hovering by its own growth.
				//
				// Inert until GCG stamps the field: absent or 1.0 scales by 1 and this is the code it
				// was. Locked to 1.0 for print by `displayScaleFor`, which reads the same
				// cover-surfaces discriminator the reader does — a comic cannot be scaled.
				const dType = product?.kapowxProductType || product?.productType || product?.type || null;
				const dScale = displayScaleFor(product, { surfaceSlots: this._lookupProductSurfaceSlots(dType) });
				if (Number.isFinite(dScale) && dScale > 0 && Math.abs(dScale - 1) > 1e-6) comic.scale.setScalar(dScale);
				// The catalogue GLB carries its own authored materials — the per-face comic
				// texture walk has nothing to bind and must not repaint them.
				// Rest the UNDERSIDE on the seat: the type card declares `origin: base`, but the
				// mesh is measured rather than trusted — the same base-meets-plane fact the gate
				// asserts of every seated item.
				comic.updateMatrixWorld(true);
				const _sinkBox = new THREE.Box3().setFromObject(comic);
				const sink = _sinkBox.min.y - comic.position.y;
				if (Math.abs(sink) > 1e-4) comic.position.y -= sink;
			}
			// 🔴 ASK-129 F-108c — A CATALOGUE MESH IS STILL A SKU, AND A SKU HAS A COVER.
			//
			// This ran only on the comic-TEMPLATE branch, so a line whose type resolves to a
			// catalogue GLB drew the bare mesh. That was a decor-only branch until the product
			// catalogue gained the umbrella keys `comic` / `book` / `vinyl` (Content, ruling 1):
			// `resolveProductRenderSource` prefers a catalogue entry for the line's OWN type over the
			// comic family's template, so every plain comic silently moved onto this branch and lost
			// its artwork. MEASURED on the deployed plane, `ci-probe-shop`, a comic pinned to
			// ZONE::floor-01 with `media.front` on the wire:
			//
			//     material "mat_front" · hasMap FALSE · colour ffffff   ← a blank white board
			//
			// and the photograph is a white sliver standing beside a fully-textured figurine.
			// `mat_front` is exactly the slot `boot.productTypes.comic.surfaceSlots` declares, so the
			// binding the registry describes was never ATTEMPTED — not attempted and unmatched.
			//
			// Now applied for every mesh source, which is D-050 §1's one render path. Safe for an
			// asset that brings its own look: a statue declares no `surfaceSlots` and carries no
			// legacy `front_surface` node, so this finds nothing to bind and leaves the GLB's own
			// materials alone (the figurine beside the comic is untouched — measured).
			// 🔴 A COVER THAT WILL NOT BIND MUST NOT TAKE THE SEAT DOWN WITH IT.
			//
			// This is awaited inside the per-seat loop and nothing caught it, so one unloadable image
			// threw past the whole loop: the item was never added, and every seat AFTER it was never
			// built either. MEASURED on the plane — the identical run with one line's `media.front`
			// swapped for an image the loader could not decode went from four floor items to NONE,
			// including the two whose own covers were fine. A merchant uploading a bad file must lose
			// their cover art, not their shop floor; the existing loader already treats a failed
			// texture as a non-event for determinism, and this is the same rule one level up.
			//
			// 🔴 ASK-142 R2 — …AND THE SEAT DOES NOT WAIT FOR IT. Awaiting here made compose (and the
			// loader) wait for every photo in the shop, fetched one at a time: 103–212 s measured on a
			// 67-product shop. The seat is built NOW in the slot's fallback colour; its covers stream in
			// through the pool (visible-first) and bind when they land. `coversSettled()` is the wait
			// for anything that genuinely needs every cover bound (captures, probes).
			const seatName = comicsRoot.name;
			// ASK-142 F-142a — every face binds its SHELF level whenever the item is drawn, a spine-out book
			// included: the browse LEANS a book out before E, and a front bound only on hold showed the 1×1
			// placeholder for that whole lean (Vanessa, E2E204). What is not drawn still waits (`_nodeDrawn`:
			// a closed longbox keeps its lid gate); the HERO level stays a hold.
			this._coverStream.track(this.applyProductTextures(comic, product, token, materialSlots).catch((err) => {
				console.warn(`[compose] ${seatName} seat ${index} (${product?.id ?? "?"}): cover did not bind — ${err?.message || err}; the item is seated without its artwork`);
			}));
			if (this._isBookLine(product)) this._dressBookSpine(comic, product);   // ASK-139 F-139af — never a blank spine
			comicsRoot.add(comic);

			// Each in-box comic mesh carries the same interactable shape (kind + pack index + product). It has
			// no `onClick`: the box's verbs are keyboard/tap-owned via the browse cursor (Phase E contract,
			// b6a2f65 — E / a tap selects `browseIndex`), and nothing has called an interactable's onClick
			// since that commit removed the pointer dispatcher (ASK-163 4s item 4, coord §2.18s finding 3).
			const inBoxInteractable = {
				kind: "boxed-comic",
				fixtureKind: fixture.templateKey,
				fixtureId: fixture.id,
				packIndex: index,
				product,
				describe: () => ({ kind: "boxed-comic", productId: product?.id, fixtureId: fixture.id, packIndex: index }),
			};
			comic.traverse((node) => {
				if (node.isMesh) {
					node.castShadow = true;
					node.receiveShadow = true;
					node.userData.packIndex = index;
					// 🔴 ASK-129 F-108c — THE FAMILY DECIDES THE VERBS, NOT WHERE THE MESH CAME FROM.
					//
					// The guard read `!catalogueMesh`, and its reason is still right: the browse/hero
					// click verbs are the longbox lifecycle and would page a statue like a back issue.
					// But "came from the catalogue" stopped meaning "is not a readable product" when
					// the catalogue gained `comic` / `book` / `vinyl` — so every plain comic fell out
					// of the registry and became scenery a customer cannot pick up. What the verbs
					// actually need is a line the READER can open: one whose type declares cover
					// surfaces in the registry. A statue declares none and stays inert, which is the
					// behaviour the old guard was reaching for.
					//
					// `interaction: "none"` is for callers that seat into the OPEN (the zone), where
					// the box lifecycle has no meaning and the caller wires the wall-comic record
					// itself — one mesh, one owner.
					if (interaction !== "none" && interaction !== "shelf" && interaction !== "flip-bin" && !panelSeat && !surfaceSeat && !verbGrain && this._isReadableProduct(product)) {
						node.userData.interactable = inBoxInteractable;
						targetMeshes.push(node);
					}
				}
			});

			// 🔴 ASK-137 F-115k — A SURFACE SEAT IS NOT A BOX, AND IT IS NOT SCENERY EITHER.
			//
			// The gate above used to be "readable → the box lifecycle, else nothing", and on a holder's
			// SURFACE both halves are wrong: the browse/hero verbs are the longbox's (there is no box to
			// page through on a pedestal), and "nothing" is what Vanessa measured in Play — three items
			// on her pedestal, none hoverable, the robot with no E. MEASURED before the fix on her shop:
			// every seat stamped, `userData.interactable` null, `interactiveMeshes` 0.
			//
			// So a surface seat gets the SEATED grain instead, and which one is decided by the product,
			// not by the furniture: something you read goes to the verified seated-comic path (the route
			// the selling-space floor already uses — one call, held by probe-zone-customer and
			// probe-floor-stock), and everything else becomes a `seated-item` the shopper can pick up
			// and turn over. Wired on the ROOT, because for a multi-mesh catalogue clone the item IS
			// the root and the hero must lift the whole thing.
			if (interaction !== "none" && surfaceSeat) this._wireSeatedItemInteraction(comic, product);
			// ASK-154 seam 2 — a LIDLESS holder's stock (wall rack, bank, spinner, case) has no box to page it through;
			// with its socket's verb it takes the seated grain, so the customer pulls it out and the panel opens.
			if (verbGrain) this._wireSeatedItemInteraction(comic, product);
			// ASK-139 F-139af — a SHELF seat: turned spine-out when it is a book, then picked up as one.
			if (interaction === "shelf") this._seatOnShelf(comic, product, anchors.approach);
			// ASK-139 F-139an — a BIN seat: stood in its column by `arrangeFlipBin` once every seat is
			// built (the lean and the flip's room are per column), then owned by the vinyl browse/hero
			// registry — one mesh, one owner, so it never joins the box lifecycle's entries below.
			if (flipBin && slotPose?.bin) { flipSeats.push({ mesh: comic, product, slot: slotPose }); continue; }
			// ASK-147 §5.4 — a PANEL seat: a sleeve joins the vinyl wall display (hover · E hero · Put back);
			// anything else on a panel (a poster, a comic) takes the seated grain every holder surface uses.
			if (wallSleeve) { wallSleeves.push({ mesh: comic, product, slot: slotPose }); continue; }
			if (panelSeat && interaction !== "none") this._wireSeatedItemInteraction(comic, product);

			// Compute how far below the root origin the comic's physical base sits.
			// Used in applyComicLean to pivot rotation at the base rather than the centre.
			comic.updateMatrixWorld(true);
			const _cbbox = new THREE.Box3().setFromObject(comic);
			const bottomOffset = _cbbox.min.y - comic.position.y;

			const entry = {
				index,
				product,
				root: comic,
				basePosition: comic.position.clone(),
				// The item's OWN pose, never the shared lane quaternion: on a scatter layout each
				// slot carries its own basis and slots[0]'s stands for nobody else's. Identical in
				// value for the linear lane, where every slot shares one basis.
				// A shelf seat may have been turned spine-out after placement (`_seatOnShelf`) — that turn
				// is its rest pose, and the lean below must start from it rather than undo it.
				baseQuaternion: (interaction === "shelf" ? comic.quaternion : (slotPose?.quaternion || anchors.slotQuaternion)).clone(),
				bottomOffset,
				// The comic lean is the browse-bin's resting pose; a surface seat's pose is the
				// packer's, and a catalogue product (a statue, a jug) never takes a comic's lean.
				leanAngle: (surfaceSeat || panelSeat || catalogueMesh) ? 0 : COMIC_LEAN_BACK,
				targetLeanAngle: (surfaceSeat || panelSeat || catalogueMesh) ? 0 : COMIC_LEAN_BACK,
				isHero: false,
				heroProgress: 1,
				heroFrom: new THREE.Vector3(),
				returning: false,
				returnProgress: 1,
				returnFrom: new THREE.Vector3(),
			};

			targetEntries.push(entry);
			this.applyComicLean(entry);
		}
		if (flipBin && flipSeats.length) {
			const n = flipBin.arrangeFlipBin(this, flipSeats);
			console.log(`[compose] ${comicsRoot.name}: ${n} sleeve(s) standing in the bin, registered for browse`);
		}
		if (sleeveKit && wallSleeves.length) {
			const n = sleeveKit.arrangeWallSleeves(this, wallSleeves);
			console.log(`[compose] ${comicsRoot.name}: ${n} sleeve(s) face-out on the panel, registered for inspect`);
		}

		// Backdrop placement: when a box has fewer comics than its physical
		// capacity, push the backdrop to the far end (maxSlots) and fill the
		// gap with thin cardboard A-Z divider panels. Reads more authentic
		// than packing the backdrop tight behind the last comic. Renders
		// even for fully-empty boxes (capacity > 0) so the box has a back
		// wall + visible dividers when opened.
		const capacity = Number.isFinite(maxSlots) && maxSlots > products.length
			? maxSlots
			: products.length;
		// A backdrop is the LANE's back wall at slotOne + step·capacity. A surface layout has no
		// lane and no linear step — its "step" is the diagonal between two scattered items — so the
		// same arithmetic drew a bare untextured panel standing edge-on among the placed stock
		// (measured on the deployed plane, the pedestal's packed-top shot).
		const surfaceLayout = Array.isArray(anchors.slots) && anchors.slots.some((s) => Number.isFinite(s?.at?.u));
		// …and a shelf has no back wall to draw: the board and the wall behind it are the fixture's own.
		if (capacity > 0 && !surfaceLayout && interaction !== "shelf" && interaction !== "flip-bin" && !panelSeat) {
			const backdropIndex = capacity;
			const backdropPos = anchors.slotOne.clone().addScaledVector(anchors.slotStepVector, backdropIndex);
			const backdropGeo = new THREE.BoxGeometry(0.16, 0.27, 0.02);
			const backdropMat = new THREE.MeshStandardMaterial({ color: 0x3a2510, roughness: 0.9, metalness: 0 });
			const backdrop = new THREE.Mesh(backdropGeo, backdropMat);
			backdrop.position.copy(backdropPos);
			backdrop.quaternion.copy(anchors.slotQuaternion);
			comicsRoot.add(backdrop);
		}
	}

	async applyProductTextures(comicRoot, product, token, materialSlots = {}) {
		// DECISION-026 Phase 2 — registry-driven per-product surface texturing.
		// Reads `boot.json.productTypes[kapowxProductType].surfaceSlots[]`
		// (per ASK-047 ship; Content's canonical registry at
		// `fixtures/product-physics-defaults.json`). Walks GLB MATERIALS by
		// declared slot name + applies the per-product feed texture per
		// the registry's `feedField` mapping. Falls back to fallbackColor
		// when feed has no image. AAA precedent: Unity Asset Store named
		// material-slot binding + Sims 4 CAS pattern_slot texturing.
		//
		// Back-compat: when registry has no entry for the product's
		// kapowxProductType, OR no surfaceSlots defined, OR the GLB has
		// no matching material names, falls through to the LEGACY
		// hardcoded sub-mesh walk (`front_surface` / `back_surface` /
		// `spine_surface` / `pages_block`) — preserves rendering on
		// existing single-`Body`/single-material product GLBs.
		const productType = product?.kapowxProductType || product?.productType;
		const surfaceSlots = this._lookupProductSurfaceSlots(productType);
		if (surfaceSlots && surfaceSlots.length > 0) {
			const applied = await this.applyRegistryDrivenTextures(comicRoot, product, surfaceSlots, token);
			// If at least one slot matched a material in the GLB, the registry
			// pipeline is in effect; skip legacy fallback. If 0 slots matched
			// (e.g. GLB ships only `cover_texture`), fall through to legacy
			// so existing single-material GLBs keep rendering.
			if (applied > 0) return;
		}
		// Legacy fallback path — pre-DECISION-026 named-sub-mesh convention.
		// Active on existing single-Body GLBs without registry-named materials.
		const front = getContractNode(comicRoot, "front_surface");
		const back = getContractNode(comicRoot, "back_surface");
		const spine = getContractNode(comicRoot, "spine_surface");
		const pages = getContractNode(comicRoot, "pages_block");
		await this.applySurfaceMaterial(front, materialSlots.front, product.frontImage, "#ffffff", token, product, "front");
		await this.applySurfaceMaterial(back, materialSlots.back, product.backImage, "#f7f4ef", token, product, "back");
		await this.applySurfaceMaterial(spine, materialSlots.spine, product.spineImage, "#d5d7df", token, product, "spine");
		await this.applySurfaceMaterial(pages, materialSlots.pages, product.pagesImage, "#f4eee3", token, product, "pages");
	}

	// Look up surfaceSlots[] for a given kapowxProductType from
	// `boot.json.productTypes[]` (per ASK-047 ship). Returns null if not
	// found — caller falls through to legacy single-material path.
	/** Is this line something a customer READS — i.e. does the registry declare cover surfaces for
	 *  its type? That is the platform's own answer to "this has a front you can look at", and it is
	 *  what separates a comic/book/magazine/vinyl from a statue. Used to decide whether a seated mesh
	 *  carries product verbs (ASK-129 F-108c); deliberately NOT a list of type ids, which would need
	 *  editing every time Content ships a type. */
	_isReadableProduct(product) {
		const type = product?.kapowxProductType || product?.productType || product?.type || null;
		const slots = this._lookupProductSurfaceSlots(type);
		return Array.isArray(slots) && slots.length > 0;
	}

	_lookupProductSurfaceSlots(productType) {
		if (!productType || !this._productTypes) return null;
		const entry = this._productTypes.find((p) => p?.productTypeId === productType);
		return entry?.surfaceSlots || null;
	}

	// Walk the comic root for materials matching the registry's slot
	// declarations + apply per-product feed textures (or fallbackColor).
	// Returns the count of slots that matched a material in the GLB.
	// Wildcard slots (`interior_spread_*`) match materials by name prefix
	// + bind ordinally against array-valued feed fields (`interiorImages[]`).
	async applyRegistryDrivenTextures(comicRoot, product, surfaceSlots, token) {
		let appliedCount = 0;
		// 🔴 ASK-137 §119b — EACH SEAT OWNS ITS COVER. A seat drawn from a catalogue GLB is
		// `gltf.scene.clone(true)`, and a three.js clone SHARES its materials — so binding this SKU's
		// image onto `mat_front` in place rebound it for EVERY comic cloned from that GLB, and the
		// shop showed the last cover loaded on all of them. Measured on the plane: "Spiderman" lifted
		// off a shelf with comic 2's front on the face the eye ray hits. The legacy branch never had
		// this (`applyMaterial` clones per mesh); this branch now does the same for the slots it binds
		// (Unity's renderer.material instancing — the shared asset stays untouched).
		const owned = (this._seatMaterials ||= new WeakSet());
		const bound = (name) => surfaceSlots.some((sl) => (sl.wildcard ? name.startsWith(sl.name.replace(/_\*$/, "_")) : sl.name === name));
		const materialsByName = new Map();
		comicRoot.traverse((n) => {
			if (!n.material) return;
			const many = Array.isArray(n.material);
			const mats = (many ? n.material : [n.material]).map((m) => {
				if (!m?.name || !bound(m.name)) return m;
				const own = owned.has(m) ? m : m.clone();
				owned.add(own);
				materialsByName.set(own.name, { node: n, material: own });
				return own;
			});
			n.material = many ? mats : mats[0];
		});
		for (const slot of surfaceSlots) {
			if (slot.wildcard) {
				const prefix = slot.name.replace(/_\*$/, "_");
				const feedArrPath = (slot.feedField || "").replace(/\[\]$/, "");
				const feedArr = (product && feedArrPath ? product[feedArrPath] : null) || [];
				const matchingNames = [...materialsByName.keys()].filter((n) => n.startsWith(prefix)).sort();
				for (let i = 0; i < matchingNames.length; i++) {
					const matInfo = materialsByName.get(matchingNames[i]);
					const url = feedArr[i] || "";
					await this.applyRegistryMaterial(matInfo.node, matInfo.material, url, slot.fallbackColor, token, product, `interior:${i}`);
					appliedCount += 1;
				}
			} else {
				const matInfo = materialsByName.get(slot.name);
				if (!matInfo) continue;
				// 🔴 ASK-139 F-139an — a slot that names a FACE reads that face's photo when the line carries
				// no type-specific field. The vinyl registry names its jacket `jacketFrontImage` /
				// `jacketBackImage`; the feed carries a listing's photos per face (`media.front/back` →
				// `frontImage`/`backImage`, normalised for every type). MEASURED on the served boot
				// (r48 `fd05b21`): no vinyl line carries a jacket field, so every record drew its jacket
				// in the slot's fallback black — the photo the merchant took was never asked for. The
				// type-specific field still wins when it is there.
				const FACE_FIELD = { front: "frontImage", back: "backImage", spine: "spineImage" };
				const feedUrl = product ? ((slot.feedField && product[slot.feedField]) || (FACE_FIELD[slot.face] && product[FACE_FIELD[slot.face]]) || "") : "";
				await this.applyRegistryMaterial(matInfo.node, matInfo.material, feedUrl, slot.fallbackColor, token, product, slot.face || null);
				appliedCount += 1;
			}
		}
		return appliedCount;
	}

	// Apply a per-slot texture to a single material — feed URL preferred,
	// fallbackColor when feed has no image. Same shape as applyMaterial
	// but operates on a specific material reference (not the whole node).
	async applyRegistryMaterial(node, material, textureUrl, fallbackColor, token, product = null, face = null) {
		const rawUrl = String(textureUrl || "").trim();
		const transform = getComicTextureTransform(node);
		// ASK-142 R1 — the seat binds the cooked SHELF level when GCG has one (KTX2, ≤ 512 px, stays
		// compressed on the GPU); the hero level is bound on hold through `_coverBindings`.
		const placeholder = () => { material.map = rawUrl ? this._coverPlaceholderTexture() : null; material.color?.set?.(fallbackColor || "#cccccc"); material.needsUpdate = true; };
		const apply = (tex) => { material.map = tex; material.color?.set?.("#ffffff"); material.needsUpdate = true; };
		const binding = { face, rawUrl, transform, node, apply, placeholder };
		if (rawUrl) this._recordCoverBinding(product, binding);
		const url = this._lodUrl(product, rawUrl, "shelf");
		// ASK-142 R2 — a cover still on its way shows the slot's fallback colour, never the GLB's
		// authoring placeholder or another seat's picture.
		// The placeholder is a 1×1 WHITE map, not `map = null`: binding the real picture later then
		// changes no shader define, so the arrival of a cover never recompiles a program mid-walk (the
		// `GetProgramiv` stalls measured in the phone trace) — the streaming engines' default-texture rule.
		if (url && !this.textureCache.has(`${url}|${transform ? JSON.stringify(transform) : ""}`)) {
			material.map = this._coverPlaceholderTexture();
			material.color?.set?.(fallbackColor || "#cccccc");
			material.needsUpdate = true;
		}
		const tex = url ? await this.resolveTexture(url, token, transform, node) : null;
		if (binding.heroUrl) return;   // picked up while its shelf was on the way: the hero owns the face
		// No dispose: a texture belongs to `textureCache` and is handed to every seat showing that
		// image — disposing it here freed the GPU copy under the other seats that were still drawing it.
		if (tex) {
			material.map = tex;
			material.color?.set?.("#ffffff");   // let texture show through
		} else {
			// A URL that did not load keeps the white placeholder (same pixels as no map, same program).
			material.map = url ? this._coverPlaceholderTexture() : null;
			material.color?.set?.(fallbackColor || "#cccccc");
		}
		material.needsUpdate = true;
	}

	async applySurfaceMaterial(node, slot = {}, productTextureUrl = "", fallbackColor = "#ffffff", token, product = null, face = null) {
		if (!node || slot.source === "authored") {
			return;
		}

		const textureUrl = slot.source === "product"
			? productTextureUrl || slot.textureUrl || ""
			: slot.textureUrl || "";
		const transform = getComicTextureTransform(node);
		const color = slot.color || fallbackColor;
		if (slot.source === "product" && textureUrl) {
			this._recordCoverBinding(product, {
				face, rawUrl: textureUrl, transform, node,
				apply: (tex) => { if (node.material) { node.material.map = tex; node.material.needsUpdate = true; } },
				placeholder: () => { if (node.material) { node.material.map = null; node.material.needsUpdate = true; } },
			});
		}
		const url = slot.source === "product" ? this._lodUrl(product, textureUrl, "shelf") : textureUrl;
		applyMaterial(node, await this.resolveTexture(url, token, transform, node), color);
	}

	// ─── ASK-142 R1 — cooked LODs for product pictures (§8.3 1–2) ──────────────────────────────
	// GCG cooks every face photo at upload into `media.derived.<face> = { shelf, hero, w, h }` (KTX2,
	// content-addressed, immutable). A seat draws `shelf` (≤ 512 px — what a cover on a shelf can
	// resolve on a phone screen); a HELD item draws `hero` (≤ 2048 px), swapped in when it is picked
	// up and released when it is put back — the screen-coverage switch `MSFT_screencoverage` encodes,
	// applied per texture because a 14-triangle comic has no geometry LOD to speak of. A line with no
	// cooked level (an external stock cover) keeps its raw URL: never a blank face.

	/** The URL to draw for `rawUrl` at `lod` ("shelf" | "hero"): the cooked level when the line has one
	 *  for the face that raw URL is, else the raw URL. Matches by URL, so every binder gets the swap. */
	_lodUrl(product, rawUrl, lod) {
		const d = product?.derived;
		if (!d || !rawUrl) return rawUrl;
		for (const [face, field] of [["front", "frontImage"], ["back", "backImage"], ["spine", "spineImage"], ["pages", "pagesImage"]]) {
			if (product[field] === rawUrl && d[face]?.[lod]) return d[face][lod];
		}
		const i = Array.isArray(product.interiorImages) ? product.interiorImages.indexOf(rawUrl) : -1;
		if (i >= 0 && Array.isArray(d.interior) && d.interior[i]?.[lod]) return d.interior[i][lod];
		return rawUrl;
	}

	_recordCoverBinding(product, binding) {
		const id = product?.id;
		if (id == null) return;
		binding.token = this.currentToken;
		binding.product = product;
		let list = this._coverBindings.get(id);
		if (!list) this._coverBindings.set(id, (list = []));
		list.push(binding);
	}

	/** Hold → every face of that product draws its hero level; release → back to shelf (or to the
	 *  spine-out placeholder) and the hero's GPU copy is freed. */
	_onHoldChange(product) {
		const nextId = product?.id ?? null;
		if (nextId === this._heldCoverId) return;
		const prevId = this._heldCoverId;
		this._heldCoverId = nextId;
		this._heldCoverProduct = product || null;
		this.cue(nextId == null ? "put-back" : "pick-up", { role: "self", pos: this._handPos() });   // ASK-174: paper from the hand (the room hears it via h)
		if (prevId != null) this._releaseHeroCovers(prevId);
		if (nextId == null) return;
		const token = this.currentToken;
		for (const b of (this._coverBindings.get(nextId) || [])) {
			if (b.token !== token) continue;
			const heroUrl = this._lodUrl(product, b.rawUrl, "hero");
			if (heroUrl === b.rawUrl) continue;   // no cooked hero: the face keeps what it already draws
			this.resolveTexture(heroUrl, token, b.transform, null).then((tex) => {
				if (!tex || this._heldCoverId !== nextId || token !== this.currentToken) return;
				b.apply(tex);
				b.heroUrl = heroUrl;
			}, () => {});
		}
	}

	_releaseHeroCovers(productId) {
		const token = this.currentToken;
		const freed = new Set();
		for (const b of (this._coverBindings.get(productId) || [])) {
			if (!b.heroUrl) continue;
			const hero = b.heroUrl; b.heroUrl = null;
			if (b.token !== token) continue;
			const shelf = this._lodUrl(b.product, b.rawUrl, "shelf");
			this.resolveTexture(shelf, token, b.transform, b.node).then((tex) => { if (tex && b.heroUrl == null) b.apply(tex); }, () => {});
			freed.add(hero);
		}
		// Free every cached copy of the released hero (base + per-transform clones).
		for (const hero of freed) {
			for (const [k, tex] of [...this.textureCache]) {
				if (k.startsWith(`${hero}|`)) { this.textureCache.delete(k); try { tex.dispose?.(); } catch { /* already gone */ } }
			}
		}
	}

	/** Resolves once every cover queued so far has bound (or failed) — the wait for captures/probes
	 *  that need the finished shelf. The customer never waits on it (ASK-142 R2). */
	coversSettled() {
		return this._coverStream.settled();
	}

	/** 🔴 ASK-142 R2b — EVERY FIXTURE AND PRODUCT MESH THIS SHOP NEEDS IS REQUESTED THE MOMENT ITS
	 *  ANCHORS ARE KNOWN, all at once, beside the wall materials. Measured on the plane (§9.3): the
	 *  compose loop asked for them one at a time — manifest → GLB → next kind → … — and only after the
	 *  LAST material finished: thirteen serial round-trips between the materials and the loader. The
	 *  loaders already cache by URL (a promise per asset), so this only STARTS what compose will ask
	 *  for; the loop below then finds each one in flight or done. Fire-and-forget: a failure here is
	 *  the same failure compose reports by name when it gets there. */
	_prefetchComposeAssets({ fixtureCatalogue, products }) {
		try {
			const kinds = [...(this._shopAnchors?.registered?.keys?.() || [])];
			for (const kind of kinds) {
				void loadFixtureManifest({ catalogue: fixtureCatalogue, kind }).catch(() => null);
				void loadFixtureCatalogue({ catalogue: fixtureCatalogue, kind, gltfLoader: this.gltfLoader }).catch(() => null);
			}
			const seen = new Set();
			for (const product of (products || [])) {
				const rs = resolveProductRenderSource({ catalogue: this._productCatalogue, fixtureCatalogue, productTypes: this._productTypes, product });
				if (rs.kind !== "catalogue") continue;
				const k = `${rs.source}:${rs.key}`;
				if (seen.has(k)) continue;
				seen.add(k);
				void loadProductCatalogue({ catalogue: rs.source === "fixture" ? fixtureCatalogue : this._productCatalogue, productType: rs.key, gltfLoader: this.gltfLoader }).catch(() => null);
			}
		} catch (err) {
			console.warn("[compose] prefetch skipped:", err?.message || err);
		}
	}

	/** Is this node part of what the renderer draws (every ancestor visible, rooted in the scene)? */
	_nodeDrawn(node) {
		for (let o = node; o; o = o.parent) {
			if (o.visible === false) return false;
			if (o === this.scene) return true;
		}
		return false;
	}

	/** Streaming priority for a node: squared distance to the camera, pushed back when behind it. */
	_coverPriority(node) {
		if (!node?.getWorldPosition || !this.camera) return null;
		const v = new THREE.Vector3(), f = new THREE.Vector3();
		return () => {
			node.getWorldPosition(v);
			const d2 = v.distanceToSquared(this.camera.position);
			this.camera.getWorldDirection(f);
			return v.sub(this.camera.position).dot(f) < 0 ? d2 + 1e4 : d2;
		};
	}

	async resolveTexture(url, token, transform = null, node = null) {
		const key = String(url || "").trim();
		const transformKey = transform ? JSON.stringify(transform) : "";
		const cacheKey = `${key}|${transformKey}`;

		if (!key) {
			return null;
		}

		if (this.textureCache.has(cacheKey)) {
			return this.textureCache.get(cacheKey);
		}

		const baseKey = `${key}|`;
		let texture = this.textureCache.get(baseKey) || null;

		if (!texture) {
			try {
				// ASK-142 §8.3 2 — LOAD WHAT IS DRAWN. A cover starts when ANY seat asking for it is in the
				// drawn scene graph: a closed box's hidden entries, a seat not yet parented, wait; the lid
				// opening makes them drawn and the next `poke()` starts them, nearest first.
				let wanters = this._textureWanters.get(baseKey);
				if (!wanters) this._textureWanters.set(baseKey, (wanters = new Set()));
				wanters.add(node || ALWAYS_WANTED);
				let inflight = this._textureInflight.get(baseKey);
				if (!inflight) {
					const ready = () => { for (const n of wanters) if (n === ALWAYS_WANTED || this._nodeDrawn(n)) return true; return false; };
					inflight = this._coverStream.load(key, { priority: this._coverPriority(node), cancelled: () => token !== this.currentToken, ready });
					this._textureInflight.set(baseKey, inflight);
					inflight.then((t) => this.textureCache.set(baseKey, t), () => {}).finally(() => { this._textureInflight.delete(baseKey); this._textureWanters.delete(baseKey); });
				}
				texture = await inflight;
			} catch (err) {
				if (token === this.currentToken) console.error("[kapowx] texture load failed:", key, err);
				return null;
			}
		}

		if (token !== this.currentToken) {
			return null;
		}

		const nextTexture = cloneTextureWithTransform(texture, transform);
		this.textureCache.set(cacheKey, nextTexture);
		return nextTexture;
	}

	/** The scene's physics world (DECISION-019), created on the first call — cannon-es loads then and not before. */
	physics() {
		const g = this.sceneGravity;
		return this._physicsLoad ||= import("./_physics-world.js").then((m) => {
			if (this.sceneGravity !== g) return null;   // the scene changed while it loaded
			this._physicsStep = m.stepPhysicsWorld;
			return (this.physicsWorld = m.createPhysicsWorld(g.isDefault ? null : g));
		});
	}

	updateAnimations(dt) {
		// Physics step (DECISION-019) — only once a system has asked for the world (`physics()`).
		if (this.physicsWorld) this._physicsStep(this.physicsWorld, dt);

		this._tickBrowsePose(dt);   // ASK-142 F-142e: the lid-open camera blend owns the camera while it runs
		if (this._browseVerb) this._bv?.tickVerb(this, dt);   // ASK-154 seam 2: a pull-out / pick-up in play
		this._tickLook(dt);   // ASK-142 R7: the heading first, so the walk below steps along the eased view
		this._applyWalkMovement(dt);
		this._applyHeadBob(dt);
		this._tickFixtureReturns(dt);   // eased invalid-drop / Esc snap-back glide (Edit)
		this._updateDust(dt);
		this._updateFlicker();
		this._updateAmbience(dt);

		// companions: hook ──────────────────────────────────────────────
		// Bailey + future pets. Each companion owns its own mixer, follow
		// state, and per-frame logic. Iterating a copy of the array so a
		// companion can dispose itself mid-tick without breaking the loop.
		for (const c of this.companions.slice()) c.tick(dt);
		// companions: end ───────────────────────────────────────────────

		// Wall comic / vinyl / book / pile / spinner all animate via the shared
		// _animateHero helper. Per-product callbacks handle teardown when the
		// return animation completes (boxState reset, callbacks, UI refresh).
		if (this.wallComicHeroEntry) {
			const h = this.wallComicHeroEntry;
			this._animateHero(h, dt, {
				mesh: h.entry.mesh,
				returnPos: h.entry.worldPos,
				onCompleteReturn: () => {
					this.boxState = "none";
					this.activeWallComic    = null;
					this.wallComicHeroEntry = null;
					this.onComicSelect?.(null);
					this.updateBrowseControls();
				},
			});
		}

		if (this.vinylHeroEntry) {
			const h = this.vinylHeroEntry;
			this._animateHero(h, dt, {
				mesh: h.entry.mesh,
				returnPos:  h.entry.worldPos,
				returnQuat: h.entry.baseQuat,    // vinyl uses baked baseQuat for return target
				onCompleteReturn: () => {
					this.boxState = "none";
					this.vinylHeroEntry = null;
					this.onComicSelect?.(null);
					this.updateBrowseControls();
				},
			});
		}

		// Record-rack browse-focus pose animation. Each rack sleeve has a
		// rest pose (worldPos / baseQuat) and a focus pose (focusPos /
		// focusQuat — pivoted forward from its bottom edge). Every sleeve at
		// index ≤ browseIdx stays in focus pose, fanning the front of the
		// stack open as the player flips through (real-bin gesture). When
		// the rack disengages, all sleeves slerp back to rest. Skip any
		// sleeve currently in hero — _animateHero owns its transform.
		const heroSleeveMesh = this.vinylHeroEntry?.entry.mesh;
		const focusedSet = new Set();
		if (this.activeVinylBin) {
			for (let i = 0; i <= this.activeVinylBin.browseIdx; i++) {
				const m = this.activeVinylBin.sleeves[i]?.mesh;
				if (m) focusedSet.add(m);
			}
		}
		const lerpAlpha = Math.min(1, dt * 10);
		for (const entry of this.vinylSleeves) {
			if (!entry.focusQuat) continue;        // not a vinyl-bin sleeve (vinyl-wall-shelf has no focus pose)
			if (entry.mesh === heroSleeveMesh) continue;
			const isFocused = focusedSet.has(entry.mesh);
			const tgtPos  = isFocused ? entry.focusPos  : entry.worldPos;
			const tgtQuat = isFocused ? entry.focusQuat : entry.baseQuat;
			entry.mesh.position.lerp(tgtPos, lerpAlpha);
			entry.mesh.quaternion.slerp(tgtQuat, lerpAlpha);
		}

		// Stack-table cycle animation. Three phases so the wrap sleeve never
		// crosses through the stack diagonally:
		//   Phase 1 [0 → 0.30] — wrap sleeve lifts UP and slides SIDEWAYS, clearing the top.
		//   Phase 2 [0.30 → 0.70] — wrap sleeve descends vertically at the side; meanwhile
		//                            every other sleeve slides up one slot.
		//   Phase 3 [0.70 → 1.00] — wrap sleeve slides HORIZONTALLY back into slot 0.
		// After 600ms cycleCount commits and all sleeves snap to their new rest positions.
		const _slotPos = (stack, slot) => stack.anchorPos.clone()
			.add(new THREE.Vector3(0, slot * (0.0032 + 0.0006) + 0.001, 0));
		const PHASE_1_END = 0.30;
		const PHASE_2_END = 0.70;
		const CYCLE_DURATION = 0.6;
		const SIDE_OFFSET = 0.30;     // 30cm sideways to clear the stack
		const LIFT_OFFSET = 0.10;     // 10cm vertical lift above the top of the stack
		const _tmpVec = new THREE.Vector3();
		for (const stack of (this.stackTables || [])) {
			if (!stack.cycleAnim) continue;
			stack.cycleAnim.progress = Math.min(1, stack.cycleAnim.progress + dt / CYCLE_DURATION);
			const t = stack.cycleAnim.progress;
			const N = stack.sleeves.length;
			const wrapIdx = stack.cycleAnim.wrapSleeveIdx;
			const sideDir = _tmpVec.set(0, 1, 0).cross(stack.faceDir).normalize();
			const topPos = _slotPos(stack, N - 1);
			const bottomPos = _slotPos(stack, 0);

			for (let i = 0; i < N; i++) {
				const sleeve = stack.sleeves[i];
				if (i === wrapIdx) {
					if (t < PHASE_1_END) {
						// Phase 1: lift up + slide out to the side simultaneously
						const tt = t / PHASE_1_END;
						sleeve.mesh.position.copy(topPos)
							.addScaledVector(new THREE.Vector3(0, 1, 0), LIFT_OFFSET * tt)
							.addScaledVector(sideDir, SIDE_OFFSET * tt);
					} else if (t < PHASE_2_END) {
						// Phase 2: stay at the side, descend from top+lift down to bottom level
						const tt = (t - PHASE_1_END) / (PHASE_2_END - PHASE_1_END);
						const liftedTop = topPos.clone()
							.addScaledVector(new THREE.Vector3(0, 1, 0), LIFT_OFFSET)
							.addScaledVector(sideDir, SIDE_OFFSET);
						const sideBottom = bottomPos.clone().addScaledVector(sideDir, SIDE_OFFSET);
						sleeve.mesh.position.lerpVectors(liftedTop, sideBottom, tt);
					} else {
						// Phase 3: slide horizontally from sideways-bottom into slot 0
						const tt = (t - PHASE_2_END) / (1 - PHASE_2_END);
						const sideBottom = bottomPos.clone().addScaledVector(sideDir, SIDE_OFFSET);
						sleeve.mesh.position.lerpVectors(sideBottom, bottomPos, tt);
					}
				} else {
					// Other sleeves: hold during phase 1, slide up one slot during
					// phase 2 (timed to clear slot 0 just as the wrap sleeve reaches
					// the side-bottom and starts sliding in).
					const oldSlot = (i + stack.cycleCount) % N;
					const newSlot = (oldSlot + 1) % N;
					if (t < PHASE_1_END) {
						sleeve.mesh.position.copy(_slotPos(stack, oldSlot));
					} else if (t < PHASE_2_END) {
						const tt = (t - PHASE_1_END) / (PHASE_2_END - PHASE_1_END);
						sleeve.mesh.position.lerpVectors(
							_slotPos(stack, oldSlot),
							_slotPos(stack, newSlot),
							tt,
						);
					} else {
						sleeve.mesh.position.copy(_slotPos(stack, newSlot));
					}
				}
			}

			if (stack.cycleAnim.progress >= 1) {
				stack.cycleCount = (stack.cycleCount + 1) % N;
				stack.cycleAnim = null;
				// Snap to final positions to clean up float-drift from the lerp
				for (let i = 0; i < N; i++) {
					const finalSlot = (i + stack.cycleCount) % N;
					stack.sleeves[i].mesh.position.copy(_slotPos(stack, finalSlot));
				}
			}
		}

		// Featured-comic rotation. Each registered featured comic spins around
		// its world Y axis at a variant-dependent rate. Rotation pauses if the
		// comic is currently being inspected (it's part of stage.wallComics so
		// hero/return animations will move it; we don't want to fight them).
		// The acrylic stand (pedestal variant only) lives as a scene-level
		// mesh and gets the same quaternion applied here so it spins in sync
		// at rest. While the comic is in inspect-hero, both updates are
		// skipped — stand stays frozen at the display position waiting for
		// the comic to return.
		const _featuredYAxis = new THREE.Vector3(0, 1, 0);
		for (const fc of (this.featuredComics || [])) {
			// Skip if this featured comic is currently the inspect-hero target.
			if (this.wallComicHeroEntry?.entry?.mesh === fc.mesh) continue;
			fc.rotationOffset = (fc.rotationOffset + dt * fc.rotationRate) % (2 * Math.PI);
			const yRot = new THREE.Quaternion().setFromAxisAngle(_featuredYAxis, fc.rotationOffset);
			fc.mesh.quaternion.copy(yRot).multiply(fc.baseQuat);
			fc.mesh.position.copy(fc.restWorldPos);
			if (fc.stand) {
				fc.stand.quaternion.copy(yRot).multiply(fc.baseQuat);
				fc.stand.position.copy(fc.standRestPos);
			}
		}

		// Pile comic arc animation (browse forward/back)
		if (this.pileArcAnim) {
			const arc = this.pileArcAnim;
			arc.progress = Math.min(1, arc.progress + dt / 0.5);
			const t = easeInOutQuad(arc.progress);
			const entry = this.pileComics[arc.comicIdx];
			entry.mesh.position.x = arc.fromPos.x + (arc.toPos.x - arc.fromPos.x) * t;
			entry.mesh.position.z = arc.fromPos.z + (arc.toPos.z - arc.fromPos.z) * t;
			entry.mesh.position.y = arc.fromPos.y + (arc.toPos.y - arc.fromPos.y) * t
				+ 0.26 * Math.sin(t * Math.PI);
			if (arc.progress >= 1) {
				entry.mesh.position.copy(arc.toPos);
				this.pileArcAnim = null;
			}
		}

		// Pile hero — two-phase (rotate-then-translate forward, reverse on return).
		if (this.pileHeroEntry) {
			const h = this.pileHeroEntry;
			const entry = this.pileComics[h.comicIdx];
			this._animateHero(h, dt, {
				mesh: entry.mesh,
				onCompleteReturn: () => {
					this.pileState = "none";
					this.pileHeroEntry = null;
					this.onComicSelect?.(null);
				},
			});
		}

		// Book slide animation — lerp each book toward the player along its
		// faceDir. Browsed (keyboard) → 12 cm, hover-only → 6 cm, idle → 0.
		// faceDir is per-entry (set by the anchor-driven builder); the legacy
		// south-wall non-anchor builder defaults to world -Z to preserve its
		// original behaviour.
		const _tmpSlideDir = new THREE.Vector3();
		for (let bi = 0; bi < this.bookEntries.length; bi++) {
			const entry = this.bookEntries[bi];
			if (this.bookHeroEntry && this.bookHeroEntry.entry === entry) continue;
			const isBrowsed = this.bookBrowseIndex === bi && this.bookState === "none";
			const isHovered = this.hoveredBook === entry && this.bookState === "none";
			const targetZ = isBrowsed ? 0.12 : (isHovered ? 0.06 : 0);
			if (Math.abs(entry.slideZ - targetZ) > 0.0005) {
				entry.slideZ += (targetZ - entry.slideZ) * Math.min(1, dt * 10);
				if (Math.abs(entry.slideZ - targetZ) < 0.0005) entry.slideZ = targetZ;
				_tmpSlideDir.copy(entry.faceDir || new THREE.Vector3(0, 0, -1));
				entry.mesh.position.copy(entry.basePosition).addScaledVector(_tmpSlideDir, entry.slideZ);
			}
		}

		// Book hero — same two-phase shape as pile, plus slideZ reset on return.
		if (this.bookHeroEntry) {
			const h = this.bookHeroEntry;
			this._animateHero(h, dt, {
				mesh: h.entry.mesh,
				onCompleteReturn: () => {
					h.entry.slideZ = 0;
					this.bookState = "none";
					this.bookHeroEntry = null;
				},
			});
		}

		// Spinner rack — momentum rotation + hero inspect animation
		if (this.spinnerRack) {
			const rack = this.spinnerRack;

			// Apply velocity and exponential friction
			if (rack.velocity !== 0) {
				rack.rotation += rack.velocity * dt;
				rack.spinRoot.rotation.y = rack.rotation;
				rack.velocity *= Math.exp(-rack.friction * dt);
				if (Math.abs(rack.velocity) < 0.005) rack.velocity = 0;
			}

			// Hero comic lift / return — single-phase, rack frozen during this.
			// Slightly faster (0.45s) than other heroes; on return, comic re-parents
			// from world space back into the spinRoot using its baked slot transform.
			if (rack.heroEntry) {
				const h = rack.heroEntry;
				this._animateHero(h, dt, {
					mesh: h.comicEntry.mesh,
					duration: 0.45,
					onCompleteReturn: () => {
						const m = h.comicEntry.mesh;
						this.scene.remove(m);
						rack.spinRoot.add(m);
						m.position.copy(h.comicEntry.slotLocalPos);
						m.quaternion.copy(h.comicEntry.slotQuat);
						rack.state     = 'none';
						rack.heroEntry = null;
					},
				});
			}
		}

		// Comic flip animation
		if (this.comicFlipAnim) {
			const fa = this.comicFlipAnim;
			fa.progress = Math.min(1, fa.progress + dt / 0.35);
			fa.mesh.quaternion.slerpQuaternions(fa.fromQuat, fa.toQuat, easeInOutQuad(fa.progress));
			if (fa.progress >= 1) {
				fa.mesh.quaternion.copy(fa.toQuat);
				this.comicFlipAnim = null;
			}
		}

		// Open-book page flip — rotate the flip wing around the spine.
		// forward: 0 → +π   backward: +π → 0   (see advanceInteriorPages)
		if (this.pageFlipAnim && this.openBookFlipWing) {
			const fp = this.pageFlipAnim;
			fp.progress = Math.min(1, fp.progress + dt / (fp.dur || 0.45));
			// Asymmetric ease — quick lift, gentle settle (a real page falls with a
			// weighted landing); avoids the symmetric easeInOutQuad "mid-flip
			// dead-stop" tell (M7 page-flip premium polish, design-research brief).
			const p = fp.progress;
			const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2.4) / 2;
			this.openBookFlipWing.rotation.y = fp.forward ? eased * Math.PI : (1 - eased) * Math.PI;
			// Dynamic fold shadow — the turning page darkens through the fold
			// (deepest edge-on at mid-flip), reading as depth vs a flat rotate.
			const shade = 1 - 0.5 * Math.sin(eased * Math.PI);
			if (this.openBookFlipFrontMat && this._flipBaseFront) this.openBookFlipFrontMat.color.copy(this._flipBaseFront).multiplyScalar(shade);
			if (this.openBookFlipBackMat && this._flipBaseBack)   this.openBookFlipBackMat.color.copy(this._flipBaseBack).multiplyScalar(shade);
			// Vertex page-curl — bow the free (outer) edge over a cylinder, peaking
			// edge-on at mid-flip, flat at both ends (a real page arcs as it turns).
			this._deformPageCurl(Math.sin(eased * Math.PI));
			if (fp.progress >= 1) this._finalizePageFlip();
		}

		// Lid open/close
		const lidDiff = this.lidTargetAngle - this.lidCurrentAngle;
		if (Math.abs(lidDiff) > 0.0005) {
			this.lidCurrentAngle += lidDiff * Math.min(1, dt * 6);
			this.applyLidAngle(this.lidCurrentAngle);
			if (Math.abs(this.lidTargetAngle - this.lidCurrentAngle) < 0.0005) {
				this.lidCurrentAngle = this.lidTargetAngle;
				this.applyLidAngle(this.lidCurrentAngle);
				if (this.boxState === "opening") {
					this.boxState = "open";
					// `openLid` already filtered the active box's hit zone out of
					// lidMeshes. Do NOT wipe lidMeshes here — that would clobber
					// every OTHER box's hit zone too, and on close only this
					// box's hit zone gets restored. Net effect: opening + closing
					// one longbox erases all sibling boxes from raycast forever.
					this.updateBrowseControls();
				} else if (this.boxState === "closing") {
					this.boxState = "closed";
					if (this.lidHitZone && !this.lidMeshes.includes(this.lidHitZone)) {
						this.lidMeshes.push(this.lidHitZone);
					}
					this.updateBrowseControls();
				}
			}
		}

		// Comic lean
		for (const entry of this.comicEntries) {
			if (entry.isHero || entry.returning) continue;
			const diff = entry.targetLeanAngle - entry.leanAngle;
			if (Math.abs(diff) > 0.05) {
				entry.leanAngle += diff * Math.min(1, dt * 9);
				this.applyComicLean(entry);
			}
		}

		// Hero lift arc — animate both position and orientation toward the
		// camera-relative hero target. Quaternion is lerped alongside position
		// so the comic doesn't snap-rotate at the start of the lift.
		for (const entry of this.comicEntries) {
			if (!entry.isHero || entry.heroProgress >= 1) continue;
			entry.heroProgress = Math.min(1, entry.heroProgress + dt / 0.45);
			const t = easeInOutQuad(entry.heroProgress);
			const target = entry.heroPos || this.heroExtract;
			entry.root.position.lerpVectors(entry.heroFrom, target, t);
			entry.root.position.y += Math.sin(t * Math.PI) * 0.1;
			if (entry.heroQuat && entry.heroQuatFrom) {
				entry.root.quaternion.slerpQuaternions(entry.heroQuatFrom, entry.heroQuat, t);
			}
		}

		// Hero return arc — back to base position + original orientation. The
		// quat-from for the return is whatever the comic was rotated to when
		// putHeroBack fired (typically heroQuat), captured into returnQuatFrom.
		for (const entry of this.comicEntries) {
			if (!entry.returning || entry.returnProgress >= 1) continue;
			entry.returnProgress = Math.min(1, entry.returnProgress + dt / 0.4);
			const t = easeInOutQuad(entry.returnProgress);
			entry.root.position.lerpVectors(entry.returnFrom, entry.basePosition, t);
			entry.root.position.y += Math.sin(t * Math.PI) * 0.06;
			if (entry.returnQuatFrom && entry.baseQuaternion) {
				entry.root.quaternion.slerpQuaternions(entry.returnQuatFrom, entry.baseQuaternion, t);
			}
			if (entry.returnProgress >= 1) {
				entry.returning = false;
				entry.root.position.copy(entry.basePosition);
				this.applyComicLean(entry);
			}
		}

		// Camera
		if (this.cameraAnim.active) {
			this.cameraAnim.progress = Math.min(1, this.cameraAnim.progress + dt / this.cameraAnim.duration);
			const t = easeInOutQuad(this.cameraAnim.progress);
			this.camera.position.lerpVectors(this.cameraAnim.fromPos, this.cameraAnim.toPos, t);
			this.controls.target.lerpVectors(this.cameraAnim.fromTarget, this.cameraAnim.toTarget, t);
			if (this.cameraAnim.progress >= 1) {
				this.cameraAnim.active = false;
				this.controls.update();
				this._logCameraPos(`anim-end:${this.boxState}`);
			}
		}
	}

	applyComicLean(entry) {
		// Positive leanAngle = lean back (top away from camera / face direction).
		// Axis = normalize(-Y × faceDir) ensures a positive angle always tilts the top
		// away from the face direction regardless of which world axis comics face along.
		const angleRad = entry.leanAngle * DEG;
		const faceDir = new THREE.Vector3(0, 0, 1).applyQuaternion(entry.baseQuaternion);
		const leanAxis = new THREE.Vector3(0, -1, 0).cross(faceDir).normalize();
		const leanQ = new THREE.Quaternion().setFromAxisAngle(leanAxis, angleRad);
		entry.root.quaternion.multiplyQuaternions(leanQ, entry.baseQuaternion);

		// Pivot correction: keep the comic's physical base pinned at the slot anchor.
		// Without this the root (at the comic's centre) rotates around the midpoint.
		// Formula: rootPos = basePos + pivotOffset − leanQ.rotate(pivotOffset)
		if (entry.bottomOffset !== undefined) {
			const pivotOffset = new THREE.Vector3(0, entry.bottomOffset, 0);
			const rotatedOffset = pivotOffset.clone().applyQuaternion(leanQ);
			entry.root.position.copy(entry.basePosition).add(pivotOffset).sub(rotatedOffset);
		}
	}

	applyLidAngle(angle) {
		if (this.lidPivotNode) {
			// Pivot Group's local +X is aligned to the hinge axis; rotating
			// around it lifts the lid's free edge away from the hinge.
			const rotQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -angle);
			this.lidPivotNode.quaternion.copy(this.lidPivotBaseQuat).multiply(rotQuat);
		} else if (this.lidNode) {
			// Legacy path for lids without a hinge pivot (older builds).
			const rotQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -angle);
			this.lidNode.quaternion.copy(this.lidBaseQuat).multiply(rotQuat);
		} else if (this.lidMeshNode) {
			// Fallback for GLBs without a lid_root pivot: translate upward
			const t = Math.min(1, angle / this._lidOpenAngle());
			this.lidMeshNode.position.y = this.lidMeshBaseY + t * 0.55;
		}
	}

	_applyWalkMovement(dt) {
		// ASK-142 R7 — velocity ramps (≈ 150 ms, `_feel.js` stepWalkVelocity) instead of 0 → full speed on
		// the first frame. The velocity lives in the walker's own frame (strafe · forward · up), so a turn
		// redirects it at once (Fortnite's feel) and only its SPEED is ramped. Anything that takes the
		// walker out of the walk (Edit, a hold, a single comic) stops it dead — a hold is not a skid.
		const wv = (this._walkVel = this._walkVel || { x: 0, y: 0, z: 0 });
		// ASK-142 F-142e: and a browse pose — the camera is the bin's until the box closes (RE4's item box).
		if (this._cameraAuthoring || this._isAnyHeroActive() || this.boxState === "single" || this._browse) {   // ASK-087 slice-c: Edit mode orbits, doesn't walk
			wv.x = wv.y = wv.z = 0;
			if (this._autoWalk) this._endAutoWalk("interrupted");
			return;
		}

		const keys = this._heldKeys;
		// ASK-139 F-139c — the phone stick is an ANALOG twin of WASD (|v| ≤ 1, deadzone applied at source).
		const stick = this._touchMove;
		const stickOn = !!stick && (stick.x !== 0 || stick.y !== 0);
		// ASK-172 §5.6 — "take me there": a walk the shopper asked for (a party member's ring / chevron). Any walk input of
		// their own (a key, the stick) cancels it at once — the shopper's own hand always wins.
		if (this._autoWalk) {
			const own = stickOn || ["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].some((k) => keys.has(k));
			if (own) this._endAutoWalk("cancelled");
			else { this._stepAutoWalk(dt); return; }
		}

		const fast = keys.has("ShiftLeft") || keys.has("ShiftRight");
		// Hub is large and meant for traversal — bump base + sprint over the
		// shop's slower exploration pace.
		const baseSpeed = (this.sceneKind === "hub") ? 2.6 : this._walkSpeed;
		const sprintMul = (this.sceneKind === "hub") ? 2.8 : 2.2;
		const top = baseSpeed * (fast ? sprintMul : 1);
		const want = (this._walkWant = this._walkWant || { x: 0, y: 0, z: 0 });
		want.x = ((keys.has("KeyD") ? 1 : 0) - (keys.has("KeyA") ? 1 : 0) + (stickOn ? stick.x : 0)) * top;
		want.y = ((keys.has("KeyW") ? 1 : 0) - (keys.has("KeyS") ? 1 : 0) + (stickOn ? stick.y : 0)) * top;
		// Vertical movement disabled in hub (player walks the street, can't fly)
		want.z = this.sceneKind === "hub" ? 0 : ((keys.has("Space") ? 1 : 0) - (keys.has("KeyQ") ? 1 : 0)) * top;
		stepWalkVelocity(wv, want, dt, baseSpeed);
		if (!wv.x && !wv.y && !wv.z) return;

		// Forward vector projected onto XZ (no vertical tilt in walk)
		const forward = new THREE.Vector3();
		this.camera.getWorldDirection(forward);
		forward.y = 0;
		forward.normalize();

		const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0));

		const delta = new THREE.Vector3()
			.addScaledVector(forward, wv.y * dt)
			.addScaledVector(right, wv.x * dt);
		delta.y += wv.z * dt;

		// Wall collision in shops — slides player along walls instead of clipping. The v2 hub clamps to a
		// rectangle; a hub-v3 street collides against its own static mesh (ASK-146 §17.10, `_hub-v3-collider.js`).
		const hw = this._hubLife?.walk;
		if (hw) hw.collide(delta);
		else if (this.sceneKind !== "hub") this._collideHorizontalDelta(delta);

		this.camera.position.add(delta);
		this.controls.target.add(delta);
		if (hw) { /* the street's walk already collided + followed the floor */ }
		else if (this.sceneKind === "hub") this._clampCameraToHub();
		else this._clampCameraToRoom();
		this.controls.update();
	}

	// ── COLLIDE-AND-SLIDE · the walls are a closed boundary ─────────────────────────────────────
	//
	// 🔴 ASK-129 F-101g. Vanessa: *"Bailey was able to walk half way through the wall."* Measured on
	// the deployed plane, 36 headings walked from the shop's own SPAWN with the real Play loop
	// (collide → move → clamp) and a down-ray at every step asking "is there still a floor under
	// me?": **10 of 36 headings left `test-store` (platform-shell)** and **30 of 36 left `test-shop`
	// (the composed 3-module shell the walk gate has been green on all along)**. It is not a
	// platform-shell defect and it never was — it is this function.
	//
	// THE MECHANISM, traced step by step at heading 30° on `test-store` (shopfront: a door leaf at
	// x −0.45…0.45 / z 1.43…1.47 meeting a mullion at x 0.45…0.8 / z 1.40…1.50, i.e. a re-entrant
	// jamb — every space-first shell is full of them):
	//
	//     #17 ray sees Shell_Door_Front   → clip the z component → step (+0.02,  0     )
	//     #18 ray sees S_R_Mullion (west) → clip the x component → step ( 0    , +0.0346)
	//     #19 ray sees Shell_Door_Front   → clip the z component → step (+0.02,  0     )
	//     …ratchet…                        #28 z=1.482 — PAST the door plane; #29 ray sees nothing.
	//
	// Each step was legal against the one surface it was resolved against and illegal against the
	// one it was not. The old code resolved the FIRST hit and `return`ed: one plane per frame, and
	// the slid remainder was never re-tested. Two alternating single-plane slides sum to a move
	// straight through the corner between them. A gate that walks the three cardinal aisles down the
	// middle cannot see this, which is why it shipped green.
	//
	// THE FIX is the one every shipped FPS character controller uses — Quake's `PM_SlideMove` /
	// Source's `ClipVelocity`, Unity `CharacterController.Move`, Godot `move_and_slide(max_slides)`:
	//
	//   1. **Iterate.** Up to MAX_PASSES collide-and-slide passes per frame, each cast from where the
	//      previous one left the body, so the slid remainder is itself collided.
	//   2. **Remember every plane hit this frame** and clip against ALL of them. If a slide along
	//      plane A would move into plane B, travel the CREASE (A × B) instead; if a third plane still
	//      refuses it, stop dead. This is what kills the ratchet — #18's slide is re-tested against
	//      #17's door and comes out zero.
	//   3. **Depenetrate.** The body is a cylinder, not a point: after the move, eight short rays
	//      push it back out of any wall it is resting inside. Without this an oblique approach can
	//      creep arbitrarily close (the backoff `hit.distance − radius` is measured ALONG THE RAY, so
	//      a grazing approach leaves `radius·cosθ` of real clearance), and "arbitrarily close" is the
	//      state the ratchet needs.
	//
	// Chest height (`cam.y − 1.0`) is unchanged and deliberate: it catches railings and parapets the
	// player should not cross, while staying above stair risers the step-climb handles.
	//
	// Horizontal only, as the name says — `delta.y` (Space/KeyQ fly) is carried through untouched.
	// The old code included y in the ray direction AND let a tilted wall's normal bleed a y component
	// back into the delta; both are gone.
	_collideHorizontalDelta(delta) {
		const RADIUS = 0.18;     // player half-width — tight, so doorways aren't squeezed
		const MAX_PASSES = 4;    // Quake ships 4; a 5th has never changed a frame's outcome
		const EPS = 1e-6;

		const dy = delta.y;
		const remaining = new THREE.Vector3(delta.x, 0, delta.z);
		if (remaining.lengthSq() < 1e-8) return;

		this._wallRaycaster = this._wallRaycaster || new THREE.Raycaster();
		const chestY = Math.max(0.3, this.camera.position.y - 1.0);
		const moved = new THREE.Vector3();
		const planes = [];

		for (let pass = 0; pass < MAX_PASSES; pass++) {
			const dist = remaining.length();
			if (dist < 1e-4) break;
			const dir = remaining.clone().multiplyScalar(1 / dist);
			const origin = new THREE.Vector3(
				this.camera.position.x + moved.x, chestY, this.camera.position.z + moved.z);
			const hit = this._firstWallHit(origin, dir, dist + RADIUS);
			if (!hit) { moved.add(remaining); remaining.set(0, 0, 0); break; }
			// Advance as far as the wall allows, then carry the rest into the clip.
			const step = Math.min(Math.max(0, hit.distance - RADIUS), dist);
			moved.addScaledVector(dir, step);
			remaining.addScaledVector(dir, -step);
			planes.push(hit.normal);
			this._clipAgainstPlanes(remaining, planes, EPS);
		}

		// ── Depenetration: the body is a cylinder, and it never rests inside a wall ──────────────
		// Two passes, because pushing out of one wall can press into its neighbour — the corner case
		// this whole function is about. Only ever pushes ALONG a wall's own normal, so it converges
		// and can never launch the player somewhere they were not.
		for (let pass = 0; pass < 2; pass++) {
			const push = this._wallDepenetration(
				this.camera.position.x + moved.x, chestY, this.camera.position.z + moved.z, RADIUS);
			if (!push) break;
			moved.add(push);
		}

		// ── Max step height: a rise taller than a stair is a ledge, not a step ──────────────────────
		// ASK-159 §2.18aa (found by probe-phone-walk K2p on round 4n's local build): the chest ray (eye − 1.0 m)
		// passes OVER a low fixture, and step-climbing then follows the ground up onto it — the walker stood on
		// `meshsmith-register`'s top (floor under the eye 0.42 → 1.00 m over four frames, store-test-2). Every
		// shipped character controller bounds the rise one step may climb (Unity CharacterController.stepOffset,
		// Unreal MaxStepHeight 45 cm, Godot floor_snap): above it the move is refused like a wall, sliding along
		// the ledge on whichever axis stays walkable. 0.35 m clears any code stair riser (≤ 0.22 m) and stops at a
		// seat, a counter, a register. Stepping DOWN is never limited.
		if (this._lastGroundY !== undefined && (moved.x || moved.z)) {
			const px = this.camera.position.x, pz = this.camera.position.z;
			const ok = (x, z) => { const g = this._groundBelow(px + x, pz + z); return g == null || g - this._lastGroundY <= MAX_STEP_UP_M; };
			if (!ok(moved.x, moved.z)) {
				if (moved.x && ok(moved.x, 0)) moved.z = 0;
				else if (moved.z && ok(0, moved.z)) moved.x = 0;
				else moved.set(0, 0, 0);
			}
		}

		delta.set(moved.x, dy, moved.z);
	}

	/** The highest upward-facing surface of the room under (x, z), below the eye — the ground a step there would
	 *  stand on (the same reading `_applyStepClimbing` makes under the camera). null where there is none. */
	_groundBelow(x, z) {
		this._stepRaycaster = this._stepRaycaster || new THREE.Raycaster();
		const n = new THREE.Vector3();
		this._stepRaycaster.set(new THREE.Vector3(x, this.camera.position.y, z), new THREE.Vector3(0, -1, 0));
		this._stepRaycaster.far = 30;
		for (const h of this._stepRaycaster.intersectObject(this.roomRoot, true)) {
			if (!h.face || h.object.visible === false) continue;
			n.copy(h.face.normal).transformDirection(h.object.matrixWorld);
			if (n.y >= 0.5) return h.point.y;
		}
		return null;
	}

	/** The nearest BLOCKING wall along a ray: visible, has a face, and is not a floor/ceiling/tread.
	 *  The returned normal is flattened to XZ and oriented against `dir`, so `v · n < 0` always means
	 *  "moving into this wall" regardless of how the mesh was wound or which side its material shows. */
	_firstWallHit(origin, dir, far) {
		this._wallRaycaster ||= new THREE.Raycaster();
		this._wallRaycaster.set(origin, dir);
		this._wallRaycaster.far = far;
		const hits = this._wallRaycaster.intersectObject(this.roomRoot, true);
		for (const h of hits) {
			if (!h.face) continue;
			// Three.js Mesh.raycast ignores `visible`, so explicitly skip invisible meshes (e.g. the
			// comic-shop south wall hidden for the doorway cutout).
			if (h.object.visible === false) continue;
			const n = h.face.normal.clone().transformDirection(h.object.matrixWorld);
			if (Math.abs(n.y) > 0.7) continue;   // floor / ceiling / stair tread — the step-climb owns those
			n.y = 0;
			if (n.lengthSq() < 1e-8) continue;
			n.normalize();
			if (n.dot(dir) > 0) n.negate();      // a DoubleSide mesh can report the far face's normal
			return { distance: h.distance, normal: n, object: h.object };
		}
		return null;
	}

	/** Quake's multi-plane clip. Slide `v` along each plane it is moving INTO; if that slide re-enters
	 *  another plane already hit this frame, travel their crease instead; if a third still refuses it,
	 *  stop. `planes` are outward normals (pointing at the player's side). Mutates `v`. */
	_clipAgainstPlanes(v, planes, EPS) {
		for (let i = 0; i < planes.length; i++) {
			const a = planes[i];
			if (v.dot(a) >= -EPS) continue;                 // not moving into this one
			v.addScaledVector(a, -v.dot(a));                // slide along it
			for (let j = 0; j < planes.length; j++) {
				if (j === i) continue;
				const b = planes[j];
				if (v.dot(b) >= -EPS) continue;
				const crease = new THREE.Vector3().crossVectors(a, b);
				if (crease.lengthSq() < 1e-8) { v.set(0, 0, 0); return; }   // opposed walls — a dead end
				crease.normalize();
				v.copy(crease.multiplyScalar(v.dot(crease)));
				for (const c of planes) {
					if (v.dot(c) < -EPS) { v.set(0, 0, 0); return; }        // a true corner — stop
				}
				break;
			}
		}
	}

	/** How far the body at (x, y, z) has to move to stop resting inside a wall. Eight short rays —
	 *  enough to find a wall at any angle at this radius — each contributing its own outward push;
	 *  the largest per direction wins rather than summing, so two rays that found the SAME wall
	 *  cannot double it. Returns null when the body is already clear. */
	_wallDepenetration(x, y, z, radius) {
		const origin = new THREE.Vector3(x, y, z);
		const dir = new THREE.Vector3();
		const push = new THREE.Vector3();
		let found = false;
		for (let i = 0; i < 8; i++) {
			const a = (i / 8) * Math.PI * 2;
			dir.set(Math.sin(a), 0, Math.cos(a));
			const hit = this._firstWallHit(origin, dir, radius);
			if (!hit || hit.distance >= radius) continue;
			const depth = radius - hit.distance;
			// `hit.normal` is oriented against the ray, i.e. back toward the body — the way out.
			const along = push.dot(hit.normal);
			if (depth > along) push.addScaledVector(hit.normal, depth - along);
			found = true;
		}
		return found && push.lengthSq() > 1e-10 ? push : null;
	}

	_updateDust(dt) {
		if (!this.dustParticles) return;
		const attr      = this.dustParticles.geometry.attributes.position;
		const colorAttr = this.dustParticles.geometry.attributes.color;
		const vel  = this.dustVelocities;
		const home = this.dustHomePositions;
		const { CLUSTER_R, CLUSTER_Y } = this.dustRoomBounds;
		const R2 = CLUSTER_R * CLUSTER_R;
		// Max spherical distance from home to cluster edge, used for fade normalisation
		const maxDist = Math.sqrt(CLUSTER_R * CLUSTER_R + CLUSTER_Y * CLUSTER_Y);

		for (let i = 0; i < attr.count; i++) {
			let x = attr.getX(i) + vel[i * 3]     * dt;
			let y = attr.getY(i) + vel[i * 3 + 1] * dt;
			let z = attr.getZ(i) + vel[i * 3 + 2] * dt;

			const hx = home[i * 3];
			const hy = home[i * 3 + 1];
			const hz = home[i * 3 + 2];

			// XZ: if beyond cluster radius, teleport back near home
			const dx = x - hx;
			const dz = z - hz;
			if (dx * dx + dz * dz > R2) {
				const angle = Math.random() * Math.PI * 2;
				const r = Math.sqrt(Math.random()) * CLUSTER_R * 0.85;
				x = hx + Math.cos(angle) * r;
				z = hz + Math.sin(angle) * r;
			}

			// Y: if beyond vertical cluster radius, teleport back near home
			if (Math.abs(y - hy) > CLUSTER_Y) {
				y = hy + (Math.random() * 2 - 1) * CLUSTER_Y * 0.7;
			}

			attr.setXYZ(i, x, y, z);

			// Fade brightness to zero as particle reaches the edge of its cluster
			const dy = y - hy;
			const ddx = x - hx, ddz = z - hz;
			const dist = Math.sqrt(ddx * ddx + dy * dy + ddz * ddz);
			const t = Math.max(0, 1 - dist / maxDist);
			const brightness = t * t; // quadratic — sharp fade near the edge
			colorAttr.setXYZ(i, brightness, brightness, brightness);
		}
		attr.needsUpdate      = true;
		colorAttr.needsUpdate = true;
	}

	_updateFlicker() {
		if (!this.shopFlickers.length) return;
		const t = this.clock.elapsedTime;
		for (const f of this.shopFlickers) {
			// Three overlapping sine waves at different frequencies — organic, non-periodic
			const noise =
				Math.sin(t * f.speed * 6.28  + f.phase) * 0.50 +
				Math.sin(t * f.speed * 13.10 + f.phase * 1.7) * 0.30 +
				Math.sin(t * f.speed * 27.30 + f.phase * 2.3) * 0.20;
			f.light.intensity = f.baseIntensity * (1 + noise * f.amount);
		}
	}

	// ── Ambient feature builders ───────────────────────────────────────────────

	_buildOpenSign(w, d, h) {
		const canvas = document.createElement("canvas");
		canvas.width = 512; canvas.height = 200;
		const ctx = canvas.getContext("2d");
		ctx.fillStyle = "#020a03";
		ctx.fillRect(0, 0, 512, 200);
		ctx.font = 'bold 120px "Arial Narrow", Arial, sans-serif';
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.shadowBlur = 70; ctx.shadowColor = "#00ff44";
		ctx.fillStyle = "rgba(0,255,60,0.28)"; ctx.fillText("OPEN", 256, 100);
		ctx.shadowBlur = 28; ctx.shadowColor = "#44ff88";
		ctx.fillStyle = "rgba(80,255,120,0.72)"; ctx.fillText("OPEN", 256, 100);
		ctx.shadowBlur = 8; ctx.shadowColor = "#aaffcc";
		ctx.fillStyle = "#ffffff"; ctx.fillText("OPEN", 256, 100);
		ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2; ctx.shadowBlur = 0;
		ctx.strokeText("OPEN", 256, 100);

		const tex = new THREE.CanvasTexture(canvas);
		tex.colorSpace = THREE.SRGBColorSpace;
		const mat = new THREE.MeshStandardMaterial({
			color: 0x000000, emissive: 0xffffff, emissiveMap: tex,
			emissiveIntensity: 2.2, roughness: 1, metalness: 0,
		});
		const backMat = new THREE.MeshStandardMaterial({ color: 0x050c07, roughness: 0.9, metalness: 0.25 });
		const back = new THREE.Mesh(new THREE.BoxGeometry(0.40, 0.14, 0.012), backMat);
		const face = new THREE.Mesh(new THREE.PlaneGeometry(0.39, 0.13), mat);
		face.position.z = 0.008;
		const group = new THREE.Group();
		group.add(back); group.add(face);
		// West wall inner face at -(w/2 - 0.065) — sign sits flush on it
		group.position.set(-(w * 0.5 - 0.072), h * 0.38, -d * 0.12);
		group.rotation.y = Math.PI / 2;
		this.scene.add(group);
		this.shopLights.push(group);

		const light = new THREE.PointLight(0x00ff55, 1.3, 1.4, 2.0);
		light.position.set(-(w * 0.5 - 0.36), h * 0.38, -d * 0.12);
		this.scene.add(light);
		this.shopLights.push(light);

		this.openSignMat   = mat;
		this.openSignLight = light;
		this.openFlicker   = { state: "stable", timer: 4.0 + Math.random() * 8.0 };
	}

	_makeStencilLabelCanvas(text) {
		const W = 512, H = 128;
		const cv = document.createElement('canvas');
		cv.width = W; cv.height = H;
		const ctx = cv.getContext('2d');
		// Dark backing so the label reads on any surface colour
		ctx.fillStyle = 'rgba(8, 5, 2, 0.72)';
		ctx.fillRect(0, 0, W, H);
		// Stencil paint — cream/yellow, as if sprayed through a cut stencil
		const fontSize = 82;
		ctx.font = `900 ${fontSize}px Impact, "Arial Black", Arial, sans-serif`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = '#d4b860';
		ctx.fillText(text, W / 2, H / 2);
		// punch stencil wear holes through the paint layer only
		ctx.globalCompositeOperation = 'destination-out';
		let s = 0;
		for (let i = 0; i < text.length; i++) s = (s * 31 + text.charCodeAt(i)) >>> 0;
		const rng = () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return (s >>> 0) / 0xffffffff; };
		for (let i = 0; i < 120; i++) {
			const cx = (rng() - 0.15) * W;
			const cy = (rng() - 0.1) * H;
			const r = rng() * 3 + 0.5;
			ctx.beginPath();
			ctx.arc(cx, cy, r, 0, Math.PI * 2);
			ctx.fillStyle = `rgba(0,0,0,${0.2 + rng() * 0.5})`;
			ctx.fill();
		}
		ctx.globalCompositeOperation = 'source-over';
		return cv;
	}

	// ── Per-frame ambient updates ───────────────────────────────────────────────

	_updateAmbience(dt) {
		// Ceiling fan rotation
		if (this.fanGroup) {
			this.fanGroup.rotation.y += this.fanSpeed * dt;
		}

		// Rising motes
		if (this.motesSystem && this.motesVel && this._motesBounds) {
			const { w, d, h } = this._motesBounds;
			const pos = this.motesSystem.geometry.attributes.position;
			const col = this.motesSystem.geometry.attributes.color;
			const count = pos.count;
			for (let i = 0; i < count; i++) {
				const i3 = i * 3;
				let x = pos.getX(i) + this.motesVel[i3];
				let y = pos.getY(i) + this.motesVel[i3 + 1];
				let z = pos.getZ(i) + this.motesVel[i3 + 2];

				// Gentle lateral drift
				this.motesVel[i3]     += (Math.random() - 0.5) * 0.00015;
				this.motesVel[i3 + 2] += (Math.random() - 0.5) * 0.00015;
				this.motesVel[i3]     = Math.max(-0.004, Math.min(0.004, this.motesVel[i3]));
				this.motesVel[i3 + 2] = Math.max(-0.004, Math.min(0.004, this.motesVel[i3 + 2]));

				// Reset to floor when mote reaches ceiling
				if (y > h + 0.08) {
					y = -0.05 + Math.random() * 0.1;
					x = (Math.random() - 0.5) * w * 0.75;
					z = (Math.random() - 0.5) * d * 0.75;
					this.motesVel[i3]     = (Math.random() - 0.5) * 0.0025;
					this.motesVel[i3 + 1] = 0.005 + Math.random() * 0.008;
					this.motesVel[i3 + 2] = (Math.random() - 0.5) * 0.0025;
				}
				x = Math.max(-w * 0.47, Math.min(w * 0.47, x));
				z = Math.max(-d * 0.47, Math.min(d * 0.47, z));
				pos.setXYZ(i, x, y, z);

				// Fade in from floor, bright in middle third, fade out near ceiling
				const norm = Math.max(0, Math.min(1, y / h));
				const b = norm < 0.18 ? norm / 0.18 : norm > 0.78 ? (1 - norm) / 0.22 : 1.0;
				col.setXYZ(i, b, b, b);
			}
			pos.needsUpdate = true;
			col.needsUpdate = true;
		}

		// Neon sign flicker (KapowX — red/pink)
		if (this.neonSignMat && this.neonFlicker) {
			this._tickNeonFlicker(dt, this.neonFlicker, this.neonSignMat, this.neonSignLight, 2.8, 2.2);
		}
		// OPEN sign flicker (green)
		if (this.openSignMat && this.openFlicker) {
			this._tickNeonFlicker(dt, this.openFlicker, this.openSignMat, this.openSignLight, 2.2, 1.3);
		}
	}

	// Simple neon-tube flicker state machine: stable → burst (rapid on/off) → stable
	_tickNeonFlicker(dt, state, mat, light, baseEmissive, baseLight) {
		const t = this.clock.elapsedTime;
		if (state.state === "stable") {
			// Barely-perceptible hum — two close frequencies for beating effect
			const hum = 1.0
				+ Math.sin(t * 7.1  + (state.phase || 0)) * 0.018
				+ Math.sin(t * 11.9 + (state.phase || 0) * 1.6) * 0.012;
			mat.emissiveIntensity = baseEmissive * hum;
			if (light) light.intensity = baseLight * hum;
			state.timer -= dt;
			if (state.timer <= 0) {
				state.state      = "burst";
				state.burstCount = 1 + Math.floor(Math.random() * 4);
				state.timer      = 0.025 + Math.random() * 0.05;
			}
		} else if (state.state === "burst") {
			state.timer -= dt;
			if (state.timer <= 0) {
				if (state.burstCount > 0) {
					state.state  = "off";
					state.timer  = 0.025 + Math.random() * 0.055;
					state.burstCount--;
				} else {
					state.state = "stable";
					state.timer = 6.0 + Math.random() * 14.0;
					state.phase = Math.random() * Math.PI * 2;
					mat.emissiveIntensity = baseEmissive;
					if (light) light.intensity = baseLight;
				}
			}
		} else { // "off"
			mat.emissiveIntensity = 0;
			if (light) light.intensity = 0;
			state.timer -= dt;
			if (state.timer <= 0) {
				mat.emissiveIntensity = baseEmissive;
				if (light) light.intensity = baseLight;
				state.state = "burst";
				state.timer = 0.018 + Math.random() * 0.04;
			}
		}
	}

	_getFocusedComicMesh() {
		if (this.boxState === "hero" && this.heroComicIndex !== null) {
			return this.comicEntries.find((e) => e.index === this.heroComicIndex)?.root ?? null;
		}
		if (this.pileState === "hero" && this.pileHeroEntry && this.pileHeroEntry.animProgress >= 1) {
			return this.pileComics[this.pileHeroEntry.comicIdx]?.mesh ?? null;
		}
		if (this.bookState === "hero" && this.bookHeroEntry && this.bookHeroEntry.animProgress >= 1) {
			return this.bookHeroEntry.entry.mesh;
		}
		if (this.boxState === "wallComic" && this.wallComicHeroEntry && this.wallComicHeroEntry.animProgress >= 1) {
			return this.wallComicHeroEntry.entry.mesh;
		}
		if (this.boxState === "vinyl" && this.vinylHeroEntry && this.vinylHeroEntry.animProgress >= 1) {
			return this.vinylHeroEntry.entry.mesh;
		}
		if (this.spinnerRack?.state === "hero" && this.spinnerRack.heroEntry && this.spinnerRack.heroEntry.animProgress >= 1) {
			return this.spinnerRack.heroEntry.comicEntry?.mesh ?? null;
		}
		return null;
	}

	// Cycle through every vinyl LP in the catalog while one is in hero state.
	// Used by both the scroll wheel and the arrow keys. Loops at the ends.
	advanceVinylBrowse(delta) {
		if (this.boxState !== "vinyl" || this.vinylSleeves.length === 0) return;
		const currentIdx = this.vinylHeroEntry?.entry.mesh.userData.vinylIdx ?? 0;
		const n = this.vinylSleeves.length;
		const nextIdx = ((currentIdx + delta) % n + n) % n;
		this.inspectVinyl(nextIdx);
	}

	// Cycle the focused sleeve within the active vinyl-bin (browse-before-
	// hero). browseIdx range is [-1, N-1] where -1 = "not browsed yet" (no
	// sleeves tipped forward, front sleeve still in focus). Clamps at both
	// ends — no wrapping, since wrap would visually break the fan-out
	// metaphor (the "I've flipped past these" gesture relies on accumulated
	// tilt-forward, which is meaningless when the index loops).
	advanceVinylBinBrowse(delta) {
		const rack = this.activeVinylBin;
		if (!rack || rack.sleeves.length === 0) return;
		const n = rack.sleeves.length;
		rack.browseIdx = Math.max(-1, Math.min(n - 1, rack.browseIdx + delta));
	}

	// Cycle the active stack-table by one position: the current top sleeve
	// arcs up + sideways then descends to the bottom of the stack; everyone
	// else slides up by one slot. delta direction is always +1 for now
	// (forward thumb-through gesture); ← and → both fire +1. cycleAnim is
	// driven from updateAnimations; we just stage it here. Calls while a
	// cycle is already in flight are ignored so the gesture stays clean.
	cycleStackTable(_delta) {
		const stack = this.activeStackTable;
		if (!stack || stack.sleeves.length === 0) return;
		if (stack.cycleAnim) return;             // ignore re-press while in flight
		const N = stack.sleeves.length;
		// Wrap sleeve = the one currently sitting at the top slot (visual top
		// before the cycle starts). Computed from cycleCount; this is the
		// sleeve that will swoosh down to the bottom during the animation.
		const wrapSleeveIdx = (N - 1 - stack.cycleCount + N * N) % N;
		stack.cycleAnim = { progress: 0, wrapSleeveIdx };
	}

	// E-Open: replace the closed cover hero mesh with an "open book" spread
	// (two flat pages side-by-side, textured from product.interiorImages).
	// Resident Evil examination pattern — stays in 3D, no full-screen UI.
	// Hero mesh is hidden (not removed) so closeInterior can put it back.
	openInterior() {
		const hero = this.activeFixtureHero;
		if (!hero?.mesh) return;
		if (this.inspectOpened) return;
		const product = hero.product;
		if (!product?.hasInterior) return;
		const images = Array.isArray(product.interiorImages) ? product.interiorImages : [];
		// images.length < 2 is the testing/stub case — open with plain colour
		// pages so the gesture is exercisable before GCG ships the schema.
		// Once real interiorImages arrive, the same flow loads textures below.

		const W = 0.170;
		const H = 0.260;
		// Open-book dihedral. Each page hinges at the vertical spine (group
		// origin) and angles slightly TOWARD the camera at its outer edge.
		// Net effect from the player's POV: spine slightly recessed, outer
		// edges leaning toward you — natural "held open in two hands" pose
		// rather than two flat pages in the same plane.
		const DIHEDRAL = 8 * Math.PI / 180;
		const group = new THREE.Group();
		// Place the spread at the closed comic's VISUAL CENTRE, not its mesh
		// origin. The comic root sits at the comic BASE (spine-down origin
		// convention), but the open-book pages are centred on the group origin —
		// copying mesh.position dropped the spread by ~half the comic height
		// ("jumps down on E"). Use the closed mesh's bounding-box centre so the
		// spread opens exactly where the cover was.
		const _heroBox = new THREE.Box3().setFromObject(hero.mesh);
		const _heroCenter = _heroBox.getCenter(new THREE.Vector3());
		group.position.copy(_heroCenter);
		// Compute the camera-facing orientation FRESH from current positions
		// rather than inheriting hero.mesh.quaternion. Then rotate 180° around
		// the spread's vertical axis. Empirically the cameraFacingQuat alone
		// pointed the inside (the side where the textures are mapped) AWAY
		// from the camera in every player position — the extra Y flip is
		// the cheapest correction. Side effect: page left/right swap relative
		// to group-local; the texture mapping below uses _refreshInteriorPages
		// which can be adjusted there if reading order matters.
		const toCam = new THREE.Vector3()
			.subVectors(this.camera.position, hero.mesh.position)
			.setY(0).normalize();
		group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), toCam);
		const flipY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
		group.quaternion.multiply(flipY);

		// Default page colour for the stubbed/testing case (empty interiorImages).
		// Two slightly different parchment shades so the player can see it's a
		// spread, not a single panel. When real textures land via plugin v0.6.0,
		// _refreshInteriorPages overrides material.map and these colours fade
		// behind the texture.
		const STUB_LEFT_COLOR  = 0xf5efd9;   // warm cream
		const STUB_RIGHT_COLOR = 0xeae3c8;   // slightly darker cream

		// DoubleSide so the spread is visible from either face. PlaneGeometry
		// has a single front face (+Z normal); the explicit 180° Y flip above
		// puts the right side toward the camera, but DoubleSide is cheap
		// defense in depth.
		const leftMat  = new THREE.MeshStandardMaterial({ color: STUB_LEFT_COLOR,  roughness: 0.92, metalness: 0, side: THREE.DoubleSide });
		const rightMat = new THREE.MeshStandardMaterial({ color: STUB_RIGHT_COLOR, roughness: 0.92, metalness: 0, side: THREE.DoubleSide });

		// Each page lives under a wing group so it can be rotated around the
		// spine (the wing's local origin) rather than around its own centre.
		const leftWing  = new THREE.Group();
		leftWing.rotation.y  = -DIHEDRAL;   // negative y-rot pushes the left page's outer edge toward +Z (camera)
		const leftPage  = new THREE.Mesh(toGltfUv(new THREE.PlaneGeometry(W, H)), leftMat);
		leftPage.position.set(-W / 2, 0, 0);  // page centred half-width left of the wing origin (= spine)
		leftWing.add(leftPage);

		const rightWing = new THREE.Group();
		rightWing.rotation.y = +DIHEDRAL;
		const rightPage = new THREE.Mesh(toGltfUv(new THREE.PlaneGeometry(W, H)), rightMat);
		rightPage.position.set(W / 2, 0, 0);
		rightWing.add(rightPage);

		group.add(leftWing, rightWing);

		// Flip-page rig: a third "page" living between the two static pages
		// that animates around the spine when the player advances spreads.
		// Both faces are rendered as separate planes (front-side normals
		// pointing opposite directions) so the visible texture swaps at
		// mid-flip (when the page is edge-on) without flicker. Defaults
		// hidden until advanceInteriorPages spins it up.
		const flipFrontMat = new THREE.MeshStandardMaterial({ color: STUB_RIGHT_COLOR, roughness: 0.92, metalness: 0 });
		const flipBackMat  = new THREE.MeshStandardMaterial({ color: STUB_LEFT_COLOR,  roughness: 0.92, metalness: 0 });
		// Subdivided across the width so the turning page can CURL over a cylinder
		// (vertex bend in the tick) rather than rotate dead-flat — the signature
		// premium page-turn tell (design-research brief: "corner-anchored curl").
		const CURL_SEGS = 16;
		const flipWing = new THREE.Group();
		const flipFront = new THREE.Mesh(toGltfUv(new THREE.PlaneGeometry(W, H, CURL_SEGS, 1)), flipFrontMat);
		flipFront.position.set(W / 2, 0, 0);  // anchored to spine; pivots around flipWing.rotation.y
		const flipBack  = new THREE.Mesh(toGltfUv(new THREE.PlaneGeometry(W, H, CURL_SEGS, 1)), flipBackMat);
		flipBack.position.set(W / 2, 0, 0);
		flipBack.rotation.y = Math.PI;        // face the opposite direction
		flipWing.add(flipFront, flipBack);
		// Cache each flip page's flat rest positions — the curl is written as
		// base + bend every frame, so it always bends from a clean flat sheet.
		// The back face is rotated 180° about Y, so its geometry x-axis (and z)
		// run OPPOSITE the front's in wing space; `curlSign` flips the bend math
		// per face so BOTH bow the free (outer) edge the same physical way —
		// without it the back face curls on the spine side (the left-page bug).
		this._flipCurlPageW = W;
		this._flipCurlMeshes = [flipFront, flipBack];
		flipFront.geometry.userData.curlSign = 1;
		flipBack.geometry.userData.curlSign = -1;
		for (const m of this._flipCurlMeshes) {
			m.geometry.userData.basePos = Float32Array.from(m.geometry.attributes.position.array);
		}
		flipWing.visible = false;
		group.add(flipWing);

		this.scene.add(group);

		this.openBookGroup    = group;
		this.openBookLeftMat  = leftMat;
		this.openBookRightMat = rightMat;
		this.openBookLeftWing  = leftWing;
		this.openBookRightWing = rightWing;
		this.openBookFlipWing  = flipWing;
		this.openBookFlipFrontMat = flipFrontMat;
		this.openBookFlipBackMat  = flipBackMat;
		// Base flip colours kept for the dynamic fold-shadow (tick multiplies them).
		this._flipBaseFront = flipFrontMat.color.clone();
		this._flipBaseBack  = flipBackMat.color.clone();
		this.pageFlipAnim     = null;
		this.inspectPageIdx   = 0;
		this.inspectOpened    = true;

		// Hide the closed cover while opened — restored on close.
		hero.mesh.visible = false;

		this._refreshInteriorPages();
		this.onComicSelect?.({ index: 0, product, openedInterior: true });
	}

	// Re-apply textures for the current spread (this.inspectPageIdx). Called
	// on open + on every page advance. Skips entirely when interiorImages
	// is empty — the stub/testing case keeps the plain parchment colours.
	_refreshInteriorPages() {
		const product = this.activeFixtureHero?.product;
		if (!product?.interiorImages || !this.openBookLeftMat || !this.openBookRightMat) return;
		const N = product.interiorImages.length;
		if (N < 2) return;
		const leftIdx  = (this.inspectPageIdx * 2) % N;
		const rightIdx = (this.inspectPageIdx * 2 + 1) % N;
		this._loadTextureCached(product.interiorImages[leftIdx], (tex) => {
			if (!this.openBookLeftMat) return;
			this.openBookLeftMat.map = tex;
			this.openBookLeftMat.needsUpdate = true;
		});
		this._loadTextureCached(product.interiorImages[rightIdx], (tex) => {
			if (!this.openBookRightMat) return;
			this.openBookRightMat.map = tex;
			this.openBookRightMat.needsUpdate = true;
		});
		// Preload the NEXT spread into the texture cache so the turn is instant
		// (research: preload adjacent pages). Fire-and-forget; no material applied.
		const nextL = (this.inspectPageIdx + 1) * 2;
		if (nextL + 1 < N) {
			this._loadTextureCached(product.interiorImages[nextL % N], () => {});
			this._loadTextureCached(product.interiorImages[(nextL + 1) % N], () => {});
		}
	}

	closeInterior() {
		if (!this.inspectOpened) return;
		if (this.openBookGroup) {
			this.scene.remove(this.openBookGroup);
			this.openBookGroup.traverse((n) => {
				if (n.geometry?.dispose) n.geometry.dispose();
				if (n.material?.dispose) n.material.dispose();
			});
			this.openBookGroup = null;
		}
		this.openBookLeftMat  = null;
		this.openBookRightMat = null;
		this.openBookLeftWing  = null;
		this.openBookRightWing = null;
		this.openBookFlipWing  = null;
		this.openBookFlipFrontMat = null;
		this.openBookFlipBackMat  = null;
		this.pageFlipAnim   = null;
		this.inspectOpened  = false;
		this.inspectPageIdx = 0;
		// Restore the closed cover so the player can flip / put it back as normal.
		const hero = this.activeFixtureHero;
		if (hero?.mesh) hero.mesh.visible = true;
	}

	// Cycle through page spreads when in opened state. Each "spread" is two
	// adjacent images from interiorImages. Wraps with modulo when delta
	// runs off either end. No-op if there's only one spread.
	//
	// Visually, the transition animates a single "page" rotating around the
	// spine (Y axis at the book group's origin):
	//
	//   forward (delta > 0):  rotation.y  0 → +π   right page sweeps to the left
	//                                              (front face = current right;
	//                                               back face  = next spread's left)
	//   backward (delta < 0): rotation.y +π → 0    left page sweeps to the right
	//                                              (back face  = current left;
	//                                               front face = previous spread's right)
	//
	// During the animation, the corresponding static page (right for forward,
	// left for backward) is hidden because its texture lives on the flip
	// page's visible face. On animation completion, inspectPageIdx advances,
	// the static pages refresh to the new spread, the flip page hides, and
	// the hidden static page restores.
	advanceInteriorPages(delta) {
		if (!this.inspectOpened) return;
		const product = this.activeFixtureHero?.product;
		const images = product?.interiorImages || [];
		const totalSpreads = Math.floor(images.length / 2);
		if (totalSpreads <= 1) return;

		// Clamp at the ends rather than wrap — a real book stops at the
		// last spread, doesn't loop back to the cover. Same on the back
		// side: spread 0 is the floor. R is the only way back out (already
		// wired in handleKeyDown's R branch via closeInterior).
		const proposed = this.inspectPageIdx + delta;
		const nextIdx  = Math.max(0, Math.min(totalSpreads - 1, proposed));
		if (nextIdx === this.inspectPageIdx) return;   // already at the clamped end

		// Mid-flip re-press: snap the in-flight flip to its end and start a
		// fresh one. Matches the comic-flip animation's pattern.
		if (this.pageFlipAnim) this._finalizePageFlip();

		const forward = delta > 0;

		// Configure the flip wing for this direction.
		const flipWing      = this.openBookFlipWing;
		const flipFrontMat  = this.openBookFlipFrontMat;
		const flipBackMat   = this.openBookFlipBackMat;
		const N = images.length;
		const wrap = (i) => ((i % N) + N) % N;
		if (forward) {
			// frontMat (visible at start)  = current right page
			// backMat  (visible at end)    = next spread's left page
			this._applyPageTexture(flipFrontMat, images[wrap(this.inspectPageIdx * 2 + 1)]);
			this._applyPageTexture(flipBackMat,  images[wrap(nextIdx * 2)]);
			flipWing.rotation.y = 0;
			this.openBookRightWing.visible = false;
		} else {
			// backMat  (visible at start)  = current left page
			// frontMat (visible at end)    = previous spread's right page
			this._applyPageTexture(flipBackMat,  images[wrap(this.inspectPageIdx * 2)]);
			this._applyPageTexture(flipFrontMat, images[wrap(nextIdx * 2 + 1)]);
			flipWing.rotation.y = Math.PI;
			this.openBookLeftWing.visible = false;
		}
		flipWing.visible = true;
		this.cue("page-turn");   // ASK-174: the rustle, ON whenever cues are on (the one preference model)

		this.pageFlipAnim = {
			progress: 0,
			forward,
			nextIdx,
			// Hard-cover stiffness — the cover spread (idx 0) turns heavier/slower
			// than newsprint interiors (StPageFlip data-density="hard" idea).
			dur: (this.inspectPageIdx === 0 || nextIdx === 0) ? 0.62 : 0.42,
		};
	}

	// Internal: snap the in-flight page flip to its end state. Called from
	// advanceInteriorPages when re-pressed mid-flip + from updateAnimations
	// when progress reaches 1.
	_finalizePageFlip() {
		const anim = this.pageFlipAnim;
		if (!anim) return;
		this.inspectPageIdx = anim.nextIdx;
		this._refreshInteriorPages();
		this._resetPageCurl();   // leave the flip pages flat for the next turn
		if (this.openBookFlipWing)  this.openBookFlipWing.visible = false;
		if (this.openBookLeftWing)  this.openBookLeftWing.visible = true;
		if (this.openBookRightWing) this.openBookRightWing.visible = true;
		this.pageFlipAnim = null;
	}

	// Internal: bend the in-flight flip pages into a cylindrical curl. `amp`
	// is 0..1 (0 = flat, 1 = deepest, driven by sin over the flip). Writes
	// base + bend so it always curls from a clean flat sheet, and recomputes
	// normals so the bow shades (a 16×1 plane → trivially cheap per frame).
	_deformPageCurl(amp) {
		const meshes = this._flipCurlMeshes;
		const W = this._flipCurlPageW;
		if (!meshes || !W) return;
		// Page spans wing-x [0..W] from spine (s=0) to free edge (s=1). Bow grows
		// toward the free edge (s²) so the spine stays anchored and the tip curls;
		// a slight x-foreshortening conserves apparent arc-length as it bends.
		const depth = W * 0.26;
		const shorten = W * 0.09;
		for (const m of meshes) {
			const base = m.geometry.userData.basePos;
			const sign = m.geometry.userData.curlSign || 1;   // +1 front face, -1 back face (π-rotated)
			const pos = m.geometry.attributes.position;
			const arr = pos.array;
			for (let i = 0; i < arr.length; i += 3) {
				const bx = base[i];
				// s in WING space: 0 at spine, 1 at the free edge. The back face's
				// π rotation mirrors local x, so fold the sign into s + the deltas.
				const s = (bx * sign + W / 2) / W;
				const k = s * s;
				arr[i]     = bx - shorten * amp * k * sign;  // pull the free-edge tip toward the spine (per-face mirror)
				arr[i + 1] = base[i + 1];                    // y unchanged
				// Bow in geometry -z (NO sign factor): the front face (no rotation)
				// bows toward world -z, the back face (π-rotated) toward world +z —
				// OPPOSITE world directions, which is what reads correctly as a
				// turning page from each side (TEST-WS1-A: right side was inverted
				// when both bowed the same world way).
				arr[i + 2] = base[i + 2] - depth * amp * k;
			}
			pos.needsUpdate = true;
			m.geometry.computeVertexNormals();
		}
	}

	// Internal: restore the flip pages to flat (called on finalize so the next
	// turn starts from a clean sheet and the hidden wing isn't left bent).
	_resetPageCurl() {
		const meshes = this._flipCurlMeshes;
		if (!meshes) return;
		for (const m of meshes) {
			const base = m.geometry.userData.basePos;
			if (!base) continue;
			m.geometry.attributes.position.array.set(base);
			m.geometry.attributes.position.needsUpdate = true;
			m.geometry.computeVertexNormals();
		}
	}

	// ── ASK-174 (Client 5d) — the sound engine's seams. The engine is a LAZY chunk (`_sound-engine.js`); these stay tiny so
	// the customer's initial JS carries none of it. `cue(id, { role, pos })` is a no-op until the engine has loaded and the
	// first gesture has unlocked it (off until the first gesture — the contract), and it never throws into a caller.
	_ensureSound() {
		if (this._soundP) return this._soundP;
		this._soundP = import("./_sound-engine.js").then((m) => {
			this._sound = m.installSound(this, { getPrefs: () => this._audioPrefs || null, published: () => this._bootConfig?.()?.soundCatalogue || null });
			return this._sound;
		}).catch((e) => { console.warn("[sound] engine unavailable:", e?.message || e); return null; });
		return this._soundP;
	}
	cue(id, opts) {
		try { return this._sound?.cue(id, opts) ?? null; } catch (e) { console.warn("[sound] cue", id, e?.message || e); return null; }
	}
	/** Client 5e (ASK-174 🟣 c) — a cue for an event the shopper meets AT THE REVEAL (the arriving friend's chime): it plays
	 *  when the engine is ready (graph built after loader-clear, the one-shots decoded), bounded by `maxWaitMs`, then goes
	 *  through `cue` like any other (locked / cues-off / min-gap still apply). Not a queue: one event, presented when it is
	 *  seen — measured, the arrival ran ahead of the graph's build ("not-built") on a fresh page. */
	cueWhenReady(id, opts, maxWaitMs = 8000) {
		const ready = this._ensureSound().then((s) => s?.ready).catch(() => null);
		return Promise.race([ready, new Promise((r) => setTimeout(r, maxWaitMs))]).then(() => this.cue(id, opts)).catch(() => null);
	}
	_cuePosOf(obj, up = 0) {
		if (!obj?.getWorldPosition) return null;
		const v = obj.getWorldPosition(new THREE.Vector3());
		return { x: v.x, y: v.y + up, z: v.z };
	}
	_handPos() { const c = this.camera.position; return { x: c.x, y: c.y - 0.45, z: c.z }; }   // the held item sits ~0.45 m under the eye

	// Tiny transient on-canvas toast. Self-contained so it needs no app.js wiring.
	_flashToast(msg) {
		try {
			if (!this.mount) return;
			const el = document.createElement("div");
			el.textContent = msg;
			el.style.cssText = "position:absolute;left:50%;bottom:84px;transform:translateX(-50%);background:rgba(12,14,18,.86);color:#eef2f7;padding:7px 14px;border-radius:999px;font:600 13px system-ui,sans-serif;pointer-events:none;z-index:40;transition:opacity .4s;opacity:1";
			this.mount.appendChild(el);
			setTimeout(() => { el.style.opacity = "0"; }, 900);
			setTimeout(() => { el.remove(); }, 1400);
		} catch { /* noop */ }
	}

	// Internal: assign a texture (or fall back to no map) onto a page
	// material. Uses the existing cached loader so re-flipped pages reuse
	// already-decoded textures instead of round-tripping the network.
	_applyPageTexture(mat, url) {
		if (!mat) return;
		if (!url) {
			mat.map = null;
			mat.needsUpdate = true;
			return;
		}
		this._loadTextureCached(url, (tex) => {
			if (!mat) return;
			mat.map = tex;
			mat.needsUpdate = true;
		});
	}

	_snapUnflip() {
		if (this.comicFlipAnim) {
			this.comicFlipAnim.mesh.quaternion.copy(this._heroBaseQuat);
			this.comicFlipAnim = null;
		} else if (this.comicFlipped) {
			const mesh = this._getFocusedComicMesh();
			if (mesh) mesh.quaternion.copy(this._heroBaseQuat);
		}
		this.comicFlipped = false;
	}

	flipActiveComic() {
		const mesh = this._getFocusedComicMesh();
		if (!mesh) return;
		if (this.comicFlipAnim) {
			// Snap to destination and cancel — pressing F mid-animation completes it instantly
			mesh.quaternion.copy(this.comicFlipAnim.toQuat);
			this.comicFlipAnim = null;
			return;
		}
		const flipY = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
		if (!this.comicFlipped) {
			this._heroBaseQuat.copy(mesh.quaternion);
			this.comicFlipAnim = {
				mesh,
				fromQuat: mesh.quaternion.clone(),
				toQuat:   flipY.clone().multiply(mesh.quaternion),
				progress: 0,
			};
			this.comicFlipped = true;
		} else {
			this.comicFlipAnim = {
				mesh,
				fromQuat: mesh.quaternion.clone(),
				toQuat:   this._heroBaseQuat.clone(),
				progress: 0,
			};
			this.comicFlipped = false;
		}
	}

	/** Every former caller of the shop hum's builder now just makes sure the sound engine is loading: the engine builds its
	 *  graph (the room tone included) after loader-clear and a gesture only RESUMES it (ASK-142 §9.2's 212 ms lesson). */
	_initAmbientAudio() { this._ensureSound(); }

	_clampCameraToRoom() {
		// Prefer shell-derived bounds when a shell GLB is loaded — keeps the
		// playable area in lockstep with the visible walls without authors
		// duplicating the footprint into project.json.
		const shell = this._shellRoomBounds;
		const room  = shell || this.currentScene?.room;
		// E1 (ASK-100 ④): the clamp box must be centred on the SHELL's true centre, not the origin.
		// A composed multi-module shell (D-057) extends its union away from origin (the ④ skeleton
		// spans z +1.5…−9.5, centre −4); an origin-centred box walled off the far module and its
		// false boundary collapsed the camera→target vector into the OrbitControls flip Shaun saw.
		// _shellRoomCenter is already computed at shell load; single-shell shops sit ~origin, so
		// their box is unchanged (golden-safe). The clamp stays an AABB safety net — inside a
		// concave union the REAL wall raycast (_collideHorizontalDelta) is what blocks, as before.
		const cx = (shell && this._shellRoomCenter) ? this._shellRoomCenter.x : 0;
		const cz = (shell && this._shellRoomCenter) ? this._shellRoomCenter.z : 0;
		const hw  = (room?.width  ?? 6) * 0.5 - 0.3;
		const hd  = (room?.depth  ?? 6) * 0.5 - 0.3;
		// Allow the camera a small step past the south wall (legacy comic-shop
		// stairwell behaviour). Harmless for shell-mode shops since a wall
		// raycast catches them before they actually leave the room.
		const maxZ = cz + (room?.depth ?? 6) * 0.5 + 0.90;
		const maxY = (room?.height ?? 3) - 0.2;

		const p = this.camera.position;
		const before = p.clone();

		p.x = Math.max(cx - hw, Math.min(cx + hw, p.x));
		p.z = Math.max(cz - hd, Math.min(maxZ, p.z));
		p.y = Math.max(0.25, Math.min(maxY, p.y));

		// Optional axis-aligned XZ obstacle rectangles (block stair access etc.)
		// Each obstacle: { minX, maxX, minZ, maxZ }. Player is pushed out along
		// the smallest-penetration axis when inside.
		const obstacles = room?.obstacles;
		if (Array.isArray(obstacles)) {
			for (const o of obstacles) {
				if (p.x <= o.minX || p.x >= o.maxX || p.z <= o.minZ || p.z >= o.maxZ) continue;
				const dxLeft  = p.x - o.minX;
				const dxRight = o.maxX - p.x;
				const dzMin   = p.z - o.minZ;
				const dzMax   = o.maxZ - p.z;
				if (Math.min(dxLeft, dxRight) < Math.min(dzMin, dzMax)) {
					p.x = (dxLeft < dxRight) ? o.minX : o.maxX;
				} else {
					p.z = (dzMin < dzMax) ? o.minZ : o.maxZ;
				}
			}
		}

		// Apply the exact same correction to the orbit target so the camera-to-target
		// vector (i.e. look direction) is preserved — walls cause sliding, not rotation.
		// E1: the correction is applied and NOT re-clamped. The old independent target re-clamp
		// could shear the look vector at a boundary — and when the walk direction pointed into the
		// clamped wall it collapsed camera→target to ~zero horizontal offset, which OrbitControls
		// renders as the camera flip. The target is a look direction in Play, not a position that
		// needs to stay in-room (it already sat past walls legitimately, e.g. looking outside).
		const correction = new THREE.Vector3().subVectors(p, before);
		if (correction.lengthSq() > 0) {
			this.controls.target.add(correction);
		}
	}

	// FPS-style mouse-look using the Pointer Lock API. Click the canvas to
	// acquire lock; mouse motion then rotates the camera in place without
	// clicking. Esc releases. Camera quaternion is set directly so the camera
	// can't orbit through walls (vs OrbitControls' camera-around-target rotation).
	// ASK-087 slice-c — Edit/Play camera. Play = the immersive FPS rig (pointer-lock look + WASD +
	// room-clamp, all default). Edit = free OrbitControls (rotate + zoom, no walk/clamp/lock). Exactly
	// one control system is live at a time — the `_cameraAuthoring` flag gates the FPS paths
	// (pointerdown lock, _applyWalkMovement, the animate FPS block) while enableRotate/enableZoom
	// hand the mouse to OrbitControls. Driven by app.js off the `galleria:editor-mode` event.
	setCameraMode(mode) {
		// Idempotent + authoritative: always enforce the mode's control state (don't early-return on
		// "unchanged" — the very first call establishes Play from the constructor's raw OrbitControls
		// defaults, and re-asserting is cheap).
		// ASK-096: Shell-compose is an AUTHORING camera (orbit, not FPS walk) like Edit, but framed
		// TOP-DOWN — the floor-plan grain the merchant composes in. It shares Edit's control state +
		// editor-layer visibility; only the framing differs (and it re-frames plan on each entry).
		const shell = mode === "shell";
		const authoring = mode === "edit" || shell;
		const enteringAuthoring = authoring && !this._cameraAuthoring;   // Play → authoring transition
		const leavingAuthoring  = !authoring && !!this._cameraAuthoring;  // authoring → Play transition
		const enteringShell = shell && this._cameraMode !== "shell";     // (re)frame plan on shell entry
		this._cameraMode = mode;
		this._cameraAuthoring = authoring;
		// ASK-087 Phase-1: gate ALL editor-only SCENE artifacts (selection box + coming move-gizmos +
		// grid/wireframe overlays) wholesale by hiding their dedicated layer. THREE renders an object
		// only if it AND every ancestor are visible, so `_editorLayer.visible=false` is authoritative
		// over each artifact's own flag — the scene-graph twin of the CSS chrome gate. Play also
		// deselects (the box is meaningless in the customer view; a stale selection must not linger).
		const layer = this._ensureEditorLayer();
		if (layer) layer.visible = authoring;
		// ASK-111 — the roof comes off in authoring (Edit AND shell-compose), goes back on in Play.
		// Authoritative on every call, like the layer gate above: a merchant who never leaves Edit
		// still gets the ceiling hidden the moment a module composes (see the recompose call site).
		this.setDollhouse(authoring);
		if (!authoring) {
			this.deselect();
			// Wireframe is a MATERIAL mutation — the only editor artifact neither the chrome gate nor
			// the layer gate can see. Play must render customer-exact, so reset is authoritative here.
			this.resetViewModes();
		}
		if (this.controls) {
			this.controls.enableRotate = authoring;   // Play: pointer-lock look owns rotation
			this.controls.enableZoom   = authoring;   // Play: fixed FOV / no dolly
			this.controls.enablePan    = authoring;   // ASK-087 slice-c amend: Edit two-finger/right-drag pan; Play off
			this.controls.enableDamping = true;       // smooth orbit/zoom/pan in both (harmless in Play)
		}
		const canvas = this.renderer?.domElement;
		if (authoring) {
			// Leaving Play: drop any active pointer-lock so the mouse is free to orbit.
			if (typeof document !== "undefined" && canvas && document.pointerLockElement === canvas) document.exitPointerLock?.();
			if (this._mouseLook) this._mouseLook.locked = false;
			if (canvas) canvas.style.cursor = "default";
			// Re-pivot ONCE on entry: FPS look leaves controls.target ~1m ahead of the camera, which
			// degenerates orbit into a head-turn. Frame the room centre for a god's-eye orbit
			// (per-selection focus lands later with the Inspector; double-click focus is available now).
			if (enteringShell) this._frameForShellPlan();
			else if (enteringAuthoring) this._frameForEdit();
		} else {
			if (canvas) canvas.style.cursor = "crosshair";
			// 🔴 ASK-129 F-102g — PLAY PUTS YOU INSIDE YOUR SHOP. Vanessa: *"Edit → Play puts you
			// outside the shop rather than inside."* `_frameForEdit` parks the orbit camera at a
			// god's-eye above and outside the shell, and Play used to inherit whatever pose the orbit
			// happened to end on — so pressing Play dropped the merchant into the sky over the street.
			// Nothing was wrong with the spawn; Play simply never asked for it.
			//
			// AAA precedent, and it is unanimous: the editor's viewport camera is NEVER the play
			// camera. Unity and Unreal start play at the PlayerStart / scene camera (Unreal's "Play
			// From Here" is a separate, explicitly-chosen verb), Godot's F5 runs the scene's own
			// camera. Pressing Play means "show me what a customer sees", and a customer arrives at
			// the shop's spawn.
			//
			// The DEFAULT spawn, never a named one: `spawns.named["from-portal-1"]` is the
			// portal-side arrival (outside the door, `SPAWN_FROM_PORTAL_1`), which is precisely the
			// "outside the shop" she reported. A merchant leaving Edit is already in their shop.
			if (leavingAuthoring) this._respawnForPlay();
		}
		return authoring ? (shell ? "shell" : "edit") : "play";
	}

	/** The exit trigger's radius: EXIT_ANCHOR's scale (= the door's half-width), else 1.0 m — ONE reading for the
	 *  trigger and the arrival rule (`_spawn-clearance.js` exitRingRadius; coord contracts/arrival-rule-fixture.json,
	 *  ASK-159 §2.18dl; the arrival rule itself is `clearShellSpawns`, applied once per compose). */
	_shellExitRadius(anchor) { return exitRingRadius(anchor?.scale); }

	/** Put the player back at the shop's own SPAWN, looking where it looks. No-op on the hub (its
	 *  camera is the street's, placed by `_applyHubSpawn`) and on any scene with no spawn marker —
	 *  there is nothing better to do than leave the camera where it is. */
	_respawnForPlay() {
		if (this.sceneKind === "hub") return false;
		const spawn = this._shopAnchors?.spawns?.default || null;
		// `closedCameraPos/Target` are the same pose, captured at compose — the fallback for a scene
		// whose anchors were rebuilt (a recompose) but whose spawn marker is no longer in the graph.
		const pos = spawn ? spawn.position : this.closedCameraPos;
		const look = spawn ? spawn.lookAt : this.closedCameraTarget;
		if (!pos || !look) return false;
		this.camera.position.set(pos.x, pos.y, pos.z);
		if (this.controls) {
			this.controls.target.set(look.x, look.y, look.z);
			this.controls.update();
		}
		// The FPS rig derives its look from the camera quaternion, not from controls.target — without
		// this the merchant lands at the spawn still facing wherever the orbit was pointing.
		this.camera.lookAt(look.x, look.y, look.z);
		// The pointer-lock rig drives the camera from its OWN yaw/pitch every locked frame, so
		// without this the first mouse move would snap the merchant straight back to the orbit's
		// heading and the respawn would look like a flicker.
		this._syncMouseLookFromCamera();
		// 🔴 A RESPAWN IS A TELEPORT, AND A TELEPORT DISARMS THE ZONE IT LANDS IN. This is the rule
		// `_armedTriggers` already encodes — "prevents an immediate fire on spawn-into-zone" — and
		// `setFullSceneContext` clears the set on every scene load for exactly this reason. The spawn
		// sits INSIDE the storefront exit's radius on every shell measured (test-shop: spawn z 1.2,
		// exit z 1.4, radius 1.0), so without this line the fix produces Vanessa's complaint in a new
		// form: a merchant who has walked away from the door (arming the exit) and presses Edit then
		// Play is respawned onto the doormat with the exit still armed, and the next animate frame
		// teleports them out to the street. Measured on the deployed plane before this line: Play
		// landed at the spawn at t+80 ms and the page was on `galleria-hub` by t+500 ms.
		// (Since ASK-159 §2.18z D3 the arrival is cleared of the ring — `_spawn-clearance.js` — so the
		// spawn no longer lands on the doormat; the disarm stays, because every teleport disarms.)
		this._armedTriggers.clear();
		console.log(`[spawn] Play entry → SPAWN pos=(${pos.x.toFixed(2)},${pos.y.toFixed(2)},${pos.z.toFixed(2)})`);
		return true;
	}

	// ── ASK-172 §5 (Client 5a) — "Meet me here": the three moves a party asks of the walker ──────────────────────
	/** The MEET arrival: stand the shopper with their FEET at `p` ([x, y, z]) facing `yaw` (the presence record's
	 *  convention, forward = (−sin y, 0, −cos y)). A teleport, so it disarms the zone it lands in (`_respawnForPlay`). */
	placeShopperAt(p, yaw) {
		if (!this.camera || !Array.isArray(p)) return false;
		const eyeY = p[1] + 1.7;
		const look = { x: p[0] - Math.sin(yaw), y: eyeY, z: p[2] - Math.cos(yaw) };
		this.camera.position.set(p[0], eyeY, p[2]);
		this.controls?.target?.set(look.x, look.y, look.z);
		this.controls?.update?.();
		this.camera.lookAt(look.x, look.y, look.z);
		this.camera.updateMatrixWorld(true);
		this._syncMouseLookFromCamera();
		this._armedTriggers?.clear();
		this._autoWalk = null;
		console.log(`[party] meet arrival → feet (${p[0].toFixed(2)},${p[1].toFixed(2)},${p[2].toFixed(2)}) yaw ${yaw.toFixed(3)}`);
		return true;
	}
	/** Ease the look onto `yaw` (the chevron's tap; the end of a follow) — the look ease's own target, so it is the one
	 *  writer (`_tickLook`, half-life 40 ms: a half turn settles inside 400 ms). The pitch levels to the horizon. */
	turnLookTo(yaw) {
		const ml = this._mouseLook;
		if (!ml || this._cameraAuthoring) return false;
		this._syncMouseLookFromCamera();
		ml.yaw = ml.viewYaw + Math.atan2(Math.sin(yaw - ml.viewYaw), Math.cos(yaw - ml.viewYaw));
		ml.pitch = 0;
		ml.easing = true;
		return true;
	}
	/** "Take me there": walk (at the shop's walk speed, colliding like the shopper's own walk) to `target()` → { x, z,
	 *  faceYaw } (re-read every frame: the friend may move), then turn to `faceYaw`. Ends on arrival, when blocked (no
	 *  progress for 0.6 s), after 20 s, or the moment the shopper walks themselves. `onEnd(reason)`. */
	walkTo(target, { onEnd = null } = {}) {
		if (typeof target !== "function") return false;
		this._autoWalk = { target, onEnd, t: 0, stuckT: 0, faced: null };
		return true;
	}
	_endAutoWalk(reason) {
		const aw = this._autoWalk;
		this._autoWalk = null;
		if (!aw) return;
		const t = reason === "arrived" || reason === "blocked" ? aw.target() : null;
		if (t && Number.isFinite(t.faceYaw)) this.turnLookTo(t.faceYaw);
		console.log(`[party] follow ${reason}`);
		try { aw.onEnd?.(reason); } catch { /* the caller's */ }
	}
	_stepAutoWalk(dt) {
		const aw = this._autoWalk;
		const t = aw.target();
		if (!t) { this._endAutoWalk("lost"); return; }
		aw.t += dt;
		const cam = this.camera.position;
		const dx = t.x - cam.x, dz = t.z - cam.z, d = Math.hypot(dx, dz);
		if (d <= 0.1) { this._endAutoWalk("arrived"); return; }
		if (aw.t > 20) { this._endAutoWalk("timeout"); return; }
		// face the way we walk (re-aimed only when the heading drifts, so the ease is not restarted every frame)
		const heading = Math.atan2(-dx, -dz);
		if (aw.faced === null || Math.abs(Math.atan2(Math.sin(heading - aw.faced), Math.cos(heading - aw.faced))) > 0.15) { aw.faced = heading; this.turnLookTo(heading); }
		const step = Math.min(d, (this.sceneKind === "hub" ? 2.6 : this._walkSpeed) * dt);
		const delta = new THREE.Vector3((dx / d) * step, 0, (dz / d) * step);
		const hw = this._hubLife?.walk;
		if (hw) hw.collide(delta);
		else if (this.sceneKind !== "hub") this._collideHorizontalDelta(delta);
		const moved = Math.hypot(delta.x, delta.z);
		aw.stuckT = moved < step * 0.25 ? aw.stuckT + dt : 0;
		if (aw.stuckT > 0.6) { this._endAutoWalk("blocked"); return; }
		this.camera.position.add(delta);
		this.controls.target.add(delta);
		if (!hw && this.sceneKind === "hub") this._clampCameraToHub();
		else if (!hw) this._clampCameraToRoom();
		this.controls.update();
	}

	// ASK-097 (B) — snapshot/restore the camera pose so a persist-and-reload (facing/label/hinge
	// commit → navigateToProject → setFullSceneContext resets the camera) can put the Edit view back
	// exactly where it was, killing the per-commit camera flick. Other knobs recompose in-place and
	// never reload; only the anchor-orientation commits reload, and they don't need to.
	getCameraPose() {
		if (!this.camera || !this.controls) return null;
		return { pos: this.camera.position.toArray(), quat: this.camera.quaternion.toArray(), target: this.controls.target.toArray() };
	}
	setCameraPose(pose) {
		if (!pose || !this.camera || !this.controls) return;
		if (Array.isArray(pose.pos))    this.camera.position.fromArray(pose.pos);
		if (Array.isArray(pose.quat))   this.camera.quaternion.fromArray(pose.quat);
		if (Array.isArray(pose.target)) this.controls.target.fromArray(pose.target);
		this.controls.update?.();
	}

	// ASK-096 — frame the shop as a TOP-DOWN floor plan for shell compose: pivot on the shell centre,
	// place the camera straight overhead looking down. The module+seam grain reads as a plan (UEFN
	// creative top-down), and seam mates are unambiguous from above. Orbit stays enabled (the merchant
	// can tilt), but entry is authoritatively plan. (True orthographic projection is a polish follow —
	// slice-1 uses an overhead perspective framing, which reads as plan at this pull-back.)
	_frameForShellPlan() {
		if (!this.controls || !this.camera) return;
		const b = this._shellRoomBounds || { width: 6, depth: 6, height: 3 };
		const center = this._shellRoomCenter
			? this._shellRoomCenter.clone()
			: new THREE.Vector3(0, 0, 0);
		this.controls.target.copy(center);
		const span = Math.max(b.width, b.depth);
		const dist = THREE.MathUtils.clamp(span * 1.6, this.controls.minDistance + 0.5, this.controls.maxDistance);
		this.camera.position.set(center.x, center.y + dist, center.z + 0.001);   // +0.001 z avoids a gimbal-locked straight-down up-vector
		this._pinCameraToTarget();
	}

	// ASK-087 slice-c amend — frame the shop for a god's-eye Edit orbit: pivot on the shell's world
	// centre and pull the camera out along its current azimuth (elevated) to see the whole room.
	_frameForEdit() {
		if (!this.controls || !this.camera) return;
		const b = this._shellRoomBounds || { width: 6, depth: 6, height: 3 };
		const center = this._shellRoomCenter
			? this._shellRoomCenter.clone()
			: new THREE.Vector3(0, b.height * 0.5, 0);
		this.controls.target.copy(center);
		const dist = THREE.MathUtils.clamp(Math.max(b.width, b.depth) * 1.25, this.controls.minDistance + 0.5, this.controls.maxDistance);
		const dir = new THREE.Vector3().subVectors(this.camera.position, center);
		if (dir.lengthSq() < 1e-4) dir.set(0.4, 0.5, 1);   // camera sat on the pivot → pick a default azimuth
		dir.y = Math.max(dir.y, dist * 0.4);               // elevate for a top-down-ish god's-eye
		dir.normalize().multiplyScalar(dist);
		this.camera.position.copy(center).add(dir);
		this._pinCameraToTarget();
	}

	// ASK-087 slice-c amend — canvas input guards for Edit orbit, robust to OrbitControls' internals:
	//   • wheel/ctrl-wheel (Mac trackpad pinch) is preventDefault'd at the canvas whenever authoring,
	//     so browser PAGE-ZOOM never fires even when OrbitControls' onMouseWheel bails early (it
	//     returns before preventDefault when enableZoom is off or a drag is mid-flight);
	//   • touch-action:none so touch pinch/scroll can't page-zoom either;
	//   • double-click focus (Sketchfab pattern) recenters the orbit pivot on the clicked point.
	_setupEditCameraGuards() {
		const canvas = this.renderer?.domElement;
		if (!canvas || this._editGuardsBound) return;
		this._editGuardsBound = true;
		canvas.style.touchAction = "none";
		// Ruled input matrix (Edit):
		//   trackpad — click-drag orbit · two-finger scroll PAN (deltaX+deltaY) · pinch zoom
		//   mouse    — left-drag orbit · right-drag pan · wheel zoom
		//   touch    — one-finger orbit · two-finger pan · pinch zoom (OrbitControls' own touch map)
		// A trackpad's two-finger drag arrives as `wheel`, and OrbitControls treats every wheel as
		// dolly — so pan simply did not exist on trackpads. We intercept in the CAPTURE phase (our
		// listener is registered after OrbitControls', so capture is the only way to pre-empt it) and
		// stopImmediatePropagation for the pan case; zoom cases fall through to OrbitControls.
		canvas.addEventListener("wheel", (e) => {
			if (!this._cameraAuthoring) return;
			e.preventDefault();                       // never let the page zoom, in any branch
			if (this._classifyWheel(e) !== "pan") return;   // → OrbitControls dollies (pinch / mouse wheel)
			e.stopImmediatePropagation();
			if (this.controls?.enablePan) {
				this.controls._pan(-e.deltaX, -e.deltaY);
				this.controls.update();
			}
		}, { passive: false, capture: true });
		// coord ASK-180 §3.18 item 4 (D-K4-4) — a ctrl+wheel / trackpad pinch over the Studio's OWN chrome (the dock, the
		// fit-out card, the toolbar) is the Studio's zoom, never the browser's page zoom (Figma: a pinch anywhere in the
		// editor zooms the canvas; the page never scales under the panels). Measured in K4 (T3): a pinch over the panel
		// zoomed the page ×1.6 and pushed the dock and toolbar off the glass. While authoring, the gesture is cancelled for
		// the page and handed to the canvas (OrbitControls' dolly, about the view's centre). Safari's own pinch events
		// (gesturestart/gesturechange) are cancelled the same way. The customer view (Play) is untouched.
		if (typeof window !== "undefined" && !this._chromeZoomGuard) {
			this._chromeZoomGuard = true;
			window.addEventListener("wheel", (e) => {
				if (!this._cameraAuthoring || !e.ctrlKey || e.target === canvas || !e.cancelable) return;
				e.preventDefault();
				const r = canvas.getBoundingClientRect();
				canvas.dispatchEvent(new WheelEvent("wheel", { deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode, ctrlKey: true, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, bubbles: true, cancelable: true }));
				this._chromeZoomed = (this._chromeZoomed || 0) + 1;   // a probe's witness: how many chrome pinches the Studio took
			}, { passive: false, capture: true });
			const noPageScale = (e) => { if (this._cameraAuthoring && e.target !== canvas) e.preventDefault(); };
			window.addEventListener("gesturestart", noPageScale, { passive: false, capture: true });
			window.addEventListener("gesturechange", noPageScale, { passive: false, capture: true });
		}
		// Single-click SELECT vs drag ORBIT (ASK-087 Phase 1): record pointerdown; on left-button
		// pointerup a sub-threshold move is a CLICK → select the fixture under the cursor (empty →
		// deselect). A drag past the threshold is an orbit and OrbitControls owns it. Right-button is
		// pan; middle is ignored. Edit-only.
		let _downX = 0, _downY = 0;
		canvas.addEventListener("pointerdown", (e) => {
			_downX = e.clientX; _downY = e.clientY;
			// A press that lands on the ALREADY-SELECTED fixture begins a MOVE, not an orbit. Requiring a
			// prior selection is what keeps this compatible with the click-select/drag-orbit matrix
			// above: the first press selects, only a second press on that same fixture drags it.
			// ASK-128 E5-1 — a SEATED item is draggable too. Offered first because a seat has no anchor
			// and `_beginFixtureDrag` is keyed on anchors, so the seat gesture is unreachable behind it;
			// it self-gates on "the selected ZONE seat is under this pointer" and returns false
			// otherwise, so a fixture drag is untouched.
			if (this._cameraAuthoring && e.button === 0 && this._zoneScene?.beginSeatDrag?.(e)) return;
			if (this._cameraAuthoring && e.button === 0) this._beginFixtureDrag(e);
		});
		canvas.addEventListener("pointerup", (e) => {
			// ASK-128 E5-1 — a seat drag ends here and commits an ADDRESS, not a transform: the scene
			// owns the gesture, `onZoneSeatMoved` owns the write (the `onFixtureMoved` split).
			if (this._zoneScene?.isSeatDragging?.()) {
				const move = this._zoneScene.endSeatDrag();
				if (move) { try { void this.onZoneSeatMoved?.(move); } catch (err) { console.warn("[zone] seat move failed:", err?.message || err); } }
				return;
			}
			if (this._dragState) { this._endFixtureDrag(e); return; }   // a move consumed this gesture
			if (!this._cameraAuthoring || e.button !== 0) return;
			// PLACE-NEW (ASK-088 slice-3b): a click (sub-threshold) commits the pending ghost; a drag was
			// an orbit and leaves placement live. Runs before select so a click doesn't also deselect.
			if (this._placeState) {
				if (Math.hypot(e.clientX - _downX, e.clientY - _downY) <= 5) this.commitPlacement();
				return;
			}
			if (Math.hypot(e.clientX - _downX, e.clientY - _downY) > 5) return;   // was a drag → orbit, not select
			const rect = canvas.getBoundingClientRect();
			const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
			this.raycaster.setFromCamera(ndc, this.camera);
			const hits = this.raycaster.intersectObjects(this._pickableSceneChildren(), true).filter((h) => h.object?.isMesh && h.object.visible);
			const hit = hits.find((h) => !this._isShellMesh(h.object)) || hits[0];
			// ASK-120 S3.5 — a SEATED hit is tested BEFORE `_anchorForHit`, and the order is the whole
			// point: `_anchorForHit` resolves a seated product to its HOST fixture (by design — that is
			// how a comic click selects its longbox), so asking it first would make the finer grain
			// unreachable. Clicking a comic now selects THAT SEAT, which still selects its holder — the
			// Inspector opens on the holder with the seat row focused, exactly the Unity click-a-child
			// model. Anything unseated falls through to the fixture unchanged.
			const seat = hit ? this._seatForHit(hit.object) : null;
			if (seat) { this.selectSeat(seat); return; }
			const anchor = hit ? this._anchorForHit(hit.object, hit.point) : null;
			if (anchor) { this.selectFixture(anchor); return; }
			this.deselect();
			// ASK-129 §E2E104 G5 — …and a click on BARE FLOOR is information, not just a deselect. The
			// Selling-space inspector edits one region at a time and the ruling's own reason for that is
			// direct manipulation: *"click a floor region in the 3D view → one inspector for that
			// region"*. Emitted on the editor bus rather than handed to the zones module directly — the
			// stage does not know zones exist, and chrome subscribes (the rule three separate defects in
			// this codebase have now paid for). The POINT is what travels; which region contains it is
			// the subscriber's question, and nothing here may answer it.
			//
			// It must be the FLOOR's own mesh, not any shell mesh. A wall is a shell mesh too, and a wall
			// hit's XZ lands inside whichever region abuts it — so the subscriber would resolve a click on
			// the back wall to the room in front of it and quietly change what the merchant is editing.
			const floorHit = hits.find((h) => /^Shell_Floor/i.test(String(h.object?.name || "")));
			this.emitEditorChange?.("floorPicked", floorHit?.point
				? { point: { x: floorHit.point.x, y: floorHit.point.y, z: floorHit.point.z } } : { point: null });
		});
		canvas.addEventListener("dblclick", (e) => {
			if (!this._cameraAuthoring || !this.controls) return;
			const rect = canvas.getBoundingClientRect();
			const ndc = new THREE.Vector2(
				((e.clientX - rect.left) / rect.width) * 2 - 1,
				-((e.clientY - rect.top) / rect.height) * 2 + 1,
			);
			this.raycaster.setFromCamera(ndc, this.camera);
			const hits = this.raycaster.intersectObjects(this._pickableSceneChildren(), true)
				.filter((h) => h.object?.isMesh && h.object.visible);
			if (!hits.length) return;
			// Prefer a real ITEM over the room shell: clicking near a fixture used to focus the floor
			// behind it (raw nearest-hit), so the pivot never landed on the clicked thing.
			const isShell = (o) => /^Shell_(Floor|Ceiling|Wall_)/i.test(o?.name || "");
			const hit = hits.find((h) => !isShell(h.object)) || hits[0];
			this._focusOnHit(hit.object, hit.point);
		});
		canvas.addEventListener("pointermove", (e) => {
			if (this._zoneScene?.isSeatDragging?.()) { this._zoneScene.updateSeatDrag(e); return; }
			if (this._dragState) this._updateFixtureDrag(e);
			else if (this._placeState) this._updatePlacement(e);
		});
		// Esc cancels an in-flight move (snap back). Capture so it beats the Edit-mode Esc handler.
		window.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && this._placeState) { e.stopImmediatePropagation(); this.cancelPlacement(); return; }
			if (this._placeState && this._ws3?.placementKey?.(this, e)) return;   // ASK-159 §2.18ep V2: Q/E turn · −/+ size
			if (e.key === "Escape" && this._zoneScene?.isSeatDragging?.()) { e.stopImmediatePropagation(); this._zoneScene.cancelSeatDrag(); return; }
			if (e.key === "Escape" && this._dragState) { e.stopImmediatePropagation(); this._cancelFixtureDrag(); }
			// ASK-088 slice-3c — Del/Backspace removes the selected PLACED luminaire (Edit only; ignored
			// while typing in a field so the Inspector's inputs aren't hijacked). removeSelectedFixture
			// self-gates to removable kinds, so this is a no-op on a baked shell fixture.
			if ((e.key === "Delete" || e.key === "Backspace") && this._cameraAuthoring && this._selectedAnchor) {
				const t = e.target;
				const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
				if (!typing) { e.preventDefault(); this.removeSelectedFixture(); }
			}
		}, { capture: true });
	}

	// ── ASK-087 slice-2: move a selected fixture by dragging ────────────────────────────────────
	// Validity is the SAME continuous rule a place uses (WS3 resolveContinuousGhost): valid at any
	// in-bounds, non-overlapping spot — including where it started. An invalid drop HARD-BLOCKS and
	// snaps back rather than committing a bad position.

	/** The point on the FIXTURE the merchant actually pressed, flattened to its base plane. Used to
	 *  anchor a drag to where it was grabbed, so the fixture stays under the cursor. */
	_fixtureTouchPoint(e, hit) {
		if (!hit?.point) return null;
		return new THREE.Vector3(hit.point.x, hit.point.y, hit.point.z);
	}

	/** World-space point under the pointer on the fixture's ground plane. */
	_dragGroundPoint(e, planeY) {
		const canvas = this.renderer?.domElement;
		if (!canvas) return null;
		const rect = canvas.getBoundingClientRect();
		const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
		this.raycaster.setFromCamera(ndc, this.camera);
		const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -planeY);
		const out = new THREE.Vector3();
		return this.raycaster.ray.intersectPlane(plane, out) ? out : null;
	}

	/** The surface under a world position — mirrors studio-v1's continuousHitUnder, including the
	 *  kapow quirk: coincident/doubled shell geometry means the FIRST down-ray hit can be a
	 *  downward-facing face (classified "ceiling"), which made every move snap back. Prefer the
	 *  topmost UP-facing hit; hits are sorted near→far (= top→down).
	 *
	 *  SHELL MESHES ONLY. Raycasting the whole shell ROOT also hit fixtures — including the fixture
	 *  being dragged, whose own top face is the nearest up-facing hit at every position. Every frame
	 *  therefore resolved to "the thing I'm holding", classified floor by the name fallback, and no
	 *  drag could ever be invalid. The surface a fixture stands on is a property of the SHELL. */
	_continuousHitUnder(pos, excludeAnchor = null) {
		// SLICE-2B — THE SUPPORT RULE. A fixture doesn't hover at the height it was picked up from: it
		// rests on whatever is beneath it. Shaun's round-4 finding was a longbox dragged off the
		// counter, still at counter height, over open floor, reading green — validity was reading the
		// surface below without ever requiring the fixture to LAND on it.
		//
		// So the ray now includes OTHER FIXTURES, not just the shell: a table top is a support
		// ("support-top"), which is what longbox/shortbox already declare in validSurfaces. The
		// dragged fixture and everything it owns are excluded — it moves with the cursor, so its own
		// top face would otherwise be the nearest hit at every position (the bug that made nothing
		// invalid before). Topmost UP-facing hit wins, which is what makes stepping off a counter
		// resolve to the floor underneath rather than staying at counter height.
		const targets = this._supportCandidateMeshes(excludeAnchor);
		if (!targets.length) return null;
		this.raycaster.set(new THREE.Vector3(pos.x, pos.y + 10, pos.z), new THREE.Vector3(0, -1, 0));
		const hits = this.raycaster.intersectObjects(targets, false).filter((h) => h.object?.isMesh && h.object.visible);
		if (!hits.length) return null;
		const nm = new THREE.Matrix3(), wn = new THREE.Vector3();
		let chosen = null;
		for (const h of hits) {
			if (!h.face || !h.object) continue;
			nm.getNormalMatrix(h.object.matrixWorld);
			wn.copy(h.face.normal).applyMatrix3(nm).normalize();
			if (wn.y >= 0.5) { chosen = h; break; }
		}
		// No UP-FACING surface beneath = nothing to stand on. Returning null makes the ghost verdict
		// null → invalid → red + snap-back, which is the "floating" case rejected by construction.
		if (!chosen) return null;
		// The REGION is what makes "off the surface" reachable: without bounds, validateContinuous
		// skips its edge-fit check (v1 graceful) and a fixture dragged past the walls, or hanging off
		// the end of a table, still reads valid.
		return {
			point: chosen.point.clone(),
			surfaceKind: this._surfaceKindFor(chosen.object),
			region: this._supportRegionFor(chosen.object, chosen.point),
			// Which fixture (if any) is acting as the support this frame — the overlap check has to
			// forgive it, see _updateFixtureDrag.
			supportAnchor: this._isShellMesh(chosen.object) ? null : this._anchorForHit(chosen.object, chosen.point),
		};
	}

	// ── ASK-106 S4 · the receiving surface for a fixture's OWN sticky face ───────────────────────
	// A fixture is not always looking down. `bottom` asks what is underneath (gravity — the shipped
	// path, unchanged); `top` asks what is ABOVE the cursor (ceiling-hang, the queued net-new leg);
	// `back` asks which wall the cursor is pointing AT. Same validator, same grammar — what changes
	// is only which surface is a candidate, which is exactly what "the sticky face touches a
	// receiving surface" means.

	/** Pointer → NDC in the canvas. Shared by every cursor raycast. */
	_pointerNdc(e) {
		const canvas = this.renderer?.domElement;
		if (!canvas || !e) return null;
		const rect = canvas.getBoundingClientRect();
		return new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
	}

	/** Up-vector for surface classification: `−normalize(scene.gravity)` (ASK-106 §4). Earth today. */
	_upVector() {
		const g = this.sceneGravity;
		return this._ws3?.upFromGravity ? this._ws3.upFromGravity(g) : { x: 0, y: 1, z: 0 };
	}

	/** World bounds of a surface mesh, on all six sides — the validator picks the two axes the
	 *  receiver's plane needs (XZ for a floor/ceiling, the wall's run × height for a wall). */
	_surfaceRegionBox(obj) {
		const b = new THREE.Box3().setFromObject(obj);
		if (b.isEmpty() || !Number.isFinite(b.min.x)) return null;
		return { xMin: b.min.x, xMax: b.max.x, yMin: b.min.y, yMax: b.max.y, zMin: b.min.z, zMax: b.max.z };
	}

	/** The receiving surface for THIS fixture's declared face. Null = nothing to stick to here. */
	_attachmentHit(fixture, target, excludeAnchor, e) {
		const face = fixture?.attachment?.face || "bottom";
		if (face === "top") return this._ceilingHitAbove(target, excludeAnchor, fixture);
		if (face === "back") return this._wallBondedHit(e, fixture);
		return this._continuousHitUnder(target, excludeAnchor);
	}

	/** Is this fixture wall-bonded (its sticky face is its BACK)? Then its target is the wall the pointer is ON, never a
	 *  ground-plane point — a ray at or above the camera's horizon meets no ground (ASK-159 §2.18gb ruling 8). */
	_isWallBonded(fixture) {
		return (fixture?.attachment?.face || "bottom") === "back";
	}

	/** 🔴 ASK-159 §2.18gb ruling 8 — ONE RULE FOR A WALL-BONDED PLACE AND MOVE: the wall the pointer is on, the item's
	 *  CENTRE at the pointer, clamped to the wall's fit by the SAME fit judgement the validator applies
	 *  (`clampToReceiverFit` — footprintOnPlane + the receiver region). Both `_updatePlacement` and `_updateFixtureDrag`
	 *  reach it through `_attachmentHit`, so a drop and the later drag agree by construction (Unreal's surface snap on
	 *  placement is the move tool's rule).
	 *
	 *  What it replaced, measured (probe-fixture-drag PP3, red on every build since ~02:34Z 2026-10-26): both paths first
	 *  asked `_dragSurfacePoint` for a GROUND-plane point and returned when there was none, so a poster aimed at eye height
	 *  — the canvas's horizon row — or above had no target at all; and the wall hit put the item's BASE at the pointer
	 *  (the pose is bbox-bottom-centre), so 4-social's one-line fallback landed a poster 50 mm over the wall top that the
	 *  drag then refused (PP3b). The pose stays base-centre: the centre-at-pointer is `y − height/2`. */
	_wallBondedHit(e, fixture) {
		const hit = this._wallHitAt(e, fixture);
		if (!hit) return null;
		const h = fixture?.placementRules?.boundingBox?.height;
		hit.pointer = hit.point.clone();   // where the pointer met the wall (stand-off applied) — kept for the record
		if (Number.isFinite(h)) hit.point.y -= h / 2;   // the CENTRE at the pointer
		const c = this._ws3?.clampToReceiverFit?.(fixture, { surfaceKind: hit.surfaceKind, point: hit.point, region: hit.region, normal: hit.normal });
		if (c?.point) { hit.point.set(c.point.x, c.point.y, c.point.z); hit.clamped = !!c.clamped; }
		return hit;
	}

	/** CEILING-HANG (ASK-106 §3.7): cast UP from the cursor's ground point and take the LOWEST
	 *  down-facing surface above it — a pendant hangs from the ceiling over the spot you point at.
	 *  The drop length is the fixture's own measured geometry, so nothing is authored per-shop. */
	_ceilingHitAbove(pos, excludeAnchor = null, fixture = null) {
		if (!pos) return null;
		const targets = this._supportCandidateMeshes(excludeAnchor);
		if (!targets.length) return null;
		this.raycaster.set(new THREE.Vector3(pos.x, pos.y + 0.05, pos.z), new THREE.Vector3(0, 1, 0));
		// ASK-111 — the UP-cast deliberately still sees a ceiling the DOLLHOUSE has hidden. Taking the
		// roof off must not delete the thing a pendant hangs from: in build mode you can still place a
		// ceiling fixture on a ceiling you cannot see, exactly as walls-down mode keeps wall items
		// placeable. The asymmetry with `_continuousHitUnder` is the whole point — you cannot REST a
		// fixture on a roof you have removed, but you can still HANG one from it. Caught by
		// probe-attachment-grammar leg 4 going red on the first dollhouse build, not by review.
		const hits = this.raycaster.intersectObjects(targets, false).filter((h) => h.object?.isMesh && this._isHangTarget(h.object));
		const nm = new THREE.Matrix3(), wn = new THREE.Vector3();
		for (const h of hits) {   // nearest-first → the lowest ceiling above the point
			if (!h.face || !h.object) continue;
			nm.getNormalMatrix(h.object.matrixWorld);
			wn.copy(h.face.normal).applyMatrix3(nm).normalize();
			if (wn.y > -0.5) continue;   // not a down-facing (ceiling-like) surface
			// DROP LENGTH — the pose reference is `bbox-bottom-centre` (what the composer renders
			// with), so a ceiling-hung fixture's stored y is its BASE: the body has to hang BELOW the
			// plane by its own measured height. Without this the pendant's base sat ON the ceiling and
			// the whole 1.18 m of it rendered above the room, out of sight — caught by the probe's
			// scene-truth leg, not by any verdict, because every rule said "valid".
			const height = fixture?.placementRules?.boundingBox?.height;
			const point = h.point.clone();
			if (Number.isFinite(height)) point.y -= height;
			return {
				point,
				surfaceKind: this._surfaceKindFor(h.object) === "ceiling" ? "ceiling"
					: (this._ws3?.classifySurfaceByNormal?.({ x: wn.x, y: wn.y, z: wn.z }, this._upVector()) || "ceiling"),
				region: this._surfaceRegionBox(h.object),
				normal: { x: wn.x, y: wn.y, z: wn.z },
				supportAnchor: null,
			};
		}
		return null;
	}

	/** WALL-PLANE (ASK-106 §3.3): the wall the cursor points at. The fixture is stood OFF the plane
	 *  by half its depth so its `back` face touches the wall rather than intersecting it — A2's one
	 *  rest pose means the asset is authored upright, so this offset is the whole of the geometry
	 *  work; auto-orient does the rest. */
	_wallHitAt(e, fixture) {
		const ndc = this._pointerNdc(e);
		if (!ndc) return null;
		this.raycaster.setFromCamera(ndc, this.camera);
		const targets = this._shellSurfaceMeshes();
		const hits = this.raycaster.intersectObjects(targets, false).filter((h) => h.object?.isMesh && h.object.visible);
		const nm = new THREE.Matrix3(), wn = new THREE.Vector3();
		const up = this._upVector();
		for (const h of hits) {
			if (!h.face || !h.object) continue;
			nm.getNormalMatrix(h.object.matrixWorld);
			wn.copy(h.face.normal).applyMatrix3(nm).normalize();
			const kind = this._ws3?.classifySurfaceByNormal
				? this._ws3.classifySurfaceByNormal({ x: wn.x, y: wn.y, z: wn.z }, up)
				: (Math.abs(wn.y) < 0.5 ? "wall" : null);
			if (kind !== "wall") continue;
			// ASK-159 §2.18ep — the stand-off is the fixture's OWN depth (its back face is local −Z), at the size it
			// lands with: the world AABB's z is its WIDTH on an east/west wall (racks hung 43 cm off the wall).
			const pr = fixture?.placementRules, k = this._placeState?.scale || fixture?.scale || 1;
			const depth = Number.isFinite(pr?.localBoundingBox?.depth) ? pr.localBoundingBox.depth * k : pr?.boundingBox?.depth;
			const point = h.point.clone().addScaledVector(wn, Number.isFinite(depth) ? depth / 2 : 0);
			return {
				point,
				surfaceKind: "wall",
				region: this._surfaceRegionBox(h.object),
				normal: { x: wn.x, y: wn.y, z: wn.z },
				supportAnchor: null,
			};
		}
		return null;
	}

	/** Everything a fixture could come to rest ON: the shell's surfaces plus every OTHER composed
	 *  fixture. Excludes the dragged fixture and everything it owns — it travels with the cursor, so
	 *  it can never be its own support. */
	_supportCandidateMeshes(excludeAnchor) {
		const excluded = excludeAnchor
			? [this._fixtureGroups?.get?.(excludeAnchor), ...this._fixtureContentsFor(excludeAnchor)].filter(Boolean)
			: [];
		const notExcluded = (m) => !excluded.some((ex) => this._isDescendantOf(m, ex));
		const out = this._shellSurfaceMeshes().filter(notExcluded);
		const seen = new Set(out);
		// Door 13 — OTHER fixtures come from the composed witness, never the rebind registry: a
		// placed derived-surface fixture (a pedestal, a counter) must be a support candidate like
		// any other, or a lamp can rest on a baked table and not on a placed one. One traverse for
		// every `__cat__`-named clone; the rebind rows still carry geometry that has no `__cat__`
		// name (GLB-authored spinner frames, decor clones).
		const root = this._shellRoot || this.stageRoot || this.scene;
		root?.traverse?.((n) => {
			if (!n.isMesh || !n.visible) return;
			const a = this._anchorFromName(n);
			if (!a || a === excludeAnchor) return;
			if (seen.has(n) || !notExcluded(n)) return;
			seen.add(n); out.push(n);
		});
		for (const rb of (this._fixtureMaterialRebinds || [])) {
			if (!rb?.anchorName || rb.anchorName === excludeAnchor) continue;
			for (const m of (rb.meshes || [])) {
				if (!m?.isMesh || !m.visible) continue;
				if (seen.has(m) || !notExcluded(m)) continue;
				seen.add(m); out.push(m);
			}
		}
		return out;
	}

	/** The XZ bounds a fixture's footprint has to fit inside to be ON this surface. The floor is the
	 *  room; a support is the supporting fixture's own extent — which is what turns "hanging off the
	 *  edge of the table" into a real rejection rather than a silent float. */
	_supportRegionFor(obj, point) {
		const name = String(obj?.name || "");
		// A SHELL SURFACE owns its own placement region — derive it straight from the surface mesh's
		// bounds, NEVER via _anchorForHit. Retirement re-walk-3 root cause: _anchorForHit's
		// containment fallback (_hostFixtureAnchorAt) resolves a SURFACE hit to a FIXTURE resting on
		// it, so the counter's region became the box's OWN footprint (region == footprint, margin 0) →
		// a box sitting squarely read "hanging over the edge" on a sub-mm overhang. A surface is never
		// bounded by the thing standing on it.
		// 🔴 ASK-129 F-104f — A FLOOR HIT'S REGION IS THE ROOM IT IS IN, INSET TO THE WALLS.
		//
		// This returned `_shellFloorRegion()`, which expands ONE box over EVERY `Shell_Floor*` mesh —
		// the union of the whole building. On a space-first shell that is three rooms wide, so
		// `validateContinuous` step 2 (which has always tested the rotated FOOTPRINT rect, not the
		// centre) was testing it against the wrong rectangle and passing anything inside the envelope.
		//
		// Measured by Coord on `store-test-2`: counter `…INST_2F6EDB31` at z −1.376 in floor-02
		// (zMin −1.5) with its rect running to z −1.576 — **7.6 cm through the wall**; bookshelf at
		// x −0.828 in floor-03 (xMin −1), rect to x −1.278 — **28 cm**. Both footprints outside their
		// room, both comfortably inside the union (z −9.5…1.5), both allowed.
		//
		// Note the asymmetry this removes rather than the special case it adds: every OTHER `Shell_`
		// mesh two lines down already uses its OWN box. The floor was the one surface that answered
		// with the building.
		if (/^Shell_Floor/i.test(name)) return this._floorRegionForMesh(obj) || this._shellFloorRegion();
		if (/^Shell_/i.test(name) || /counter/i.test(name)) {
			const sbox = new THREE.Box3().setFromObject(obj);
			if (!sbox.isEmpty()) return { xMin: sbox.min.x, xMax: sbox.max.x, zMin: sbox.min.z, zMax: sbox.max.z };
			return this._shellFloorRegion();
		}
		// A NON-shell support is a FIXTURE TOP (e.g. a table a lamp rests on) — there the fixture's own
		// footprint legitimately IS the region, and _anchorForHit resolves the table by mesh identity
		// (not the buggy containment path), so this stays correct.
		const anchor = this._anchorForHit(obj, point);
		const box = new THREE.Box3();
		if (anchor) {
			const rb = (this._fixtureMaterialRebinds || []).find((r) => r?.anchorName === anchor);
			for (const m of (rb?.meshes || [])) if (m?.visible) box.expandByObject(m);
		}
		if (box.isEmpty() && obj) box.setFromObject(obj);
		if (box.isEmpty()) return this._shellFloorRegion();
		return { xMin: box.min.x, xMax: box.max.x, zMin: box.min.z, zMax: box.max.z };
	}

	/** The shell's own surface meshes — the only geometry a placement surface can come from. */
	_shellSurfaceMeshes() {
		const root = this._shellRoot || this.stageRoot || this.scene;
		if (!root) return [];
		const out = [];
		// Counters are shell geometry a fixture stands ON, but they don't match the
		// Shell_(Floor|Ceiling|Wall_) naming _isShellMesh tests, so they were absent from the support
		// set entirely — a box dragged over the counter resolved to the FLOOR beneath it and sank.
		// A COMPOSED clone is NOT shell geometry: the counter-name test swallowed a placed
		// wooden-checkout-counter's own `__cat__` body into the shell set — where per-fixture
		// exclusion never applies — and the fixture became its own support (parity read "needs
		// floor; this is a counter" against a counter standing on the floor). Composed fixtures
		// enter the support set per-anchor in _supportCandidateMeshes, where they can be excluded.
		root.traverse((n) => { if (n.isMesh && !/__cat__/.test(n.name || "") && (this._isShellMesh(n) || /counter/i.test(n.name || ""))) out.push(n); });
		return out;
	}

	/** World-XZ bounds of the walkable floor — the region a dropped fixture's footprint must fit
	 *  inside. Cached per composed scene (invalidated by currentToken, which changes on recompose). */
	/**
	 * The room a floor mesh IS, in XZ, inset to the inner faces of the walls that bound it.
	 *
	 * 🔴 THE INSET IS THE "wall thickness respected" HALF, and it is not optional: a shell kit lays
	 * the floor UNDER its walls. Measured on `platform-shell`: `Shell_Floor` spans x[−2, 2] while
	 * `Shell_Wall_E` occupies x[1.9, 2] — so the floor's own box still allows 10 cm of a fixture
	 * inside the wall. Insetting by the walls that meet each edge gives x[−1.9, 1.9], which is the
	 * surface a merchant can actually see.
	 *
	 * Only walls that REACH an edge count. A jamb, mullion or pier standing in the middle of the room
	 * is an obstacle for the neighbour test (step 3), not a boundary — clipping the whole room to it
	 * would shrink a doorway into a wall.
	 */
	/**
	 * One mesh's OWN world box in XZ — its geometry, through its world matrix, all eight corners so a
	 * rotated mesh is measured rather than approximated.
	 *
	 * 🔴 NOT `Box3.setFromObject`, and that distinction is the whole reason this helper exists: that
	 * call expands over the object AND ITS DESCENDANTS. A shell wall that carries children returns a
	 * box the size of everything under it, which is not a wall and cannot be reasoned about as one.
	 */
	_meshWorldBoxXZ(mesh) {
		const g = mesh?.geometry;
		if (!g) return null;
		if (!g.boundingBox) g.computeBoundingBox();
		const bb = g.boundingBox;
		if (!bb) return null;
		mesh.updateWorldMatrix(true, false);
		const v = new THREE.Vector3();
		let xMin = Infinity, xMax = -Infinity, zMin = Infinity, zMax = -Infinity;
		for (const x of [bb.min.x, bb.max.x]) for (const y of [bb.min.y, bb.max.y]) for (const z of [bb.min.z, bb.max.z]) {
			v.set(x, y, z).applyMatrix4(mesh.matrixWorld);
			if (v.x < xMin) xMin = v.x;
			if (v.x > xMax) xMax = v.x;
			if (v.z < zMin) zMin = v.z;
			if (v.z > zMax) zMax = v.z;
		}
		return Number.isFinite(xMin) ? { xMin, xMax, zMin, zMax } : null;
	}

	_floorRegionForMesh(floorMesh) {
		if (!floorMesh) return null;
		this._floorRegionByMesh = this._floorRegionByMesh || new Map();
		if (this._floorRegionMeshToken !== this.currentToken) {
			this._floorRegionByMesh.clear();
			this._floorRegionMeshToken = this.currentToken;
		}
		const cached = this._floorRegionByMesh.get(floorMesh);
		if (cached !== undefined) return cached;

		const raw = this._meshWorldBoxXZ(floorMesh);
		if (!raw) { this._floorRegionByMesh.set(floorMesh, null); return null; }
		const EPS = 0.02;
		const fx = raw.xMax - raw.xMin, fz = raw.zMax - raw.zMin;
		// 🔴 EVERY TEST IS AGAINST THE **RAW** FLOOR, AND A BOUNDING WALL MUST BE THIN ON THE AXIS IT
		// BOUNDS. Both halves are paid for. The first cut clipped against the progressively mutated
		// region, so once the north wall had moved `zMin` to −1.4 the EAST wall (z −1.4…1.4, spanning
		// the whole room) "met" that edge and contributed its FAR side, 1.4, as the new `zMin` — an
		// inverted rectangle, the degenerate guard, and a silent return of the raw box. Measured on
		// test-shop as `zMin 1.5, zMax 1.399` with `Shell_Wall_E:zMinxMax` in the trace.
		//
		// Thinness is what tells an END wall from a SIDE wall: a wall bounding the z− edge is thin in
		// z. Without it, any wall touching an edge donates its opposite face to the opposite side.
		let inX0 = 0, inX1 = 0, inZ0 = 0, inZ1 = 0;
		for (const m of this._shellSurfaceMeshes()) {
			const n = m?.name || "";
			// The kit's boundary vocabulary. `Shell_Door_*` counts: a closed door leaf is a wall until
			// something opens it, and the F-101g walk measured it blocking exactly like one.
			if (!/^Shell_(Wall|Door)/i.test(n)) continue;
			if (m.visible === false) continue;
			const w = this._meshWorldBoxXZ(m);
			if (!w) continue;
			// Must overlap this room's floor in BOTH axes to be one of its walls at all.
			if (w.xMax <= raw.xMin + EPS || w.xMin >= raw.xMax - EPS) continue;
			if (w.zMax <= raw.zMin + EPS || w.zMin >= raw.zMax - EPS) continue;
			const tx = w.xMax - w.xMin, tz = w.zMax - w.zMin;
			if (tx < fx * 0.5) {
				if (w.xMin <= raw.xMin + EPS) inX0 = Math.max(inX0, w.xMax - raw.xMin);
				if (w.xMax >= raw.xMax - EPS) inX1 = Math.max(inX1, raw.xMax - w.xMin);
			}
			if (tz < fz * 0.5) {
				if (w.zMin <= raw.zMin + EPS) inZ0 = Math.max(inZ0, w.zMax - raw.zMin);
				if (w.zMax >= raw.zMax - EPS) inZ1 = Math.max(inZ1, raw.zMax - w.zMin);
			}
		}
		const region = {
			xMin: raw.xMin + Math.max(0, inX0), xMax: raw.xMax - Math.max(0, inX1),
			zMin: raw.zMin + Math.max(0, inZ0), zMax: raw.zMax - Math.max(0, inZ1),
		};
		// A degenerate inset means something swallowed the room — keep the raw floor rather than hand
		// the validator an impossible rectangle that would refuse every drop with the wrong reason.
		// 🔴 AND SAY SO. This branch fired silently on the first two cuts and cost two measurement
		// passes. A fallback nobody can see is how a correct-looking function does nothing.
		const ok = region.xMax - region.xMin > 0.05 && region.zMax - region.zMin > 0.05;
		if (!ok) console.warn(`[shell] floor region for ${floorMesh.name || "(unnamed)"} collapsed under its walls — using the raw floor box`);
		const out = ok ? region : { ...raw };
		this._floorRegionByMesh.set(floorMesh, out);
		return out;
	}

	_shellFloorRegion() {
		if (this._floorRegionToken === this.currentToken && this._floorRegion) return this._floorRegion;
		const box = new THREE.Box3();
		let found = 0;
		for (const m of this._shellSurfaceMeshes()) {
			if (!/^Shell_Floor/i.test(m.name || "")) continue;
			box.expandByObject(m);
			found++;
		}
		this._floorRegionToken = this.currentToken;
		this._floorRegion = found ? { xMin: box.min.x, xMax: box.max.x, zMin: box.min.z, zMax: box.max.z } : null;
		return this._floorRegion;
	}

	_surfaceKindFor(obj) {
		const n = obj?.name || "";
		if (/Shell_Floor/i.test(n)) return "floor";
		// VOCAB (Coord flat-registry ruling 2026-07-24): a SHELL counter emits "counter-top"; a
		// fixture/table TOP stays "support-top" (below). This is what Content's manifest validSurfaces
		// distinguish — longbox = [floor, counter-top] (green on the counter, correctly NOT on the
		// pile-table); candle = [counter-top, support-top, floor] (all three). It supersedes the earlier
		// counter→support-top collapse (which existed only because no rule listed counters back then).
		if (/counter/i.test(n)) return "counter-top";
		// ASK-106 S4: the ceiling is its OWN receiver kind, not a wall. It was folded into "wall"
		// here, which is harmless while nothing hangs from ceilings and fatal the moment something
		// does — a pendant (receivers: [ceiling]) would be refused by the only surface it accepts.
		if (/Shell_Ceiling/i.test(n)) return "ceiling";
		if (/Shell_Wall_/i.test(n)) return "wall";
		// Not shell geometry → it's another fixture, and standing on one is "support-top" (the kind
		// longbox/shortbox/book-row already declare in validSurfaces). Classifying these as "floor",
		// as the old fallback did, let anything rest on anything.
		if (!this._isShellMesh(obj)) return "support-top";
		return "floor";
	}

	/** Where the pointer is on the SURFACES, not on an invented plane.
	 *
	 *  The drag used to map the pointer onto a horizontal plane at the height the fixture was picked
	 *  up from. That silently makes raised surfaces unreachable: aiming at a counter top maps, by
	 *  parallax, to a floor point well beyond the counter, so the fixture lands past it and never on
	 *  it. A merchant aiming at the counter watched the box refuse to go back on it — which is the
	 *  same grammar the WS3 place flow already uses (raycast the surfaces, place at the hit).
	 *  Falls back to the pick-up plane when the pointer is over nothing, so dragging into empty space
	 *  still tracks the cursor and is judged invalid rather than freezing. */
	_dragSurfacePoint(e, excludeAnchor, planeY) {
		const canvas = this.renderer?.domElement;
		if (!canvas) return null;
		const rect = canvas.getBoundingClientRect();
		const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
		this.raycaster.setFromCamera(ndc, this.camera);
		const targets = this._supportCandidateMeshes(excludeAnchor);
		const hits = this.raycaster.intersectObjects(targets, false).filter((h) => h.object?.isMesh && h.object.visible);
		const nm = new THREE.Matrix3(), wn = new THREE.Vector3();
		for (const h of hits) {
			if (!h.face || !h.object) continue;
			nm.getNormalMatrix(h.object.matrixWorld);
			wn.copy(h.face.normal).applyMatrix3(nm).normalize();
			if (wn.y >= 0.5) return h.point.clone();   // the nearest up-facing surface you're pointing AT
		}
		return this._dragGroundPoint(e, planeY);
	}

	_beginFixtureDrag(e) {
		const anchor = this._selectedAnchor;
		if (!anchor) return;
		const canvas = this.renderer?.domElement;
		const rect = canvas.getBoundingClientRect();
		const ndc = new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1);
		this.raycaster.setFromCamera(ndc, this.camera);
		const hits = this.raycaster.intersectObjects(this._pickableSceneChildren(), true).filter((h) => h.object?.isMesh && h.object.visible);
		const hit = hits.find((h) => !this._isShellMesh(h.object)) || hits[0];
		if (!hit || this._anchorForHit(hit.object, hit.point) !== anchor) return;   // not the selected fixture → orbit

		const base = this._fixtureBaseCentre(anchor);
		if (!base) return;
		// WHERE YOU TOUCHED THE FIXTURE — not where a ray through it lands.
		// _dragSurfacePoint deliberately excludes the dragged fixture, which is right for tracking a
		// support mid-drag but wrong at the moment of GRABBING: at press the fixture is under the
		// cursor, so the ray passes straight through it and returns the floor somewhere behind. At a
		// natural low angle that point can be a metre away, and the resulting grab offset shifted every
		// subsequent target by that much — the pointer sat on the counter while the fixture's target
		// was off in space, so the drop found no surface at all and fell to the floor.
		// The grab point is the actual touch on the fixture, projected onto its base plane.
		const grab = this._fixtureTouchPoint(e, hit) || this._dragSurfacePoint(e, anchor, base.y);
		if (!grab) return;
		this._ensureWs3();   // editor-only validity rules — see _ensureWs3 for why this is lazy
		// 🔴 ASK-129 §E2E104 G1 — WARM THE MANIFEST THE MOVE IS ABOUT TO BE JUDGED BY.
		//
		// `beginPlacement` AWAITS `_ensureFixtureManifest` (line ~7095); this path never asked for it at
		// all. When the manifest has not landed, `readAttachment(null)` refuses with "This fixture's
		// placement rules haven't loaded yet" — which is what a merchant met when she tried to move a
		// fixture she had just placed, with no way forward: nothing was loading it.
		//
		// A pointer handler cannot await, and it does not need to: drag validity is recomputed every
		// frame in `_updateFixtureDrag`, so starting the load here means the gesture RECOVERS as soon as
		// it lands rather than staying refused for its whole life. The unreachability comes from
		// `ensureFloorClearances` on entering Edit (studio/index.js); this is the belt for a kind that
		// arrives after that.
		void this._ensureFixtureManifest(this._kindForAnchor(anchor)).catch(() => {});
		this._fixtureReturns?.delete(anchor);   // a fresh grab cancels any in-flight snap-back glide
		// OrbitControls must not also consume this gesture, or the camera orbits while the fixture moves.
		const wasRotate = this.controls ? this.controls.enableRotate : false;
		if (this.controls) this.controls.enableRotate = false;
		const grabOffset = base.clone().sub(grab);
		grabOffset.y = 0;   // height comes from the support each frame, never from where you grabbed
		// lastValid seeds to the origin (always a valid pose — it's where the fixture sits). The drag
		// keeps the fixture AT lastValid whenever the cursor is over an invalid spot, so the object
		// never slides into walls/air and never has to teleport home on release. easedY smooths the
		// support Y so crossing a counter/floor edge glides instead of popping. (Retirement eyes-on B:
		// floor drag was already smooth; this brings red-recovery + counter/wall to the same quality.)
		this._dragState = { anchor, origin: base.clone(), grabOffset, wasRotate, valid: true, moved: false,
			easedY: base.y, exactY: base.y };
	}

	_updateFixtureDrag(e) {
		const d = this._dragState;
		if (!d) return;
		// The descriptor MUST carry a resolved attachment contract: validateContinuous refuses a bare
		// {kind} (no contract → no placement), so this is resolved before the hit, which now depends
		// on it — a wall fixture is dragged along the wall, not along the floor beneath it.
		const fixture = this._placementFixtureFor(d.anchor);
		// ASK-159 §2.18gb ruling 8 — a wall-bonded item is moved by the placement's own rule (the wall hit, its centre at
		// the pointer, the fit clamp), so it needs no pick-up-plane point; every other fixture keeps the plane mapping.
		const p = this._dragSurfacePoint(e, d.anchor, d.origin.y);
		if (!p && !this._isWallBonded(fixture)) return;
		let target = p ? p.clone().add(d.grabOffset) : null;
		// The pointer→world mapping stays on the pick-up plane (a stable, predictable XZ mapping); the
		// fixture's HEIGHT comes from whatever it is attached to, not from where it was picked up.
		const hit = this._attachmentHit(fixture, target, d.anchor, e);
		if (!target) { if (!hit) return; target = hit.point.clone(); }
		// Neighbours were hardwired to [] — so the overlap/clearance branch of validateContinuous
		// could never fire and a fixture dragged straight through another one read green. They're
		// resolved once per drag (geometry doesn't change mid-gesture) against the live scene.
		d.neighbours ||= this._dragNeighbours(d.anchor, d.origin, fixture);
		// Until the editor-only module resolves (first drag, ~1 frame) the fixture HOLDS — an unjudged pose is not an
		// accepted one (ASK-159 §1 item 3: a quick drag released before WS3 landed used to commit wherever it was).
		// RESTING ON SOMETHING IS NOT OVERLAPPING IT. The support's footprint necessarily contains the
		// fixture's, so leaving it in the neighbour set would make every "put the box on the table"
		// drop fail the clearance check — the support rule and the overlap rule would cancel out and
		// nothing could ever be placed on anything.
		const neighbours = (d.neighbours || []).filter((n) => n.anchorName !== hit?.supportAnchor);
		let ghost = this._ws3
			? this._ws3.resolveContinuousGhost({ hit, fixture, neighbours, modifierFree: e.altKey })
			: { verdict: { valid: false }, status: "Checking where it can go…" };   // unjudged is not accepted (123qy9rtpyh): it holds
		// Assist: if the pose under the cursor is refused, glide to the nearest valid one within reach
		// (unless the merchant holds the free-place modifier). The rule is unchanged — an invalid pose
		// is still invalid; it is moved to a valid one before judging, rather than simply rejected.
		let restPoint = hit ? hit.point : null;
		let assisted = false;
		if (!(ghost.verdict && ghost.verdict.valid) && !e.altKey) {
			const a = this._seekAssistedPose(target, fixture, neighbours, d.anchor);
			if (a) {
				ghost = a.ghost;
				restPoint = a.point;
				target.x = a.point.x; target.z = a.point.z;
				assisted = true;
			}
		}
		d.valid = !!(ghost.verdict && ghost.verdict.valid);
		// 🔴 card 123qy9rtpyh — and INSIDE THE SHOP: not through a wall, not through the ceiling, not off the shell.
		const out = d.valid && this._ws3?.fixtureContainment?.(this, d.anchor, { x: ghost.position?.x ?? target.x, y: restPoint ? restPoint.y : d.origin.y, z: ghost.position?.z ?? target.z }, { yaw: ghost.bondYaw ?? undefined });   // judged at the yaw it will hang at
		if (out) { d.valid = false; ghost = { ...ghost, status: `✕ ${out.reason}` }; }
		else if (!hit && !d.valid) ghost = { ...ghost, status: "✕ That's outside the shop — there's no floor there." };
		d.status = ghost.status;
		d.moved = true;
		// ASK-106 §1 — a MOVE re-records the attachment too: the fixture may have changed receiver
		// (counter → floor), and a record that only ever reflects the first placement is a record of
		// where something used to be.
		if (d.valid && this._ws3?.placementAttachmentRecord) {
			d.attachment = this._ws3.placementAttachmentRecord(fixture, ghost.surfaceKind || hit?.surfaceKind || null);
		}
		// SLICE-2B: Y comes from the SUPPORT beneath the footprint (counter→floor as you cross an edge,
		// table lifts it up). Retirement eyes-on B: EASE the VISUAL Y toward it instead of hard-setting,
		// so a surface-height change glides rather than pops (the counter/wall jank). The EXACT support
		// Y is remembered separately (exactY) and is what a commit uses — the ease is cosmetic only, so
		// a release mid-settle still lands the fixture precisely on the surface.
		const exactY = restPoint ? restPoint.y : d.origin.y;
		d.exactY = exactY;
		d.easedY += (exactY - d.easedY) * 0.4;
		if (Math.abs(exactY - d.easedY) < 0.002) d.easedY = exactY;   // land exactly, no asymptotic crawl
		// WYSIWYG (retirement re-walk-3): render the fixture AT the validated pose (ghost.position),
		// not the raw cursor target. With the absolute grid dropped, ghost.position == the raw point
		// except where a magnet aligns it to a real neighbour — so the floor follow stays smooth, and
		// the box can never render in a spot different from the one validity judged (the divergence).
		const placeX = ghost.position ? ghost.position.x : target.x;
		const placeZ = ghost.position ? ghost.position.z : target.z;
		// A refused pose is never DRAWN: the fixture holds at its last legal pose (the Sims/Planet Coaster build-mode
		// rule) while the red patch below shows where the cursor is — it was drawn outside, in a wall, or floating.
		// ASK-159 §2.18dr — a wall-bonded fixture FACES THE ROOM from whichever wall it is on: the yaw its surface gives it
		// (the same `bondYaw` a placement commits), re-derived as it crosses onto another wall — a poster dragged from the
		// north wall to the west one used to keep its north yaw and hang face-to-the-plaster.
		this._ws3?.rebondDrag?.(this, d, ghost);
		if (d.valid) { this.moveFixtureTo(d.anchor, { x: placeX, y: d.easedY, z: placeZ }); d.lastPose = { x: placeX, y: exactY, z: placeZ }; }
		else if (d.lastPose) this.moveFixtureTo(d.anchor, d.lastPose);
		// ASK-128 S3 — THE FLOOR JUST CHANGED, so anything derived from it re-derives NOW, in this
		// frame, from the pose that was just rendered. A merch zone shrinking around the shelf a
		// merchant is still holding is the whole point of a derived shape (M2), and a shape that
		// waited for the write's response would arrive a round-trip after the fixture did.
		this.onFloorChanged?.({ anchorName: d.anchor, phase: "drag" });
		this._tintSelection(d.valid ? 0x35c46a : 0xe5484d);   // validity on the object itself, not just a status line
		// AAA surface + footprint-contact highlight: show the valid region + the footprint patch on
		// the surface, coloured by validity — the merchant SEES the fit, and a patch outside the
		// region is the divergence made visible.
		const fbb = fixture?.placementRules?.boundingBox;
		this._updateContactHighlight({ region: hit?.region, surfaceY: exactY,
			footprint: { cx: placeX, cz: placeZ, w: fbb?.width, d: fbb?.depth }, valid: d.valid });
		// SAY WHY. The verdict already carries a plain-English reason ("Needs 2 cm clearance — Long Box
		// 1 is too close") and nothing was ever displaying it: a merchant saw red and had to guess,
		// which cost a diagnosis round-trip per rejection.
		this._showDragHint(ghost.status, d.valid, assisted);
		this.onFixtureDragStatus?.(ghost.status);
	}

	_endFixtureDrag(e) {
		const d = this._dragState;
		if (!d) return;
		if (this.controls) this.controls.enableRotate = d.wasRotate;
		this._dragState = null;
		this._tintSelection(null);
		this._hideDragHint();
		this._hideContactHighlight();
		if (!d.moved) return;                       // a press with no movement is a click, not a move
		// Invalid drop → snap back HOME (the ratified round-4 contract Shaun passed). Retirement
		// eyes-on B: ANIMATE the return instead of teleporting — a ~160ms eased glide reads as
		// "it didn't fit, it went back", not a jarring pop. Commits nothing.
		if (!d.valid) { if (d.rebonded) this.rotateFixtureTo(d.anchor, d.originYaw); this._ws3?.announcePlacementRefusal?.(this, d.anchor, d.status); this._animateFixtureReturn(d.anchor, d.origin.clone()); this.onFloorChanged?.({ anchorName: d.anchor, phase: "cancelled" }); return; }
		// Valid drop → land EXACTLY on the support (finish any mid-settle ease) before committing, so
		// the persisted Y is the surface height, not a partially-eased value.
		this.moveFixtureTo(d.anchor, { x: d.lastPose.x, y: d.exactY, z: d.lastPose.z });
		// The COMMIT frame: the fixture is at its final pose and nothing has been sent yet, so this is
		// the frame in which the zone must already have shrunk (ASK-128 M2 · the P4-ATTRIB
		// one-transition rule). The server's authoritative shape replaces this one when it answers.
		this.onFloorChanged?.({ anchorName: d.anchor, phase: "commit" });
		const base = this._fixtureBaseCentre(d.anchor);
		if (!base) return;
		// Guardrail (b): a moved instance persists its transform AND the §2 free-place provenance flag,
		// so the mint stops re-deriving it from the shell anchor.
		// The fixture's CURRENT yaw rides along. Without it the persisted row stores rotation.y = 0
		// (GCG normalises one in regardless), and §2's per-field `row.rotation ?? anchor.rotation`
		// then lets that 0 beat the anchor's authored yaw — a move would straighten any fixture on a
		// rotated anchor at the next reload.
		const entry = this._anchorEntryFor(d.anchor);
		const rotationY = d.rebonded ? this.getFixtureYaw(d.anchor) : (Number.isFinite(entry?.rotationY) ? entry.rotationY : null);   // a re-bonded wall item persists the wall's yaw
		this.onFixtureMoved?.({ anchorName: d.anchor, position: { x: base.x, y: base.y, z: base.z }, rotationY, ...freePlaceKnobs("merchant-move", d.attachment) });
	}

	_cancelFixtureDrag() {
		const d = this._dragState;
		if (!d) return;
		if (this.controls) this.controls.enableRotate = d.wasRotate;
		this._dragState = null;
		this._tintSelection(null);
		this._hideDragHint();
		this._hideContactHighlight();
		if (d.rebonded) this.rotateFixtureTo(d.anchor, d.originYaw);   // home means its old facing too
		this._animateFixtureReturn(d.anchor, d.origin.clone());   // Esc-cancel also glides home
	}

	/** Eased glide of a fixture back to a target base-centre — the smooth form of an invalid-drop /
	 *  Esc snap-back (retirement eyes-on B). Driven by the animate loop via _tickFixtureReturns. */
	_animateFixtureReturn(anchorName, toBase) {
		const from = this._fixtureBaseCentre(anchorName);
		if (!from) { this.moveFixtureTo(anchorName, toBase); return; }
		if (from.distanceTo(toBase) < 1e-4) { this.moveFixtureTo(anchorName, toBase); return; }
		this._fixtureReturns ||= new Map();
		this._fixtureReturns.set(anchorName, { from, to: toBase, t: 0, dur: 0.16 });   // 160ms
	}

	_tickFixtureReturns(dt) {
		if (!this._fixtureReturns?.size) return;
		for (const [anchor, a] of this._fixtureReturns) {
			a.t += dt;
			const k = Math.min(1, a.t / a.dur);
			const e = 1 - (1 - k) * (1 - k);   // ease-out quad
			const x = a.from.x + (a.to.x - a.from.x) * e;
			const y = a.from.y + (a.to.y - a.from.y) * e;
			const z = a.from.z + (a.to.z - a.from.z) * e;
			this.moveFixtureTo(anchor, { x, y, z });
			if (k >= 1) this._fixtureReturns.delete(anchor);
		}
	}

	// ─── Place a NEW fixture (ASK-088 slice-3b) ──────────────────────────────────────────────────
	// Move (above) repositions an EXISTING fixture; place instantiates one that doesn't exist yet. It
	// reuses the exact slice-2b/2c support+validity grammar (a ghost that rests on whatever's beneath
	// it, judged by the same ruled validator), then commits through `onFixturePlaced` (studio POSTs
	// /instances → server mints instanceId + anchorName) and renders the real instance live via the
	// single-instance path recomposeTemplate doesn't cover. Ghost geometry is unregistered, so it is
	// invisible to the support/pick raycasts (`_supportCandidateMeshes` walks the registry) — it can
	// never be its own support and never blocks itself.
	async beginPlacement(fixtureKind, { fitoutLineId = null } = {}) {   // ASK-152 P3: a placement started from a fit-out line carries its id
		if (!this._cameraAuthoring || !fixtureKind || !this._fixtureCatalogueCached?.[fixtureKind]) return false;
		this.cancelPlacement();   // one placement at a time
		this._ensureWs3();
		await this._ensureFixtureManifest(fixtureKind);   // real validSurfaces + castShadow (Content's live manifest)
		let gltf;
		try {
			gltf = await loadFixtureCatalogue({ catalogue: this._fixtureCatalogueCached, kind: fixtureKind, gltfLoader: this.gltfLoader });
		} catch (err) {
			console.warn(`[place] failed to load ${fixtureKind}:`, err?.message || err);
			return false;
		}
		const ref = this._catalogueSourceRef(gltf);
		if (!ref) return false;
		const ghost = cloneInWorldFrame(ref.srcBody);   // the drop renders the same node in the same frame (ASK-159 §2.18gb)
		ghost.name = `__place_ghost__${fixtureKind}`;
		ghost.traverse((n) => {
			if (!n.isMesh || !n.material) return;
			const skin = (m) => { const c = m.clone(); c.transparent = true; c.opacity = 0.5; c.depthWrite = false; return c; };
			n.material = Array.isArray(n.material) ? n.material.map(skin) : skin(n.material);
			n.castShadow = false; n.receiveShadow = false;
		});
		// The ghost lives on the EDITOR LAYER — excluded from picking + room-bounds + Play (its
		// visibility is the mode gate), so a preview can never be selected, block a raycast, or leak
		// into the customer view. Placement is Edit-only, so the layer is already visible.
		(this._ensureEditorLayer() || this._shellRoot || this.scene).add(ghost);
		this._placeState = { kind: fixtureKind, ghost, srcBodyPos: ghost.position.clone() /* the clone's own world-frame position, not the node's local one */, cataRef: ref.cataRef, valid: false, pose: null, fitoutLineId };
		// 🔴 ASK-137 F-114a — ONE SOURCE FOR THE OFF-SURFACE LINE, AND IT NAMES THE RULE.
		//
		// This was stage.js's OWN copy of "move over a shop surface" — a second rendering of the
		// state `resolveContinuousGhost` already describes (a live placement with no hit yet), which
		// every subsequent pointer frame then overwrote from WS3. E2E114 step 4 read that generic
		// line while standing on a floor holding a wall shelf: unactionable, and for a wall-mount
		// asset actively misleading, because she WAS over a shop surface — the wrong kind of one.
		// WS3's version names the surface the fixture needs ("move over a wall"), from the same
		// `mountTargetPhrase` as the refusal, so the hint and the rejection cannot drift apart.
		//
		// Asking WS3 for the opening hint also moves this copy off the CUSTOMER path (placement is
		// Edit-only, WS3 is a lazy editor chunk) — the budget gate measured the duplicate at +0.1 KB
		// over a ceiling with zero headroom, and the AAA answer to a duplicated string is one source,
		// not a raised ceiling. The label comes from WS3 too, so a merchant cannot be shown one name
		// on the first frame and another on the second.
		await this._ws3Loading;   // fire-and-forget above; the opening hint is the one caller that needs it resolved
		if (this._placeState?.ghost !== ghost) return false;   // cancelled / superseded while WS3 loaded
		// ASK-159 §2.18ep V2 — the held ghost carries a POSE (turn + size) the merchant sets before the drop.
		this._ws3?.initPlacementPose?.(this, this._placeState);
		this._placeState.detachGestures = this._ws3?.attachPlacementGestures?.(this);
		this._showDragHint(this._ws3?.openHint(this._placementFixtureForKind(fixtureKind)), false);
		return true;
	}

	_updatePlacement(e) {
		const st = this._placeState;
		if (!st) return;
		st.lastEvt = { clientX: e.clientX, clientY: e.clientY, altKey: !!e.altKey };   // a pose key re-judges at the same pointer
		this._ws3?.poseGhost?.(st, null);   // turn + size first, so the footprint below is the one that lands
		const meshes = [];
		st.ghost.traverse((n) => { if (n.isMesh) meshes.push(n); });
		const fixture = this._placementFixtureForKind(st.kind, meshes);
		// ASK-159 §2.18gb ruling 8 — a wall-bonded ghost needs no ground point: its target is the wall hit.
		let target = this._dragSurfacePoint(e, null, 0);
		if (!target && !this._isWallBonded(fixture)) return;
		// ASK-106 S4 — the candidate surface follows the fixture's OWN face (a pendant looks up at
		// the ceiling, a sconce at the wall the cursor is on, a longbox at what's underneath).
		const hit = this._attachmentHit(fixture, target, null, e);
		if (!target) { if (!hit) return; target = hit.point.clone(); }
		const neighbours = this._placementNeighbours();
		let ghost = this._ws3
			? this._ws3.resolveContinuousGhost({ hit, fixture, neighbours, modifierFree: e.altKey })
			: { position: hit ? { x: target.x, y: hit.point.y, z: target.z } : null, verdict: { valid: false }, status: "Checking where it can go…" };
		let assisted = false;
		// Same snap-as-assist as a move: if the pose under the cursor is refused, glide to the nearest
		// valid one within reach (unless the free-place modifier is held).
		if (!(ghost.verdict && ghost.verdict.valid) && !e.altKey) {
			const a = this._seekAssistedPose(target, fixture, neighbours, null);
			if (a) { ghost = a.ghost; assisted = true; }
		}
		const pose = ghost.position;
		st.valid = !!(ghost.verdict && ghost.verdict.valid) && !!pose;
		const out = st.valid && this._ws3?.poseContainment?.(this, fixture, pose, ghost.bondYaw ?? st.yaw ?? 0, st.scale || 1);   // inside the shop (123qy9rtpyh)
		if (out) { st.valid = false; ghost = { ...ghost, status: `✕ ${out.reason}`, verdict: { valid: false, reason: out.code, hint: out.reason } }; }
		st.pose = st.valid ? { x: pose.x, y: pose.y, z: pose.z } : null;
		// KEEP THE VERDICT, not just the boolean it collapses to. The resolver already decided WHY a
		// spot is refused — `surface_mismatch`, a clearance number, an overlap — and every one of those
		// reasons was being thrown away one line above, leaving `valid: false` and nothing else. So a
		// blocked placement could not explain itself to a merchant OR to a gate: the wall half of
		// probe-attachment-grammar reported "not placed · valid=false · null" across six runs and no
		// amount of re-running could say more, because the answer no longer existed by the time anyone
		// could look. The refusal hints at legs 3/3b prove this path HAS good words for its refusals.
		st.verdict = ghost.verdict ?? null;
		// ASK-137 ruling 1 — the bonded yaw, resolved where the receiving surface is known (the same
		// reason the attachment record is decided here). null = no yaw to derive, NOT zero.
		st.bondYaw = ghost.bondYaw ?? null;
		// The per-instance attachment record, and the bonded yaw, both come from the resolver that knew
		// the receiving surface — not re-derived here from the pose, which would be a second opinion
		// about one placement (and, on this file, editor grammar on the customer path).
		st.attachment = st.valid ? ghost.attachment ?? null : null;
		if (st.valid) st.lastValid = { ...st.pose };
		const drawAt = st.valid ? pose : (st.lastValid || pose);   // a refused pose is never drawn once a legal one was
		// WYSIWYG: preview the pose that commits — the bonded yaw, else hers; her size; about the base-centre.
		if (st.baseScale) { if (drawAt) this._ws3.poseGhost(st, drawAt); }
		else if (drawAt) { this._positionGhost(drawAt.x, drawAt.y, drawAt.z); if (st.bondYaw !== null) st.ghost.rotation.y = st.bondYaw; }
		this._tintGhost(st.valid);
		this._showDragHint(this._ws3?.placementStatus?.(this, st, ghost.status) ?? ghost.status, st.valid, assisted);
	}


	/** Place the ghost's base-centre at (x,y,z) — same base-centre convention as a real clone. */
	_positionGhost(x, y, z) {
		const st = this._placeState;
		if (!st) return;
		const off = new THREE.Vector3(x, y, z).sub(st.cataRef);
		st.ghost.position.copy(st.srcBodyPos).add(off);
		st.ghost.updateMatrixWorld(true);
	}

	/** Green/red validity outline around the ghost (mirrors the move's selection tint). ALPHA006 F2 (ASK-159 §2.18fv): the
	 *  outline is the ghost's OWN box drawn as the ghost's CHILD — one transform turns and sizes mesh and outline together;
	 *  it was the ghost's world AABB, which swelled and shrank as a turned shape swung while a round mesh looked unturned. */
	_tintGhost(valid) {
		const st = this._placeState;
		if (!st) return;
		if (!st.ghostHelper) {
			const lb = this._ws3?.ghostLocalBox?.(st);
			const box = lb ? new THREE.Box3(lb.min, lb.max) : new THREE.Box3().setFromObject(st.ghost);
			if (box.isEmpty()) return;
			st.ghostBox = box;
			st.ghostHelper = new THREE.Box3Helper(st.ghostBox, valid ? 0x35c46a : 0xe5484d);
			st.ghostHelper.name = "__place_ghost_box__";
			st.ghostHelper.renderOrder = 9999;
			st.ghostHelper.raycast = () => {};
			if (st.ghostHelper.material) { st.ghostHelper.material.depthTest = false; st.ghostHelper.material.transparent = true; }
			if (lb) st.ghost.add(st.ghostHelper);   // in the ghost's frame: its turn + size ARE the outline's
			else (this._ensureEditorLayer() || this._shellRoot || this.scene).add(st.ghostHelper);
		} else if (st.ghostHelper.parent !== st.ghost) {
			st.ghostBox.copy(new THREE.Box3().setFromObject(st.ghost));   // the world-frame fallback (WS3 not loaded) stays live
		}
		if (st.ghostHelper.material) st.ghostHelper.material.color.set(valid ? 0x35c46a : 0xe5484d);
	}

	/** Commit the pending placement. Renders the real instance live at the server-minted anchor and
	 *  returns the created row (or null if invalid / the persist declined / no handler). */
	async commitPlacement() {
		const st = this._placeState;
		if (!st || !st.valid || !st.pose) return null;
		const kind = st.kind;
		const pose = { ...st.pose };
		const attachment = st.attachment || null;   // ASK-106 §1 — what stuck, to what, and how held
		this._exitPlacement();
		// coord ASK-180 §3.18 item 1 — whoever started this placement hears how it ENDED (the written row, or null when
		// nothing was written): "Replace stand-in" clears the line's pose only on a write and puts the box back otherwise.
		const settle = (r) => { try { Promise.resolve(st.onSettle?.(r || null)).catch((e) => console.warn("[place] settle:", e?.message || e)); } catch (e) { console.warn("[place] settle:", e?.message || e); } return r || null; };
		if (typeof this.onFixturePlaced !== "function") {
			console.warn("[place] no onFixturePlaced handler — placement not persisted");
			return settle(null);
		}
		let row;
		// 🔴 ASK-137 ruling 1 — THE YAW BONDING RESOLVED, not a hardcoded zero.
		//
		// This read `rotationY: 0` on both the persist and the render below, which is why E2E114 step 5
		// put Vanessa's wall shelf on the wall SIDE-ON: `orientationForFace` has demanded auto-orient
		// for `back`/`top` since ASK-106 (spec §3.5) and returns the string "wall-mount", but nothing
		// ever turned that string into an angle. Her hand correction was exactly 1.5707963267948966 —
		// the number `bondedYaw` returns for that wall.
		//
		// `null` means the face is not yaw-orientable (a vertical normal — gravity already aligned it),
		// and there 0 is the right committed value: a fresh placement has no merchant yaw yet. On
		// RELOAD the stored `rotation.y` is what renders (`renderPlacedRow`), so a merchant rotation
		// after this still wins — D-067, the merchant's own address beats a derived one.
		const yaw = st.bondYaw ?? (Number.isFinite(st.yaw) ? st.yaw : 0);   // ASK-159 §2.18ep V2: her turn when no surface decides
		const uScale = st.scale !== 1 && st.scale > 0 ? st.scale : null;
		try { row = await this.onFixturePlaced({ fixtureKind: kind, position: pose, rotationY: yaw, attachment, uniformScale: uScale, ...(st.fitoutLineId ? { fitoutLineId: st.fitoutLineId } : {}) }); }
		catch (err) { console.warn(`[place] persist of ${kind} failed:`, err?.message || err); return settle(null); }
		if (!row?.anchorName) { console.warn(`[place] ${kind} not persisted — no row/anchorName returned`); return settle(null); }
		const anchor = {
			name: row.anchorName, index: 0,
			position: new THREE.Vector3(pose.x, pose.y, pose.z),
			scale: new THREE.Vector3(1, 1, 1), rotationY: yaw,
			fixtureKind: kind, extras: { ...(row.knobs || {}) }, instanceId: row.instanceId,
		};
		await this._renderSingleCatalogueInstance({ kind, anchor, fixtureCatalogue: this._fixtureCatalogueCached, token: this.currentToken });
		// The row's rotation IS the fixture's current yaw, so record it — `renderPlacedRow` learned this
		// the hard way (ASK-129 F5): without it the rotation control reads 0 beside a fixture that is
		// visibly turned, and her first nudge is measured from the wrong origin.
		(this._fixtureYaw ||= new Map()).set(row.anchorName, yaw);
		if (uScale) this.scaleFixtureTo(row.anchorName, uScale);   // lands at the size she held it at
		this.selectFixture?.(row.anchorName);
		console.log(`[place] ${kind} placed → ${row.anchorName} (instanceId ${row.instanceId})`);
		return settle(row);
	}

	/** Render a placed instance from a full /instances row — the commitPlacement tail, reusable.
	 *  Undo-of-remove uses it: a removed row is re-POSTed (fresh instanceId/anchorName) and handed
	 *  here to re-materialise in the scene. Returns the rendered anchorName, or null. */
	async renderPlacedRow(row) {
		try { return await this._renderPlacedRowNow(row); }
		finally { this._notifyContents(); }
	}

	async _renderPlacedRowNow(row) {
		if (!row?.anchorName || !row?.fixtureKind) return null;
		const kind = row.fixtureKind;
		const rY = Number.isFinite(row?.rotation?.y) ? row.rotation.y : 0;
		const anchor = {
			name: row.anchorName, index: 0,
			position: new THREE.Vector3(row.position?.x || 0, row.position?.y || 0, row.position?.z || 0),
			scale: new THREE.Vector3(1, 1, 1), rotationY: rY,
			fixtureKind: kind, extras: { ...(row.knobs || {}) }, instanceId: row.instanceId,
		};
		await this._renderSingleCatalogueInstance({ kind, anchor, fixtureCatalogue: this._fixtureCatalogueCached, token: this.currentToken });
		// ASK-129 §E2E103 F5 — the row's rotation IS the fixture's current yaw, so record it. Without
		// this the fix survives the edit and dies on the next reload: the fixture would come back
		// rotated with the control reading 0, which is the same defect one round-trip later.
		(this._fixtureYaw ||= new Map()).set(row.anchorName, rY);
		// ASK-128 S3 — a fixture arriving on the floor is a floor change, exactly as a move is. Fired
		// here (rather than only from the drag) so an undo, a pack activation and a hydrate all
		// re-derive too: a zone that only tracked the drag would go stale the first time something
		// reached the floor by any other route.
		this.onFloorChanged?.({ anchorName: row.anchorName, phase: "placed" });
		return row.anchorName;
	}

	cancelPlacement() {
		const st = this._placeState;
		if (!st) return false;
		this._exitPlacement();
		try { Promise.resolve(st.onSettle?.(null)).catch((e) => console.warn("[place] settle:", e?.message || e)); } catch (e) { console.warn("[place] settle:", e?.message || e); }   // §3.18 item 1: abandoned → nothing written
		return true;
	}

	// ─── Remove a placed fixture (ASK-088 slice-3c gap) ──────────────────────────────────────────
	// AAA: a placed object is removable where it was placed (Del, or the Inspector's Remove). Scoped
	// to merchant-PLACED luminaires — baked shell fixtures (longbox/table/spinner) are not Del-able,
	// so a stray keypress can't gut an authored shop. Persistence (DELETE /instances) is the studio's
	// half via `onFixtureRemoved`; this tears the geometry + light + registries out of the live scene.
	/**
	 * 🔴 ASK-128 §confirming-walk B — CAN THIS ONE BE REMOVED, AND IF NOT, WHY.
	 *
	 * One decider, so the bin's DISABLED state and the click's refusal can never disagree, and so no
	 * branch can return a bare `false` into silence. Every refusal carries words a merchant can act on.
	 *
	 * The classification itself was wrong and Coord measured it: `MESHSMITH_ROCKET_ANCHOR_02` is a real
	 * `/instances` row with `provenance:"merchant-place"` and the bin refused it at the `_shellPose`
	 * guard. `_shellPose` records **the anchor's authored pose**, and it is stamped on any registered
	 * entry an instance row matches (`stage.js:2119`) — so it says "this ANCHOR is declared by the
	 * shell", which is not the same claim as "this FIXTURE is baked into the shell". A merchant who
	 * places a rocket at a shell-declared anchor owns that rocket; the anchor being authored says
	 * nothing about who put the thing there.
	 *
	 * The honest discriminator is in the scene: a fixture the CATALOGUE rendered for a row is a
	 * `__cat__` clone (`_placeCatalogueClone`), and deleting the row removes it. Baked shell geometry
	 * is not, and DELETE `/instances` could not remove it — which is what the guard was really for.
	 * That is the architecture note's own rule restored: *any runtime-placed instance is removable.*
	 */
	canRemoveFixture(anchorName) {
		const anchor = anchorName ?? this._selectedAnchor;
		if (!anchor) return { ok: false, reason: "no-selection", message: "Nothing is selected." };
		// The kind the way `composedFixtures()` resolves it — registry OR rebind. Reading only the
		// rebinds is the same too-narrow lookup that made a placed pedestal unselectable (door 10), and
		// it surfaced again here: `DECOR_BOOKSHELF_ANCHOR_01` is a real merchant-place row and the bin
		// called it "part of the shop itself" because no rebind entry happened to carry its kind.
		const kind = this._kindForAnchor(anchor);
		if (!kind || kind === "fixture" || !this._fixtureCatalogueCached?.[kind]) {
			return { ok: false, reason: "not-catalogue", anchorName: anchor,
				message: "This is part of the shop itself, not something added to it — it can't be removed." };
		}
		const row = (this.currentScene?.instances || []).find((r) => r?.anchorName === anchor);
		// ASK-159 §2.18ep V1 — a TEMPLATE DEFAULT (the mint's derived id `<shop>-<kind>[-NN]`) is removable too: the
		// Studio keeps the removal as an override tombstone (studio/_remove-plan.js), not by deleting a row it has not got.
		const ord = /_(\d+)$/.exec(anchor), sid = this.currentShopId;
		const isDefault = !!(row && sid && row.instanceId === `${sid}-${row.fixtureKind}${ord ? `-${ord[1]}` : ""}`);
		if (!isFreePlaced(row) && !isDefault) {
			return { ok: false, reason: row ? "not-free-placed" : "no-row", anchorName: anchor,
				message: "This one came with the shop's own layout. You can move it, but it can't be removed." };
		}
		// Runtime-placed? The catalogue clone's name is the record of who rendered it.
		const rendered = (this._composedMeshesFor(anchor) || []).some((m) => {
			for (let n = m; n; n = n.parent) if (typeof n.name === "string" && n.name.includes("__cat__")) return true;
			return false;
		});
		if (!rendered) {
			return { ok: false, reason: "baked-geometry", anchorName: anchor,
				message: "This one is built into the shop's own model, so removing it here wouldn't stick." };
		}
		return { ok: true, anchorName: anchor, kind };
	}

	/**
	 * 🔴 ASK-129 §E2E103 F2 — CAN THIS FIXTURE BE REVERTED, and if not, why.
	 *
	 * The mirror of `canRemoveFixture`, and it exists for the same reason: one decider, so a control's
	 * disabled state and its click's refusal are the same judgement rather than two that can drift.
	 *
	 * The defect it closes: ⟲ "Revert to baseline" clears the merchant's `/instances` row, and for a
	 * fixture the MERCHANT PLACED the baseline is genuinely ABSENCE — so the control silently deleted
	 * her work. The persistence is right and stays untouched (`_persistFixtureRevert` is doing exactly
	 * what it documents); it is the OFFER that was a trap. No AAA editor ships a Revert that destroys:
	 * Unity and Unreal grey it out when there is nothing to revert to, and a destructive act carries
	 * its own verb. The verb already exists three pixels away — the bin — so this refuses and POINTS
	 * AT IT rather than growing a second way to delete the same thing.
	 *
	 * `canRemoveFixture(anchor).ok === true` is precisely "the merchant put this here" (a `__cat__`
	 * clone against a free-placed row), which is why it is the discriminator rather than a new one:
	 * two predicates for one fact is how the `_shellPose` misclassification happened.
	 */
	canRevertFixture(anchorName) {
		const anchor = anchorName ?? this._selectedAnchor;
		if (!anchor) return { ok: false, reason: "no-selection", message: "Nothing is selected." };
		const row = (this.currentScene?.instances || []).find((r) => r?.anchorName === anchor);
		if (!row?.instanceId) {
			return { ok: false, reason: "no-override", anchorName: anchor,
				message: "This one is already where the shop put it — there's nothing of yours to undo here." };
		}
		if (this.canRemoveFixture(anchor).ok) {
			return { ok: false, reason: "merchant-placed", anchorName: anchor,
				message: "You added this one, so it has no earlier position to go back to. Use the bin to remove it." };
		}
		return { ok: true, anchorName: anchor };
	}

	/**
	 * 🟣 ASK-129 §E2E103 F6 — DOES *ANY* FIXTURE IN THIS SHOP HAVE AN AUTHORED POSE?
	 *
	 * Ruled by Coord: a control greyed on every single fixture in the shop is clutter, not guidance.
	 * Greyed-with-a-reason is right when SOME items can revert and this one cannot; it is noise when
	 * nothing can. A space-first shop (D-068) arrives empty and the merchant places everything, so
	 * nothing in it has an author but her — "nothing to revert to" is the truthful answer for the
	 * whole shop, and the honest response is to not offer the affordance at all.
	 *
	 * The same rule Unity and Unreal apply: Revert is lit for a PREFAB INSTANCE and absent for an
	 * object created in the scene. It is a shop-level question, so it is answered once here rather
	 * than re-derived per selection.
	 *
	 * 📌 This is not the feature going away. A template-stamped shop ships baked fixtures with poses,
	 * and a PACK places a themed set to a designed layout — the moment a merchant applies one, nudges
	 * it and wants it back, this is the control that answers. It appears exactly when it can act.
	 */
	shopHasAuthoredPoses() {
		const reg = this._shopAnchors?.registered;
		if (reg) {
			for (const list of reg.values()) {
				for (const a of (list || [])) if (a?._shellPose?.position || a?.position) return true;
			}
		}
		return false;
	}

	async removeSelectedFixture() {
		const anchor = this._selectedAnchor;
		if (!anchor) return false;
		// 🔴 NO SILENT REFUSAL. Every branch that says no says WHY, on a seam the surfaces subscribe to:
		// a merchant who clicks the bin and gets nothing at all has been told the app is broken.
		const verdict = this.canRemoveFixture(anchor);
		if (!verdict.ok) {
			console.warn(`[remove] ${anchor} refused — ${verdict.reason}: ${verdict.message}`);
			this.onFixtureRemoveRefused?.(verdict);
			return false;
		}
		const kind = verdict.kind;
		// The scene answers the bin AT ONCE (every editor's delete); the save follows (coord ASK-178 §3, probe-fixture-drag PP1:
		// the tear-out used to wait on the persist — a template default's overlay-row DELETE + its tombstone PUT, two GCG round
		// trips — so a slow write left the fixture standing after the bin; the sweep read it still composed 3.5 s later). What
		// the persist does on a refusal or a failure is unchanged: it re-materialises a refused draft edit, or says so in a toast.
		this._removeFixtureFromScene(anchor);
		this.deselect();
		console.log(`[remove] ${anchor} (${kind}) removed`);
		if (typeof this.onFixtureRemoved === "function") {
			try { await this.onFixtureRemoved(anchor); }
			catch (err) { console.warn(`[remove] persist of ${anchor} failed:`, err?.message || err); }
		}
		return true;
	}

	// Tear a fixture instance's geometry, lights, transform group and registries out of the live scene.
	// Idempotent. Used by removeSelectedFixture (and safe to call for any catalogue instance anchor).
	_removeFixtureFromScene(anchorName) {
		if (!anchorName) return;
		// ASK-177 §9 item 2 (staging walk 13e) — a removed HOLDER takes what it holds with it, the same tick. Measured on
		// dd3385a (3/3): after the bin the figurine auto-seated on a pedestal hung in the air at the deleted socket (y 0.90),
		// stamped to the deleted anchor, and the HUD still counted the holder, until a reload. This path tore out the
		// fixture's group + `__cat__` strays only; the composed entry (its seated products, interactions, surface regions)
		// is what the re-seat tears down per anchor — the SAME teardown now runs here. Where the product goes next is the
		// server's (the Studio re-reads the feed after the delete: the pool, or another holder that admits it).
		const held = this.boxEntries?.has?.(anchorName);
		this._teardownComposedAnchor(anchorName);
		const group = this._fixtureGroups?.get?.(anchorName);
		const disposeTree = (root) => this._disposeInstanceTree(root);
		if (group) { disposeTree(group); group.parent?.remove(group); this._fixtureGroups.delete(anchorName); }
		// Any stray clone/lights not under the group (defensive — the clone is normally IN the group).
		const tag = `__cat__${anchorName}`, lumTag = `__luminaire__${anchorName}__`;
		const strays = [];
		(this._shellRoot || this.scene)?.traverse?.((o) => {
			if (o?.name && (o.name.includes(tag) || o.name.startsWith(lumTag))) strays.push(o);
		});
		for (const o of strays) { disposeTree(o); o.parent?.remove(o); }
		this._flipBin?.releaseBin(this, anchorName);   // ASK-139 F-139an — its sleeves leave the browse registry
		// Registries: drop the rebind + the anchor entry so a recompose can't resurrect it.
		if (Array.isArray(this._fixtureMaterialRebinds)) {
			const i = this._fixtureMaterialRebinds.findIndex((r) => r?.anchorName === anchorName);
			if (i >= 0) this._fixtureMaterialRebinds.splice(i, 1);
		}
		for (const entries of (this._shopAnchors?.registered?.values?.() || [])) {
			const i = entries.findIndex((e) => e?.name === anchorName);
			if (i >= 0) entries.splice(i, 1);
		}
		// ASK-128 S3 / M3 — the zone RECLAIMS the space in the same frame the fixture leaves it. No
		// stale hole where the fixture used to be: the derivation re-runs over what is left, so there
		// is nothing to invalidate and nothing that can be forgotten.
		this.onFloorChanged?.({ anchorName, phase: "removed" });
		if (held) { this._pruneDetachedGlobals(); this._seatedSignature = null; this._notifyContents(); }   // the HUD counts what is left
	}

	/** Dispose an instance subtree — never the catalogue cache's own geometry/materials its clones share (dd3385a). */
	_disposeInstanceTree(root) {
		root?.traverse?.((o) => {
			if (o.geometry?.dispose && !isCatalogueShared(o.geometry)) o.geometry.dispose();
			const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
			for (const m of mats) if (!isCatalogueShared(m)) m?.dispose?.();
		});
	}

	/** Tear one composed fixture instance out: interactions, animations, then every owned Object3D from the authoritative
	 *  registries (`_fixtureContentsFor`: the composed entry, the socket-ownership registry, name-declared `__cat__` nodes —
	 *  its SEATED PRODUCTS included; the adoption group carries a moved fixture) and its registry rows. One teardown for the
	 *  re-seat (which recomposes after) and the remove (which does not). Textures stay — label/cover textures are cached. */
	_teardownComposedAnchor(anchorName) {
		const entry = this.boxEntries?.get?.(anchorName);
		for (const d of (entry?._interactionDisposes || [])) { try { d(); } catch { /* best-effort */ } }
		for (const s of (entry?._animationStops || []))      { try { s(); } catch { /* best-effort */ } }
		const roots = new Set(this._fixtureContentsFor(anchorName));
		const group = this._fixtureGroups?.get?.(anchorName);
		if (group) roots.add(group);
		for (const root of roots) { this._disposeInstanceTree(root); root.parent?.remove(root); }
		this._fixtureGroups?.delete?.(anchorName);
		this._fixtureContents?.delete?.(anchorName);
		this._surfaceRegions?.delete?.(anchorName);   // ASK-121: re-registered on recompose, like the rebinds
		this.boxEntries?.delete?.(anchorName);
		// Rebind entries re-register on recompose — drop the stale ones (disposed meshes).
		if (Array.isArray(this._fixtureMaterialRebinds)) this._fixtureMaterialRebinds = this._fixtureMaterialRebinds.filter((r) => r?.anchorName !== anchorName);
		if (Array.isArray(this._fixtureSignageRebinds)) this._fixtureSignageRebinds = this._fixtureSignageRebinds.filter((r) => r?.anchorName !== anchorName);
	}

	/** Globals that referenced torn-down meshes keep only what is still attached to the scene. */
	_pruneDetachedGlobals() {
		const attached = (o) => { for (let n = o; n; n = n.parent) if (n === this.scene) return true; return false; };
		this.shopLights        = (this.shopLights || []).filter(attached);
		this.lidMeshes         = (this.lidMeshes || []).filter(attached);
		this.interactiveMeshes = (this.interactiveMeshes || []).filter(attached);
		if (Array.isArray(this.seatedItems)) this.seatedItems = this.seatedItems.filter((e) => e?.root && attached(e.root));
	}

	/** Tear down the ghost + placement state + hint. Shared by commit and cancel. */
	_exitPlacement() {
		const st = this._placeState;
		if (!st) return;
		this._placeState = null;
		try { st.detachGestures?.(); } catch { /* listeners already gone */ }
		this._hideDragHint();
		const dispose = (o) => {
			if (o?.material) { const ms = Array.isArray(o.material) ? o.material : [o.material]; ms.forEach((m) => m?.dispose?.()); }
		};
		if (st.ghostHelper) { st.ghostHelper.parent?.remove(st.ghostHelper); dispose(st.ghostHelper); }
		if (st.ghost) { st.ghost.traverse(dispose); st.ghost.parent?.remove(st.ghost); }
	}

	/** True while a place-new gesture is live (drives the pointer/keydown routing). */
	get isPlacing() { return !!this._placeState; }

	// The boot catalogue carries only `manifestAssetKey`; the authored fields the runtime consumes live
	// in the fixture MANIFEST — placementRules (validSurfaces/orientation/clearance, ASK-088) AND the
	// top-level `castShadow` boolean (ASK-084, release-2026-07-24-01). Lazy-load the manifest once and
	// stash it onto the cached catalogue entry so both the ghost validity and the clone's castShadow
	// read the REAL authored values. Idempotent + cached (loadFixtureManifest memoises).
	async _ensureFixtureManifest(kind) {
		const entry = this._fixtureCatalogueCached?.[kind];
		if (!entry || entry.manifest) return;   // already stashed
		try {
			const manifest = await loadFixtureManifest({ catalogue: this._fixtureCatalogueCached, kind });
			if (manifest && typeof manifest === "object") {
				entry.manifest = manifest;
				// 🔴 ASK-128 §chrome — A MANIFEST ARRIVING CHANGES THE FLOOR.
				//
				// The manifest carries the kind's authored CLEARANCE, and until it lands every fixture of
				// that kind is dilated by the default instead. The selling space is derived from exactly
				// those dilations, so a manifest resolving after the derive leaves a shape nobody would
				// compute a moment later — measured on the deployed plane: 6.80 m² drawn while the
				// model said 7.56, 0.76 m² of real floor the merchant was not offered and the drop
				// validator refused, and it never corrected itself because nothing had "moved".
				//
				// Found by the new chrome gate asking the app to re-derive with nothing moved. Same rule
				// as everywhere else in this batch: announce it where it changes, once, rather than
				// hoping each consumer re-reads at the right time. Coalesced — a mount loads a dozen
				// manifests in parallel and one re-derive covers them all.
				if (!this._floorClearanceNoticeQueued) {
					this._floorClearanceNoticeQueued = true;
					setTimeout(() => {
						this._floorClearanceNoticeQueued = false;
						this.onFloorChanged?.({ anchorName: null, phase: "clearances" });
					}, 0);
				}
			}
		} catch (err) { console.warn(`[fixture] manifest unavailable for ${kind}:`, err?.message || err); }
	}

	/** The placement descriptor for a KIND (not an anchor) — real footprint measured from the ghost's
	 *  meshes, same substitution the move path does from composed meshes. */
	_placementFixtureForKind(kind, meshes) {
		const base = this._ws3
			? this._ws3.fixtureForPlacement({ fixtureCatalogue: this._fixtureCatalogueCached }, kind)
			: { kind };
		const box = new THREE.Box3();
		for (const m of (meshes || [])) if (m?.visible) box.expandByObject(m);
		if (!base.placementRules || box.isEmpty()) return base;
		const size = box.getSize(new THREE.Vector3());
		const local = this._placeState ? this._ws3?.ghostLocalFootprint?.(this._placeState) : null;   // ASK-159 §2.18ep: the yaw-0 box
		return { ...base, placementRules: { ...base.placementRules, boundingBox: { width: size.x, height: size.y, depth: size.z }, ...(local ? { localBoundingBox: local } : {}) } };
	}

	/**
	 * Every composed fixture as a clearance neighbour for a NEW placement (no self to exclude, no
	 * origin to grandfather — a fresh fixture must clear everything already in the shop).
	 *
	 * 🔴 THE FOURTH DOOR OF THE REGISTRY-STANDING-IN DEFECT (found + fixed 2026-09-04, ASK-128 S3).
	 * This enumerated `_fixtureMaterialRebinds`, which `composeFixture` writes ONLY for a kind
	 * declaring `materialSlots` — and none of the five derived-surface kinds declares any. So a
	 * `fan-table` seating real products was in nobody's neighbour list, and MEASURED on the deployed
	 * plane: `validateFloorItemDrop` at the exact centre of Fan Table 1 came back `{valid:true,
	 * code:"ok"}`. A merchant could hand-place the piano INSIDE a table and be told it was fine.
	 * The three doors `composedFixtures()` documents are the same bug; this is the fourth, and the
	 * fix is the same — enumerate what COMPOSED, and take its bounds from the composed meshes.
	 */
	_placementNeighbours() {
		const out = [];
		const covering = this._zoneScene?.floorCoveringAnchors?.() ?? null;   // ASK-159 §2.18ei: a rug is floor, not a neighbour
		for (const { anchorName } of this.composedFixtures()) {
			if (!anchorName || covering?.has(anchorName)) continue;
			const box = this._selectionBounds(anchorName);
			if (!box) continue;
			const c = box.getCenter(new THREE.Vector3());
			const size = box.getSize(new THREE.Vector3());
			out.push({ anchorName, label: this._fixtureLabelFor(anchorName),
				position: { x: c.x, y: box.min.y, z: c.z }, boundingBox: { width: size.x, height: size.y, depth: size.z } });
		}
		// ASK-128 S3.2 — a NEW fixture must clear the floor stock too. Nothing is grandfathered on a
		// placement (there is no origin to be already-conflicting at), so the whole set applies.
		out.push(...(this._zoneScene?.floorSeatNeighbours() || []));
		return out;
	}

	/** WS3 validity rules live in studio-v1 (EDITOR code). stage.js is on the CUSTOMER path, so a
	 *  static import would pull the placement machinery into the customer bundle and trip the
	 *  no-editor-code-leak budget gate. Loading it on first drag keeps it in an editor-only chunk. */
	_ensureWs3() {
		if (this._ws3 || this._ws3Loading) return;
		this._ws3Loading = Promise.all([
			import("./studio-v1/ws3/_ghost-controller.js"),
			import("./studio-v1/ws3/_fixture-rules.js"),
			// ASK-106 S4 — the attachment grammar itself (classifier + up-vector + the per-instance
			// record). Editor-only, so it stays behind the same lazy gate as the rest of WS3.
			import("./studio-v1/ws3/_placement-rules.js"),
			import("./studio-v1/ws3/_drag-hint.js"),   // the why-pill's DOM (off the customer bundle)
			import("./studio-v1/ws3/_shell-containment.js"),   // a fixture stays inside the shop (ASK-159 §1 item 3)
			import("./studio-v1/ws3/_placement-pose.js"),   // turn + size while placing; the local footprint (ASK-159 §2.18ep)
		])
			.then(([ghost, rules, place, hint, shell, pose]) => { this._ws3 = { ...ghost, ...rules, ...place, ...hint, ...shell, ...pose }; })
			.catch((err) => { console.warn("[edit] WS3 placement rules unavailable — moves are unvalidated", err); });
	}

	/** The other composed fixtures, as clearance neighbours for a move.
	 *
	 *  Two rules make this honest:
	 *   • SELF is excluded — a fixture always overlaps where it currently is.
	 *   • Anything ALREADY conflicting at the origin is GRANDFATHERED out. Coord's rule is that a
	 *     move is valid anywhere in-bounds and non-overlapping "including where it started"; scenes
	 *     authored before clearance existed have fixtures closer than minClearance, and without this
	 *     the drag would go red the instant it began and hard-block every move. Judged with the same
	 *     ruled validator, one neighbour at a time — no second opinion about what "too close" means.
	 *
	 *  Footprints are the fixture's REAL world bounds, not the declared boundingBox, so what the
	 *  merchant sees on screen is what the check uses. */
	_dragNeighbours(selfAnchor, origin, fixture) {
		const out = [];
		const covering = this._zoneScene?.floorCoveringAnchors?.() ?? null;   // ASK-159 §2.18ei: a rug is floor, not a neighbour
		for (const rb of (this._fixtureMaterialRebinds || [])) {
			const name = rb?.anchorName;
			if (!name || name === selfAnchor || covering?.has(name)) continue;
			const box = new THREE.Box3();
			for (const m of (rb.meshes || [])) if (m?.visible) box.expandByObject(m);
			if (box.isEmpty()) continue;
			const c = box.getCenter(new THREE.Vector3());
			const size = box.getSize(new THREE.Vector3());
			out.push({ anchorName: name, label: this._fixtureLabelFor(name), position: { x: c.x, y: box.min.y, z: c.z },
				boundingBox: { width: size.x, height: size.y, depth: size.z } });
		}
		// ASK-128 S3.2 — A THING STANDING ON THE FLOOR IS A NEIGHBOUR LIKE ANY FIXTURE. The piano is
		// seated stock, not a fixture, so it appears in none of the registries above — and without this
		// a merchant could drag a shelf straight through it and the drag would read green. One
		// validator, one opinion about "too close" (S3.2): these join the SAME neighbour list and are
		// judged by the SAME `resolveContinuousGhost`, including the origin-grandfathering below.
		out.push(...(this._zoneScene?.floorSeatNeighbours(selfAnchor) || []));
		if (!this._ws3 || !fixture) return out;
		return out.filter((n) => {
			const v = this._ws3.resolveContinuousGhost({ hit: { point: origin, surfaceKind: "floor", region: null }, fixture, neighbours: [n], modifierFree: true });
			const conflictsAtOrigin = v?.verdict && !v.verdict.valid && v.verdict.code === "clearance";
			if (conflictsAtOrigin) console.info(`[edit] ${n.anchorName} already inside ${selfAnchor}'s clearance at rest — not blocking this move`);
			return !conflictsAtOrigin;
		});
	}

	/** A merchant-facing name for a fixture: LONGBOX_ANCHOR_01 → "Long Box 1". Anchor ids are
	 *  internal; nobody editing a shop has ever seen one. */
	_fixtureLabelFor(anchorName) {
		// D-073 item 3: the pattern lives in _anchor-name.js, once. This used to carry its own copy,
		// which had no branch for `<KIND>_ANCHOR_INST_<id>` — so a free-placed fixture's label was the
		// whole raw name, mint id and all ("Wall Shelf Anchor Inst A1b2c3d4"), in a method whose own
		// docstring promises "anchor ids are internal; nobody editing a shop has ever seen one".
		return fixtureLabelFor(anchorName);
	}

	/** SNAP-AS-ASSIST (slice-2c). When the pose under the cursor is invalid but a valid one sits
	 *  within reach, glide into it — the Sims/Roblox grammar, and the WS3 assist principle applied to
	 *  a MOVE rather than a place.
	 *
	 *  Why this is required and not a nicety: demo-store's longboxes are authored 0.400m apart and
	 *  measure 0.26m deep, so the vacated slot between two siblings is a few centimetres wider than
	 *  the box. Every hand-held drop landed centimetres off and was correctly refused, which made an
	 *  authored arrangement un-restorable — the fixture could BE there but could never be PUT there.
	 *  Assist closes exactly that gap without weakening the rule: an invalid pose is still invalid,
	 *  it is simply moved to the nearest valid one before being judged.
	 *
	 *  Searched nearest-first so the fixture never jumps further than it must. Hold the free-place
	 *  modifier to switch it off and place raw. */
	_seekAssistedPose(target, fixture, neighbours, anchorName) {
		if (!this._ws3) return null;
		const RADII = [0.06, 0.12, 0.20, 0.30];
		const STEPS = 8;
		for (const r of RADII) {
			for (let i = 0; i < STEPS; i++) {
				const a = (i / STEPS) * Math.PI * 2;
				const cand = { x: target.x + Math.cos(a) * r, y: target.y, z: target.z + Math.sin(a) * r };
				const hit = this._continuousHitUnder(cand, anchorName);
				if (!hit) continue;
				const nb = (neighbours || []).filter((n) => n.anchorName !== hit.supportAnchor);
				const g = this._ws3.resolveContinuousGhost({ hit, fixture, neighbours: nb, modifierFree: true });
				if (g?.verdict?.valid) return { ghost: g, point: hit.point, distance: r };
			}
		}
		return null;
	}

	/** ⟲ Revert-to-baseline (ASK-087 §10.5 generalization, 2026-08-06). Live-write commits every
	 *  move/rotate/tune immediately, so after a refresh there is no cross-session undo — this is the
	 *  cross-session complement. Restores a fixture to its AUTHORED shell-anchor pose (the `_shellPose`
	 *  captured before any override, DECISION-050 §2). SNAPPY in-scene now; the caller then DELETES the
	 *  /instances row so §2 re-derives from the anchor exactly on the next compose/reload.
	 *  A merchant-PLACED fixture has no shell baseline (`_shellPose` absent) → returns {placed:true} so
	 *  the caller removes it (its baseline is non-existence). Returns the restored baseline pose. */
	revertFixtureToBaseline(anchorName) {
		const entry = this._anchorEntryFor(anchorName);
		// Baseline source: `_shellPose` when a hydrated /instances row overwrote entry.position (reload
		// case), ELSE entry.position/rotationY — which a LIVE move never mutates (moveFixtureTo only
		// translates the transform group), so it stays the authored anchor pose. A merchant-PLACED
		// fixture isn't a registered shell anchor → no entry → no baseline → remove.
		const shellPose = entry?._shellPose
			|| (entry?.position ? { position: { x: entry.position.x, y: entry.position.y, z: entry.position.z }, rotationY: entry.rotationY } : null);
		if (!shellPose?.position) return { placed: true };   // net-new placed fixture → no baseline
		const pos = shellPose.position, yaw = Number.isFinite(shellPose.rotationY) ? shellPose.rotationY : 0;
		this.moveFixtureTo(anchorName, { x: pos.x, y: pos.y, z: pos.z });
		this.rotateFixtureTo(anchorName, yaw);
		// sync the live anchor entry back to baseline so a later move reads the authored origin, not the
		// stale moved one.
		if (entry.position?.set) entry.position.set(pos.x, pos.y, pos.z);
		entry.rotationY = yaw;
		if (this._selectedAnchor === anchorName) this._refreshSelectionHelper();
		return { placed: false, baseline: { position: { x: pos.x, y: pos.y, z: pos.z }, rotationY: yaw } };
	}

	/** Rotate a fixture instance to an absolute world yaw (radians), pivoting about its BASE-CENTRE
	 *  so it turns in place — rotating the transform group about its own origin would swing the
	 *  fixture around the world origin instead (the group starts at 0 with world-placed children).
	 *  Updates the anchor entry's rotationY so the move-commit path and future drags read the new
	 *  value — the same single yaw source _endFixtureDrag persists (ASK-087 Phase-2 row 4). */
	/**
	 * 🔴 ASK-129 H1 — A TRANSFORM CHANGES THE FLOOR, AND THE FLOOR HAS TO HEAR ABOUT IT.
	 *
	 * *"The blue outline bounding box moves with rotation of a fixture, the green floor space does not
	 * respond to the rotation."* — Vanessa, E2E104 re-run.
	 *
	 * Both representations were already correct. MEASURED on the deployed bundle with a
	 * `decor-bookshelf`, sampling every 150 ms across a 45° turn:
	 *
	 *     t+157ms   outline 0.849×0.849   obstacle 0.849×0.849   green 13.353   ← geometry already right
	 *     t+667ms   outline 0.849×0.849   obstacle 0.849×0.849   green 13.353
	 *     t+727ms   ← GET /admin/shops/…/merch-zones
	 *     t+1172ms  outline 0.849×0.849   obstacle 0.849×0.849   green 13.030   ← the floor finally moves
	 *
	 * `floorObstacles()` reads a LIVE world Box3, so the rect the derivation wants was right in frame
	 * one (0.900×0.300 → 0.849×0.849, the AABB of a 45° turn). Nothing asked it. The green caught up
	 * only when a server round trip came back and `pullZones` re-derived as a side effect — a full
	 * second later, through a network call that has nothing to do with the geometry, and NEVER at all
	 * if that call fails. On the slider itself it is worse: `input` rotates live and only `change`
	 * writes, so the fixture turns under her hand while the floor sits still for the whole gesture.
	 *
	 * The bus for this already existed and already had a subscriber (`_zones.js` → `rederiveZones`);
	 * the drag, the placement, the removal and the clearance warm all fire it. The three verbs that
	 * change a footprint WITHOUT moving it — rotate, facing, scale — simply never emitted. That is not
	 * three bugs, it is one wiring rule stated in four places and missed in three, so the fix is one
	 * emitter every transform verb calls rather than three call sites to keep in step.
	 *
	 * AAA precedent: Unity's `NavMeshObstacle` re-carves when its transform moves past
	 * `carvingMoveThreshold`, locally and every frame; Unreal dirties the affected navmesh tiles on a
	 * dynamic obstacle's transform change. Neither waits for an authority to answer first — the
	 * editor's preview is local and immediate, and the server's shape replaces it when it arrives
	 * (`applyServed`), which is exactly the order this restores.
	 */
	_footprintChanged(anchorName, phase) {
		try { this.onFloorChanged?.({ anchorName, phase }); }
		catch (err) { console.warn("[stage] floor-changed subscriber threw:", err?.message || err); }
	}

	// ASK-097 AAA — apply a FACING edit as a LIVE in-place transform (no scene reload). Facing is
	// baked at compose (bodyYaw = (π − facingRad) + rotationY, so a facing INCREASE of Δ° turns the
	// box by −Δ° about Y); an engine editor never reloads the scene for a property change, so we
	// rotate the placed fixture GROUP by that delta. The group carries the body, lid, label (child of
	// the body) and seated contents, so they all turn together — this is why B (camera flick) and C
	// (label decouples on rotation) are ONE fix: no navigateToProject → the camera never resets, and
	// the label follows for free. Path-agnostic: the caller (Studio) owns the facing BASELINE (the
	// slider's rendered value = the composed facing, correct for the legacy longbox AND the engine-
	// composer families) and passes the DELTA — so this needs no per-family entry bookkeeping. Facing
	// stays its own knob; this does NOT touch entry.rotationY (the §2 move-rotate). Pivot = base-centre
	// (identical to a merchant drag-rotate), so position is preserved.
	rotateFixtureFacingBy(anchorName, deltaDeg) {
		if (!Number.isFinite(deltaDeg) || Math.abs(deltaDeg) < 1e-6) return false;
		const group = this.adoptFixtureContents(anchorName);
		const base = this._fixtureBaseCentre(anchorName);
		if (!group || !base) return false;
		const deltaRad = -(deltaDeg * Math.PI / 180);
		const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), deltaRad);
		group.position.sub(base).applyQuaternion(q).add(base);   // pivot at base-centre, same as a move-rotate
		group.quaternion.premultiply(q);
		group.updateMatrixWorld(true);
		if (this._selectedAnchor === anchorName) this._refreshSelectionHelper();
		this._footprintChanged(anchorName, "rotated");
		return true;
	}
	rotateFixtureTo(anchorName, yawRad) {
		if (!Number.isFinite(yawRad)) return null;
		const group = this.adoptFixtureContents(anchorName);
		const entry = this._anchorEntryFor(anchorName);
		const base = this._fixtureBaseCentre(anchorName);
		if (!group || !base) return null;
		// 🔴 ASK-129 §E2E103 F5 — the current yaw comes from `getFixtureYaw`, which knows about fixtures
		// the registry does not. This read `entry.rotationY` directly, and a merchant-PLACED fixture is
		// not a registered shell anchor (`revertFixtureToBaseline` says so three methods up: "no entry
		// → no baseline"). So for everything she adds herself `entry` was undefined, `current` was
		// ALWAYS 0, and the new value was never recorded — turning an ABSOLUTE control into a
		// CUMULATIVE one. Measured on the deployed bundle with a `decor-bookshelf`:
		//
		//     set 45 → world yaw  45   (delta 45 − 0)
		//     set 90 → world yaw 135   (delta 90 − 0, applied on top)
		//     …with the slider and readout stuck at 0 the whole time, since getFixtureYaw read the
		//     same missing entry
		//
		// Her report word for word, and the same two symptoms F3 fixed for boxes reaching here by a
		// completely different mechanism — F3 could not have touched it, the facing control was
		// rendered only for the box kinds (a name list then; the declared open-close interaction since ASK-159 §2.18gb).
		const current = this.getFixtureYaw(anchorName);
		const delta = yawRad - current;
		if (Math.abs(delta) > 1e-9) {
			const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), delta);
			group.position.sub(base).applyQuaternion(q).add(base);   // pivot at base-centre
			group.quaternion.premultiply(q);
			group.updateMatrixWorld(true);
			if (entry) entry.rotationY = yawRad;   // still written where one exists — §2 precedence reads it
			(this._fixtureYaw ||= new Map()).set(anchorName, yawRad);
			if (this._selectedAnchor === anchorName) this._refreshSelectionHelper();
			this._footprintChanged(anchorName, "rotated");
		}
		return yawRad;
	}

	/** Uniformly scale a fixture instance to an absolute factor, pivoting about its BASE-CENTRE so it
	 *  scales IN PLACE (ASK-094 94b). Composes on the same transform group as move/rotate — the group
	 *  holds world-placed children, so scaling about the base keeps the fixture grounded and its move/
	 *  yaw intact (uniform scale commutes with rotation). Because seated products live in the group and
	 *  the footprint/validity read REAL WORLD bounds, the geometry, the slot poses, AND the placement
	 *  footprint all scale together (#535 — scale is footprint-affecting like a move, handled for free).
	 *  Uniform-only is load-bearing (D-050 §3): socket.capacity is invariant under uniform scale. */
	scaleFixtureTo(anchorName, factor) {
		const s = Number(factor);
		if (!Number.isFinite(s) || s <= 0) return null;
		const group = this.adoptFixtureContents(anchorName);
		const entry = this._anchorEntryFor(anchorName);
		const base = this._fixtureBaseCentre(anchorName);
		if (!group || !base) return null;
		// current = the group's ACTUAL geometric scale, NOT the entry field. _collect pre-seeds
		// entry.uniformScale with the persisted target on a fresh compose, so reading the entry here
		// would make ds=1 and skip the multiply — the reload render bug (group stays at identity while
		// the data says 0.5). group.scale is identity on a fresh attach and tracks entry during
		// interactive scaling, so it's the correct "current" in both paths.
		const current = Number.isFinite(group.scale?.x) && group.scale.x > 0 ? group.scale.x : 1;
		const ds = s / current;
		if (Math.abs(ds - 1) > 1e-9) {
			group.position.sub(base).multiplyScalar(ds).add(base);   // pivot the position about base-centre
			group.scale.multiplyScalar(ds);
			group.updateMatrixWorld(true);
			if (entry) entry.uniformScale = s; else (this._fixtureScale ||= new Map()).set(anchorName, s);
			if (this._selectedAnchor === anchorName) this._refreshSelectionHelper();
			this._footprintChanged(anchorName, "scaled");
		} else if (entry) { entry.uniformScale = s; }
		return s;
	}

	/** Current uniform scale of a fixture instance (default 1.0) — the panel's read side. */
	getFixtureScale(anchorName) {
		const u = this._anchorEntryFor(anchorName)?.uniformScale ?? this._fixtureScale?.get(anchorName);   // a placed one has no entry
		return u > 0 ? u : 1;
	}

	/** The author-declared scalePolicy for a kind (ASK-094): { min, max } enables merchant scaling;
	 *  ABSENT or min==max = LOCKED (no merchant control). Read live from the warmed manifest. */
	_scalePolicyFor(kind) {
		const sp = this._fixtureCatalogueCached?.[kind]?.manifest?.scalePolicy;
		if (!sp || !Number.isFinite(sp.min) || !Number.isFinite(sp.max) || sp.max <= sp.min) return null;
		return { min: sp.min, max: sp.max };
	}

	/** After a compose, apply each instance's persisted uniformScale (#535 / reload-holds). Reads the
	 *  AUTHORITATIVE scene.instances rows, NOT the registered anchor entries: a fixture PLACED after boot
	 *  (a "newcomer" — e.g. a potted-plant that lives only in GCG /instances, never in boot.json) reloads
	 *  through _renderHydratedCatalogueNewcomers, and its registered entry's uniformScale can be stale/
	 *  unset (the hydrate merges the GCG row into scene.instances, not into the entry). The scene row is
	 *  kept in step by BOTH the hydrate merge and the persist Object.assign, so it always carries the
	 *  live scale. Runs on the boot compose AND the post-newcomer recompose (after the newcomer renders).
	 *  Idempotent: scaleFixtureTo measures ds against the group's live geometric scale, so a group already
	 *  at the target is a no-op, while a fresh-attach group (scale 1) gets the real multiply. */
	/** ASK-151 step 4 — the placements' declared settings (`scene.instances[].settings`), applied by the ONE lazy
	 *  `applySettings` (`_fixture-settings.js`). A shop whose rows carry none never fetches the chunk (the bytes). */
	_applyFixtureSettings() {
		const rows = (this.currentScene?.instances || []).filter((r) => r?.settings && Object.keys(r.settings).length);
		if (!rows.length && !this._fs) return null;
		const scene = this.currentScene;
		return (this._fsLoad ||= import("./_fixture-settings.js")).then((m) => { this._fs = m; if (scene === this.currentScene) return m.applyAllSettings(this, rows, this._fixtureCatalogueCached); })
			.catch((e) => console.warn("[settings] unavailable:", e?.message || e));
	}

	_applyInstanceScales() {
		this._applyFixtureSettings();   // ASK-151 step 4 — every site that re-applies a placement's persisted state
		const rows = Array.isArray(this.currentScene?.instances) ? this.currentScene.instances : [];
		for (const row of rows) {
			if (!row?.anchorName) continue;
			const u = Number.isFinite(row.uniformScale) && row.uniformScale > 0 ? row.uniformScale : 1;
			if (Math.abs(u - 1) <= 1e-9) continue;                 // identity — default fixtures stay at 1
			this.scaleFixtureTo(row.anchorName, u);                // no-op if group already at u; skips if not composed
		}
	}

	/** F-CU16a — emit the box hint only when (hovered box × open state) changes its answer. */
	_syncBoxHint() {
		const id = this.hoveredBoxId || null;
		const hint = boxHintFor({
			hoveredBoxId: id, activeBoxId: this.activeBoxId, boxState: this.boxState,
			name: id ? this.boxEntries?.get?.(id)?.fixture?.name : null,
		});
		const key = boxHintKey(hint);
		if (key === (this._boxHintKey || "")) return;
		this._boxHintKey = key;
		this.onBoxHover?.(hint);
	}

	/** 🔴 ASK-145 §13.4 — every compose path records WHICH path drew a fixture and the move-rotate yaw
	 *  it drew it at. That is what `getFixtureYaw` must answer after a hydrate: the anchor ENTRY is
	 *  overwritten with the row's yaw before the applier runs, so the entry cannot say where the mesh
	 *  is. The path name is the measurement §13.3 asked for (`composer` = `<kind>__cat__<A>__Body`,
	 *  `catalogue-clone` = `__fixture__<A>` › `<kind>__cat__<A>`). */
	_noteComposedPose(anchorName, path, yaw) {
		if (!anchorName) return;
		const y = Number.isFinite(yaw) ? yaw : 0;
		(this._composedPoses ||= new Map()).set(anchorName, { path, yaw: y });
		(this._fixtureYaw ||= new Map()).set(anchorName, y);
	}

	/** Which path drew a fixture, and at what yaw — read by probes (`probe-attachment-grammar` 9j). */
	composedPoseFor(anchorName) {
		const p = this._composedPoses?.get(anchorName);
		return p ? { ...p, tracked: this.getFixtureYaw(anchorName) } : null;
	}

	/** Current world yaw of a fixture instance (radians) — the panel's read side. */
	/** The fixture's CURRENT move-rotate yaw. The anchor-keyed record first — it exists for a
	 *  merchant-placed fixture, which has no registry entry at all (ASK-129 §E2E103 F5) — then the
	 *  registry entry, then 0. Deliberately NOT read off the group's world matrix: for a box that
	 *  group also carries the FACING rotation, and conflating the two is the very split F5's ruling is
	 *  about. This is the move-rotate yaw and only that. */
	getFixtureYaw(anchorName) {
		const tracked = this._fixtureYaw?.get(anchorName);
		if (Number.isFinite(tracked)) return tracked;
		const entry = this._anchorEntryFor(anchorName);
		return Number.isFinite(entry?.rotationY) ? entry.rotationY : 0;
	}

	/** Base-centre of a composed fixture — the same convention moveFixtureTo targets. Door 7 of the
	 *  registry-standing-in defect: read from the composed witness, never the rebind registry. */
	_fixtureBaseCentre(anchorName) {
		const box = new THREE.Box3();
		for (const m of this._composedMeshesFor(anchorName)) if (m?.visible) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
		if (box.isEmpty()) return null;
		const c = box.getCenter(new THREE.Vector3());
		return new THREE.Vector3(c.x, box.min.y, c.z);
	}

	/** The fixture's REAL footprint (metres), measured from its composed meshes. The declared
	 *  placementRules.boundingBox is an approximation of the kind, not of this asset — demo-store's
	 *  longbox declares 0.60x0.30 but measures 0.73x0.26, and that mismatch made three longboxes
	 *  authored on the counter fail the edge-fit check ("hanging over the edge") at the exact spot
	 *  they were already sitting. Validity should judge what the merchant can see. */
	_fixtureFootprint(anchorName) {
		// Door 8: the composed witness, so placement validity judges a placed derived-surface
		// fixture by its real meshes instead of a null footprint.
		const box = new THREE.Box3();
		for (const m of this._composedMeshesFor(anchorName)) if (m?.visible) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
		if (box.isEmpty()) return null;
		const size = box.getSize(new THREE.Vector3());
		return { width: size.x, height: size.y, depth: size.z };
	}

	/** The placement descriptor for a fixture, with its real footprint substituted in. */
	_placementFixtureFor(anchorName) {
		const kind = this._kindForAnchor(anchorName);
		const base = this._ws3
			? this._ws3.fixtureForPlacement({ fixtureCatalogue: this._fixtureCatalogueCached }, kind)
			: { kind };
		const measured = this._fixtureFootprint(anchorName);
		if (!base.placementRules || !measured) return base;
		const local = this._ws3?.placedLocalFootprint?.(this, anchorName);   // ASK-159 §2.18ep: the yaw-0 box, unscaled
		return { ...base, scale: this.getFixtureScale(anchorName), placementRules: { ...base.placementRules, boundingBox: measured, ...(local ? { localBoundingBox: local } : {}) } };
	}

	/**
	 * Every fixture COMPOSED in this scene, as {anchorName, kind} — the one honest answer to "what is
	 * standing in this shop", for anything that needs to enumerate holders.
	 *
	 * 🔴 `_fixtureMaterialRebinds` is NOT that answer, and reading it as one is now a defect this
	 * codebase has had at three separate doors. It is a MATERIAL-BINDING registry: `composeFixture`
	 * writes to it only when the kind declares `materialSlots`, and none of the five derived-surface
	 * kinds declares any. MEASURED on the deployed plane: a `display-pedestal` seating five real
	 * products was absent from the merchant's `Pin to…` list and could not be dropped onto at all —
	 * the pointer resolved an address over it, and the drag controller then had no target, because the
	 * fixture was not in the list it was scanning.
	 *
	 * `boxEntries` is keyed by anchor for every fixture the composer composed, and the registered
	 * anchor carries the kind, so the union answers for holders AND for decor clones. Same shape GCG
	 * closed server-side (a validator reading what the SHELL declares while the shop COMPOSES
	 * something else); ours was one registry standing in for another.
	 */
	composedFixtures() {
		const kindOf = (name) => {
			for (const list of (this._shopAnchors?.registered?.values?.() || [])) {
				for (const a of list) if (a?.name === name) return a.fixtureKind || null;
			}
			return null;
		};
		const out = new Map();
		for (const name of (this.boxEntries?.keys?.() || [])) out.set(name, kindOf(name));
		for (const rb of (this._fixtureMaterialRebinds || [])) {
			if (!rb?.anchorName) continue;
			if (!out.has(rb.anchorName) || !out.get(rb.anchorName)) out.set(rb.anchorName, rb.kind || kindOf(rb.anchorName));
		}
		return [...out].filter(([, kind]) => !!kind).map(([anchorName, kind]) => ({ anchorName, kind }));
	}

	_kindForAnchor(anchorName) {
		return this.composedFixtures().find((f) => f.anchorName === anchorName)?.kind
			|| (this._fixtureMaterialRebinds || []).find((r) => r?.anchorName === anchorName)?.kind || "fixture";
	}

	/** The composed meshes standing for a fixture instance. The rebind registry answers only for a
	 *  kind declaring materialSlots — none of the five derived-surface kinds does — so it is the
	 *  fast path, and the composer's own `<kind>__cat__<anchor>__<part>` mesh naming (stamped by
	 *  BOTH render paths) is the witness that answers for everything composed. */
	_composedMeshesFor(anchorName) {
		if (!anchorName) return [];
		const rb = (this._fixtureMaterialRebinds || []).find((r) => r?.anchorName === anchorName);
		if (Array.isArray(rb?.meshes) && rb.meshes.length) return rb.meshes;
		const out = [];
		// ASK-147 §14.21 — by the NEAREST `__cat__` ancestor, not the mesh's own name: a grouped Body
		// (backissue-bank) has child meshes with generic names, and attributed 0 → no box, no pack mark.
		const isReg = (a) => this._anchorIsRegistered(a);
		this.scene?.traverse?.((n) => { if (n.isMesh && composedAnchorOf(n, isReg) === anchorName) out.push(n); });
		return out;
	}

	/** The live why-hint during a drag. EDITOR-ONLY DOM: created on first use inside a drag and
	 *  removed the moment the drag ends, so no editor chrome can survive into Play (the rule
	 *  probe-mode-chrome enforces). Green states show the confirm line, red states show the verdict's
	 *  own reason verbatim — the grammar already writes merchant-facing English, it was simply never
	 *  being shown to anyone. */
	_showDragHint(text, valid, assisted = false) {
		// The pill's DOM lives in the lazy WS3 chunk (ASK-159 §1 — a customer-bundle reduction: editor-only chrome
		// off the customer path). A show requested before WS3 lands draws when it does — unless a later show/hide
		// superseded it (the sequence number), so a stale hint can never outlive its drag.
		if (!text) return this._hideDragHint();
		const seq = this._hintSeq = (this._hintSeq || 0) + 1;
		const go = () => { if (seq === this._hintSeq) this._ws3?.showDragHint?.(this, text, valid, assisted); };
		if (this._ws3) return go();
		this._ensureWs3();
		this._ws3Loading?.then(go);
	}

	_hideDragHint() {
		this._hintSeq = (this._hintSeq || 0) + 1;
		this._dragHintEl?.remove();
		this._dragHintEl = null;
	}

	// ── ASK-087 Phase-2 — surface + footprint-contact highlight (retirement re-walk-3 fix-vehicle) ──
	// Makes the invisible placement computation VISIBLE (Sims per-tile-footprint / Unreal-Unity
	// placement-projection): while dragging, the target surface's valid REGION and the fixture's
	// FOOTPRINT contact-patch are drawn flat on the surface, the patch coloured by validity. It's the
	// AAA affordance (the merchant SEES why a drop is red) AND the standing diagnostic — a patch drawn
	// outside the region is the divergence, on screen, the instant it happens. Lives on _editorLayer,
	// so it inherits the wholesale Play gate + picking exclusion like every other editor artifact.
	_ensureContactHighlight() {
		if (this._contactHi) return this._contactHi;
		const layer = this._ensureEditorLayer();
		if (!layer) return null;
		const mkPlane = (color, opacity, renderOrder) => {
			const m = new THREE.Mesh(
				new THREE.PlaneGeometry(1, 1),
				new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthTest: false, depthWrite: false, side: THREE.DoubleSide }),
			);
			m.rotation.x = -Math.PI / 2;   // flat on the ground plane
			m.renderOrder = renderOrder;
			m.visible = false;
			layer.add(m);
			return m;
		};
		// region = neutral guide beneath; footprint patch = validity-coloured on top
		this._contactHi = { region: mkPlane(0x6aa6ff, 0.12, 9990), patch: mkPlane(0x35c46a, 0.30, 9991) };
		return this._contactHi;
	}

	/** @param {{region:{xMin,xMax,zMin,zMax}|null, surfaceY:number, footprint:{cx,cz,w,d}, valid:boolean}} */
	_updateContactHighlight({ region, surfaceY, footprint, valid }) {
		const hi = this._ensureContactHighlight();
		if (!hi) return;
		const y = (Number.isFinite(surfaceY) ? surfaceY : 0) + 0.012;   // just above the surface, no z-fight
		if (region && region.xMax > region.xMin && region.zMax > region.zMin) {
			hi.region.position.set((region.xMin + region.xMax) / 2, y, (region.zMin + region.zMax) / 2);
			hi.region.scale.set(region.xMax - region.xMin, region.zMax - region.zMin, 1);
			hi.region.visible = true;
		} else hi.region.visible = false;
		if (footprint && footprint.w > 0 && footprint.d > 0) {
			hi.patch.position.set(footprint.cx, y + 0.004, footprint.cz);
			hi.patch.scale.set(footprint.w, footprint.d, 1);
			hi.patch.material.color.setHex(valid ? 0x35c46a : 0xe5484d);
			hi.patch.visible = true;
		} else hi.patch.visible = false;
	}

	_hideContactHighlight() {
		if (!this._contactHi) return;
		this._contactHi.region.visible = false;
		this._contactHi.patch.visible = false;
	}

	/** Tint the selection box to show live drop validity; null restores the neutral selection colour. */
	_tintSelection(hex) {
		const h = this._selectionHelper;
		if (h?.material?.color) h.material.color.setHex(hex ?? 0x4a9eff);
	}

	// ASK-087 slice-c amend-2 — double-click focus: frame the clicked ITEM, not the raw surface point.
	// Composer names fixture meshes `<kind>__cat__<ANCHOR>__<part>`, so a click on any part resolves
	// the whole fixture INSTANCE (every mesh sharing that anchor) and frames its bounds. Falls back to
	// the single mesh, then to the raw hit point (e.g. a shell surface).
	_focusOnHit(object, hitPoint) {
		if (!this.controls || !this.camera) return;
		const box = new THREE.Box3();
		// Prefab-root pick (ASK-087 ruling): focus the FIXTURE INSTANCE — the Inspector's selection
		// unit — never a loose product. Direct fixture hits carry the composer's `__cat__<ANCHOR>__`
		// naming; seated products are parented to the room/stage root (NOT the fixture), so a
		// parent-walk can't find their host — they resolve spatially to the fixture whose bounds
		// contain them. (Products become their own focusable unit only once they're first-class
		// editable, post-Inspector.)
		// A shell hit (floor/wall/ceiling) focuses WHERE YOU CLICKED — it must never resolve to a
		// nearby fixture, which is how a floor double-click used to snap onto a neighbour.
		const anchor = this._isShellMesh(object) ? null : this._anchorForHit(object, hitPoint);
		if (anchor) {
			// Bounds from the composed-mesh witness: the rebind registry first — it's authoritative
			// and covers GLB-authored meshes that carry no `__cat__` name (e.g. the spinner-rack's
			// 207 frame meshes) — with the composer's `__cat__` naming answering for every instance
			// not in it.
			for (const m of this._composedMeshesFor(anchor)) if (m?.visible) box.expandByObject(m);
		}
		// Never fall back to a shell mesh's own bounds — that would frame the whole floor/wall.
		if (box.isEmpty() && object?.isMesh && !this._isShellMesh(object)) box.setFromObject(object);
		let center, radius;
		if (box.isEmpty()) { center = hitPoint.clone(); radius = 0.35; }
		else {
			center = box.getCenter(new THREE.Vector3());
			radius = Math.max(0.18, box.getSize(new THREE.Vector3()).length() * 0.5);
		}
		this._frameAround(center, radius);
	}

	// The composer names fixture meshes `<kind>__cat__<ANCHOR>` (stage) or `<kind>__cat__<ANCHOR>__<part>`
	// (composer.js — `__Body` / `__Lid`, the only two parts it mints) — pull the anchor out.
	//
	// 🔴 AN ANCHOR NAME MAY ITSELF CONTAIN `__`, and this used to read `split("__")[2]`, which
	// TRUNCATED it. Measured on the deployed plane 2026-09-04 (ASK-127 E4): GCG mints a pack
	// placement's anchor as `PACK_<PACK>__<PLACEMENTREF>` — e.g. `PACK_HALLOWEEN__HALLOWEEN_SHELF` —
	// so every pack-placed fixture resolved to `PACK_HALLOWEEN` and `_composedMeshesFor` returned
	// NOTHING for it. That is not one feature's bug: `_selectionBounds`, `_fixtureFootprint`,
	// `_placementFixtureFor` and the click→select path all read through here, so a pack fixture had no
	// selection box, no measured footprint and no bounds — it was simply invisible to every part of
	// the editor that asks "which fixture is this mesh?". Found by the pack-mark leg going red with
	// two rows on the wire and zero marks on screen.
	//
	// The anchor is resolved against the ones this scene actually REGISTERED, so a name with any
	// number of `__` segments lands on the real anchor rather than on a guess about where it ends.
	// The `__Body`/`__Lid` strip is the fallback for the window before registration exists.
	_anchorFromName(object) {
		return anchorFromCatName(object?.name, (a) => this._anchorIsRegistered(a));
	}

	/** Is this a fixture anchor the scene knows about? Reads the registry the composer fills, plus the
	 *  per-anchor transform groups — the two places an anchor's identity is already recorded. */
	_anchorIsRegistered(anchorName) {
		if (!anchorName) return false;
		if (this._fixtureGroups?.has?.(anchorName)) return true;
		for (const entries of (this._shopAnchors?.registered?.values?.() || [])) {
			if (entries?.some?.((e) => e?.name === anchorName)) return true;
		}
		return false;
	}

	_isShellMesh(object) {
		return /^Shell_(Floor|Ceiling|Wall_)/i.test(String(object?.name || ""));
	}

	// ─── DOLLHOUSE — the roof comes off in Edit (ASK-111) ───────────────────────────────────────
	// God's-eye Edit is a DOLLHOUSE view: the merchant looks down into the shop, so the ceiling must
	// not be between them and it. This is the AAA build-mode convention (Sims 4 roof toggle, Planet
	// Coaster / Cities build view) and it is not cosmetic — `_continuousHitUnder` casts DOWN from 10 m
	// above the cursor and takes the first UP-facing hit, so an unhidden ceiling's TOP face is the
	// first thing every placement ray lands on. The fixture is then offered the roof, and the
	// attachment grammar correctly refuses it ("a meshsmith rocket needs floor; this is a ceiling
	// surface"). Coord's shopfit gate measured exactly that on a composed shop: 33 ceiling hits, 0
	// floor. The refusal was right; the surface should never have been a candidate.
	//
	// MATCH BY NAME UNDER TRAVERSAL, not getObjectByName: S2 keeps module names verbatim, so a
	// composed shell carries one `Shell_Ceiling` PER MODULE — test-shop has three, one each inside
	// `MODULE_entrance` / `MODULE_corridor` / `MODULE_room` (measured against the served cooked GLB,
	// 2026-08-23). getObjectByName returns the first and would leave the other two overhead.
	//
	// Restores only what IT hid: a mesh already invisible for another reason (the per-scene
	// `hideCeiling` filter, a view mode) stays that way when Play returns.
	_shellCeilingMeshes() {
		const root = this._shellRoot || this.roomRoot || this.stageRoot || this.scene;
		if (!root) return [];
		const out = [];
		root.traverse((n) => { if (n.isMesh && /^Shell_Ceiling(_|$)/i.test(String(n.name || ""))) out.push(n); });
		return out;
	}

	/** May this mesh receive a HUNG fixture? Visible, or hidden only because the dollhouse took the
	 *  roof off — see `_ceilingHitAbove`. Anything hidden for another reason stays out. */
	_isHangTarget(obj) {
		return !!obj && (obj.visible || !!this._dollhouseHidden?.has(obj));
	}

	/** Hide (Edit) or restore (Play) the shell's ceilings. Idempotent and authoritative — safe to call
	 *  on every mode change and after every recompose, which is what keeps a module composed AFTER
	 *  entering Edit from dropping a roof back over the merchant's head. */
	setDollhouse(on) {
		this._dollhouse = !!on;
		this._dollhouseHidden ||= new Set();
		const ceilings = this._shellCeilingMeshes();
		if (on) {
			for (const m of ceilings) {
				if (m.visible === false) continue;      // already hidden by someone else — not ours to restore
				m.visible = false;
				this._dollhouseHidden.add(m);
			}
		} else {
			for (const m of this._dollhouseHidden) { if (m) m.visible = true; }
			this._dollhouseHidden.clear();
		}
		return { on: this._dollhouse, ceilings: ceilings.length, hidden: this._dollhouseHidden.size };
	}

	// Resolve the fixture INSTANCE a hit belongs to — deterministic, in confidence order, and never
	// by "nearest neighbour" (that made a floor/counter click snap focus onto an adjacent fixture):
	//   1. composer `__cat__<ANCHOR>__` naming (pile-table),
	//   2. REGISTRY MEMBERSHIP by object identity — the authoritative test, and the only one that
	//      works for GLB-authored meshes with generic names (spinner `Body_*`, longbox `Mesh001`/`Mesh_1`),
	//   3. a known anchorName embedded in the mesh name — interaction proxies that aren't registry
	//      members (e.g. `lid_hit_zone_LONGBOX_ANCHOR_01`),
	//   4. STRICT containment for seated products (parented to the room root, not the fixture).
	// Returns null when the hit belongs to no fixture — the caller then focuses the clicked POINT
	// rather than hijacking to something the user didn't click.
	_anchorForHit(object, point) {
		const named = this._anchorFromName(object);
		if (named) return named;
		const rebinds = this._fixtureMaterialRebinds || [];
		for (const rb of rebinds) {
			if (rb?.anchorName && Array.isArray(rb.meshes) && rb.meshes.includes(object)) return rb.anchorName;
		}
		const nm = String(object?.name || "");
		for (const rb of rebinds) {
			if (rb?.anchorName && nm.includes(rb.anchorName)) return rb.anchorName;
		}
		return this._hostFixtureAnchorAt(point);
	}

	// Seated products are parented to the room/stage root, not their fixture, so resolve them by
	// STRICT containment in a fixture instance's (slightly padded) bounds. No nearest-match fallback:
	// if the point isn't inside a fixture, it belongs to no fixture.
	_hostFixtureAnchorAt(point) {
		if (!point) return null;
		// Door 9: enumerate holders from the composed witness, so a product seated on a placed
		// derived-surface fixture resolves its host like any other.
		const box = new THREE.Box3();
		for (const { anchorName } of this.composedFixtures()) {
			const meshes = this._composedMeshesFor(anchorName);
			if (!meshes.length) continue;
			box.makeEmpty();
			for (const m of meshes) if (m?.visible) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
			if (box.isEmpty()) continue;
			box.expandByScalar(0.15);   // products sit just on/above the surface
			if (box.containsPoint(point)) return anchorName;
		}
		return null;
	}

	// Pivot on `center` and dolly along the CURRENT view direction so a sphere of `radius` fits,
	// then PIN the camera on the pivot. Setting position+target alone is not enough: the camera may
	// still carry an orientation written directly by the FPS pointer-lock look (quaternion from
	// yaw/pitch euler), and with damping OrbitControls would interpolate the next frames from that
	// stale state — the "repositions but doesn't face the target, one drag snaps it correct" defect.
	// So we clear the interpolation deltas, SEED the spherical state from the new offset, and
	// look-at explicitly rather than relying on update() alone. Result: the item is dead-centre and
	// stays pinned — turntable orbit about the pivot, never camera-axis rotation.
	_frameAround(center, radius) {
		const c = this.controls;
		c.target.copy(center);
		const fovY = THREE.MathUtils.degToRad(this.camera.fov || 42);
		const fit  = (radius / Math.sin(Math.min(Math.PI / 2, fovY / 2))) * 1.3;
		const dist = THREE.MathUtils.clamp(fit, c.minDistance + 0.2, c.maxDistance);
		const dir = new THREE.Vector3().subVectors(this.camera.position, center);
		if (dir.lengthSq() < 1e-4) dir.set(0.4, 0.4, 1);
		dir.normalize().multiplyScalar(dist);
		this.camera.position.copy(center).add(dir);
		this._pinCameraToTarget();
	}

	// ASK-087 — Esc in Edit is cancel/deselect (never the Play-only pause surface). There is no
	// selection model yet, so today this only drops any in-flight camera interaction; per-object
	// deselect hooks in here when the Inspector's selection unit lands.
	cancelEditInteraction() {
		if (!this._cameraAuthoring || !this.controls) return false;
		this.controls._sphericalDelta?.set(0, 0, 0);
		this.controls._panOffset?.set(0, 0, 0);
		this.deselect();   // Esc in Edit also clears the current selection (cancel/deselect grammar)
		return true;
	}

	// ─── Selection (ASK-087 Phase 1, slice 1) ────────────────────────────────────────────────────
	// The fixture INSTANCE is the selection unit — same unit as double-click focus and the Inspector.
	// A single click in Edit selects (a drag orbits; the pointer-move threshold discriminates);
	// clicking empty space deselects; Esc deselects. Selection drives a Box3Helper highlight + a
	// `galleria:selection` broadcast that the Inspector, per-light editor, and place/move subscribe to.
	// Play never selects (clicks lock the FPS pointer). The unit resolves through the same registry
	// membership path as focus, so products resolve to their host fixture.
	selectFixture(anchorName) {
		if (!anchorName) return this.deselect(), null;
		// Door 10: a fixture is selectable because it COMPOSED, not because it needed a material
		// rebind — a placed pedestal could not be selected at all (eyes-on, Shaun 2026-09-02).
		const composed = this.composedFixtures().find((f) => f.anchorName === anchorName);
		if (!composed && !this._composedMeshesFor(anchorName).length) return null;
		this._selectedAnchor = anchorName;
		this._selectedKind = composed?.kind || null;
		this._selectedSeat = null;                 // a fixture-grained select clears any seat focus
		this._broadcastSelection();                // …and the bus refreshes both helpers (§chrome)
		return this.getSelection();
	}

	/**
	 * Select ONE SEAT (ASK-120 S3.5) — the finer grain inside a holder.
	 *
	 * This does NOT replace fixture selection: it selects the holder AND names the seat, because a
	 * seat has no independent existence (Unity's click-a-child → the parent's inspector, with the
	 * child row focused). So the Inspector still opens on the holder, the generic body still shows
	 * position/rotation/scale, and the STOCK section scrolls that seat's row into view.
	 */
	selectSeat(seat) {
		if (!seat?.anchorName) return null;
		// Door 11: the holder's kind from the composed witness — a seat on a placed pedestal opened
		// a kind-less "Position —" Inspector with no STOCK section (eyes-on, Shaun 2026-09-02).
		const kind = this.composedFixtures().find((f) => f.anchorName === seat.anchorName)?.kind;
		this._selectedAnchor = seat.anchorName;
		this._selectedKind = kind || this._selectedKind || null;
		this._selectedSeat = { anchorName: seat.anchorName, socketId: seat.socketId ?? null, slot: seat.slot ?? 0, productId: seat.productId ?? null, via: seat.via ?? null, at: seat.at ?? null };
		this._broadcastSelection();                // …and the bus refreshes both helpers (§chrome)
		return this.getSelection();
	}

	deselect() {
		if (!this._selectedAnchor) return false;
		this._selectedAnchor = null;
		this._selectedKind = null;
		this._selectedSeat = null;
		if (this._selectionHelper) this._selectionHelper.visible = false;
		this._broadcastSelection();                // …and the bus hides the seat outline (§chrome)
		return true;
	}

	// ── ASK-128 §chrome — THE RULE: SCENE CHROME SUBSCRIBES; MUTATORS EMIT ──────────────────────
	//
	// Six E5 rounds were one defect class fixed per instance: chrome that did not hear about a state
	// change (the seat outline vs the drag · vs the re-seat turn · the Inspector vs a dead subject).
	// Every one of them was a call-site someone forgot, and a rule enforced at every call-site is a
	// rule that fails at the next call-site. The panel has always lived the other way — it listens for
	// `kx:zones-changed` — and this is the same shape for the SCENE.
	//
	// The precedent is the one every editor uses: Unity's SceneView subscribes to editor-wide events
	// and repaints (it does not ask each mutator to call it); Unreal's editor multicasts
	// `OnObjectModified` / `SelectionChangedEvent` and the gizmo rebuilds FROM selection through one
	// path. Four kinds are enough for everything this editor draws:
	//
	//   selectionChanged — the selection unit changed (fixture or seat)
	//   seatMoved        — a seat is being dragged, or has just landed / gone home
	//   seatsChanged     — the seats a zone draws were rebuilt (re-seat, clear)
	//   shapeChanged     — the derived selling space changed (local re-derive or served adoption)
	//
	// A new mutator updates every chrome for free; a new chrome subscribes once.
	onEditorChange(fn) {
		if (typeof fn !== "function") return () => {};
		(this._editorChangeSubs || (this._editorChangeSubs = new Set())).add(fn);
		return () => { this._editorChangeSubs?.delete(fn); };
	}

	/** Tell every chrome. A subscriber that throws must not take the others down with it — the whole
	 *  point of the bus is that one broken piece of chrome cannot stop the rest from settling. */
	emitEditorChange(kind, detail = null) {
		for (const fn of [...(this._editorChangeSubs || [])]) {
			try { fn(kind, detail); } catch (err) { console.warn(`[edit] chrome subscriber failed on ${kind}:`, err?.message || err); }
		}
	}

	/** The ONE subscription that owns the selection chrome — installed once, in the constructor. */
	_bindSelectionChrome() {
		if (this._selectionChromeBound) return;
		this._selectionChromeBound = true;
		this.onEditorChange((kind) => {
			// The outline is refreshed on EVERY kind, immediately: a box one frame behind the item is
			// the frame the merchant is looking at (E5-R1), and after a re-seat the pose itself may
			// have changed (E5-R4).
			this._refreshSeatHelper();
			if (kind === "selectionChanged") this._refreshSelectionHelper();
			// Whether the selected seat still EXISTS, and under which slot, can only be answered once
			// the redraw has settled — a re-seat clears before it seats, and deciding mid-cycle would
			// drop the selection on every redraw. So that half is deferred by a tick and coalesced.
			if (kind === "seatsChanged") this._scheduleSeatSelectionReconcile();
		});
	}

	/**
	 * ⚠️ AUDIT CELLS 1 & 2 — the selection must not outlive, or lose track of, its seat.
	 *
	 * A seat is not a durable object: the re-seat rebuilds every mesh, the packer may hand the same
	 * product a different slot, and turning the zone off deletes it outright. Selection stored the
	 * slot it was made with, so after a redraw it could point at a slot nobody drew (outline gone,
	 * Inspector still editing a subject that no longer exists — the merchant's own words: "it's
	 * selected but there's nothing there").
	 *
	 * The identity that survives a redraw is the PRODUCT in the region, not the slot — so the seat is
	 * re-resolved by product, and released only when the product really is not seated here any more.
	 */
	_scheduleSeatSelectionReconcile() {
		if (this._seatReconcileQueued) return;
		this._seatReconcileQueued = true;
		setTimeout(() => {
			this._seatReconcileQueued = false;
			this._reconcileSeatSelection();
		}, 0);
	}

	_reconcileSeatSelection() {
		const seat = this._selectedSeat;
		if (!seat || seat.anchorName !== "ZONE") return;
		// Mid-redraw the scene is momentarily empty and looks exactly like "the seat is gone". Wait for
		// the re-seat to finish rather than releasing a selection the merchant never dropped; bounded,
		// so a re-seat that never settles cannot leave this rescheduling for ever.
		if (this.zoneSeatingInFlight()) {
			if ((this._seatReconcileWaits = (this._seatReconcileWaits || 0) + 1) < 60) {
				setTimeout(() => this._reconcileSeatSelection(), 100);
				return;
			}
		}
		this._seatReconcileWaits = 0;
		const drawn = this.zoneSeatedItems(seat.socketId ?? null);
		const mine = drawn.find((it) => it.productId === seat.productId) || null;
		if (!mine) {
			// The seat is gone — the zone was switched off, or the product left the shop. Release the
			// whole selection: a dead subject in the Inspector is worse than no selection at all.
			if (this.deselect()) console.log(`[edit] selection released — the seat it named is no longer drawn (${seat.productId})`);
			return;
		}
		const moved = mine.slot !== seat.slot
			|| (mine.at?.u ?? null) !== (seat.at?.u ?? null) || (mine.at?.v ?? null) !== (seat.at?.v ?? null)
			|| (mine.pinned === true) !== (seat.via === "pin");
		if (!moved) { this._refreshSeatHelper(); return; }
		// Same product, new slot or new address: follow it, and tell the Inspector so its words are
		// about the seat as it now stands.
		// …provenance included: a seat that was pinned and is now packed is a different fact about the
		// same product, and the Inspector's words are about exactly that.
		this._selectedSeat = { ...seat, slot: mine.slot, at: mine.at ?? null, via: mine.pinned ? "pin" : (mine.at ? "fill" : null) };
		this._refreshSeatHelper();
		this._broadcastSelection();
	}

	getSelection() {
		if (!this._selectedAnchor) return null;
		const sel = { anchorName: this._selectedAnchor, kind: this._selectedKind };
		if (this._selectedSeat) sel.seat = this._selectedSeat;   // additive — subscribers that predate seats ignore it
		return sel;
	}

	/** The selected seat's own outline — the seat is a selection unit, so it needs a mark of its own
	 *  distinct from the fixture's box. Drawn on the editor layer like every other authoring artifact
	 *  (`probe-editor-layer` gates that nothing here leaks into Play). */
	_refreshSeatHelper() {
		const seat = this._selectedSeat;
		const meshes = seat ? this._meshesForSeat(seat) : [];
		if (!meshes.length) { if (this._seatHelper) this._seatHelper.visible = false; return; }
		const box = new THREE.Box3();
		for (const m of meshes) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
		if (box.isEmpty()) { if (this._seatHelper) this._seatHelper.visible = false; return; }
		box.expandByScalar(0.004);                     // a hair proud of the cover, so the line reads
		if (!this._seatBox) this._seatBox = new THREE.Box3();
		this._seatBox.copy(box);
		if (!this._seatHelper) {
			this._seatHelper = new THREE.Box3Helper(this._seatBox, 0xffc93c);   // amber — not the fixture's blue
			this._seatHelper.name = "__edit_seat_selection__";
			this._seatHelper.renderOrder = 9999;
			if (this._seatHelper.material) { this._seatHelper.material.depthTest = false; this._seatHelper.material.transparent = true; }
			this._ensureEditorLayer()?.add(this._seatHelper);
		} else {
			this._seatHelper.box = this._seatBox;
			this._seatHelper.visible = true;
			if (!this._seatHelper.parent) this._ensureEditorLayer()?.add(this._seatHelper);
		}
	}

	/**
	 * The drop-target mark for a pin drag: a validity-coloured box over the holder (or the seat, when
	 * the pointer is over one) plus the reason line. Deliberately the SAME green/red pair and the SAME
	 * hint bar the fixture-move drag uses (`_showDragHint`) — a merchant should not have to learn two
	 * grammars for "this drop will work" — and it lives on the editor layer, so the wholesale Play gate
	 * `probe-editor-layer` enforces covers it with no new rule.
	 */
	showPinDropTarget({ anchorName, seat = null, at = null, region = null, footprint = null, valid = false, hint = "" } = {}) {
		// ASK-121 — over a REGION the mark is the item's own FOOTPRINT at the cursor, drawn in the
		// region's plane. A box around the whole table would answer "can I drop on this fixture?" when
		// the merchant is asking "will it fit HERE?", which on a continuous domain is the only question
		// worth asking. Drawn as a line loop from the region's own axes so it stays exact on a rotated
		// fixture, where an axis-aligned Box3 would read as a larger, wrongly-turned rectangle.
		if (at && region && footprint) {
			this._showRegionDropMark(region, at, footprint, valid);
			if (hint) this._showDragHint(hint, valid);
			if (this._pinDropHelper) this._pinDropHelper.visible = false;
			return true;
		}
		if (this._regionDropMark) this._regionDropMark.visible = false;
		const box = new THREE.Box3();
		const meshes = seat ? this._meshesForSeat(seat) : [];
		if (meshes.length) { for (const m of meshes) { m.updateWorldMatrix(true, false); box.expandByObject(m); } }
		else if (anchorName) {
			const b = this._selectionBounds(anchorName);
			if (b) box.copy(b);
		}
		if (box.isEmpty()) { this.hidePinDropTarget(); return false; }
		box.expandByScalar(seat ? 0.006 : 0.02);
		if (!this._pinDropBox) this._pinDropBox = new THREE.Box3();
		this._pinDropBox.copy(box);
		if (!this._pinDropHelper) {
			this._pinDropHelper = new THREE.Box3Helper(this._pinDropBox, valid ? 0x35c46a : 0xe5484d);
			this._pinDropHelper.name = "__edit_pin_drop__";
			this._pinDropHelper.renderOrder = 9999;
			if (this._pinDropHelper.material) { this._pinDropHelper.material.depthTest = false; this._pinDropHelper.material.transparent = true; }
			this._ensureEditorLayer()?.add(this._pinDropHelper);
		} else {
			this._pinDropHelper.box = this._pinDropBox;
			this._pinDropHelper.visible = true;
			this._pinDropHelper.material?.color?.setHex(valid ? 0x35c46a : 0xe5484d);
			if (!this._pinDropHelper.parent) this._ensureEditorLayer()?.add(this._pinDropHelper);
		}
		if (hint) this._showDragHint(hint, valid);
		return true;
	}

	hidePinDropTarget() {
		if (this._pinDropHelper) this._pinDropHelper.visible = false;
		if (this._regionDropMark) this._regionDropMark.visible = false;
		this._hideDragHint();
	}

	/** The footprint outline for a region drop: a closed loop in the region's plane, at `{u,v}`,
	 *  sized to the item and coloured by validity. Editor-layer, so the Play-mode sweep covers it. */
	_showRegionDropMark(region, at, footprint, valid) {
		const o = new THREE.Vector3(region.uvOrigin.x, region.uvOrigin.y, region.uvOrigin.z);
		const U = new THREE.Vector3(region.uAxis.x, region.uAxis.y, region.uAxis.z);
		const Vv = new THREE.Vector3(region.vAxis.x, region.vAxis.y, region.vAxis.z);
		const N = new THREE.Vector3(region.normal.x, region.normal.y, region.normal.z);
		const hu = (footprint.u || 0.05) / 2, hv = (footprint.v || 0.05) / 2;
		const corner = (su, sv) => o.clone()
			.addScaledVector(U, at.u + su * hu).addScaledVector(Vv, at.v + sv * hv)
			.addScaledVector(N, 0.002);                       // a hair proud of the top, so the line reads
		const pts = [corner(-1, -1), corner(1, -1), corner(1, 1), corner(-1, 1), corner(-1, -1)];
		if (!this._regionDropMark) {
			const geom = new THREE.BufferGeometry().setFromPoints(pts);
			const mat = new THREE.LineBasicMaterial({ color: valid ? 0x35c46a : 0xe5484d, depthTest: false, transparent: true });
			this._regionDropMark = new THREE.Line(geom, mat);
			this._regionDropMark.name = "__edit_pin_region_mark__";
			this._regionDropMark.renderOrder = 9999;
			this._ensureEditorLayer()?.add(this._regionDropMark);
		} else {
			this._regionDropMark.geometry.setFromPoints(pts);
			this._regionDropMark.geometry.attributes.position.needsUpdate = true;
			this._regionDropMark.material.color.setHex(valid ? 0x35c46a : 0xe5484d);
			this._regionDropMark.visible = true;
			if (!this._regionDropMark.parent) this._ensureEditorLayer()?.add(this._regionDropMark);
		}
	}

	// ─── Surface regions (ASK-121) ───────────────────────────────────────────────────────────────
	// A `surface` socket is a REGION, not a spot, so "where did the merchant drop it?" has a real
	// answer for the first time: the `{u,v}` under the cursor. The composer registers the region the
	// grammar resolved (world metres + the socket's own axes), and the two functions below are the
	// only place a screen pixel becomes an address — the drag, the armed click and the probe all go
	// through them, so they cannot disagree about where a drop landed.

	/** Register (or replace) the regions a fixture's surface sockets resolved to. Called by the
	 *  composer with `slotResult.extras.region`; idempotent per (anchor, socket). */
	_registerSurfaceRegion(region) {
		if (!region?.anchorName) return;
		this._surfaceRegions ||= new Map();
		const list = (this._surfaceRegions.get(region.anchorName) || []).filter((r) => r.socketId !== region.socketId);
		list.push(region);
		this._surfaceRegions.set(region.anchorName, list);
	}

	/** Every registered region for a holder (`[]` when it has none — a point/spline holder). */
	surfaceRegionsFor(anchorName) {
		return this._surfaceRegions?.get?.(anchorName) || [];
	}

	/**
	 * A WORLD point → the region's `{u,v}` in metres, or null when the point is off the region.
	 *
	 * REFUSES rather than clamping. A drop 50 mm past the edge of a table is not a drop at the edge:
	 * clamping would write an address the merchant never chose and then show the item somewhere they
	 * did not put it. Outside is outside, and the drop target reads red. The same goes for a `blocked`
	 * patch — the mint measures the till standing on the counter, and a drop on the till is not a
	 * drop on the counter (`wooden-checkout-counter` carries 7 such patches, measured).
	 */
	surfaceAddressAt(region, worldPoint) {
		if (!region?.uAxis || !worldPoint) return null;
		const d = new THREE.Vector3(worldPoint.x, worldPoint.y, worldPoint.z).sub(new THREE.Vector3(region.uvOrigin.x, region.uvOrigin.y, region.uvOrigin.z));
		const u = d.dot(new THREE.Vector3(region.uAxis.x, region.uAxis.y, region.uAxis.z));
		const v = d.dot(new THREE.Vector3(region.vAxis.x, region.vAxis.y, region.vAxis.z));
		return frameContains(region, u, v) ? { u: +u.toFixed(4), v: +v.toFixed(4) } : null;
	}

	/**
	 * What is under this screen point, at PIN grain? The drag controller's every-move question.
	 * Returns the seat when the pointer is over a seated product, else the holder under it, else null.
	 * One raycast, the same pick set the click path uses — so the drop target and the click target can
	 * never be different things.
	 *
	 * ASK-121: on a holder carrying a `surface` socket the hit ALSO carries the `{u,v}` under the
	 * cursor, because on a continuous domain the placement IS the pin (ASK-120 S3.6) — a drop there
	 * is not "somewhere on this table", it is that spot, and the address has to come from the pixel
	 * the merchant is looking at rather than from the resolver's later opinion.
	 * @returns {{seat?, anchorName, kind, at?, socketId?}|null}
	 */
	hitTestPinTarget(clientX, clientY) {
		const canvas = this.renderer?.domElement;
		if (!canvas || !this.camera) return null;
		const rect = canvas.getBoundingClientRect();
		const ndc = new THREE.Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
		this.raycaster.setFromCamera(ndc, this.camera);
		const hits = this.raycaster.intersectObjects(this._pickableSceneChildren(), true).filter((h) => h.object?.isMesh && h.object.visible);
		const hit = hits.find((h) => !this._isShellMesh(h.object)) || hits[0];
		if (!hit) return null;
		const seat = this._seatForHit(hit.object);
		const anchorName = seat?.anchorName || this._anchorForHit(hit.object, hit.point);
		if (!anchorName) return null;
		// The kind comes from the composed-fixture witness, NOT the rebind registry — the fifth
		// door of the registry-standing-in defect a658f45 closed at four. A derived-surface holder
		// has no rebind row, so `kind` came back null here, `evaluate` found no catalogue entry,
		// and the drag showed no hint, no mark and wrote nothing over a fixture whose region this
		// same function had just resolved to a {u,v}.
		const kind = this.composedFixtures().find((f) => f.anchorName === anchorName)?.kind || null;
		const out = { anchorName, kind, seat: seat || null, point: hit.point };
		// The cursor is over a region when the ray meets its PLANE inside its polygon — not merely when
		// it hits some mesh of the fixture. A ray that clips the table's leg is not a drop on the top.
		for (const region of this.surfaceRegionsFor(anchorName)) {
			const onPlane = this._rayPointOnRegion(region);
			const at = onPlane ? this.surfaceAddressAt(region, onPlane) : null;
			if (at) { out.at = at; out.socketId = region.socketId; out.region = region; break; }
		}
		return out;
	}

	/** Where the current raycast meets a region's plane, in world metres (null if parallel/behind). */
	_rayPointOnRegion(region) {
		const n = new THREE.Vector3(region.normal.x, region.normal.y, region.normal.z);
		const o = new THREE.Vector3(region.uvOrigin.x, region.uvOrigin.y, region.uvOrigin.z);
		const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(n, o);
		const p = new THREE.Vector3();
		return this.raycaster.ray.intersectPlane(plane, p) ? p : null;
	}

	// Box3 over the selected instance's composed meshes (the same bounds focus frames). Sixth door
	// of the registry-standing-in defect: reading rebinds alone left this empty for a derived-surface
	// holder, and showPinDropTarget's holder-grain path then hid the hint instead of drawing a box.
	_selectionBounds(anchorName) {
		const meshes = this._composedMeshesFor(anchorName);
		if (!meshes.length) return null;
		const box = new THREE.Box3();
		// Full ancestor-chain update first: expandByObject only refreshes the object's OWN matrix
		// (updateWorldMatrix(false,false)), so a freshly placed clone selected before the next render
		// tick still carried stale parent matrices — the selection box framed the world origin (read:
		// the pile-table) instead of the luminaire. Eyes-on catch, 2026-08-03.
		for (const m of meshes) if (m?.visible) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
		return box.isEmpty() ? null : box;
	}

	// ─── ASK-127 E4/F3 — PACK ATTRIBUTION IN THE SCENE ──────────────────────────────────────────
	//
	// The panel says `from "«pack»"` on pool cards and seat rows; the OBJECT standing in the shop said
	// nothing. Shaun's E4 walk: after the Halloween pack was put away, vanessa's OWN bookshelf and
	// pedestals — the same kinds the pack had placed — read as furniture the pack had failed to take
	// with it. Belonging has to be shown on the thing, not only in a list.
	//
	// AAA precedent: UE Data Layers colour-code actors by their owning layer; Unity marks
	// prefab-instance objects distinctly in the scene/Hierarchy. Both draw the mark in EDITOR chrome
	// only — so this lives in `__editor_layer__`, which is already gated on `_cameraAuthoring`, and is
	// therefore pixel-inert in Play and in every customer view by construction (the same door the
	// selection box, seat helper and drop marks use).
	//
	// The INPUT is a feed fact the caller computed (`packMarkedInstances` — rows carrying `packId`);
	// the stage's whole job is to draw it. No pack vocabulary reaches the customer bundle: Studio owns
	// the decision and this method owns the box.
	setPackMarks(marks) {
		this._packMarks ||= new Map();   // anchorName → { helper, box, packId }
		const wanted = new Map();
		for (const m of (Array.isArray(marks) ? marks : [])) if (m?.anchorName) wanted.set(m.anchorName, m);
		// Drop marks whose row no longer carries a packId (put-away, or sticky conversion made it the
		// merchant's) — and any whose fixture has left the scene.
		for (const [anchorName, entry] of [...this._packMarks]) {
			if (wanted.has(anchorName)) continue;
			entry.helper?.parent?.remove(entry.helper);
			entry.helper?.material?.dispose?.();
			this._packMarks.delete(anchorName);
		}
		for (const [anchorName, m] of wanted) {
			const bounds = this._selectionBounds(anchorName);
			if (!bounds) { // composed mesh not there (yet) — no box is honest, a guessed one is not
				const stale = this._packMarks.get(anchorName);
				if (stale) { stale.helper?.parent?.remove(stale.helper); this._packMarks.delete(anchorName); }
				continue;
			}
			let entry = this._packMarks.get(anchorName);
			if (!entry) {
				const box = new THREE.Box3().copy(bounds);
				// Pack purple — the same hue the panel's attribution chip and edit pill already use, so
				// "this came with a pack" reads as ONE colour across panel and scene.
				const helper = new THREE.Box3Helper(box, 0xb06cf0);
				helper.name = `__pack_mark__${anchorName}`;
				helper.renderOrder = 9998;   // under the selection box (9999): selection still wins
				helper.userData.packMark = true;
				if (helper.material) { helper.material.depthTest = false; helper.material.transparent = true; helper.material.opacity = 0.9; }
				entry = { helper, box, packId: m.packId };
				this._packMarks.set(anchorName, entry);
			} else {
				entry.box.copy(bounds);
				entry.packId = m.packId;
			}
			entry.helper.visible = true;
			if (!entry.helper.parent) this._ensureEditorLayer()?.add(entry.helper);
		}
		return this._packMarks.size;
	}

	/** The anchors currently carrying a pack mark — the probe seam for the render-truth leg, and the
	 *  honest answer to "what does the scene say belongs to a pack right now". */
	packMarkedAnchors() {
		return [...(this._packMarks || new Map()).entries()]
			.filter(([, e]) => e.helper?.visible && e.helper?.parent)
			.map(([anchorName, e]) => ({ anchorName, packId: e.packId }));
	}

	// ─── ASK-128 S3 — MERCH ZONES IN THE SCENE (thin delegators) ────────────────────────────────
	//
	// The implementation lives in `studio-v1/ws3/_zone-scene.js` and is LAZY, for the reason stated in
	// full there and in `_ensureWs3` below: stage.js is on the CUSTOMER path, and drawing/picking a
	// merch zone is authoring machinery. Measured before it was moved — inline, it cost the customer's
	// initial JS **+6.1 KB** (483.9 → 490.0 KB against a 1392 KB ceiling), which the budget gate passed
	// and should not have had to. What stays here is the SEAM: a handle, a loader, and one line per
	// verb, so callers talk to the stage and the bytes ship with the editor.
	//
	// Every verb is null/no-op safe before the chunk resolves. Studio awaits `ensureFloorClearances()`
	// at mount, so by the time a merchant can drag, every call below is synchronous — a same-frame
	// shrink cannot await a chunk load.

	_ensureZoneScene() {
		if (this._zoneScene || this._zoneSceneLoading) return this._zoneSceneLoading;
		this._zoneSceneLoading = import("./studio-v1/ws3/_zone-scene.js")
			.then((m) => { this._zoneScene = m.makeZoneScene(this); })
			.catch((err) => { console.warn("[edit] zone scene unavailable — the selling-space overlay will not draw", err); });
		return this._zoneSceneLoading;
	}

	/** Draw the DERIVED selling space (`[{regionId, y, rects}]`). `[]` clears it. */
	setZoneOverlay(shapes) { return this._zoneScene?.setOverlay(shapes) ?? 0; }
	/** What the SCENE says the selling space is right now — the render-truth seam. */
	zoneOverlayRects() { return this._zoneScene?.getOverlayRects() ?? []; }
	/** Total drawn selling area in m² — the one number a probe can compare across a drag. */
	zoneOverlayArea() { return this._zoneScene?.getOverlayArea() ?? 0; }
	/** Install the shapes the floor gesture picks against (the same list the overlay draws). */
	setZoneDropShapes(shapes) { return this._zoneScene?.setDropShapes(shapes) ?? 0; }
	/** The zone under a screen point, or null when the point is not on derived selling space. */
	hitTestZoneTarget(clientX, clientY) { return this._zoneScene?.hitTest(clientX, clientY) ?? null; }
	/** Every composed fixture as a floor obstacle: real world footprint + its declared clearance. */
	floorObstacles(exclude = null) { return this._zoneScene?.floorObstacles(exclude) ?? []; }
	/** Is this spot on the floor clear? The EXISTING clearance validator, over the existing neighbours. */
	validateFloorItemDrop(args) { return this._zoneScene?.validateFloorItemDrop(args) ?? null; }

	/** ASK-128 F-E — draw the stock the feed seats on a zone. Renders through `populateComics`, the
	 *  same per-line seat renderer every fixture uses; see `_zone-scene.js` for why that is the whole
	 *  design. Returns the number of items drawn. */
	async seatZone(args) {
		await this._ensureZoneScene();
		return (await this._zoneScene?.seatZone({ token: this.currentToken, ...args })) ?? 0;
	}
	/** Drop what a previous pass drew for one zone. Idempotent. */
	clearZoneSeats(regionId) { return this._zoneScene?.clearZoneSeats(regionId) ?? 0; }
	/** What the SCENE says is seated on a zone — read off the seat stamps, not off the plan. */
	zoneSeatedItems(regionId = null) { return this._zoneScene?.zoneSeatedItems(regionId) ?? []; }
	/** Is a re-seat mid-flight? The selection reconcile needs to tell "the redraw has not finished"
	 *  from "this seat is really gone" — they look identical in the scene for a few hundred ms. */
	zoneSeatingInFlight() { return this._zoneScene?.isSeating?.() ?? false; }
	/** ASK-128 E5-R2 — re-shade the floor that is already taken. Presentation only: it reads
	 *  `zoneSeatedItems()` and never touches the derivation or `zoneOverlayArea()`. */
	refreshZoneOccupancy() { return this._zoneScene?.refreshOccupied() ?? 0; }
	/** The occupied patches currently DRAWN — the render-truth seam for the shading. */
	zoneOccupiedPatches() { return this._zoneScene?.occupiedPatches() ?? []; }
	/** ASK-128 §post-close — draw the SERVED reachable set (`walkability.walkable[]`). `[]` clears it.
	 *  Nothing derives a walkable set anywhere in the client; this only ever draws what it is given. */
	setZoneWalkable(rects) { return this._zoneScene?.setWalkable(rects) ?? 0; }
	/** The walkway rectangles currently DRAWN, and their total — the render-truth seam for the tint. */
	zoneWalkableRects() { return this._zoneScene?.walkableDrawn() ?? []; }
	zoneWalkableArea() { return this._zoneScene?.walkableDrawnArea() ?? 0; }
	/** W2-5 — pale while a floor write is in flight: the server's answer is about the floor as it was. */
	setZoneWalkablePending(p) { return this._zoneScene?.setWalkablePending(p) ?? false; }
	zoneWalkablePending() { return this._zoneScene?.walkablePending() ?? false; }
	/** W2-4 — the merchant's own keep-outs, drawn as bodies rather than as holes in the green. */
	setZoneKeepOuts(rects) { return this._zoneScene?.setKeepOuts(rects) ?? 0; }
	zoneKeepOutsDrawn() { return this._zoneScene?.keepOutsDrawn() ?? []; }
	/** ASK-128 §stranded — draw the pockets that are open floor but not reachable from the door.
	 *  `open − served`, computed by the model; this only ever draws what it is handed. */
	setZoneStranded(rects) { return this._zoneScene?.setStranded(rects) ?? 0; }
	/** ASK-129 §E2E104 G3 — mark the door the walkways are anchored at, or take the mark down. Only
	 *  ever called with a point when the SERVER says it seeded from the entrance. */
	setZoneWalkOrigin(point) { return this._zoneScene?.setWalkOrigin(point) ?? false; }
	zoneWalkOriginDrawn() { return this._zoneScene?.walkOriginDrawn() ?? null; }
	/** ASK-129 §E2E104 G5 — outline the floor region the Selling-space inspector is editing, so the
	 *  thing being edited is the thing highlighted. `null` takes the outline down. */
	setZoneRegionOutline(rect) { return this._zoneScene?.setRegionOutline(rect) ?? false; }
	zoneRegionOutlineDrawn() { return this._zoneScene?.regionOutlineDrawn() ?? null; }
	zoneStrandedDrawn() { return this._zoneScene?.strandedDrawn() ?? []; }
	/** §walk-polish (b) — a momentary ring where a seat now stands, for when the shop moved it. */
	flashZoneSeat(bounds, opts) { return this._zoneScene?.flashSeat(bounds, opts) ?? false; }
	zoneSeatFlashing() { return this._zoneScene?.seatFlashing() ?? false; }
	/** Is a seat being dragged right now? The scene has always known; nothing outside it could ask. */
	isZoneSeatDragging() { return this._zoneScene?.isSeatDragging() ?? false; }

	/**
	 * Make `floorObstacles()` answer with REAL clearances, and the zone verbs answer at all — load the
	 * zone scene, the WS3 rules module and every composed kind's manifest, once. Awaited by the Zones
	 * panel before its first derive, which is what lets every later derive be SYNCHRONOUS.
	 */
	async ensureFloorClearances() {
		this._ensureWs3();
		await Promise.all([this._ws3Loading || Promise.resolve(), this._ensureZoneScene() || Promise.resolve()]);
		const kinds = [...new Set(this.composedFixtures().map((f) => f.kind).filter(Boolean))];
		await Promise.all(kinds.map((k) => this._ensureFixtureManifest(k).catch(() => {})));
		return this.floorObstacles();
	}

	/** Where a screen point meets a horizontal plane, in world metres. The exclusion gesture needs the
	 *  REGION's plane rather than the zone's rectangles — a merchant drawing a keep-out over the space
	 *  a shelf occupies is saying something meaningful, and a pick that only answered inside the
	 *  derived shape could not hear it. Public wrapper over the drag's own ground-point read, so the
	 *  two cannot map a pixel to two different metres. */
	floorPointOnPlane(clientX, clientY, planeY = 0) {
		return this._dragGroundPoint({ clientX, clientY }, planeY);
	}

	/** The hint bar, for an editor gesture this class does not itself own (the exclusion draw). Same
	 *  bar the move/place drags use — a merchant should not learn two places to look for the sentence
	 *  telling them what to do next. */
	showDragHint(text, valid = true) { return this._showDragHint(text, valid); }
	hideDragHint() { return this._hideDragHint(); }

	// ─── ASK-127 E4/F2 — ONE TRANSITION, NOT TWO ────────────────────────────────────────────────
	//
	// A pack's fixtures reach the scene through the /instances recompose and its props through the
	// feed re-seat. Those are two code paths, and on the walk Shaun SAW them land separately. The
	// caller now fetches both halves before touching the scene (see Studio's `__settlePackTransition`)
	// — this closes the last gap: while a settle is open the frame is HELD, so the browser never
	// presents a half-applied shop. AAA precedent: an editor applies a variant/layer swap as one
	// transaction and presents once; a partially-applied state is never a frame anyone sees.
	//
	// The hold is BOUNDED and always self-releases (`_atomicHoldUntil`). A cold catalogue fetch inside
	// the settle must never be able to freeze a merchant's viewport: past the ceiling the stage
	// presents again and the swap merely looks the way it does today. A hold that can hang is worse
	// than the stagger it removes.
	beginAtomicUpdate(maxMs = 2000) {
		this._atomicDepth = (this._atomicDepth || 0) + 1;
		this._atomicHoldUntil = Math.max(this._atomicHoldUntil || 0, Date.now() + Math.max(0, maxMs));
		return this._atomicDepth;
	}
	endAtomicUpdate() {
		this._atomicDepth = Math.max(0, (this._atomicDepth || 0) - 1);
		if (!this._atomicDepth) this._atomicHoldUntil = 0;
		return this._atomicDepth;
	}
	/** True while the presented frame is deliberately held. Read per-frame by `animate()`. */
	_frameHeld() {
		if (this._composeDepth > 0) return true;   // ASK-142 R2 — see setFullSceneContext
		if (!this._atomicDepth) return false;
		if (Date.now() >= (this._atomicHoldUntil || 0)) { this._atomicDepth = 0; this._atomicHoldUntil = 0; return false; }
		return true;
	}

	_refreshSelectionHelper() {
		const box = this._selectedAnchor ? this._selectionBounds(this._selectedAnchor) : null;
		if (!box) { if (this._selectionHelper) this._selectionHelper.visible = false; return; }
		if (!this._selectionBox) this._selectionBox = new THREE.Box3();
		this._selectionBox.copy(box);
		if (!this._selectionHelper) {
			// Box3Helper reads its `.box` each frame, so a persistent box we copy into keeps it live
			// (and correct after a recompose / future move). Drawn on top (depthTest off) so the
			// selection outline is never occluded by the fixture itself.
			this._selectionHelper = new THREE.Box3Helper(this._selectionBox, 0x4ea1ff);
			this._selectionHelper.name = "__edit_selection__";
			this._selectionHelper.renderOrder = 9999;
			if (this._selectionHelper.material) {
				this._selectionHelper.material.depthTest = false;
				this._selectionHelper.material.transparent = true;
			}
			this._ensureEditorLayer()?.add(this._selectionHelper);
		} else {
			this._selectionHelper.box = this._selectionBox;
			this._selectionHelper.visible = true;
			if (!this._selectionHelper.parent) this._ensureEditorLayer()?.add(this._selectionHelper);
		}
	}

	_broadcastSelection() {
		try { window.dispatchEvent(new CustomEvent("galleria:selection", { detail: this.getSelection() })); } catch { /* no window */ }
		this.emitEditorChange("selectionChanged", this.getSelection());
	}

	// ─── Per-anchor fixture transform group (ASK-087 Phase-1 slice-2) ───────────────────────────
	// DECISION-050 §2 needs a per-fixture-INSTANCE transform handle: required infrastructure whether
	// the transform is applied runtime-side (now) or mint-side (Forge Phase-B). The composer adds a
	// fixture's meshes individually to the shell root, so there is no natural handle — this creates
	// one per anchor and `attach()`es the meshes in (attach preserves WORLD transform, so composing
	// is visually identical; nothing moves until a move asks it to).
	//
	// DECISION-044 (Coord pre-ruling): seated products FOLLOW their host fixture — products seat on
	// sockets and socket poses derive from the fixture transform (same principle as LIGHT_ emitters
	// travelling with their lamp). Re-parenting contents into this group is the mechanism: because
	// the objects are IDENTITY-STABLE (same refs, just a new parent), every interaction handler stays
	// bound with no re-binding, and contents can't be left behind — "no floating comics" by
	// construction rather than by a post-move re-seat pass.
	_ensureFixtureGroup(anchorName, host = null) {
		if (!anchorName || !this.scene) return null;
		this._fixtureGroups ||= new Map();
		let g = this._fixtureGroups.get(anchorName);
		if (g && g.parent) return g;
		g = new THREE.Group();
		g.name = `__fixture__${anchorName}`;
		// DECISION-061 §3 — mount under the HOST when the row names one, shell root otherwise. Absent
		// host = shell root is the zero-migration clause, so this is the same line it always was for
		// every legacy row. Under a host, the group's transform is host-LOCAL and a host move carries
		// its placements for free: the scene graph does it, not a re-seat pass (the same reason
		// adoptFixtureContents re-parents rather than re-seating — "no floating comics" by construction).
		const hostNode = host ? this._hostNode(host) : null;
		if (host && !hostNode) {
			// Never mount a hosted placement at shell root in SILENCE — that reads as "the merchant
			// moved it to the middle of the shop". Two causes and they need telling apart: the host is
			// genuinely gone (an ORPHAN — D-045 surfaces it via orphanedPlacementRows) or it simply has
			// not composed yet (ordering). Either way the pose is not trustworthy until it is resolved.
			console.warn(`[d-061] ${anchorName}: host ${host} is not in the scene — mounting at shell root for now. If the host was removed this is an orphaned placement (D-045), not a move.`);
		}
		(hostNode || this._shellRoot || this.stageRoot || this.scene).add(g);
		// Stamped so the reverse lookup works: a raycast hit walks up to this group and reads the
		// instanceId straight off it (hostFromAncestors), which is how fixture-on-fixture gets its key.
		g.userData.anchorName = anchorName;
		g.userData.hostKey = host || null;
		this._fixtureGroups.set(anchorName, g);
		return g;
	}

	/** Resolve a D-061 host key to the node a placement mounts under, or null when it is not in the
	 *  scene (an ORPHAN — surfaced per D-045 by `orphanedPlacements`, never silently re-rooted). */
	_hostNode(hostKey) {
		if (!hostKey || !this._shellRoot) return null;
		if (hostKey.startsWith("MODULE_")) return this._shellRoot.getObjectByName(hostKey) || null;
		// Fixture-on-fixture: the host is an instanceId, and the group carrying it is the mount.
		for (const g of (this._fixtureGroups?.values?.() || [])) {
			if (g?.userData?.instanceId === hostKey) return g;
		}
		return null;
	}

	/** The host key for a raycast hit — the D-061 write-path rule, walked on the real scene graph and
	 *  answered by the pure model so the contract lives in ONE place (`_instance-transform.js`). */
	hostForObject(object) {
		const chain = [];
		for (let n = object; n; n = n.parent) chain.push({ name: n.name, userData: n.userData });
		return hostFromAncestors(chain);
	}

	/** The D-061 host a PLACED fixture is currently mounted under — read off the live scene graph
	 *  (where it actually is), not off the row (what someone once wrote). Null = shell root. */
	hostForAnchor(anchorName) {
		const g = this._fixtureGroups?.get?.(anchorName);
		return g?.parent ? this.hostForObject(g.parent) : null;
	}

	/** Placements whose host has left the scene. D-045 refuse-with-hint: NAMED, never dropped. */
	orphanedPlacementRows(rows) {
		const live = new Set();
		this._shellRoot?.traverse?.((n) => { if (typeof n.name === "string" && n.name.startsWith("MODULE_")) live.add(n.name); });
		for (const g of (this._fixtureGroups?.values?.() || [])) if (g?.userData?.instanceId) live.add(g.userData.instanceId);
		return orphanedPlacements(rows, live);
	}

	/** Adopt a fixture instance's meshes + its seated contents into the per-anchor group.
	 *  `attach` preserves world transforms, so this is a no-op visually. Contents come from the
	 *  per-anchor ownership registries (see _fixtureContentsFor) — the authoritative record of what
	 *  belongs to a fixture, written where the products are created. Idempotent: already-adopted
	 *  children are skipped. */
	adoptFixtureContents(anchorName) {
		const group = this._ensureFixtureGroup(anchorName);
		// Door 12: the fixture's own meshes come from the composed witness — the rebind row was
		// both the existence gate and the mesh source, so a placed derived-surface fixture could
		// never be adopted, and with it never moved.
		const bodyMeshes = this._composedMeshesFor(anchorName);
		if (!group || !bodyMeshes.length) return null;

		// Ownership comes from the per-anchor REGISTRY, never from spatial overlap (DECISION-044
		// ruling): `stage.boxEntries` (keyed by fixtureId = anchorName) for everything the composer
		// and its interactions record, plus `stage._fixtureContents` for socket-seated products.
		// See _fixtureContentsFor — it enumerates the entry rather than naming families, because the
		// hand-listed version missed every family but two.
		//
		// ROOTS ONLY. A node already inside the group, or inside another node being adopted, travels
		// with its parent — re-attaching it would flatten that hierarchy. This is exactly the
		// spinner-rack case (DECISION-051): its comics are seated under SPIN_ROOT inside `worldGroup`,
		// so adopting the leaf frame meshes both stranded the comics and would have broken the spin.
		const owned = this._fixtureContentsFor(anchorName);
		const isCarried = (o) => this._isDescendantOf(o, group) || owned.some((other) => other !== o && this._isDescendantOf(o, other));
		for (const obj of owned) {
			if (!obj || obj === group || isCarried(obj)) continue;
			group.attach(obj);
		}

		// The fixture's own meshes, for kinds whose geometry ISN'T held by a registered root (the
		// composer's Body-only clone family — longbox, tables). Anything already carried above is
		// skipped, so this never reaches inside an adopted hierarchy.
		for (const m of bodyMeshes) {
			if (!m || isCarried(m)) continue;
			group.attach(m);
		}
		return group;
	}

	/** Is `node` inside `ancestor`'s subtree? (Also true for node === ancestor.) */
	_isDescendantOf(node, ancestor) {
		if (!node || !ancestor) return false;
		for (let n = node; n; n = n.parent) if (n === ancestor) return true;
		return false;
	}

	/** The registered shell-anchor entry for a name, across all kind buckets. */
	_anchorEntryFor(anchorName) {
		for (const entries of (this._shopAnchors?.registered?.values?.() || [])) {
			const e = entries.find((x) => x?.name === anchorName);
			if (e) return e;
		}
		return null;
	}

	/** Put every already-composed fixture where its /instances row says it belongs.
	 *
	 *  Updating anchor ENTRIES is not enough on a recompose: recomposeTemplate deliberately keeps the
	 *  existing fixture meshes and only re-skins them (signage, materials) — nothing re-instances a
	 *  fixture at a new anchor position. So a hydrated move updated the anchor and changed nothing on
	 *  screen, which is how a merchant's move kept vanishing on reload even once the row was being
	 *  fetched and the precedence was being evaluated.
	 *
	 *  Applied through moveFixtureTo, so contents travel with the fixture by the same verified path a
	 *  drag uses. Two-way: a row that no longer owns a transform is returned to its shell pose rather
	 *  than left wherever it was last dragged. Idempotent — re-running with the same rows is a no-op
	 *  delta. */
	applyPersistedInstanceTransforms(instances) {
		const rows = Array.isArray(instances) ? instances : (this.currentScene?.instances || []);
		let applied = 0;
		for (const row of rows) {
			if (!row?.anchorName) continue;
			const entry = this._anchorEntryFor(row.anchorName);
			// 🔴 A NET-NEW FREE PLACEMENT HAS NO SHELL POSE, AND USED TO BE SKIPPED HERE.
			//
			// D-050 §2 is a PRECEDENCE rule — `row.field ?? shellAnchor.field` — so this applier was
			// written for rows that OVERRIDE a baked anchor, and `_shellPose` is the thing they
			// override. A fixture the merchant placed from nothing overrides no anchor (D-073 says so
			// in as many words), so it fell out of the loop and kept whatever pose it was composed at.
			//
			// MEASURED on e2e-multiroom: seeded at (0, -7.5), dragged to (-0.86, -4.22), persisted —
			// and after reload the fixture stood at (0, -7.5) again. The row was right the whole time;
			// nothing ever moved the mesh to it. For such a row the row's own position IS the answer,
			// with no precedence to resolve.
			if (!entry) continue;
			// 🔴 ASK-145 §13.4 — POSITION AND YAW. Resolving position alone meant a fixture composed from a
			// boot whose mint lagged her rotate stood at her position and the BONDED yaw forever: the
			// Cloud Run sweep venue drew a 5π/4 arched-window-frame at π after reload (4 of 5 runs), while
			// the hub — where the row arrived first and the fixture composed as a newcomer at her yaw —
			// was green. Same row, two arrival orders, and only one of them reached the yaw.
			const pose = persistedPose(row, entry);
			if (!pose.position && pose.rotationY === null) continue;
			const at = this._fixtureBaseCentre(row.anchorName);
			if (!at) continue;                                   // not composed (yet) — compose places it
			let did = false;
			const want = pose.position;
			if (want && Number.isFinite(want.x)) {
				// HEIGHT IS PART OF THE TRANSFORM (slice-2b): a fixture resting on a table has a real Y, and
				// restoring only X/Z would drop it through the table on reload.
				const wantY = Number.isFinite(want.y) ? want.y : at.y;
				if (Math.hypot(at.x - want.x, at.z - want.z) >= 0.005 || Math.abs(at.y - wantY) >= 0.005) {
					if (this.moveFixtureTo(row.anchorName, { x: want.x, y: wantY, z: want.z })) did = true;
				}
			}
			// The yaw it was DRAWN at (recorded by whichever path composed it), not entry.rotationY — the
			// hydrate has already written the row's yaw onto the entry, so reading the entry would say
			// "already there" about a mesh that is not.
			if (pose.rotationY !== null) {
				const drawn = this.getFixtureYaw(row.anchorName);
				if (Math.abs(yawDelta(pose.rotationY, drawn)) > 1e-6) {
					const path = this._composedPoses?.get(row.anchorName)?.path || "unknown";
					console.log(`[instances] ${row.anchorName}: drawn by ${path} at yaw ${drawn.toFixed(4)}, row says ${pose.rotationY.toFixed(4)} — turning it (ASK-145 §13.4)`);
					if (this.rotateFixtureTo(row.anchorName, pose.rotationY) !== null) did = true;
				}
			}
			if (did) applied++;
		}
		if (applied) console.log(`[instances] applied ${applied} persisted transform(s) to composed fixtures`);
		return applied;
	}

	/** Every scene object owned by a fixture instance, from the authoritative registries.
	 *
	 *  ENUMERATED, not enumerated-by-hand: an earlier version listed two families (comicEntries +
	 *  the socket registry) and silently missed every other one — the spinner-rack seats its comics
	 *  under SPIN_ROOT inside `worldGroup` (DECISION-051), shelf-show-all renders into `shelfMeshes`,
	 *  open-close owns a `lidHitZone` proxy. A per-family list is a standing invitation to miss the
	 *  next kind, so this walks the composed entry itself and takes every Object3D it holds —
	 *  directly, in an array, or as a `.root` — plus the socket-ownership registry. A new fixture
	 *  family is carried the moment the composer records it, with no edit here.
	 *
	 *  Descendants are dropped by the caller (see adoptFixtureContents): re-parenting a node whose
	 *  ancestor is already adopted would flatten that hierarchy — which is how a lid hinge or a
	 *  rotating rack breaks. */
	_fixtureContentsFor(anchorName) {
		const out = [];
		const push = (o) => { if (o && o.isObject3D) out.push(o); };
		const entry = this.boxEntries?.get?.(anchorName);
		for (const value of Object.values(entry || {})) {
			if (Array.isArray(value)) for (const v of value) { push(v); push(v?.root); }
			else { push(value); push(value?.root); }
		}
		for (const m of (this._fixtureContents?.get?.(anchorName) || [])) push(m);
		// The composer NAMES what it builds for an instance: `<kind>__cat__<ANCHOR>__<part>`. That name
		// is a declaration of ownership, so honour it — it catches the per-instance GROUP the composer
		// creates, which a leaf-mesh adoption left stranded (empty) at the old position. Scanning by
		// name is still structural: it's the builder's own statement about what belongs to whom, not a
		// guess from proximity (DECISION-044 forbids spatial adoption, not declared ownership).
		const tag = `__cat__${anchorName}__`;
		const root = this._shellRoot || this.stageRoot || this.scene;
		root?.traverse?.((o) => { if (o?.name && o.name.includes(tag)) push(o); });
		return out;
	}

	/** Move a fixture instance to a new world position — the whole instance, contents included.
	 *  Returns the applied position, or null when the anchor isn't composed. */
	moveFixtureTo(anchorName, position) {
		if (!position || !Number.isFinite(position.x)) return null;
		const group = this.adoptFixtureContents(anchorName);
		if (!group) return null;
		// The group starts at the origin with world-placed children attached, so its position IS the
		// world delta. Track the fixture's own bounds-centre to translate rather than teleport —
		// read from the composed witness (door 12's other half).
		const box = new THREE.Box3();
		for (const m of this._composedMeshesFor(anchorName)) if (m?.visible) { m.updateWorldMatrix(true, false); box.expandByObject(m); }
		if (box.isEmpty()) return null;
		const centre = box.getCenter(new THREE.Vector3());
		const base = new THREE.Vector3(centre.x, box.min.y, centre.z);   // base-centre (DECISION-050 convention)
		group.position.add(new THREE.Vector3(position.x - base.x, position.y - base.y, position.z - base.z));
		group.updateMatrixWorld(true);
		if (this._selectedAnchor === anchorName) this._refreshSelectionHelper();
		// 🔴 ASK-128 §chrome — THE FLOOR CHANGED, so say so HERE, where it changed.
		//
		// Found by the new chrome gate: the seam was fired by the DRAG path, by place and by remove —
		// four call-sites, and every other caller of this verb was missing. `revertFixtureToBaseline`
		// (the Inspector's ⟲, a shipped control) and `applyPersistedTransforms` (every Refresh, every
		// hydrate) both move floor-resting fixtures and neither told anything: the selling space stayed
		// derived from where the fixture USED to be, so the green was wrong and the drop validator with
		// it. Exactly the class this batch exists to end, one level down from the seat chrome.
		//
		// The drag emits its own phases (they carry commit/cancelled, which the walkability notice
		// reads), so it is left to do that rather than re-deriving twice in a frame it already derives
		// in.
		if (!this._dragState) this.onFloorChanged?.({ anchorName, phase: "moved" });
		return { x: position.x, y: position.y, z: position.z };
	}

	// The single group ALL editor-only scene artifacts live under (selection box now; move-gizmos,
	// grid, wireframe overlays next). Its visibility is the wholesale mode gate — never render editor
	// geometry into Play. Created hidden (Play is default). Excluded from picking + room-bounds.
	_ensureEditorLayer() {
		if (!this.scene) return null;
		if (!this._editorLayer) {
			this._editorLayer = new THREE.Group();
			this._editorLayer.name = "__editor_layer__";
			this._editorLayer.visible = this._cameraAuthoring === true;   // hidden unless already in Edit
			this.scene.add(this._editorLayer);
		}
		return this._editorLayer;
	}

	// ── ASK-087 Phase-1: Edit-mode view modes (Shaun-ruled 2026-07-20 chip set) ─────────────────
	// Shell solid/wireframe · Instances solid/wireframe · Grid on/off — AAA viewport furniture
	// (Unreal View Modes / Blender shading / Unity shading). Scope classification reuses
	// _isShellMesh — the same single predicate the selection system picks with, so "what is shell"
	// has one truth and two consumers. Wireframe is a MATERIAL mutation, which neither the CSS
	// chrome gate nor the _editorLayer gate can see — so Play entry resets it authoritatively
	// (setCameraMode calls resetViewModes); the grid IS an editor-layer artifact and rides the
	// wholesale layer gate like every other one.

	/** scope: "shell" | "instances" · mode: "solid" | "wireframe" */
	setViewMode(scope, mode) {
		if (!["shell", "instances"].includes(scope) || !["solid", "wireframe"].includes(mode)) return;
		this._viewModes ||= { shell: "solid", instances: "solid", grid: false };
		this._viewModes[scope] = mode;
		this._applyViewModes();
	}

	setGridVisible(on) {
		this._viewModes ||= { shell: "solid", instances: "solid", grid: false };
		this._viewModes.grid = !!on;
		const grid = this._ensureEditGrid();
		if (grid) grid.visible = this._viewModes.grid;
	}

	getViewModes() {
		return { ...(this._viewModes || { shell: "solid", instances: "solid", grid: false }) };
	}

	/** Play must render exactly as a customer sees it — solid + solid + no grid, regardless of what
	 *  Edit toggled. Called from setCameraMode on Play entry (idempotent, authoritative). */
	resetViewModes() {
		this._viewModes = { shell: "solid", instances: "solid", grid: false };
		this._applyViewModes();
		if (this._editGrid) this._editGrid.visible = false;
	}

	/** Re-walk the scene and set material.wireframe per scope. Also re-run after a recompose —
	 *  freshly composed meshes must inherit the active mode, not arrive solid into a wireframe view. */
	_applyViewModes() {
		const vm = this._viewModes;
		if (!vm || !this.scene) return;
		const inLayer = new Set();
		if (this._editorLayer) this._editorLayer.traverse((o) => inLayer.add(o));
		this.scene.traverse((o) => {
			if (!o.isMesh || inLayer.has(o)) return;
			const wire = (this._isShellMesh(o) ? vm.shell : vm.instances) === "wireframe";
			const mats = Array.isArray(o.material) ? o.material : [o.material];
			for (const m of mats) if (m && m.wireframe !== wire) { m.wireframe = wire; m.needsUpdate = true; }
		});
	}

	/** Floor grid, room-bounds sized, on the editor layer (class ruling: ALL editor-only scene
	 *  artifacts live there — Play cannot render it even if its own flag is left on). */
	_ensureEditGrid() {
		if (!this.scene) return null;
		if (!this._editGrid) {
			const b = this._shellRoomBounds || { width: 8, depth: 8 };
			const size = Math.ceil(Math.max(b.width || 8, b.depth || 8)) + 2;   // small overhang reads intentional
			const grid = new THREE.GridHelper(size, size * 2, 0x5a7ba6, 0x2e3b4e);
			grid.name = "__edit_grid__";
			grid.position.y = 0.012;   // just above the floor — no z-fight shimmer
			grid.material.transparent = true;
			grid.material.opacity = 0.55;
			grid.material.depthWrite = false;
			grid.visible = false;
			this._ensureEditorLayer()?.add(grid);
			this._editGrid = grid;
		}
		return this._editGrid;
	}

	// Scene children to pick against — everything EXCEPT the editor layer, so clicks never hit the
	// selection box or (future) gizmos, only real content.
	_pickableSceneChildren() {
		const layer = this._editorLayer;
		return this.scene.children.filter((c) => c !== layer);
	}

	// Wheel intent: "zoom" (pinch / mouse wheel) vs "pan" (trackpad two-finger scroll).
	// ctrl+wheel is the pinch gesture on every platform → always zoom. Otherwise we classify the
	// DEVICE: precision devices emit a horizontal component and/or small, often fractional pixel
	// deltas; mouse wheels emit large integer steps or line-mode deltas. The class is LATCHED so a
	// fast two-finger flick (which can exceed the fine-delta threshold) doesn't flip to zoom
	// mid-gesture; a genuine mouse wheel event latches it back.
	_classifyWheel(e) {
		if (e.ctrlKey) return "zoom";
		const fine = e.deltaMode === 0 && (Math.abs(e.deltaY) < 40 || !Number.isInteger(e.deltaY));
		if (e.deltaX !== 0 || fine) this._wheelPrecision = true;
		else if (e.deltaMode !== 0 || Math.abs(e.deltaY) >= 100) this._wheelPrecision = false;
		return this._wheelPrecision ? "pan" : "zoom";
	}

	// Seed OrbitControls' internal state from the CURRENT camera/target pair and orient the camera at
	// the pivot, so no damped interpolation can run from stale orientation/pan/dolly state.
	_pinCameraToTarget() {
		const c = this.controls;
		if (!c || !this.camera) return;
		c._sphericalDelta?.set(0, 0, 0);   // drop leftover orbit delta
		c._panOffset?.set(0, 0, 0);        // drop leftover pan
		if ("_scale" in c) c._scale = 1;   // drop leftover dolly
		// Seed the spherical from the new offset, in OrbitControls' y-up working space.
		if (c._spherical && c._quat) {
			const off = new THREE.Vector3().subVectors(this.camera.position, c.target).applyQuaternion(c._quat);
			c._spherical.setFromVector3(off);
			c._spherical.makeSafe?.();
		}
		this.camera.lookAt(c.target);      // explicit: don't depend on update() having run
		c.update();
	}

	_setupPointerLockControls() {
		const canvas = this.renderer.domElement;
		canvas.style.cursor = "crosshair";

		// First click acquires the lock — block it from triggering in-game
		// selection so the player isn't accidentally selecting a longbox just
		// to start looking around. Use capture phase + stopImmediatePropagation
		// so the stage's pointerdown raycaster doesn't fire on this click.
		canvas.addEventListener("pointerdown", (e) => {
			if (this._cameraAuthoring) return;   // ASK-087 slice-c: no FPS pointer-lock in Edit — the click drives OrbitControls (rotate/zoom) instead
			// D-076 cl.5 — focus mode: while something is held the pointer belongs to the panel and the
			// drag-turn. A click on the canvas then is a turn, never a re-lock; R/Esc is the way back.
			if (this.activeFixtureHero) return;
			// ASK-139 F-139c — a finger never asks for pointer lock (no phone grants it), and swallowing the
			// event here is what used to eat every tap on a phone. Touch belongs to `_touch-walk.js`.
			if (e.pointerType === "touch") return;
			// ASK-154 seam 2 — locked, a press on a verb-bearing item at the crosshair GRABS it: the held mouse drags
			// it out along its exit axis (the look pauses), a click commits. One grab for mouse and finger.
			if (document.pointerLockElement === canvas && e.button === 0 && !this._mouseGrab) {
				const g = this.browseGrab();
				if (g) { this._mouseGrab = { g, dx: 0, dy: 0, t0: performance.now() }; e.stopImmediatePropagation(); return; }
			}
			if (document.pointerLockElement !== canvas) {
				// Chrome enforces a ~1.5s cooldown after exitPointerLock; the
				// promise version of requestPointerLock rejects with a
				// SecurityError if called inside that window. Swallow it.
				const p = canvas.requestPointerLock?.();
				if (p && typeof p.catch === "function") p.catch(() => {});
				e.stopImmediatePropagation();
				e.preventDefault();
			}
		}, true); // capture phase

		document.addEventListener("pointerup", () => {
			const mg = this._mouseGrab;
			if (!mg) return;
			this._mouseGrab = null;
			mg.g.release({ tap: performance.now() - mg.t0 < 300 && Math.hypot(mg.dx, mg.dy) < 8 });
		});

		document.addEventListener("pointerlockchange", () => {
			const locked = document.pointerLockElement === canvas;
			if (!locked && this._mouseGrab) { this._mouseGrab.g.release({ tap: false }); this._mouseGrab = null; }   // Esc mid-grab: springs back
			this._mouseLook.locked = locked;
			canvas.style.cursor = locked ? "none" : "crosshair";
			if (locked) {
				// Sync yaw/pitch from current camera orientation so the first
				// movement doesn't snap the view.
				this._syncMouseLookFromCamera();
			}
		});

		document.addEventListener("mousemove", (event) => {
			if (this._cameraAuthoring) return;   // Edit owns the camera — FPS look must not stomp orientation/target
			if (!this._mouseLook.locked) return;
			const mg = this._mouseGrab;
			if (mg) { mg.dx += event.movementX; mg.dy += event.movementY; mg.g.move(mg.dx, mg.dy); return; }
			if (this._radialCapture) { this._radialCapture(event.movementX, event.movementY); return; }   // the gesture radial is open: the mouse picks, the view holds
			this._applyLookDelta(event.movementX, event.movementY);
		});
	}

	/** The ONE yaw/pitch writer — pointer-lock mouse and the touch drag-look both land here (ASK-139
	 *  F-139c), so the clamp and the target sync cannot differ between the two devices. ASK-142 R7: it
	 *  moves the TARGET; `_tickLook` eases the camera onto it, frame-rate independently. */
	_applyLookDelta(dx, dy, sensitivity = this._mouseLook.sensitivity) {
		const ml = this._mouseLook;
		ml.yaw   -= dx * sensitivity;
		ml.pitch -= dy * sensitivity;
		const PITCH_LIMIT = Math.PI / 2 - 0.05;
		ml.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, ml.pitch));
		// ASK-142 F-142e — over an open bin the look stays within ± 25° of the browse pose; during the
		// blend in or out the camera is the pose's, so a delta is dropped (the blend's end re-syncs).
		const b = this._browse;
		if (b) {
			if (b.phase !== "held") { ml.yaw = ml.viewYaw; ml.pitch = ml.viewPitch; return; }
			const c = this._bp.clampToPose(ml.yaw, ml.pitch, b.poseYaw, b.posePitch);
			ml.yaw = c.yaw; ml.pitch = c.pitch;
		}
		ml.easing = true;
	}

	/** ASK-142 F-142e (RULED by Shaun 2026-10-15) — the BROWSE POSE. On lid open the camera blends (260 ms
	 *  house ease) to a pose over the bin OPENING (F-142e.2: its larger extent ≈ 78 % of the area free now,
	 *  pitched 40° down so the front cover reads; the lid is never framed); drag-look stays within ± 25°;
	 *  closing blends back. The pose lives in the LAZY
	 *  `_browse-pose-stage.js` (a lid-open gesture fetches it; the blend starts as the lid swings) — the
	 *  eager half is these three doors and the gates that read `_browse`. */
	_enterBrowsePose() {
		if (this._cameraAuthoring || this._cameraPinned || this._browsePoseOff) return;
		const id = this.activeBoxId;
		import("./_browse-pose-stage.js").then((m) => {
			this._bp = m;
			if (this.activeBoxId === id && (this.boxState === "opening" || this.boxState === "open")) m.enterBrowsePose(this);
		}).catch((err) => console.warn("[browse-pose] unavailable:", err?.message || err));
	}
	_exitBrowsePose() { if (this._browse) this._bp.exitBrowsePose(this); }
	_tickBrowsePose(dt) { if (this._browse) this._bp.tickBrowsePose(this, dt); }

	/** ASK-142 R7 — ease the camera onto the look target: `v = lerp(target, v, exp2(-dt/40 ms))`
	 *  (`_feel.js`). Writes the camera ONLY while easing, and drops the ease the moment another writer owns
	 *  the camera (a camera animation, Edit, a capture pin, a hold), so it can never fight one. */
	_tickLook(dt) {
		const ml = this._mouseLook;
		if (!ml?.easing) return;
		if (this.cameraAnim?.active || this._cameraAuthoring || this._cameraPinned || this.activeFixtureHero || (this._browse && this._browse.phase !== "held")) {
			ml.easing = false; ml.yaw = ml.viewYaw; ml.pitch = ml.viewPitch;
			return;
		}
		if (stepLook(ml, dt)) ml.easing = false;
		this._tmpEuler = this._tmpEuler || new THREE.Euler(0, 0, 0, "YXZ");
		this._tmpEuler.set(ml.viewPitch, ml.viewYaw, 0);
		this.camera.quaternion.setFromEuler(this._tmpEuler);
		// Keep controls.target in sync so other code reading it gets a
		// consistent "what the camera is looking at" point.
		const fwd = (this._tmpFwd = this._tmpFwd || new THREE.Vector3()).set(0, 0, -1).applyQuaternion(this.camera.quaternion);
		this.controls.target.copy(this.camera.position).add(fwd);
	}

	/** ASK-139 F-139c — a TAP is the interact verb aimed where the finger landed: the touch twin of
	 *  crosshair + E. Same hover dispatcher, same E dispatcher — no second interaction path. */
	tapInteract(clientX, clientY) {
		const bounds = this.renderer.domElement.getBoundingClientRect();
		this.pointer.x = ((clientX - bounds.left) / bounds.width) * 2 - 1;
		this.pointer.y = -((clientY - bounds.top) / bounds.height) * 2 + 1;
		_bk._occAt = 0;
		this._updateHoverAtPointer();
		this.handleKeyDown({ code: "KeyE", key: "e", fromTap: true, preventDefault() {} });
		this._heldKeys.delete("KeyE");
	}

	// ── ASK-154 seam 2 — THE BROWSE VERB PAIR'S DOOR ─────────────────────────────────────────────────────────────
	// A seat stamped with its socket's derived verb (`userData.browse`, r55) plays pull-out / pick-up before the kind's
	// own hold takes over, and the hold opens the ONE product panel (D-075/D-076). Three ways in, one verb: E or a
	// click at the crosshair, a tap at the finger (all `_browseCommit`), and a drag that starts on the item (a finger
	// in ASK-139's look half, or a held mouse button) — `browseGrab`. The verb itself is the lazy `_browse-verbs.js`.

	/** The verb-bearing object under the live hover, and the kind's own commit for it — or null. */
	_browseTarget() {
		const hit = activeInteractable(this);
		// A shelf book's hold already IS its pull-out (`setBookHero` phase 1 slides it off the shelf along its face).
		if (!hit || hit.kind.id === "book-row") return null;
		const obj = browseTargetOf(hit.active);
		return obj ? { obj, commit: () => hit.kind.interactE?.(this, hit.active) } : null;
	}

	_loadBrowseVerbs() {
		return (this._bvLoad ||= import("./_browse-verbs.js").then((m) => (this._bv = m)));
	}

	/** E / click / tap on a verb-bearing seat: play the verb, then the kind's hold. True = the press is the verb's. */
	_browseCommit() {
		if (this._browseVerb) return true;   // a verb is mid-play — the press belongs to it, never to a second item
		if (this.activeFixtureHero || this._cameraAuthoring) return false;   // Edit owns input; the pair is a Play gesture
		const t = this._browseTarget();
		if (!t) return false;
		// The chunk was prefetched when the compose seated its first verb-bearing item, so this resolves at once.
		this._loadBrowseVerbs().then((m) => m.commitAt(this, t), (err) => { console.warn(`[browse-verb] unavailable — ${err?.message || err}`); t.commit(); });
		return true;
	}

	/** A drag that starts on a verb-bearing item → a grab handle `{ move(dx,dy), release({tap,cancel}) }`, else null. A
	 *  screen point re-aims the hover there first (a finger); none keeps the crosshair (a held mouse, pointer-locked). */
	browseGrab(clientX, clientY) {
		return (!this._browseVerb && !this.activeFixtureHero && !this._cameraAuthoring && this._bv?.grabAt(this, clientX, clientY)) || null;
	}

	_syncMouseLookFromCamera() {
		const dir = new THREE.Vector3();
		this.camera.getWorldDirection(dir);
		// Forward = -Z applied to quaternion; back-solve yaw/pitch (YXZ order).
		const ml = this._mouseLook;
		ml.yaw   = ml.viewYaw   = Math.atan2(-dir.x, -dir.z);
		ml.pitch = ml.viewPitch = Math.asin(THREE.MathUtils.clamp(dir.y, -1, 1));
		ml.easing = false;   // ASK-142 R7: a teleport/aim is the new rest pose, not something to ease toward
	}

	// Auto step-climbing via absolute Y targeting. Each frame raycasts down to
	// find the highest upward-facing surface below the camera, then up to find
	// any ceiling. Eye height is the smaller of 1.7m (normal stance) and
	// (ceiling - groundY - margin) so the player auto-crouches under low
	// ceilings (e.g. a mezzanine with limited headroom). Camera Y lerps to
	// `groundY + eyeHeight` smoothly so walking on/off ledges and stairs flows.
	_applyStepClimbing(dt) {
		if (this._isAnyHeroActive?.()) return;
		if (this.cameraAnim.active) return;

		this._stepRaycaster = this._stepRaycaster || new THREE.Raycaster();
		const tmpNormal = new THREE.Vector3();

		// Down ray — find ground (highest upward-facing surface below the camera)
		this._stepRaycaster.set(
			new THREE.Vector3(this.camera.position.x, this.camera.position.y + 5, this.camera.position.z),
			new THREE.Vector3(0, -1, 0),
		);
		this._stepRaycaster.far = 30;
		let groundY = -Infinity;
		for (const h of this._stepRaycaster.intersectObject(this.roomRoot, true)) {
			if (!h.face || h.object.visible === false) continue;
			tmpNormal.copy(h.face.normal).transformDirection(h.object.matrixWorld);
			if (tmpNormal.y < 0.5) continue;
			if (h.point.y > this.camera.position.y) continue;
			if (h.point.y > groundY) groundY = h.point.y;
		}
		if (groundY === -Infinity) return;

		// Up ray — find ceiling (lowest downward-facing surface above the ground)
		this._stepRaycaster.set(
			new THREE.Vector3(this.camera.position.x, groundY + 0.05, this.camera.position.z),
			new THREE.Vector3(0, 1, 0),
		);
		this._stepRaycaster.far = 10;
		let ceilingY = Infinity;
		for (const h of this._stepRaycaster.intersectObject(this.roomRoot, true)) {
			if (!h.face || h.object.visible === false) continue;
			tmpNormal.copy(h.face.normal).transformDirection(h.object.matrixWorld);
			if (tmpNormal.y > -0.5) continue;
			if (h.point.y < ceilingY) ceilingY = h.point.y;
		}

		const FULL_EYE = 1.7;
		const HEAD_MARGIN = 0.10;
		const MIN_EYE = 0.5;

		// First frame after spawn: snap camera Y to a sensible eye height above
		// the ground (ceiling-aware so we don't clip a low mezzanine ceiling).
		if (this._lastGroundY === undefined) {
			let eyeH = FULL_EYE;
			if (ceilingY < Infinity) {
				eyeH = Math.min(FULL_EYE, Math.max(MIN_EYE, ceilingY - HEAD_MARGIN - groundY));
			}
			const target = groundY + eyeH;
			const dy = target - this.camera.position.y;
			if (Math.abs(dy) <= 6) {
				this.camera.position.y += dy;
				this.controls.target.y += dy;
			}
			this._lastGroundY = groundY;
			return;
		}

		// Steady state: 1:1 ground follow makes every step of a staircase
		// visibly raise/lower the camera. Without this the eye stays glued to
		// `ceiling - margin` for the upper portion of any descent under a low
		// ceiling — looks like the player is floating across the stairs.
		const groundDelta = groundY - this._lastGroundY;
		this._lastGroundY = groundY;
		if (Math.abs(groundDelta) > 0.001 && Math.abs(groundDelta) < 6) {
			this.camera.position.y += groundDelta;
			this.controls.target.y += groundDelta;
		}

		// Hard caps: never let the head clip the ceiling or sink below MIN_EYE.
		if (ceilingY < Infinity) {
			const maxY = ceilingY - HEAD_MARGIN;
			if (this.camera.position.y > maxY) {
				const drop = this.camera.position.y - maxY;
				this.camera.position.y -= drop;
				this.controls.target.y -= drop;
			}
		}
		const minY = groundY + MIN_EYE;
		if (this.camera.position.y < minY) {
			const rise = minY - this.camera.position.y;
			this.camera.position.y += rise;
			this.controls.target.y += rise;
		}

		// Soft rise toward natural eye height, bounded by ceiling. Restores
		// full stance after descent without rebounding away the per-step drop.
		const naturalTarget = groundY + FULL_EYE;
		const cap = (ceilingY < Infinity) ? ceilingY - HEAD_MARGIN : Infinity;
		const target = Math.min(naturalTarget, cap);
		if (this.camera.position.y < target) {
			const dy = target - this.camera.position.y;
			const k = Math.min(1, dt * 2);
			this.camera.position.y += dy * k;
			this.controls.target.y += dy * k;
		}
	}

	// First-person headbob. Adds a small Y offset that sine-waves while moving
	// and fades out smoothly when the player stops. Best-practice values: ~4cm
	// at walking pace (1.8 Hz), ~7cm at running pace (2.5 Hz). Bob is delta-applied
	// so it stacks cleanly on top of the clamp without fighting it.
	_applyHeadBob(dt) {
		if (this._browse) return;   // ASK-142 F-142e: the pose owns the eye (its home keeps the bob offset it left with)
		if (this._isAnyHeroActive?.()) {
			// While inspecting an item, ensure no residual bob offset
			if (this._lastBobY) {
				this.camera.position.y -= this._lastBobY;
				this._lastBobY = 0;
				this._bobAmp = 0;
				this._bobPhase = 0;
			}
			return;
		}
		const keys = this._heldKeys;
		const isMoving  = keys.has("KeyW") || keys.has("KeyA") || keys.has("KeyS") || keys.has("KeyD");
		const isRunning = keys.has("ShiftLeft") || keys.has("ShiftRight");
		// Subtler than the prior pass — best-practice for first-person games is
		// just enough motion to feel grounded, not noticeable as "bobbing".
		const targetAmp = isMoving ? (isRunning ? 0.040 : 0.020) : 0;

		this._bobAmp = (this._bobAmp || 0) + (targetAmp - (this._bobAmp || 0)) * Math.min(1, dt * 6);

		if (isMoving) {
			const freq = isRunning ? 1.9 : 1.4;
			this._bobPhase = (this._bobPhase || 0) + dt * freq * 2 * Math.PI;
		} else if (this._bobAmp < 0.001) {
			this._bobPhase = 0;
			this._bobAmp = 0;
		}

		const bobY = Math.sin(this._bobPhase || 0) * this._bobAmp;
		this.camera.position.y += bobY - (this._lastBobY || 0);
		this._lastBobY = bobY;
	}

	// Stage 4: trigger zones — XZ distance check with arming. A trigger only
	// fires when the player has been *outside* its radius (armed), then walks
	// back into it. Prevents an immediate fire on spawn-into-zone.
	_checkTriggers() {
		if (this.activeTriggers.length) this._doorPoints = this.activeTriggers;   // ASK-174: the doors the room's door cue derives from (kept after a fire clears the list)
		if (!this.activeTriggers.length || !this.onPortalEnter) return;
		const p = this.camera.position, prev = this._trigPrev || p.clone();
		this._trigPrev = p.clone();
		for (const t of this.activeTriggers) {
			const dx = p.x - t.position.x;
			const dz = p.z - t.position.z;
			// D-086 cl.2 (ASK-177 §10): a door BOX in the door plane fires only on an INWARD crossing (velocity · the slot's
			// normal < 0, `_hub-entrance.js` boxStep); a trigger without one is today's disc
			const inside = t.box ? t.box.inside(p) : (dx * dx + dz * dz) < (t.radius * t.radius);
			if (inside) {
				if (this._armedTriggers.has(t.id) && (!t.box || t.box.inward(prev, p))) {
					// Disarm everything — once we fire, we're navigating away
					this._armedTriggers.clear();
					this.activeTriggers = []; // prevent re-fire while transitioning
					this.onPortalEnter(t);
					return;
				}
			} else {
				this._armedTriggers.add(t.id);
			}
		}
	}

	// Hub bounds: rectangular outer clamp on XZ. Y is left free so mouse-drag
	// can look up/down (Space/Q are blocked in walk movement). Optional
	// scene.bounds.obstacles array — axis-aligned XZ rects — pushes the player
	// out of building footprints if any are authored.
	_clampCameraToHub() {
		const bounds = this.currentScene?.bounds;
		if (!bounds) return;
		const minX = bounds.minX ?? -40, maxX = bounds.maxX ?? 40;
		const minZ = bounds.minZ ?? -10, maxZ = bounds.maxZ ?? 60;

		const p = this.camera.position;
		const before = p.clone();

		p.x = Math.max(minX, Math.min(maxX, p.x));
		p.z = Math.max(minZ, Math.min(maxZ, p.z));

		// Push out of any obstacle rectangles (axis-aligned in XZ)
		if (Array.isArray(bounds.obstacles)) {
			for (const o of bounds.obstacles) {
				if (p.x <= o.minX || p.x >= o.maxX || p.z <= o.minZ || p.z >= o.maxZ) continue;
				// Inside this obstacle — push out along smallest-penetration axis
				const dxLeft  = p.x - o.minX;
				const dxRight = o.maxX - p.x;
				const dzMin   = p.z - o.minZ;
				const dzMax   = o.maxZ - p.z;
				const dx = Math.min(dxLeft, dxRight);
				const dz = Math.min(dzMin, dzMax);
				if (dx < dz) {
					p.x = (dxLeft < dxRight) ? o.minX : o.maxX;
				} else {
					p.z = (dzMin < dzMax) ? o.minZ : o.maxZ;
				}
			}
		}

		// Apply the same XZ correction to the orbit target so look direction
		// is preserved — walls cause sliding, not rotation.
		const correction = new THREE.Vector3().subVectors(p, before);
		if (correction.lengthSq() > 0) {
			const t = this.controls.target;
			t.add(correction);
		}
	}

	_logCameraPos(label = "") {
		const p = this.camera.position;
		const t = this.controls.target;
		const fmt = (v) => `${v.toFixed(4)}`;
		console.log(
			`%c[KapowX cam | ${label}]\n` +
			`  position : { x: ${fmt(p.x)}, y: ${fmt(p.y)}, z: ${fmt(p.z)} }\n` +
			`  target   : { x: ${fmt(t.x)}, y: ${fmt(t.y)}, z: ${fmt(t.z)} }\n` +
			`  boxState : ${this.boxState}`,
			"color:#59b379;font-family:monospace",
		);
	}

	startCameraAnimation(toPos, toTarget, duration = 0.75) {
		this.cameraAnim.fromPos.copy(this.camera.position);
		this.cameraAnim.fromTarget.copy(this.controls.target);
		this.cameraAnim.toPos.copy(toPos);
		this.cameraAnim.toTarget.copy(toTarget);
		this.cameraAnim.progress = 0;
		this.cameraAnim.duration = duration;
		this.cameraAnim.active = true;
	}

	// ── Audio preference (ASK-174): app.js owns the ONE model (kapowx_settings.audio) and pushes it here ──────────
	setAudioPrefs(audio) { this._audioPrefs = audio; this._sound?.setPrefs(audio); }

	// Cast a ray from the screen centre (crosshair) and return true if it hits the active longbox.
	_crosshairHitsLongbox() {
		if (this.boxState !== "open" && this.boxState !== "browsing" && this.boxState !== "hero") return false;
		if (this.interactiveMeshes.length > 0) {
			return this.raycaster.intersectObjects(this.interactiveMeshes, false).length > 0;
		}
		return this.lidMeshes.length > 0
			&& this.raycaster.intersectObjects(this.lidMeshes, false).length > 0;
	}

	// Cast a ray from the screen centre and return true if it hits any comic in the pile.
	_crosshairHitsPile() {
		if (!this.pileComics.length || this.pileState === "hero") return false;
		return this.raycaster.intersectObjects(this.pileComics.map((c) => c.mesh), false).length > 0;
	}

	_crosshairHitsBookShelf() {
		if (!this.bookEntries.length || this.bookState === "hero") return false;
		if (this.bookBrowseIndex >= 0) return true; // already browsing — keep arrow keys active
		return this.raycaster.intersectObjects(this.bookEntries.map((b) => b.mesh), true).length > 0;   // a book is a multi-mesh GLB root
	}

	advanceBookBrowse(delta) {
		if (this.bookState === "hero" || !this.bookEntries.length) return;

		// Determine which shelf to scroll: prefer the shelf the crosshair is currently over,
		// fall back to whichever shelf already has a selected book.
		const hoveredShelf = this.hoveredBook?.shelfIndex ?? -1;
		const browsingShelf = this.bookBrowseIndex >= 0
			? (this.bookEntries[this.bookBrowseIndex]?.shelfIndex ?? -1)
			: -1;
		const targetShelf = hoveredShelf >= 0 ? hoveredShelf : browsingShelf;
		if (targetShelf < 0) return;

		// Slice to only the books on this shelf, keeping their global indices.
		const shelfSlice = this.bookEntries
			.map((e, i) => ({ entry: e, gi: i }))
			.filter(({ entry }) => entry.shelfIndex === targetShelf);
		if (!shelfSlice.length) return;

		// Find current local position within the shelf.
		let localIdx = shelfSlice.findIndex(({ gi }) => gi === this.bookBrowseIndex);

		if (localIdx < 0) {
			// No current selection on this shelf — seed from hovered book or default end.
			if (this.hoveredBook) {
				localIdx = shelfSlice.findIndex(({ entry }) => entry === this.hoveredBook);
			}
			if (localIdx < 0) localIdx = delta > 0 ? 0 : shelfSlice.length - 1;
		} else {
			localIdx = Math.max(0, Math.min(shelfSlice.length - 1, localIdx + delta));
		}

		this.bookBrowseIndex = shelfSlice[localIdx].gi;
	}

	handleKeyDown(event) {
		// coord ASK-180 §3.11 item 4 (K-COM D3) — ONE focus rule: a key typed into an editable field is the field's (no walk, no
		// radial, no door, no hero arrows). The sheets still stop their own events; this is what a future surface inherits.
		if (isEditableTarget(event.target)) return;
		this._initAmbientAudio();
		this._heldKeys.add(event.code);
		// D-086 cl.2 — E / Enter at a door's prompt (a tap on the screen is NOT the prompt: its pill is the tap target)
		if (/^(KeyE|Enter|NumpadEnter)$/.test(event.code) && !event.repeat && !event.fromTap && this._doorPrompt?.act(event)) { event.preventDefault?.(); return; }

		if (event.key === "ArrowRight" || event.key === "ArrowDown"
			|| event.key === "ArrowLeft" || event.key === "ArrowUp") {
			const delta = (event.key === "ArrowRight" || event.key === "ArrowDown") ? 1 : -1;
			// Opened-interior state takes top priority — arrows page through
			// the spreads, not the catalog.
			if (this.inspectOpened) {
				event.preventDefault();
				this.advanceInteriorPages(delta);
				return;
			}
			// Modal hero state — vinyl LP in hero cycles the catalog.
			if (this.boxState === "vinyl") {
				event.preventDefault();
				this.advanceVinylBrowse(delta);
				return;
			}
			// Record-rack browse mode (aim-driven): arrows cycle the focused
			// sleeve within the active rack, without entering hero. Matches
			// the comic-box browse pattern.
			if (this.activeVinylBin && this.activeVinylBin.sleeves.length > 1) {
				event.preventDefault();
				this.advanceVinylBinBrowse(delta);
				return;
			}
			// Stack-table cycle (aim-driven): arrows fire the top→bottom
			// thumb-through gesture. Direction is always forward — both ← and
			// → cycle the same way; the cycle is one-shot, ignored while a
			// previous cycle is animating.
			if (this.activeStackTable && this.activeStackTable.sleeves.length > 1) {
				event.preventDefault();
				this.cycleStackTable(delta);
				return;
			}
			// Otherwise cursor position determines which browsable object the key targets
			this.raycaster.setFromCamera(this.pointer, this.camera);
			if (this._crosshairHitsLongbox()) {
				event.preventDefault();
				this.advanceBrowse(delta);
			} else if (this._crosshairHitsPile()) {
				event.preventDefault();
				this.advancePileBrowse(delta);
			} else if (this._crosshairHitsSpinner()) {
				event.preventDefault();
				this.advanceSpinnerBrowse(delta);
			} else if (this._crosshairHitsBookShelf()) {
				event.preventDefault();
				this.advanceBookBrowse(delta);
			}
			return;
		}

		// E — "interact" verb. Enters hero from idle / browse states; in
		// hero state, opens the interior pages for products that carry
		// hasInterior=true. While in opened-interior or non-opening hero
		// (product has no interior), E is a no-op — R is the sole back verb.
		// companions: bailey commands ──────────────────────────────────
		// Digit keys 1–8 dispatch commands to Bailey while engaged.
		// 1–4 pose (sit/lie/sphinx/sleep), 5–8 action (speak/hop/dig/
		// pickup). See bailey.command(). Consumes the press so digit-
		// bound fixture handlers (none today) don't double-fire.
		if (this.bailey?.engaged && event.code.startsWith("Digit")) {
			const n = parseInt(event.code.replace("Digit", ""), 10);
			const KEY_TO_COMMAND = {
				1: "sit", 2: "lie", 3: "sphinx", 4: "sleep",
				5: "speak", 6: "hop", 7: "dig", 8: "pickup",
			};
			const cmd = KEY_TO_COMMAND[n];
			if (cmd && this.bailey.command(cmd)) {
				event.preventDefault?.();
				return;
			}
		}
		// companions: end ──────────────────────────────────────────────

		// ASK-170 §2: G held opens the gesture radial (wave · point · nod) around the cursor, a tap repeats the last gesture
		if (event.code === "KeyG" && (event.repeat ? !!this._presence?.radialState : this._presence?.radialDown?.())) { event.preventDefault?.(); return; }
		if (event.code === "KeyE") {
			// ASK-172 §5.6 — a party member's RING under the crosshair / the finger is "take me there" (figures stay
			// non-interactable; the ring is the party's own marker, drawn only for members).
			if (!event.repeat && this._ringInteract?.()) {
				event.preventDefault?.();
				return;
			}
			// ASK-154 seam 2 — a seat carrying the socket's derived verb plays it first (pull-out / pick-up), then
			// hands over to the same kind's interactE below. No verb on the seat (an r54 boot) → straight through.
			if (this._browseCommit()) {
				event.preventDefault?.();
				return;
			}
			// Generic interactable dispatcher — every interactable kind
			// (fixtures + Bailey) registers an interactE callback that
			// returns true to consume the press. Bailey's engage path is
			// now dispatched here alongside fixture E.
			if (dispatchInteractE(this)) {
				event.preventDefault?.();
				return;
			}

			this.raycaster.setFromCamera(this.pointer, this.camera);
			if (this.activeFixtureHero) {
				if (!this.inspectOpened && this.activeFixtureHero.product?.hasInterior) {
					this.openInterior();
				}
				return;
			}

			// Promote current front comic to hero while box is open/browsing
			if (this.boxState === "open" || this.boxState === "browsing") {
				this.setSelectedComic(this.browseIndex);
				return;
			}

			// All fixture E branches (wall-comic, vinyl-bin, vinyl-wall-shelf,
			// pile-table, book-row, spinner-rack, longbox-lid) are dispatched
			// at the top of this handler via dispatchInteractE. Nothing else
			// to do here for fixture interactions — the dispatcher returned
			// false meaning no migrated kind owned the press.
			return;
		}

		// R — universal "back". Steps out one level: from opened interior →
		// back to closed cover; from closed cover hero → put item back; from
		// box-open (no hero) → close the lid. Single back-verb in the AAA
		// contract — Skyrim's Tab equivalent.
		if (event.code === "KeyR") {
			// companions: bailey release ───────────────────────────────
			// When the crosshair is on an engaged Bailey, R drops him
			// back into free-roam mode. Consumed so the fixture-level
			// fallbacks below don't fire on the same press.
			if (!this.activeFixtureHero && this.boxState === "none" && this.bailey?.engaged && this.bailey.release()) {   // ASK-162: R is his sheet's Release
				event.preventDefault?.();
				return;
			}
			// companions: end ──────────────────────────────────────────
			if (this.inspectOpened) { this.closeInterior(); return; }
			if (this.activeFixtureHero) { this.exitHero(); return; }
			if (this.boxState === "open" || this.boxState === "browsing") {
				this.closeLid(); return;
			}
			return;
		}

		// O — open/read, the print verb of D-076 cl.3 (E stays an alias: it was the verb before).
		if (event.code === "KeyO" && this.activeFixtureHero && !this.inspectOpened && this.activeFixtureHero.product?.hasInterior) {
			this.openInterior();
			return;
		}

		// F — flip focused comic to show back cover
		if (event.code === "KeyF") {
			this.flipActiveComic();
			return;
		}

		// C — close box / exit. If interior is open, close it first; then if
		// a fixture is in hero state, exit it; otherwise close the lid.
		if (event.code === "KeyC") {
			if (this.inspectOpened) { this.closeInterior(); return; }
			if (this.activeFixtureHero) { this.exitHero(); return; }
			if (this.boxState === "open" || this.boxState === "browsing" || this.boxState === "hero") {
				this.closeLid(); return;
			}
			return;
		}
	}

	handleKeyUp(event) {
		this._heldKeys.delete(event.code);
		if (event.code === "KeyG") this._presence?.radialUp?.();
	}

	// Single source-of-truth setter for "something is now in hero state".
	// Called from every per-kind entry method (setSelectedComic, inspectVinyl,
	// inspectWallComic, setPileHero, setBookHero, setSpinnerHero). Keeps
	// activeFixtureHero in sync with the per-state objects so consumers (hint,
	// exitHero dispatcher) have a single thing to read.
	_setActiveHero(kind, product, mesh) {
		this.activeFixtureHero = { kind, product: product || null, mesh: mesh || null };
		// The hold releases the pointer (focus mode), which stops the per-frame hover read — so the hover
		// state is dropped here (a held thing is not "hovered": its outline goes) and the hint and the
		// strip are told directly, on both edges.
		resetDispatcher(this);
		this._liftHeld(mesh);
		this.updateBrowseControls();
		// D-076 cl.3 — print items gain drag-turn and scroll-zoom. The gesture is the 3D hero's, in the
		// same LAZY module; it watches this hold and lets go when it ends (the item hero binds its own).
		if (kind !== "item") import("./_item-hero.js").then((m) => m.holdForInspect(this, this.activeFixtureHero))
			.catch((err) => console.warn(`[inspect] turn/zoom unavailable — ${err?.message || err}`));
	}

	/**
	 * 🔴 D-076 cl.5 — THE HELD OBJECT DRAWS OVER THE WORLD (the examine overlay: RE4 remake, Starfield,
	 * Death Stranding). It is framed for the free area beside the panel, and on a portrait phone that
	 * puts a 1.26 m owl ~4.5 m down the eye ray — measured: behind the shop wall, so the free area
	 * showed bricks. Pulling it nearer would cut it off at the frame instead. So the held object goes
	 * on its own render layer and is drawn after the world with the depth buffer cleared: still lit by
	 * the shop's lights, never occluded by the shop.
	 */
	_liftHeld(obj) {
		this._lowerHeld();
		if (!obj) return;
		obj.traverse((o) => o.layers.set(HELD_LAYER));
		this.scene.traverse((o) => { if (o.isLight) o.layers.enable(HELD_LAYER); });
		this._held = obj;
	}

	_lowerHeld() {
		this._held?.traverse((o) => o.layers.set(0));
		this._held = null;
	}

	/** ASK-156 — the zoom continuum's rig (lazy `_camera-zoom.js`, mounted on the first wheel / pinch), then `fn(rig)`. */
	_zoom(fn) {
		if (this._zoomRig) return fn(this._zoomRig);
		(this._zoomLoad ||= import("./_camera-zoom.js").then((m) => m.installZoomRig(this))).then(fn).catch((e) => console.warn("[zoom] unavailable:", e?.message || e));
	}
	_zoomPinch(prev, now) { this._zoom((z) => z.pinch(prev, now)); }

	_render() {
		const z = this._zoomRig;
		if (!z) return this._renderFrame();
		z.pre();   // the boom offset lives only around the draw: everything else reads camera.position as the eye
		try { this._renderFrame(); } finally { z.post(); }
	}

	_renderFrame() {
		const r = this.renderer, cam = this.camera, held = this._held;
		if (!held && this._hubLife && this._hubLife.render(r, this.scene, cam)) return;   // hub-v3 bloom (mid/high) draws its own frame
		r.render(this.scene, cam);
		if (!held) return;
		const auto = r.autoClear, bg = this.scene.background, fog = this.scene.fog;
		r.autoClear = false;
		r.clearDepth();
		this.scene.background = null; this.scene.fog = null;
		cam.layers.set(HELD_LAYER);
		r.render(this.scene, cam);
		cam.layers.set(0);
		this.scene.background = bg; this.scene.fog = fog;
		r.autoClear = auto;
	}

	_clearActiveHero() {
		const was = this.activeFixtureHero;
		this.activeFixtureHero = null;
		this._lowerHeld();
		this.updateBrowseControls();
		// D-076 cl.5 — focus mode ends when the item is PUT BACK, not when its return flight lands:
		// three kinds only told the app at the end of the animation, so the panel and the dim outlived
		// the hold by ~0.5 s. One notice here, for every kind.
		if (was) this.onComicSelect?.(null);
	}

	// Single dispatcher for R/E/C exit keys. Routes to the right per-kind exit
	// based on activeFixtureHero. Returns true when an exit was dispatched so
	// the caller (handleKeyDown) can early-return.
	exitHero() {
		// If the player still has an interior open, tear it down first so the
		// procedural open-book mesh doesn't get orphaned in the scene.
		if (this.inspectOpened) this.closeInterior();
		const kind = this.activeFixtureHero?.kind;
		if (!kind) {
			if (this.bailey?.engaged) return this.bailey.release();   // ASK-162: the companion sheet's put-back slot (×, drag-down)
			// Fall back to legacy state flags in case populate-on-entry hasn't
			// caught up — defensive only. Remove once every entry method is
			// wired through _setActiveHero.
			if (this.boxState === "hero")      { this.putHeroBack(); return true; }
			if (this.boxState === "wallComic") { this.exitWallComic(); return true; }
			if (this.boxState === "vinyl")     { this.exitVinylHero(); return true; }
			if (this.pileState === "hero")     { this.exitPileHero(); return true; }
			if (this.bookState === "hero")     { this.exitBookHero(); return true; }
			if (this.spinnerRack?.state === "hero") { this.exitSpinnerHero(); return true; }
			return false;
		}
		switch (kind) {
			case "longbox":    this.putHeroBack();     return true;
			case "wall-comic": this.exitWallComic();   return true;
			case "vinyl":      this.exitVinylHero();   return true;
			case "pile":       this.exitPileHero();    return true;
			case "book":       this.exitBookHero();    return true;
			case "spinner":    this.exitSpinnerHero(); return true;
			// F-115k — the open is lazy, so the EXIT is the closure that open installed. R and Esc both
			// land here through this one dispatcher, like every other kind.
			case "item":       this._exitItemHero?.();  return true;
		}
		return false;
	}

	// Crop long product titles so the hint stays on one line on small screens.
	_productTitle(product) {
		const t = String(product?.title || product?.name || "").trim();
		if (!t) return "";
		return t.length > 38 ? t.slice(0, 37) + "…" : t;
	}

	/**
	 * Which product the crosshair is pointed at (no hero active), or null.
	 *
	 * 🔴 ASK-137 §84 — ONE READ, NOT NINE BRANCHES. This enumerated every interactable kind by hand
	 * — `activeVinylBin`, `spinnerRack.hoveredComic`, `hoveredWallComic`, `hoveredVinyl`,
	 * `hoveredBook`, `hoveredPileComic`, `hoveredSeatedItem` — and then ran its OWN raycast against
	 * the top of each pile stack, duplicating the hit test `pile-table` had already done that frame.
	 * `_updateEHint` carried the same ladder a second time. `_interactable-registry.js`'s header
	 * predicted exactly this: *"adding a new interactable kind needs either a dedicated branch in
	 * both places or … a real registry the dispatcher iterates without core edits."* Adding
	 * `seated-item` for F-115k meant adding a branch to both, which is the proof, and §76d named this
	 * as the payment for the ceiling raises those branches caused.
	 *
	 * The dispatcher already resolves which kind owns the frame, in precedence order, and the kinds
	 * already suppress one another — so the hand-written order was re-stating the registry's own.
	 */
	_hoveredProduct() {
		const hit = activeInteractable(this);
		return hit?.kind?.productOf?.(this, hit.active) ?? null;
	}

	// D-076 cl.3: the per-kind hero hint table that stood here is gone — the PRODUCT PANEL lists the
	// held item's verbs (same order as its buttons) for every type, so a second copy on the stage could
	// only drift from it.
	_updateEHint() {
		if (!this._eHintEl) return;
		let text = null;
		let title = "";

		// 1. Active hero state: the panel's verb line speaks for every held item (below); only an opened
		//    interior keeps a stage hint.
		const heroKind = this.activeFixtureHero?.kind;
		// D-076 cl.3 — the PRODUCT PANEL carries the verb line for every held type now, in the same
		// order as its buttons, so this hint stands down while anything is held. Reading an interior is
		// the one state the panel does not describe (the pages take the screen), so it keeps its line.
		if (heroKind && !this.inspectOpened) { this._eHintEl.hidden = true; return; }
		if (heroKind) {
			const product = this.activeFixtureHero.product;
			const totalSpreads = Math.floor((product?.interiorImages?.length || 0) / 2);
			text = (totalSpreads > 1)
				? '<kbd>←→</kbd> Pages &nbsp;·&nbsp; <kbd>R</kbd> Close'
				: '<kbd>R</kbd> Close';
			title = this._productTitle(product);
		}
		// 2. Longbox open / browsing state — physical "lid open, comics
		//    visible" precursor to picking one up. Keyboard owns selection
		//    via browseIndex; the hint names the comic E will read so the
		//    player can confirm before pressing.
		else if (this.boxState === "open" || this.boxState === "browsing") {
			text  = '<kbd>E</kbd> Read &nbsp;·&nbsp; <kbd>R</kbd> Close';
			const focused = this.comicEntries.find((e) => e.index === this.browseIndex);
			title = this._productTitle(focused?.product);
		}
		// 3. Record-rack browse — aiming at a crate engages browse mode for
		//    that rack. Arrows fan the stack open; E inspects the focused
		//    sleeve. Same mental model as box-open/browsing.
		else if (this.activeVinylBin) {
			text  = '<kbd>E</kbd> Inspect &nbsp;·&nbsp; <kbd>←→</kbd> Browse';
			const focusIdx = Math.max(0, this.activeVinylBin.browseIdx);
			const focused = this.activeVinylBin.sleeves[focusIdx];
			title = this._productTitle(focused?.product);
		}
		// 4. Stack-table browse — aiming at a comic stack engages it; the
		//    top sleeve is the inspect target; arrows cycle top→bottom.
		else if (this.activeStackTable) {
			text  = '<kbd>E</kbd> Inspect &nbsp;·&nbsp; <kbd>←→</kbd> Cycle';
			const stack = this.activeStackTable;
			const N = stack.sleeves.length;
			const topSleeveIdx = (N - 1 - stack.cycleCount + N * N) % N;
			title = this._productTitle(stack.sleeves[topSleeveIdx]?.product);
		}
		// 3. Idle — show the hover prompt for whatever the crosshair is on.
		else if (this.boxState === "none" && this.pileState === "none" && this.bookState === "none" && (this.spinnerRack?.state ?? 'none') === 'none') {
			// Bailey ENGAGED is not a hover — it is a modal command strip that must stay up while the
			// player aims elsewhere, so it keeps its own branch above the hover read.
			if (this.bailey?.engaged) {
				text = null;   // ASK-162 option C: his commands are the panel's verb line now (_companion-sheet.js), not a HUD strip
			} else {
				// 🔴 ASK-137 §84 — THE HOVER PROMPT COMES FROM THE KIND THAT OWNS THE FRAME.
				//
				// This was a nine-branch `else if` ladder re-stating the registry's precedence order by
				// hand, with its own second raycast at the end for the pile. See `_hoveredProduct`
				// above for the whole argument; this is its twin, and the two had to be edited together
				// every time a kind was added — which is how `seated-item` ended up in both.
				const hit = activeInteractable(this);
				if (hit?.kind?.hoverHint) {
					text = hit.kind.hoverHint;
					title = hit.kind.hoverTitle
						? hit.kind.hoverTitle(this, hit.active)
						: this._productTitle(hit.kind.productOf?.(this, hit.active));
				} else if (this.bookBrowseIndex >= 0) {
					// A browse index with nothing under the crosshair is a non-hover state, like Bailey
					// engaged: the player has fanned a shelf open and looked away, and the press still
					// takes the focused book. Preserved deliberately — the registry answers for hover.
					text = '<kbd>E</kbd> Pick Up';
					title = this._productTitle(this.bookEntries?.[this.bookBrowseIndex]?.product);
				}
			}
		}

		// ASK-139 F-139c — a phone has no keys: the E verb reads as the tap that performs it, and the
		// other keys' verbs are dropped (each has an on-screen button, or a gesture the first-run hint names).
		if (text && this._touchWalk?.isTouchDevice?.()) text = this._touchWalk.hintText(text);
		if (text && title) text = `${text}&nbsp;&nbsp;&mdash;&nbsp;<span class="kapowx-hint-title">${title}</span>`;
		this._eHintEl.innerHTML = text ?? '';
		this._eHintEl.hidden = !text;
	}

	handleWheel(event) {
		if (this._wheelCooldown) return;
		// Opened-interior state takes top priority — wheel pages through
		// spreads. Same loop is also driven by arrow keys via
		// advanceInteriorPages (Catalog v2: matches the arrow-key wiring's
		// own priority order in handleKeyDown).
		if (this.inspectOpened) {
			event.stopImmediatePropagation();
			event.preventDefault();
			this._wheelCooldown = true;
			setTimeout(() => { this._wheelCooldown = false; }, 200);
			const delta = event.deltaY > 0 ? 1 : -1;
			this.advanceInteriorPages(delta);
			return;
		}
		// D-076 cl.3 — while anything is held, scroll is ZOOM for every type (the inspect layer owns
		// it); the vinyl hero's catalogue browse stays on the arrow keys.
		if (this.activeFixtureHero) return;
		// Crosshair (screen centre) routes the wheel to browse or lets OrbitControls zoom
		this.raycaster.setFromCamera(this.pointer, this.camera);
		const onLongbox = this._crosshairHitsLongbox();
		const onPile    = !onLongbox && this._crosshairHitsPile() && !this.pileArcAnim;
		// ASK-156 — anywhere else in Play the wheel IS the zoom continuum (lazy `_camera-zoom.js`)
		if (!onLongbox && !onPile) { if (!this._cameraAuthoring) { event.preventDefault(); this._zoom((z) => z.wheel(event)); } return; }
		// stopImmediatePropagation prevents OrbitControls from also zooming on this tick
		event.stopImmediatePropagation();
		event.preventDefault();
		this._wheelCooldown = true;
		setTimeout(() => { this._wheelCooldown = false; }, onLongbox ? 180 : 220);
		const delta = event.deltaY > 0 ? 1 : -1;
		if (onLongbox) {
			this.advanceBrowse(delta);
		} else {
			this.advancePileBrowse(delta);
		}
	}

	inspectWallComic(idx) {
		const entry = this.wallComics[idx];
		if (!entry) return;
		if (this.boxState === "closed") this.forceCloseActiveBox();
		if (this.boxState !== "none" && this.boxState !== "wallComic") return;

		// Snap any current wall comic back immediately before starting new one
		if (this.wallComicHeroEntry) {
			const oh = this.wallComicHeroEntry;
			this._snapUnflip();
			oh.entry.mesh.position.copy(oh.entry.worldPos);
			oh.entry.mesh.quaternion.copy(oh.fromQuat);
			this.wallComicHeroEntry = null;
		}

		this.activeWallComic = entry;
		this.boxState = "wallComic";
		removeOutline(entry.mesh);

		const fromPos  = entry.mesh.position.clone();
		const fromQuat = entry.mesh.quaternion.clone();
		const heroPos  = this._computeHeroPos(0.260, entry.mesh);
		const heroQuat = this._faceCameraQuat(heroPos);

		this.wallComicHeroEntry = { entry, fromPos, fromQuat, heroPos, heroQuat, animProgress: 0, returning: false };

		if (this.heroSpotLight) {
			this.heroSpotLight.position.set(heroPos.x, heroPos.y + 0.25, heroPos.z + 0.20);
			this.heroSpotLight.intensity = 3.5;
			this.heroSpotLight.visible   = true;
		}

		this._setActiveHero("wall-comic", entry.product, entry.mesh);
		this.updateBrowseControls();
		this.onComicSelect?.({ index: idx, product: entry.product });
	}

	exitWallComic() {
		if (this.boxState !== "wallComic") return;
		if (!this.wallComicHeroEntry || this.wallComicHeroEntry.returning) return;
		this._snapUnflip();
		this.wallComicHeroEntry.returning    = true;
		this.wallComicHeroEntry.animProgress = 0;
		this._clearActiveHero();
		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible   = false;
		}
		if (this.hoveredWallComic) {
			removeOutline(this.hoveredWallComic);
			this.hoveredWallComic = null;
		}
		this.renderer.domElement.style.cursor = "";
	}

	// Vinyl sleeve hero — parallel to inspectWallComic.
	// Same flight-to-camera animation; sleeves are square (~31cm) instead of
	// rectangular (~26cm tall), so heroPos uses 0.31 as the item dimension.
	inspectVinyl(idx) {
		const entry = this.vinylSleeves[idx];
		if (!entry) return;
		if (this.boxState === "closed") this.forceCloseActiveBox();
		if (this.boxState !== "none" && this.boxState !== "vinyl") return;

		// Snap any existing vinyl hero back before starting a new one
		if (this.vinylHeroEntry) {
			const oh = this.vinylHeroEntry;
			oh.entry.mesh.position.copy(oh.entry.worldPos);
			oh.entry.mesh.quaternion.copy(oh.entry.baseQuat);
			this.vinylHeroEntry = null;
		}

		this.boxState = "vinyl";
		removeOutline(entry.mesh);

		const fromPos  = entry.mesh.position.clone();
		const fromQuat = entry.mesh.quaternion.clone();
		const heroWorld = this._computeHeroPos(0.314, entry.mesh);
		const heroQuatWorld = this._faceCameraQuat(heroWorld);
		// The hero animates the sleeve's LOCAL transform, and a composer-seated sleeve lives under its
		// fixture's contents group — which a Studio move re-parents and moves (ASK-139 F-139an). The
		// camera-relative hero pose is a WORLD pose, so it is taken into the parent's frame; under an
		// identity parent (the legacy scene-root sleeve) this is the pose it always was.
		const parent = entry.mesh.parent;
		let heroPos = heroWorld, heroQuat = heroQuatWorld;
		if (parent) {
			parent.updateMatrixWorld(true);
			heroPos = parent.worldToLocal(heroWorld.clone());
			heroQuat = parent.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(heroQuatWorld);
		}

		this.vinylHeroEntry = { entry, fromPos, fromQuat, heroPos, heroQuat, animProgress: 0, returning: false };

		if (this.heroSpotLight) {
			this.heroSpotLight.position.set(heroWorld.x, heroWorld.y + 0.30, heroWorld.z + 0.20);
			this.heroSpotLight.intensity = 3.5;
			this.heroSpotLight.visible   = true;
		}
		this._setActiveHero("vinyl", entry.product, entry.mesh);
		this.onComicSelect?.({ index: idx, product: entry.product });
		this.updateBrowseControls();
	}

	exitVinylHero() {
		if (this.boxState !== "vinyl") return;
		if (!this.vinylHeroEntry || this.vinylHeroEntry.returning) return;
		this.vinylHeroEntry.returning    = true;
		this.vinylHeroEntry.animProgress = 0;
		this._clearActiveHero();
		if (this.heroSpotLight) {
			this.heroSpotLight.intensity = 0;
			this.heroSpotLight.visible   = false;
		}
		if (this.hoveredVinyl) {
			removeOutline(this.hoveredVinyl);
			this.hoveredVinyl = null;
		}
		this.renderer.domElement.style.cursor = "";
		this.updateBrowseControls();
	}

	// Mouse clicks no longer interact with products — E is the sole world-
	// interaction verb (AAA convention: Skyrim / Cyberpunk / HL:Alyx). The
	// pointerdown handler exists only to (a) initialise the audio context on
	// first user gesture and (b) let the capture-phase listener in
	// `_setupPointerLockControls` acquire pointer-lock. No raycast, no
	// per-mesh dispatch. Fixture `userData.interactable` objects still carry
	// metadata (kind, product, productIndex) read by the hint + AI describe
	// surfaces — see documentation/SHOP_CONTRACT.md.
	handlePointer(event) {
		// ASK-142 §9.2 — the first touch only RESUMES the ambient graph (autoplay policy needs a
		// gesture to start sound). Building it here — AudioContext + oscillators + 3 s of noise —
		// was a 212 ms task inside the customer's first pointerdown (LoAF-attributed on the plane).
		this._initAmbientAudio();
		if (this._audioCtx?.state === "suspended") this._audioCtx.resume().catch(() => {});
	}

	createBrowseControls() {
		const el = document.createElement("div");
		el.className = "kapowx-browse-controls";
		el.hidden = true;
		el.innerHTML = `
			<div class="kapowx-browse-controls__nav">
				<button class="kapowx-browse-controls__btn" data-browse="-1" title="Previous (← key)">&#8592;</button>
				<span class="kapowx-browse-controls__counter" data-browse-counter>1 / 1</span>
				<button class="kapowx-browse-controls__btn" data-browse="1" title="Next (→ key)">&#8594;</button>
			</div>
			<button class="kapowx-browse-controls__return" data-put-back hidden>Put back <span class="kapowx-kbd">R</span></button>
			<button class="kapowx-browse-controls__return" data-close-box>Close <span class="kapowx-kbd">R</span></button>
		`;
		el.addEventListener("click", (event) => {
			const browseBtn = event.target.closest("[data-browse]");
			const putBackBtn = event.target.closest("[data-put-back]");
			const closeBtn = event.target.closest("[data-close-box]");
			if (browseBtn) {
				const delta = Number(browseBtn.dataset.browse);
				if (this.pileComics.length > 0 && this.boxState === "none") {
					this.advancePileBrowse(delta);
				} else {
					this.advanceBrowse(delta);
				}
			}
			if (putBackBtn) {
				// Dispatch to whichever hero is currently active.
				if (this.boxState === "wallComic") this.exitWallComic();
				else if (this.boxState === "vinyl") this.exitVinylHero();
				else if (this.bookState === "hero") this.exitBookHero();
				else if (this.spinnerRack?.state === "hero") this.exitSpinnerHero();
				else if (this.pileState === "hero") this.exitPileHero();
				else this.putHeroBack();
			}
			if (closeBtn) this.closeLid();
		});
		return el;
	}

	/** A touch-first device (the primary pointer is a finger, or a finger has touched the world). */
	_touchFirstDevice() {
		try { if (window.matchMedia?.("(pointer: coarse)").matches) return true; } catch { /* no matchMedia */ }
		return this.usingTouch === true;
	}

	updateBrowseControls() {
		if (!this.browseControlsEl) return;

		const pileMode    = this.pileComics.length > 0 && this.boxState === "none";
		const longboxMode = this.boxState === "open" || this.boxState === "browsing" || this.boxState === "hero";
		// D-076 — while anything is HELD the product panel is the one surface: its action row carries
		// Put back (and the type's verbs), so this strip's own "Put back" would be a second copy of the
		// same verb in a second place. It stands down for every held kind.
		this.browseControlsEl.hidden = !!this.activeFixtureHero || !(pileMode || longboxMode);

		// 🔴 ASK-139 §94.10 (MAN010, measured 2026-10-20) — on a TOUCH device in Play OrbitControls must never answer a
		// finger: the thumbs own the camera (`_touch-walk.js`). This line used to re-enable rotate unconditionally, so in
		// Play on a phone every canvas touch ALSO orbited the camera — a stick drag turned the view 39.6° and its damping
		// carried it to 53.4° after the lift: Shaun's "left sometimes looks" and "the momentum". Desktop Play (pointer-
		// lock look; OrbitControls cannot see a locked mouse move) and Edit (the author's touch orbit) are unchanged.
		const touchPlay = !this._cameraAuthoring && this._touchFirstDevice();
		this.controls.enableRotate = !touchPlay;
		this.controls.enablePan    = !touchPlay;
		this.controls.maxDistance  = 24;
		// Any HOLD, the 3D item included: `_isAnyHeroActive` predates the item hero and does not know
		// it, so a held robot's drag also orbited the camera (measured §91b: 0.6 m off where she stood).
		if (this.activeFixtureHero || this._isAnyHeroActive()) {
			this.controls.enableRotate = false;
			this.controls.enablePan    = false;
		}

		const counter    = this.browseControlsEl.querySelector("[data-browse-counter]");
		const putBackBtn = this.browseControlsEl.querySelector("[data-put-back]");
		const closeBtn   = this.browseControlsEl.querySelector("[data-close-box]");
		const navEl      = this.browseControlsEl.querySelector(".kapowx-browse-controls__nav");

		if (pileMode && !longboxMode) {
			if (navEl)      navEl.hidden = false;
			const total  = this.pileComics.length;
			const viewed = this.pileLeftStack.length;
			if (counter)    counter.textContent = `${viewed} / ${total}`;
			if (putBackBtn) putBackBtn.hidden = true;
			if (closeBtn)   closeBtn.hidden   = true;
		} else {
			if (navEl)      navEl.hidden = false;
			const total = this.comicEntries.length;
			if (counter)    counter.textContent = `${Math.min(this.browseIndex + 1, total || 1)} / ${total || 0}`;
			if (putBackBtn) putBackBtn.hidden = this.heroComicIndex === null;
			if (closeBtn)   closeBtn.hidden   = false;
		}

		this._updateEHint();
	}

	handleResize() {
		const width = Math.max(this.mount.clientWidth, 320);
		const height = Math.max(this.mount.clientHeight, 320);
		this.camera.aspect = width / height;
		this.camera.fov = fovForAspect(this.camera.aspect);
		this.renderer.setSize(width, height, false);
		try { this._compassCompact = matchMedia("(pointer: coarse) and (max-width: 1023.98px), (orientation: landscape) and (max-height: 500px)").matches; } catch { /* no window */ }
		this._applyViewportInset();   // recompute the frustum offset against the new width
		// ASK-142 R7 — the device DPR changed (a window moved monitor, a browser zoom): a new ladder for it.
		if ((window.devicePixelRatio || 1) !== this._drsDpr) { this._newDrs(); this.renderer.setPixelRatio(this._drs.dpr); }
	}

	_newDrs() {
		this._drsDpr = window.devicePixelRatio || 1;
		this._drs = createDynamicResolution({ steps: this.tierProfile.drsSteps || [this.tierProfile.pixelRatioCap], deviceDpr: this._drsDpr });
	}

	// ASK-087 Phase-2 slice-3 (parity row 8): the Studio panel is a fixed overlay on the RIGHT of a
	// full-width canvas, so framing/selection — which centre on the whole canvas — could land a
	// fixture BEHIND the panel. AAA docked-viewport fix: an asymmetric frustum offset shifts the
	// rendered image left by half the covered width, so the usable (visible) region's centre is what
	// the camera treats as centre. No canvas resize (no aspect distortion, no gap behind the blur);
	// Studio owns the DOM measurement and pushes it here — the stage never reaches into panel DOM.
	setViewportInset(rightPx) {
		this._viewportInsetRight = Math.max(0, Number(rightPx) || 0);
		this._applyViewportInset();
	}

	/** 🔴 D-076 cl.2 — the PRODUCT PANEL is the same docked-viewport case as the Studio panel: a right
	 *  side sheet (≥ 1024 px) or a bottom sheet (phone) covers part of the canvas, and the held object
	 *  belongs in the REMAINING area. The app measures its sheet and pushes it here; the frustum offset
	 *  below eases in while a hero is held, so every hero kind is framed in the free area by the one
	 *  mechanism, with no per-kind maths. */
	setInspectInset({ right = 0, bottom = 0, top = 0 } = {}) {
		// ASK-139 §94.7-1 — `top` is the phone's HUD band (the compass + the safe-area inset): the held item is
		// framed BELOW it, never behind it (Shaun's handset: a comic's top hid behind the compass).
		this._inspectInset = { right: Math.max(0, +right || 0), bottom: Math.max(0, +bottom || 0), top: Math.max(0, +top || 0) };
	}

	/** The free area as fractions of the canvas, at FULL engagement — what the framers size against. */
	_freeFrac() {
		const w = Math.max(this.mount?.clientWidth || 0, 320), h = Math.max(this.mount?.clientHeight || 0, 320);
		const i = this._inspectInset || {};
		return { fx: Math.max(0.3, 1 - (i.right || 0) / w), fy: Math.max(0.3, 1 - ((i.bottom || 0) + (i.top || 0)) / h) };
	}

	_applyViewportInset() {
		if (!this.camera) return;
		const w = Math.max(this.mount?.clientWidth || 0, 320);
		const h = Math.max(this.mount?.clientHeight || 0, 320);
		const k = this._inspectK || 0, i = this._inspectInset || {};
		const right = Math.max(this._viewportInsetRight || 0, (i.right || 0) * k);
		const bottom = (i.bottom || 0) * k, top = (i.top || 0) * k;
		// shift content → the free area's centre: up by half the sheet, down by half the HUD band (§94.7-1)
		if (right > 0 || bottom > 0 || top > 0) this.camera.setViewOffset(w, h, right / 2, (bottom - top) / 2, w, h);
		else this.camera.clearViewOffset();
		this.camera.updateProjectionMatrix();
	}

	/** ASK-142 R7 — one rAF interval into the Report's ring (fps · worst frame) and into the dynamic-
	 *  resolution governor. A hidden tab's gap (> 1 s) is not a frame anybody saw: it is skipped. */
	_tickFrameStats() {
		const now = performance.now(), ms = now - (this._frameAt || now);
		this._frameAt = now;
		if (!(ms > 0) || ms > 1000) return;
		this._frameMs[this._frameN++ % this._frameMs.length] = ms;
		if (this._drsOff || this._cameraPinned || this._frameHeld()) return;   // a capture harness wants a fixed drawing buffer
		const dpr = this._drs.push(ms);
		if (dpr) {
			this.renderer.setPixelRatio(dpr);
			console.log(`[drs] dpr → ${dpr} (rung ${this._drs.level + 1}/${this._drs.levels.length})`);
		}
	}

	/** ASK-142 R7 — the frame numbers the Report sends: fps and worst frame over the last ~10 s. */
	frameStats() {
		const n = Math.min(this._frameN, this._frameMs.length);
		let sum = 0, worst = 0;
		for (let i = 0; i < n; i++) { const v = this._frameMs[i]; sum += v; if (v > worst) worst = v; }
		return { fps: n ? +(1000 * n / sum).toFixed(1) : null, worstFrameMs: n ? Math.round(worst) : null, windowMs: Math.round(sum), frames: n, dpr: this.renderer.getPixelRatio(), drsRung: this._drs.level + 1, drsRungs: this._drs.levels.length, drsDrops: this._drs.drops };
	}

	/** Ease the inspect offset toward "held" or "not held" — the house 260 ms, frame-rate independent. */
	_tickInspectFrame(dt) {
		const want = this.activeFixtureHero ? 1 : 0, k = this._inspectK || 0;
		if (k === want) return;
		const next = want > k ? Math.min(1, k + dt / 0.26) : Math.max(0, k - dt / 0.26);
		this._inspectK = next;
		this._applyViewportInset();
	}

	animate() {
		requestAnimationFrame(this.animate);
		const dt = Math.min(this.clock.getDelta(), 0.1);
		this._tickFrameStats();
		// ASK-142 §8.3 2 — re-check covers waiting to be drawn (a lid opened, a seat got parented).
		this._coverPokeT = (this._coverPokeT || 0) + dt;
		if (this._coverPokeT > 0.2) { this._coverPokeT = 0; this._coverStream.poke(); }
		this.updateAnimations(dt);
		this._tickInspectFrame(dt);
		this._syncBoxHint();   // F-CU16a — a box that opens (or closes) re-decides its prompt, hover or not
		if (!this.cameraAnim.active) {
			this.controls.update();
		}
		// ASK-087 slice-c: the immersive FPS block (room-clamp · step-climb · portal triggers ·
		// head-bob) is Play-only. In Edit the author orbits freely — OrbitControls.update() above
		// still runs (rotate/zoom enabled), but the camera is neither clamped to the room nor
		// walked, so it can pull out to frame the whole shop.
		//
		// `_cameraPinned` (ASK-106 CI camera-jitter item, 2026-08-25): a capture harness owns the
		// camera outright — do not walk it. Step-climbing lerps camera.y toward `groundY + 1.7m`
		// over ~e^{-2t}, so a frame shot N seconds after an external aim is shot MID-LERP: the
		// pixel gate's camera height was a function of the runner's frame rate, which is what made
		// `verify-golden` a coin-flip the retry masked (3-4 px of frame-wide vertical shift =
		// 10-18% of pixels vs a 1.5% budget). Play/Edit behaviour is untouched — nothing in the
		// app sets this flag; only `render-truth-knobs.mjs` does, and only around a capture.
		// ASK-142 F-142e: not under a browse pose either — step-climbing would lift the lowered eye back to 1.7 m.
		if (!this.cameraAnim.active && !this._cameraAuthoring && !this._cameraPinned && !this._browse) {
			if (this.sceneKind === "hub") this._hubLife?.walk ? this._hubLife.walk.settle() : this._clampCameraToHub();
			else if (this._shellRoomBounds || this.currentScene?.room) this._clampCameraToRoom();
			if (this.sceneKind !== "hub") this._applyStepClimbing(dt);
			this._checkTriggers();
			this._applyHeadBob(dt);
		}
		this._animatePortalWaypoints();
		this._doorTick?.(dt);   // ASK-177 §10 — the door prompt, its prefetch and the entry push (lazy `_hub-entrance.js`; null elsewhere)
		this._v2DoorTick?.(dt);   // v2 kit doors swing (lazy `_hub-v2-dressing.js`, set by collectV2KitDoors; null elsewhere)
		this._zoomRig?.tick(dt);   // ASK-156 — the boom + the mannequin follow the eye the block above just placed
		this._presence?.tick(dt);   // ASK-169 — the room: our record out (8 Hz moving / 1 Hz still), the others drawn 150 ms behind (lazy; null when presence is off)
		if (this._hubLife) this._hubLife.tick(dt);   // ASK-146 §16 — the hub-v3 street's ambient life (lazy chunk; null elsewhere)
		this._updateCompass();
		// When pointer is locked the cursor is fixed at screen centre — hover
		// detection has to run per-frame against the crosshair so longboxes /
		// comics / books / spinner racks highlight as the player aims at them.
		if (this._mouseLook?.locked) this._updateHoverFromCenter();
		// ASK-127 E4/F2 — hold the presented frame while a pack activation/deactivation settles, so the
		// swap lands as ONE transition instead of fixtures-then-props. Everything above still ticks
		// (controls, animations, hover): only the PRESENT is deferred, and `_frameHeld` self-releases
		// at its ceiling so this can never wedge the viewport.
		if (!this._frameHeld()) this._render();
		this._updateHeroScreenPos();
	}

	_updateHeroScreenPos() {
		if (!this.onHeroPosUpdate) return;

		let worldPos = null;

		if (this.boxState === "hero" && this.heroComicIndex !== null) {
			const hero = this.comicEntries[this.heroComicIndex];
			if (hero?.root) {
				worldPos = new THREE.Vector3();
				hero.root.getWorldPosition(worldPos);
			}
		} else if (this.boxState === "wallComic" && this.wallComicHeroEntry) {
			worldPos = this.wallComicHeroEntry.heroPos.clone();
		} else if (this.pileState === "hero" && this.pileHeroEntry) {
			worldPos = this.pileHeroEntry.heroPos.clone();
		} else if (this.bookState === "hero" && this.bookHeroEntry) {
			const mesh = this.bookHeroEntry.entry.mesh;
			if (mesh) {
				worldPos = new THREE.Vector3();
				mesh.getWorldPosition(worldPos);
			}
		}

		if (!worldPos) { this.onHeroPosUpdate(null); return; }

		const rect = this.renderer.domElement.getBoundingClientRect();
		const p = worldPos.project(this.camera);
		this.onHeroPosUpdate({
			x: (p.x + 1) * 0.5 * rect.width  + rect.left,
			y: (-p.y + 1) * 0.5 * rect.height + rect.top,
		});
	}
}

// ─── Report helpers ──────────────────────────────────────────────────────────

function createEmptyReport() {
	return {
		summary: "Runtime test did not finish.",
		meta: [],
		fixture: { label: "Fixture render", mode: "none", errors: [], warnings: [] },
		content: { label: "Comic render", mode: "none", errors: [], warnings: [] },
	};
}

// Builds an emissive image panel (for shop signage). Optional UV crop lets a
// single brand-pack image expose only one cell (e.g. the projecting-sign panel).
function makeImagePanel(textureLoader, assetUrl, opts = {}) {
	const tex = textureLoader.load(assetUrl);
	tex.colorSpace = THREE.SRGBColorSpace;
	tex.anisotropy = 8;
	tex.minFilter = THREE.LinearMipmapLinearFilter;
	tex.magFilter = THREE.LinearFilter;
	if (opts.uv) {
		tex.repeat.set(opts.uv.repeatX ?? 1, opts.uv.repeatY ?? 1);
		tex.offset.set(opts.uv.offsetX ?? 0, opts.uv.offsetY ?? 0);
	}
	const w = opts.width  || 1;
	const h = opts.height || 1;
	const isEmissive = opts.emissive !== false;
	const material = isEmissive
		? new THREE.MeshStandardMaterial({
			color:           0x000000,        // base diffuse off — only emissive contributes
			emissive:        0xffffff,
			emissiveMap:     tex,
			emissiveIntensity: opts.emissiveIntensity ?? 1.5,
			toneMapped:      false,           // keep neon-bright through ACES
			side:            THREE.DoubleSide,
			transparent:     opts.transparent === true,
		})
		: new THREE.MeshBasicMaterial({
			map:        tex,
			toneMapped: false,
			side:       THREE.DoubleSide,
			transparent: opts.transparent === true,
		});
	return new THREE.Mesh(new THREE.PlaneGeometry(w, h), material);
}

// Builds a flat THREE.Mesh with a CanvasTexture rendering wrapped text.
// Used by placeholder shops for "Coming Soon — <name>" panels.
function makeCanvasTextPanel(text, widthMeters = 3, heightMeters = 1.5) {
	const canvas = document.createElement("canvas");
	canvas.width  = 1024;
	canvas.height = Math.round(1024 * (heightMeters / widthMeters));
	const ctx = canvas.getContext("2d");

	// Dark backdrop with a thin amber border (matches the Kapow palette)
	ctx.fillStyle = "#1a1014";
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.strokeStyle = "#e6a347";
	ctx.lineWidth = 8;
	ctx.strokeRect(8, 8, canvas.width - 16, canvas.height - 16);

	ctx.fillStyle = "#e6a347";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	const lines = String(text).split(/\r?\n/);
	const lineHeight = Math.round(canvas.height / (lines.length + 1));
	const fontSize = Math.round(lineHeight * 0.7);
	ctx.font = `bold ${fontSize}px sans-serif`;
	const startY = canvas.height / 2 - (lines.length - 1) * lineHeight / 2;
	lines.forEach((line, i) => {
		ctx.fillText(line, canvas.width / 2, startY + i * lineHeight);
	});

	const tex = new THREE.CanvasTexture(canvas);
	tex.colorSpace = THREE.SRGBColorSpace;
	tex.minFilter = THREE.LinearFilter;
	tex.magFilter = THREE.LinearFilter;

	return new THREE.Mesh(
		new THREE.PlaneGeometry(widthMeters, heightMeters),
		new THREE.MeshBasicMaterial({ map: tex, transparent: false, toneMapped: false }),
	);
}

// ─── Product resolution ───────────────────────────────────────────────────────

// ─── Fixture type check ───────────────────────────────────────────────────────

// ─── Asset inspection ─────────────────────────────────────────────────────────

function inspectComicAsset(gltf) {
	const root = gltf?.scene;
	const errors = [];
	const warnings = [];
	const guideNodes = [];

	root?.traverse?.((node) => {
		if (isGuideNode(node)) {
			guideNodes.push(node.name || "(unnamed guide)");
		}
	});

	for (const name of requiredComicNodes) {
		if (!getContractNode(root, name)) {
			errors.push(`Missing required node ${name}.`);
		}
	}

	if (guideNodes.length) {
		warnings.push(`${guideNodes.length} guide/proxy helper node${guideNodes.length === 1 ? "" : "s"} are still present.`);
	}

	return {
		valid: !errors.length,
		errors,
		warnings,
		optionalFound: optionalComicNodes.filter((name) => getContractNode(root, name)),
	};
}

// ─── Proxy geometry ───────────────────────────────────────────────────────────

// addOutline / removeOutline now in helpers.js — imported above.

// Box3.setFromObject in Three.js r183 doesn't respect visibility, so hidden
// authoring helpers inflate the bbox. This version only measures visible meshes.

// boxForwardFromAnchors now in helpers.js — imported above.

// ─── Procedural comic geometry ────────────────────────────────────────────────

function createProceduralComicTemplate(sourceMetrics = boxedComicReferenceMeters) {
	const metrics = {
		width: Number(sourceMetrics?.width) || boxedComicReferenceMeters.width,
		height: Number(sourceMetrics?.height) || boxedComicReferenceMeters.height,
		thickness: Number(sourceMetrics?.thickness) || boxedComicReferenceMeters.thickness,
	};
	const group = new THREE.Group();
	group.name = "comic_root";
	group.userData = {
		kapowx: {
			semanticName: "comic_root",
			semanticRole: "contract-root",
		},
	};

	const surfaceOffset = 0.00002;
	const surfaceDepth = metrics.thickness + surfaceOffset * 2;

	const pages = new THREE.Mesh(
		createPagesEdgeGeometry(metrics),
		new THREE.MeshStandardMaterial({ color: new THREE.Color("#f4eee3"), roughness: 0.92, metalness: 0.01 }),
	);
	pages.name = "pages_block";
	pages.position.set(0, metrics.height * 0.5, 0);
	pages.userData = { kapowx: { semanticName: "pages_block", semanticRole: "contract-surface" } };
	pages.castShadow = true;
	pages.receiveShadow = true;
	group.add(pages);

	const front = new THREE.Mesh(
		new THREE.PlaneGeometry(metrics.width, metrics.height),
		new THREE.MeshStandardMaterial({ color: new THREE.Color("#ffffff"), roughness: 0.78, metalness: 0.02 }),
	);
	front.name = "front_surface";
	front.position.set(0, metrics.height * 0.5, metrics.thickness * 0.5 + surfaceOffset);
	front.userData = {
		kapowx: { semanticName: "front_surface", semanticRole: "contract-surface" },
		kapowxPreviewTextureTransform: { flipV: true },
	};
	front.castShadow = true;
	front.receiveShadow = true;
	group.add(front);

	const back = new THREE.Mesh(
		new THREE.PlaneGeometry(metrics.width, metrics.height),
		new THREE.MeshStandardMaterial({ color: new THREE.Color("#f7f4ef"), roughness: 0.8, metalness: 0.02 }),
	);
	back.name = "back_surface";
	back.position.set(0, metrics.height * 0.5, -(metrics.thickness * 0.5 + surfaceOffset));
	back.rotation.y = Math.PI;
	back.userData = {
		kapowx: { semanticName: "back_surface", semanticRole: "contract-surface" },
		kapowxPreviewTextureTransform: { flipV: true },
	};
	back.castShadow = true;
	back.receiveShadow = true;
	group.add(back);

	const spine = new THREE.Mesh(
		new THREE.PlaneGeometry(surfaceDepth, metrics.height),
		new THREE.MeshStandardMaterial({ color: new THREE.Color("#d5d7df"), roughness: 0.8, metalness: 0.02 }),
	);
	spine.name = "spine_surface";
	spine.position.set(-(metrics.width * 0.5 + surfaceOffset), metrics.height * 0.5, 0);
	spine.rotation.y = -Math.PI * 0.5;
	spine.userData = {
		kapowx: { semanticName: "spine_surface", semanticRole: "contract-surface" },
		kapowxPreviewTextureTransform: { flipV: true },
	};
	spine.castShadow = true;
	spine.receiveShadow = true;
	group.add(spine);

	return group;
}

function createPagesEdgeGeometry(metrics) {
	const width = metrics.width;
	const height = metrics.height;
	const thickness = metrics.thickness;
	const x0 = -width * 0.5;
	const x1 = width * 0.5;
	const y0 = -height * 0.5;
	const y1 = height * 0.5;
	const z = thickness * 0.5;
	const positions = new Float32Array([
		x1, y0, z, x1, y0, -z, x1, y1, -z, x1, y1, z,
		x0, y1, z, x1, y1, z, x1, y1, -z, x0, y1, -z,
		x0, y0, -z, x1, y0, -z, x1, y0, z, x0, y0, z,
	]);
	const normals = new Float32Array([
		1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0,
		0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0,
		0, -1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0,
	]);
	const uvs = new Float32Array([
		0, 0, 1, 0, 1, 1, 0, 1,
		0, 0, 1, 0, 1, 1, 0, 1,
		0, 0, 1, 0, 1, 1, 0, 1,
	]);
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
	geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
	geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
	geometry.setIndex([0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7, 8, 9, 10, 8, 10, 11]);
	return geometry;
}

// ─── Material helpers ─────────────────────────────────────────────────────────

function cloneTextureWithTransform(texture, transform = null) {
	if (!texture) {
		return null;
	}

	const nextTexture = texture.clone();
	nextTexture.needsUpdate = true;

	if (transform?.flipU) {
		nextTexture.wrapS = THREE.RepeatWrapping;
		nextTexture.repeat.x = -1;
		nextTexture.offset.x = 1;
	}

	if (transform?.flipV) {
		nextTexture.wrapT = THREE.RepeatWrapping;
		nextTexture.repeat.y = -1;
		nextTexture.offset.y = 1;
	}

	return nextTexture;
}

function getComicTextureTransform(node) {
	if (node.userData?.kapowxPreviewTextureTransform) {
		return node.userData.kapowxPreviewTextureTransform;
	}

	const semanticName = node.userData?.kapowx?.semanticName || node.name;
	return semanticName === "front_surface" || semanticName === "back_surface" || semanticName === "spine_surface"
		? { flipV: true }
		: null;
}

function applyMaterial(mesh, texture, colorHex) {
	mesh.material = mesh.material.clone();
	mesh.material.color = new THREE.Color(colorHex);
	mesh.material.map = texture;
	mesh.material.roughness = 1.0;
	mesh.material.metalness = 0;
	mesh.material.envMapIntensity = 0;
	mesh.material.needsUpdate = true;
}

// ─── Scene graph helpers ──────────────────────────────────────────────────────

function stripGuideNodes(root) {
	const removals = [];
	root.traverse((node) => {
		if (isGuideNode(node)) {
			removals.push(node);
		}
	});
	removals.forEach((node) => {
		if (node.parent) {
			node.parent.remove(node);
		}
	});
}

function isGuideNode(node) {
	const name = node?.name || "";
	return name.startsWith("__guide__") || name.startsWith("__proxy__");
}

function getContractNode(root, semanticName) {
	let match = null;
	root?.traverse?.((node) => {
		if (match) return;
		const exportedName = node.userData?.kapowx?.semanticName || node.userData?.kapowxAnchorName;
		if (exportedName === semanticName || node.name === semanticName) {
			match = node;
		}
	});
	return match;
}

// ─── Anchor helpers ───────────────────────────────────────────────────────────

// getStorageLaneSlotQuaternion + normalizeLaneVector now in helpers.js — imported above.

function capitalizeLabel(value) {
	const text = String(value || "").trim();
	return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "Comic";
}
